/**
 * Visual Config Editor
 * Form-based editor for creating and editing WebXR experiences
 */

// Initialize Sentry as early as possible
import * as Sentry from "@sentry/browser";

Sentry.init({
  dsn: "https://98f444c73599f68edcf65d9db57c665f@o4510773732245504.ingest.us.sentry.io/4510774427189248",
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
  ],
  _experiments: {
    enableLogs: true,
  },
  tracesSampleRate: 1.0,
  tracePropagationTargets: ["localhost", /^https:\/\/.*\.xrgallery\.io/],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

import { requireAuth, getCurrentUser, onAuthChange, logOut } from '../src/firebase/auth';
import { getSequence, updateSequence, getShareableLink, type Sequence } from '../src/api/SequenceAPI';
import type { ExperienceConfig, ExperienceStage, SkyboxConfig, ModelConfig, PlaneConfig, HotspotConfig, LightConfig, TextureVariant } from '../src/ui/UIManager';
import type { PostProcessingConfig, VRSettingsConfig } from '../src/types/config';
import { FORM_PRESETS, type LeadFormConfig, type LeadFormType, type FormField, type FormFieldType, FORM_LIMITS_BY_TIER, isFormLimitReached, canCreateForms } from '../src/types/forms';
import { EditorPreview } from './EditorPreview';
import { generateAndUploadThumbnail, waitForSceneSettle, uploadCustomThumbnail } from '../src/utils/ThumbnailGenerator';
import {
  getUserProfile,
  ensureUserProfile,
  hasFeatureAccess,
  type UserProfile,
  getStorageLimit,
  getMuxMinutesLimit,
  getMuxMinutesUsed,
  getMuxMinutesRemaining,
  canUploadMuxVideo,
  formatMuxMinutes,
  TIER_CONFIG,
} from '../src/api/UserProfileAPI';
import { getUsernameByUserId } from '../src/api/UsernameAPI';
import { uploadFile, uploadFileWithQuotaCheck, type AssetType, formatFileSize } from '../src/utils/FileUploader';
import { TextureOptimizer } from '../src/utils/TextureOptimizer';
import { PanelManager } from './PanelManager';
import { showUpgradeModal, showFeatureLockedModal } from '../src/ui/UpgradeModal';
import { showAlert, showConfirm, showCustomModal, closeModal } from '../src/ui/ModalManager';
import { openLocationPicker } from './MapLocationPicker';
import { openBatchUploadModal } from './BatchUploadModal';
import { openSceneMapEditor } from './SceneMapEditor';
import * as AIAPI from '../src/api/AIAPI';
import { showUsageModal, showSettingsModal, setupDropdownToggle } from '../src/ui/UserDropdown';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getVideoUploader, type UploadStatus } from '../src/media/VideoUploader';

// State
let sequenceId: string | null = null;
let sequence: Sequence | null = null;
let config: ExperienceConfig | null = null;
let currentStageIndex: number = 0;
let hasUnsavedChanges: boolean = false;
let isIntentionalNavigation: boolean = false; // Flag to bypass beforeunload warning
let editorPreview: EditorPreview | null = null;
let previewVisible: boolean = true;
let currentUser: any = null;
let currentUserProfile: UserProfile | null = null;
let tourOwnerUsername: string | null = null; // The tour owner's username (differs from currentUserProfile when admin edits another user's tour)
let panelManager: PanelManager | null = null;

// DOM Elements
const headerTitle = document.getElementById('headerTitle') as HTMLElement;
const cancelBtn = document.getElementById('cancelBtn') as HTMLButtonElement;
// const togglePreviewBtn = document.getElementById('togglePreviewBtn') as HTMLButtonElement; // Removed
const previewBtn = document.getElementById('previewBtn') as HTMLButtonElement;
const saveBtn = document.getElementById('saveBtn') as HTMLButtonElement;
const saveBtnText = document.getElementById('saveBtnText') as HTMLElement;
const successMessage = document.getElementById('successMessage') as HTMLElement;

const userDropdown = document.getElementById('user-dropdown') as HTMLElement;
const userDropdownToggle = document.getElementById('user-dropdown-toggle') as HTMLElement;
const userEmailSpan = document.getElementById('user-email') as HTMLElement;
const logoutBtn = document.getElementById('logout-btn') as HTMLButtonElement;
const userSettingsBtn = document.getElementById('user-settings-btn') as HTMLButtonElement;
const userUsageBtn = document.getElementById('user-usage-btn') as HTMLButtonElement;

// Preview panel elements (docked panel)
const resetCameraFloatBtn = document.getElementById('resetCameraFloatBtn') as HTMLButtonElement;

const stageList = document.getElementById('stageList') as HTMLElement;
const addStageBtn = document.getElementById('addStageBtn') as HTMLButtonElement;

const currentStageTitle = document.getElementById('currentStageTitle') as HTMLElement;
const currentStageSubtitle = document.getElementById('currentStageSubtitle') as HTMLElement;

// Tab system
const tabs = document.querySelectorAll('.tab');
const tabContents = document.querySelectorAll('.tab-content');

// Skybox elements
const skyboxTypeImage = document.getElementById('skyboxTypeImage') as HTMLInputElement;
const skyboxTypeVideo = document.getElementById('skyboxTypeVideo') as HTMLInputElement;
const skyboxTypeColor = document.getElementById('skyboxTypeColor') as HTMLInputElement;
const skyboxUrl = document.getElementById('skyboxUrl') as HTMLInputElement;
const skyboxUrlGroup = document.getElementById('skyboxUrlGroup') as HTMLElement;
const skyboxColor = document.getElementById('skyboxColor') as HTMLInputElement;
const skyboxColorHex = document.getElementById('skyboxColorHex') as HTMLInputElement;
const skyboxColorGroup = document.getElementById('skyboxColorGroup') as HTMLElement;
const skyboxRotation = document.getElementById('skyboxRotation') as HTMLInputElement;
const skyboxRotationNumber = document.getElementById('skyboxRotationNumber') as HTMLInputElement;
const skyboxRotationGroup = document.getElementById('skyboxRotationGroup') as HTMLElement;
const skyboxVideoOptions = document.getElementById('skyboxVideoOptions') as HTMLElement;
const skyboxAutoplay = document.getElementById('skyboxAutoplay') as HTMLInputElement;
const skyboxLoop = document.getElementById('skyboxLoop') as HTMLInputElement;
const clearSkyboxBtn = document.getElementById('clearSkyboxBtn') as HTMLButtonElement;

// Models elements
const modelsList = document.getElementById('modelsList') as HTMLElement;
const addModelBtn = document.getElementById('addModelBtn') as HTMLButtonElement;
const uploadModelBtn = document.getElementById('uploadModelBtn') as HTMLButtonElement;
const modelFileInput = document.getElementById('modelFileInput') as HTMLInputElement;

// Planes elements
const planesList = document.getElementById('planesList') as HTMLElement;
const addPlaneBtn = document.getElementById('addPlaneBtn') as HTMLButtonElement;
const uploadPlaneBtn = document.getElementById('uploadPlaneBtn') as HTMLButtonElement;
const planeFileInput = document.getElementById('planeFileInput') as HTMLInputElement;

// Hotspots elements
const hotspotsList = document.getElementById('hotspotsList') as HTMLElement;
const addHotspotClickBtn = document.getElementById('addHotspotClickBtn') as HTMLButtonElement;

// Initial camera target (first scene only)
const initialCameraSection = document.getElementById('initialCameraSection') as HTMLElement;
const setCameraTargetBtn = document.getElementById('setCameraTargetBtn') as HTMLButtonElement;

// Navigation elements (portals & floor arrows)
const portalsList = document.getElementById('portalsList') as HTMLElement;
const addPortalClickBtn = document.getElementById('addPortalClickBtn') as HTMLButtonElement;
const addFloorArrowClickBtn = document.getElementById('addFloorArrowClickBtn') as HTMLButtonElement;

// Lighting elements
const lightsList = document.getElementById('lightsList') as HTMLElement;
const addLightBtn = document.getElementById('addLightBtn') as HTMLButtonElement;
const presetStudio = document.getElementById('presetStudio') as HTMLButtonElement;
const presetOutdoor = document.getElementById('presetOutdoor') as HTMLButtonElement;
const presetDramatic = document.getElementById('presetDramatic') as HTMLButtonElement;
const presetNight = document.getElementById('presetNight') as HTMLButtonElement;
const presetClear = document.getElementById('presetClear') as HTMLButtonElement;

// Audio elements (scene-specific)
const audioTitle = document.getElementById('audioTitle') as HTMLInputElement;
const audioUrl = document.getElementById('audioUrl') as HTMLInputElement;
const sceneAudioVolume = document.getElementById('sceneAudioVolume') as HTMLInputElement;
const sceneAudioVolumeValue = document.getElementById('sceneAudioVolumeValue') as HTMLElement;
const clearAudioBtn = document.getElementById('clearAudioBtn') as HTMLButtonElement;
const audioHotspotsList = document.getElementById('audioHotspotsList') as HTMLElement;
const addAudioHotspotClickBtn = document.getElementById('addAudioHotspotClickBtn') as HTMLButtonElement;

// Global audio elements (persists across all scenes)
const globalAudioTitle = document.getElementById('globalAudioTitle') as HTMLInputElement;
const globalAudioUrl = document.getElementById('globalAudioUrl') as HTMLInputElement;
const globalAudioVolume = document.getElementById('globalAudioVolume') as HTMLInputElement;
const globalAudioVolumeValue = document.getElementById('globalAudioVolumeValue') as HTMLElement;
const clearGlobalAudioBtn = document.getElementById('clearGlobalAudioBtn') as HTMLButtonElement;
const uploadGlobalAudioBtn = document.getElementById('uploadGlobalAudioBtn') as HTMLButtonElement;
const globalAudioFileInput = document.getElementById('globalAudioFileInput') as HTMLInputElement;

// Audio preview elements
const audioPreviewContainer = document.getElementById('audioPreviewContainer') as HTMLElement;
const audioPreviewPlayBtn = document.getElementById('audioPreviewPlayBtn') as HTMLButtonElement;
const audioPreviewTitle = document.getElementById('audioPreviewTitle') as HTMLElement;
const audioPreviewTime = document.getElementById('audioPreviewTime') as HTMLElement;
const audioPreviewVolumeSlider = document.getElementById('audioPreviewVolumeSlider') as HTMLInputElement;
const audioPreviewProgress = document.getElementById('audioPreviewProgress') as HTMLElement;
const audioPreviewProgressFill = document.getElementById('audioPreviewProgressFill') as HTMLElement;
const audioPreviewElement = document.getElementById('audioPreviewElement') as HTMLAudioElement;

// Placement overlay elements
const placementOverlay = document.getElementById('placementOverlay') as HTMLElement;
const placementOverlaySubtitle = document.getElementById('placementOverlaySubtitle') as HTMLElement;
const cancelPlacementBtn = document.getElementById('cancelPlacementBtn') as HTMLButtonElement;

// Hotspot/portal creation mode state flags (fixes second creation bug)
let isHotspotCreationMode = false;
let isPortalCreationMode = false;
let isFloorArrowCreationMode = false;
let isAudioHotspotCreationMode = false;
let isCameraTargetMode = false;

// Settings elements
const stageName = document.getElementById('stageName') as HTMLInputElement;
const deleteStageBtn = document.getElementById('deleteStageBtn') as HTMLButtonElement;

// Sequence settings elements (in floating panel)
const sequenceNamePanel = document.getElementById('sequenceNamePanel') as HTMLInputElement;
const sequenceDescriptionPanel = document.getElementById('sequenceDescriptionPanel') as HTMLTextAreaElement;
const sequencePublicPanel = document.getElementById('sequencePublicPanel') as HTMLInputElement;
const sequenceDiscoverablePanel = document.getElementById('sequenceDiscoverablePanel') as HTMLInputElement;
const discoverableGroupPanel = document.getElementById('discoverableGroupPanel') as HTMLElement;
const sequencePasswordPanel = document.getElementById('sequencePasswordPanel') as HTMLInputElement;
const sequenceAnalyticsPanel = document.getElementById('sequenceAnalyticsPanel') as HTMLInputElement;
const enableTransitionsPanel = document.getElementById('enableTransitionsPanel') as HTMLInputElement;
const enableGazeNavigationPanel = document.getElementById('enableGazeNavigationPanel') as HTMLInputElement;

// Portal transition settings
const portalGlowColorPanel = document.getElementById('portalGlowColorPanel') as HTMLInputElement;
const portalGlowColorHexPanel = document.getElementById('portalGlowColorHexPanel') as HTMLInputElement;
const portalEdgeOpacitySliderPanel = document.getElementById('portalEdgeOpacitySliderPanel') as HTMLInputElement;
const portalEdgeOpacityPanel = document.getElementById('portalEdgeOpacityPanel') as HTMLInputElement;
const portalEffectStylePanel = document.getElementById('portalEffectStylePanel') as HTMLSelectElement;

// Logo elements (white-label feature)
const logoSection = document.getElementById('logoSection') as HTMLElement;
const logoLockedState = document.getElementById('logoLockedState') as HTMLElement;
const logoProState = document.getElementById('logoProState') as HTMLElement;
const removeBrandingCheckbox = document.getElementById('removeBrandingCheckbox') as HTMLInputElement;
const removeFooterBrandingCheckbox = document.getElementById('removeFooterBrandingCheckbox') as HTMLInputElement;
const showFloorArrowTooltipsCheckbox = document.getElementById('showFloorArrowTooltipsCheckbox') as HTMLInputElement;
const uploadLogoBtn = document.getElementById('uploadLogoBtn') as HTMLButtonElement;
const logoFileInput = document.getElementById('logoFileInput') as HTMLInputElement;
const logoPreviewContainer = document.getElementById('logoPreviewContainer') as HTMLElement;
const logoPreviewImg = document.getElementById('logoPreviewImg') as HTMLImageElement;
const clearLogoBtn = document.getElementById('clearLogoBtn') as HTMLButtonElement;
const logoUpgradeBtn = document.getElementById('logoUpgradeBtn') as HTMLButtonElement;
const logoUploadProgress = document.getElementById('logoUploadProgress') as HTMLElement;
const logoProgressFill = document.getElementById('logoProgressFill') as HTMLElement;

// Portal config removed from sequence-level (now per-portal)

// Custom URL slug elements (Panel - legacy, keeping for compatibility)
const userSlugInput = document.getElementById('userSlugInput') as HTMLInputElement;
const userSlugStatus = document.getElementById('userSlugStatus') as HTMLElement;
const userSlugHelperText = document.getElementById('userSlugHelperText') as HTMLElement;
const usernameDisplay = document.getElementById('usernameDisplay') as HTMLElement;
const usernameSetupPrompt = document.getElementById('usernameSetupPrompt') as HTMLElement;
const usernameSetupInput = document.getElementById('usernameSetupInput') as HTMLInputElement;
const claimUsernameBtn = document.getElementById('claimUsernameBtn') as HTMLButtonElement;
const usernameSetupError = document.getElementById('usernameSetupError') as HTMLElement;
const slugUrlPreview = document.getElementById('slugUrlPreview') as HTMLElement;
const slugUrlPreviewText = document.getElementById('slugUrlPreviewText') as HTMLElement;
const copySlugUrlBtn = document.getElementById('copySlugUrlBtn') as HTMLButtonElement;

// Custom URL slug elements (Sidebar - primary)
const userSlugInputSidebar = document.getElementById('userSlugInputSidebar') as HTMLInputElement;
const userSlugStatusSidebar = document.getElementById('userSlugStatusSidebar') as HTMLElement;
const userSlugHelperTextSidebar = document.getElementById('userSlugHelperTextSidebar') as HTMLElement;
const usernameDisplaySidebar = document.getElementById('usernameDisplaySidebar') as HTMLElement;
const usernameSetupPromptSidebar = document.getElementById('usernameSetupPromptSidebar') as HTMLElement;
const usernameSetupInputSidebar = document.getElementById('usernameSetupInputSidebar') as HTMLInputElement;
const claimUsernameBtnSidebar = document.getElementById('claimUsernameBtnSidebar') as HTMLButtonElement;
const usernameSetupErrorSidebar = document.getElementById('usernameSetupErrorSidebar') as HTMLElement;
const usernameChangeSectionSidebar = document.getElementById('usernameChangeSectionSidebar') as HTMLElement;
const changeUsernameBtnSidebar = document.getElementById('changeUsernameBtnSidebar') as HTMLButtonElement;
const usernameChangeFormSidebar = document.getElementById('usernameChangeFormSidebar') as HTMLElement;
const usernameChangeInputSidebar = document.getElementById('usernameChangeInputSidebar') as HTMLInputElement;
const confirmUsernameChangeBtnSidebar = document.getElementById('confirmUsernameChangeBtnSidebar') as HTMLButtonElement;
const usernameChangeErrorSidebar = document.getElementById('usernameChangeErrorSidebar') as HTMLElement;
const cancelUsernameChangeBtnSidebar = document.getElementById('cancelUsernameChangeBtnSidebar') as HTMLButtonElement;
const slugUrlPreviewSidebar = document.getElementById('slugUrlPreviewSidebar') as HTMLElement;
const slugUrlPreviewTextSidebar = document.getElementById('slugUrlPreviewTextSidebar') as HTMLElement;

// Performance tab elements (removed - only using floating panel now)

// Toolbar toggle buttons
const togglePreviewPanel = document.getElementById('togglePreviewPanel') as HTMLInputElement;
const toggleSequenceSettingsPanel = document.getElementById('toggleSequenceSettingsPanel') as HTMLButtonElement;
const togglePerformancePanel = document.getElementById('togglePerformancePanel') as HTMLButtonElement;

// Floating panel close buttons
const previewPanelClose = document.getElementById('previewPanelClose') as HTMLButtonElement;
const sequenceSettingsPanelClose = document.getElementById('sequenceSettingsPanelClose') as HTMLButtonElement;
const performancePanelClose = document.getElementById('performancePanelClose') as HTMLButtonElement;
const refreshMetricsFloatBtn = document.getElementById('refreshMetricsFloatBtn') as HTMLButtonElement;
const saveSequenceSettingsBtn = document.getElementById('saveSequenceSettingsBtn') as HTMLButtonElement;

// Preview aspect ratio buttons
const aspectHorizontal = document.getElementById('aspectHorizontal') as HTMLButtonElement;
const aspectSquare = document.getElementById('aspectSquare') as HTMLButtonElement;
const aspectVertical = document.getElementById('aspectVertical') as HTMLButtonElement;

/**
 * Update the editor loading overlay
 */
function updateEditorLoading(status: string, sceneName?: string, progress?: number): void {
  const overlay = document.getElementById('editorLoadingOverlay');
  const statusEl = document.getElementById('editorLoadingStatus');
  const sceneNameEl = document.getElementById('editorLoadingSceneName');
  const progressBar = document.getElementById('editorLoadingBar');

  if (statusEl) statusEl.textContent = status;
  if (sceneNameEl && sceneName) sceneNameEl.textContent = sceneName;
  if (progressBar && progress !== undefined) progressBar.style.width = `${progress}%`;
}

/**
 * Hide the editor loading overlay
 */
function hideEditorLoading(): void {
  const overlay = document.getElementById('editorLoadingOverlay');
  if (overlay) {
    overlay.classList.add('hidden');
  }
}

/**
 * Initialize editor
 */
async function init() {
  try {
    updateEditorLoading('Authenticating...', '', 10);

    // Wait for authentication
    currentUser = await waitForAuth();
    if (!currentUser) {
      console.warn('⚠️ No user authenticated, redirecting');
      window.location.href = '/dashboard/auth.html';
      return;
    }

    console.log('✅ User authenticated:', currentUser.email);
    updateEditorLoading('Loading profile...', '', 20);

    // Update user email in dropdown
    if (userEmailSpan) {
      userEmailSpan.textContent = currentUser.email || 'User';
    }

    // Load user profile and check tier
    await checkUserTier();

    // Get sequence ID from URL
    const urlParams = new URLSearchParams(window.location.search);
    sequenceId = urlParams.get('id');

    if (!sequenceId) {
      await showAlert('No sequence ID provided', 'Error', '❌');
      window.location.href = '/dashboard/';
      return;
    }

    updateEditorLoading('Loading tour...', '', 40);

    // Load sequence
    sequence = await getSequence(sequenceId);

    if (!sequence) {
      await showAlert('Sequence not found', 'Error', '❌');
      window.location.href = '/dashboard/';
      return;
    }

    // Check ownership - allow admins to edit any tour, block non-owners
    if (sequence.userId !== currentUser.uid) {
      if (currentUserProfile?.tier === 'admin') {
        // Show admin editing banner
        showAdminEditingBanner(sequence.userId);
        console.log(`🔧 Admin editing another user's tour (owner: ${sequence.userId})`);
        // Look up the tour owner's username for custom URL display
        tourOwnerUsername = await getUsernameByUserId(sequence.userId);
      } else {
        // Non-admin trying to edit someone else's tour
        await showAlert('You do not have permission to edit this tour.', 'Permission Denied', '🔒');
        window.location.href = '/dashboard/';
        return;
      }
    } else {
      // User is editing their own tour
      tourOwnerUsername = currentUserProfile?.username || null;
    }

    config = sequence.config;
    headerTitle.textContent = `Edit: ${sequence.name}`;
    updateEditorLoading('Initializing editor...', sequence.name, 60);

    // Initialize empty scenes if none exist
    if (!config.stages || config.stages.length === 0) {
      config.stages = [{
        id: generateId(),
        name: 'Scene 1'
      }];
      markUnsaved();
    }

    // Render stage list
    renderStageList();

    // Load global audio settings (tour-level, not scene-specific)
    loadGlobalAudioSettings();

    // Setup event listeners
    setupEventListeners();

    // Setup upload handlers for Pro users
    setupUploadHandlers();

    // Initialize 3D preview (hidden by default)
    initializePreview();

    // Initialize panel manager for remaining floating panels (Performance panel is legacy, Settings is now in sidebar)
    panelManager = new PanelManager();
    setupPanelToggles();

    // Setup sidebar tab switching
    setupSidebarTabs();

    // Load sidebar settings immediately so checkboxes reflect actual values
    // (prevents privacy settings from being reset when saving without visiting Settings tab)
    loadSidebarSequenceSettings();

    // Set default aspect ratio to horizontal
    if (aspectHorizontal) {
      aspectHorizontal.classList.add('active');
    }

    updateEditorLoading('Loading scene...', sequence.name, 80);

    // Select first stage
    if (config.stages.length > 0) {
      selectStage(0);

      // Force preview update after a delay to ensure engine is ready
      setTimeout(() => {
        console.log('[Editor] Delayed preview update after init');
        updatePreview();

        // Hide loading overlay after preview starts
        updateEditorLoading('Ready', sequence?.name || '', 100);
        setTimeout(() => {
          hideEditorLoading();
        }, 300);
      }, 500);
    } else {
      // No stages, hide loading immediately
      hideEditorLoading();
    }

    console.log('✅ Editor initialized');
  } catch (error: any) {
    // Hide loading overlay on error
    hideEditorLoading();

    // Ignore auth errors as they trigger redirect
    if (error.message === 'Authentication required') {
      return;
    }
    console.error('Failed to initialize editor:', error);
    await showAlert('Failed to load editor. Please try again.', 'Error', '❌');
  }
}

/**
 * Check user tier and show/hide Pro features
 */
async function checkUserTier() {
  if (!currentUser) return;

  try {
    currentUserProfile = await ensureUserProfile(currentUser.uid, currentUser.email, currentUser.displayName);

    if (!currentUserProfile) {
      console.warn('⚠️ User profile not found');
      return;
    }

    // Check if user has access to upload feature
    const hasUploadAccess = hasFeatureAccess(currentUserProfile, 'uploads');

    // Show upload buttons (always visible, but disabled for free users)
    const uploadSkyboxBtn = document.getElementById('uploadSkyboxBtn');
    const uploadAudioBtn = document.getElementById('uploadAudioBtn');
    const ktx2ToggleSection = document.getElementById('ktx2ToggleSection');

    // Self-host header for free users (shows "or host yourself" + help button)
    const selfHostHeader = document.getElementById('selfHostHeader');
    // Advanced Settings section with all URL inputs
    const skyboxAdvancedSection = document.getElementById('skyboxAdvancedToggle')?.parentElement;
    const skyboxAdvancedContent = document.getElementById('skyboxAdvancedContent');
    const skyboxAdvancedToggle = document.getElementById('skyboxAdvancedToggle');
    const skyboxAdvancedChevron = document.getElementById('skyboxAdvancedChevron');

    if (uploadSkyboxBtn) {
      uploadSkyboxBtn.style.display = 'block';
      if (!hasUploadAccess) {
        uploadSkyboxBtn.classList.add('locked');
        uploadSkyboxBtn.title = 'Upgrade to Pro to upload files';
        // Show self-host header for free users
        if (selfHostHeader) {
          selfHostHeader.style.display = 'block';
        }
        // Show Advanced Settings expanded for free users (they need all URL inputs)
        if (skyboxAdvancedSection) {
          skyboxAdvancedSection.style.display = 'block';
        }
        // Auto-expand Advanced Settings for free users
        if (skyboxAdvancedContent) {
          skyboxAdvancedContent.style.display = 'block';
        }
        if (skyboxAdvancedToggle) {
          skyboxAdvancedToggle.style.borderRadius = 'var(--radius-md) var(--radius-md) 0 0';
        }
        if (skyboxAdvancedChevron) {
          skyboxAdvancedChevron.style.transform = 'rotate(180deg)';
        }
        // Move rotation slider inside the section for free users (after URL inputs)
        const skyboxRotationGroup = document.getElementById('skyboxRotationGroup');
        if (skyboxRotationGroup && skyboxAdvancedContent) {
          skyboxAdvancedContent.appendChild(skyboxRotationGroup);
          skyboxRotationGroup.style.marginTop = '16px';
          skyboxRotationGroup.style.paddingTop = '16px';
          skyboxRotationGroup.style.borderTop = '1px solid rgba(0,0,0,0.08)';
        }
        // Rename "Advanced Settings" to "Skybox URLs" for free users (it's their main input, not advanced)
        if (skyboxAdvancedToggle) {
          const labelSpan = skyboxAdvancedToggle.querySelector('span');
          if (labelSpan) {
            labelSpan.innerHTML = `
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
              Skybox URLs
            `;
          }
        }
        // Hide the "Manual Entry" info box for free users (it's obvious they need to enter URLs)
        const manualVariantInputs = document.getElementById('manualVariantInputs');
        const infoBox = manualVariantInputs?.querySelector('div[style*="background: rgba(74, 144, 226"]');
        if (infoBox) {
          (infoBox as HTMLElement).style.display = 'none';
        }
      } else {
        // Pro users: Show KTX2 toggle, hide self-host header
        if (ktx2ToggleSection) {
          ktx2ToggleSection.style.display = 'block';
        }
        if (selfHostHeader) {
          selfHostHeader.style.display = 'none';
        }
        // Show Advanced Settings for Pro users (collapsed by default)
        if (skyboxAdvancedSection) {
          skyboxAdvancedSection.style.display = 'block';
        }
      }
    }

    if (uploadAudioBtn) {
      uploadAudioBtn.style.display = 'flex';
      if (!hasUploadAccess) {
        uploadAudioBtn.classList.add('locked');
        uploadAudioBtn.title = 'Upgrade to Pro to upload files';
      }
    }

    // Show global audio upload button
    if (uploadGlobalAudioBtn) {
      uploadGlobalAudioBtn.style.display = 'flex';
      if (!hasUploadAccess) {
        uploadGlobalAudioBtn.classList.add('locked');
        uploadGlobalAudioBtn.title = 'Upgrade to Pro to upload files';
      }
    }

    if (hasUploadAccess) {
      console.log(`✅ Pro features enabled for ${currentUserProfile.tier} tier`);
    } else {
      console.log(`ℹ️ Free tier - uploads locked`);
    }

    // Check if user has white-label access for logo feature
    const hasWhiteLabelAccess = hasFeatureAccess(currentUserProfile, 'white_label');
    if (logoLockedState && logoProState) {
      if (hasWhiteLabelAccess) {
        logoLockedState.style.display = 'none';
        logoProState.style.display = 'block';
        console.log('✅ White-label logo feature enabled');
      } else {
        logoLockedState.style.display = 'block';
        logoProState.style.display = 'none';
        console.log('ℹ️ White-label logo feature locked');
      }
    }

    // Show/hide admin dashboard button based on tier
    const adminDashboardBtn = document.getElementById('admin-dashboard-btn');
    if (adminDashboardBtn) {
      adminDashboardBtn.style.display = currentUserProfile.tier === 'admin' ? 'flex' : 'none';
    }

    // Show/hide newsletter admin button based on tier
    const newsletterAdminBtn = document.getElementById('newsletter-admin-btn');
    if (newsletterAdminBtn) {
      newsletterAdminBtn.style.display = currentUserProfile.tier === 'admin' ? 'flex' : 'none';
    }

    // Show/hide blog admin button based on tier
    const blogAdminBtn = document.getElementById('blog-admin-btn');
    if (blogAdminBtn) {
      blogAdminBtn.style.display = currentUserProfile.tier === 'admin' ? 'flex' : 'none';
    }

    // AI Tools section - always accessible so users can add their own API key (BYOK)
    // The individual AI tools inside are disabled for free users, but BYOK section stays enabled
    const hasAIAccess = hasFeatureAccess(currentUserProfile, 'ai_generation');
    const aiToolsSection = document.getElementById('aiToolsSection');
    const aiToolsToggle = document.getElementById('aiToolsToggle');
    const aiToolsContent = document.getElementById('aiToolsContent');
    const aiUsageDisplay = document.getElementById('aiUsageDisplay');

    if (aiToolsSection && aiToolsToggle) {
      // Always keep section clickable so users can access BYOK
      aiToolsSection.style.opacity = '1';
      aiToolsSection.style.pointerEvents = 'auto';
      aiToolsToggle.style.cursor = 'pointer';

      if (!hasAIAccess) {
        // User doesn't have AI feature - show tooltip encouraging BYOK
        aiToolsToggle.title = 'Add your own API key to unlock AI features';
        // Grey out the quota display (they'll use their own key)
        if (aiUsageDisplay) {
          aiUsageDisplay.style.opacity = '0.5';
        }
        // Disable AI buttons (will be enabled if they add BYOK key)
        setAIButtonsEnabled(false);
        console.log('ℹ️ AI Tools: No platform access - BYOK available');
      } else {
        // Ensure it's enabled for paid users
        aiToolsToggle.title = '';
        if (aiUsageDisplay) {
          aiUsageDisplay.style.opacity = '1';
        }
        console.log('✅ AI Tools enabled');
      }
    }

    // Check BYOK status (may enable AI buttons for users with personal key)
    await checkBYOKStatus();
  } catch (error) {
    console.error('Error checking user tier:', error);
  }
}

/**
 * Setup upload handlers for Pro users
 */
function setupUploadHandlers() {
  // Check if user has Pro access
  const hasUploadAccess = currentUserProfile && hasFeatureAccess(currentUserProfile, 'uploads');

  // Skybox upload
  const uploadSkyboxBtn = document.getElementById('uploadSkyboxBtn');
  const skyboxFileInput = document.getElementById('skyboxFileInput') as HTMLInputElement;

  uploadSkyboxBtn?.addEventListener('click', async () => {
    // If locked, show upgrade modal
    if (!hasUploadAccess) {
      if (currentUser) {
        await showUpgradeModal(currentUser.uid, currentUser.email);
      }
      return;
    }

    // Determine asset type based on current skybox type
    const skyboxType = (document.querySelector('input[name="skyboxType"]:checked') as HTMLInputElement)?.value;
    const assetType: AssetType = skyboxType === 'video' ? 'video' : 'image';
    skyboxFileInput.accept = assetType === 'video' ? 'video/*' : 'image/*';
    skyboxFileInput.click();
  });

  skyboxFileInput?.addEventListener('change', async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file && sequenceId && currentUser) {
      const skyboxType = (document.querySelector('input[name="skyboxType"]:checked') as HTMLInputElement)?.value;
      const assetType: AssetType = skyboxType === 'video' ? 'video' : 'image';

      await handleFileUpload(
        file,
        assetType,
        assetType === 'video' ? 'videoSkyboxUrl' : null,  // Only videos use single URL input
        'skyboxUploadProgress',
        'skyboxProgressFill',
        'skyboxProgressText'
      );
    }
    // Clear input so same file can be selected again
    skyboxFileInput.value = '';
  });

  // Audio upload
  const uploadAudioBtn = document.getElementById('uploadAudioBtn');
  const audioFileInput = document.getElementById('audioFileInput') as HTMLInputElement;

  uploadAudioBtn?.addEventListener('click', async () => {
    // If locked, show upgrade modal
    if (!hasUploadAccess) {
      if (currentUser) {
        await showUpgradeModal(currentUser.uid, currentUser.email);
      }
      return;
    }

    audioFileInput.click();
  });

  audioFileInput?.addEventListener('change', async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file && sequenceId && currentUser) {
      await handleFileUpload(
        file,
        'audio',
        'audioUrl',
        'audioUploadProgress',
        'audioProgressFill',
        'audioProgressText'
      );
    }
    // Clear input so same file can be selected again
    audioFileInput.value = '';
  });

  // Global audio upload (tour-wide)
  uploadGlobalAudioBtn?.addEventListener('click', async () => {
    if (!hasUploadAccess) {
      if (currentUser) {
        await showUpgradeModal(currentUser.uid, currentUser.email);
      }
      return;
    }
    globalAudioFileInput?.click();
  });

  globalAudioFileInput?.addEventListener('change', async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file || !sequenceId || !currentUser || !currentUserProfile) return;

    try {
      // Show progress
      const progressEl = document.getElementById('globalAudioUploadProgress');
      const progressFill = document.getElementById('globalAudioProgressFill');
      const progressText = document.getElementById('globalAudioProgressText');
      if (progressEl) progressEl.style.display = 'block';
      if (progressText) progressText.textContent = 'Uploading...';

      const result = await uploadFileWithQuotaCheck(
        file,
        currentUser.uid,
        sequenceId,
        'audio',
        currentUserProfile,
        (progress) => {
          if (progressFill) progressFill.style.width = `${progress.progress}%`;
          if (progressText) progressText.textContent = `Uploading... ${progress.progress}%`;
        }
      );

      // Set the URL in the input
      if (globalAudioUrl) globalAudioUrl.value = result.url;
      handleGlobalAudioChange();

      if (progressText) progressText.textContent = '✅ Upload complete!';
      setTimeout(() => {
        if (progressEl) progressEl.style.display = 'none';
      }, 2000);
    } catch (error: any) {
      console.error('Global audio upload failed:', error);
      const progressText = document.getElementById('globalAudioProgressText');
      if (progressText) progressText.textContent = `❌ Error: ${error.message}`;
    }
    globalAudioFileInput.value = '';
  });

  // Logo upload (white-label feature)
  const hasWhiteLabelAccess = currentUserProfile && hasFeatureAccess(currentUserProfile, 'white_label');

  uploadLogoBtn?.addEventListener('click', () => {
    if (!hasWhiteLabelAccess) {
      if (currentUser) {
        showUpgradeModal(currentUser.uid, currentUser.email);
      }
      return;
    }
    logoFileInput?.click();
  });

  logoFileInput?.addEventListener('change', async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file || !sequenceId || !currentUser) return;

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      await showAlert('Logo must be under 2MB', 'File Too Large', '⚠️');
      logoFileInput.value = '';
      return;
    }

    try {
      // Show progress
      if (logoUploadProgress) logoUploadProgress.style.display = 'block';

      // Upload to GCS with quota check
      if (!currentUserProfile) {
        await showAlert('User profile not loaded. Please refresh and try again.', 'Upload Error', '❌');
        return;
      }
      const result = await uploadFileWithQuotaCheck(
        file,
        currentUser.uid,
        sequenceId,
        'image',
        currentUserProfile,
        (progress) => {
          if (logoProgressFill) {
            logoProgressFill.style.width = `${progress.progress}%`;
          }
        }
      );

      // Update sequence with logo URL
      await updateSequence(sequenceId, { logoUrl: result.url });

      // Update local sequence object
      if (sequence) {
        sequence.logoUrl = result.url;
      }

      // Update preview
      updateLogoPreview(result.url);

      console.log('[Logo] Upload complete:', result.url);

    } catch (error) {
      console.error('[Logo] Upload failed:', error);
      await showAlert('Failed to upload logo. Please try again.', 'Upload Error', '❌');
    } finally {
      if (logoUploadProgress) logoUploadProgress.style.display = 'none';
      logoFileInput.value = '';
    }
  });

  clearLogoBtn?.addEventListener('click', async () => {
    if (!sequenceId) return;

    const confirmed = await showConfirm(
      'Are you sure you want to remove the custom logo?',
      'Remove Logo',
      'Remove',
      'Cancel'
    );

    if (!confirmed) return;

    try {
      await updateSequence(sequenceId, { logoUrl: null });

      // Update local sequence object
      if (sequence) {
        sequence.logoUrl = undefined;
      }

      updateLogoPreview(null);
      console.log('[Logo] Removed successfully');
    } catch (error) {
      console.error('[Logo] Failed to remove:', error);
      await showAlert('Failed to remove logo. Please try again.', 'Error', '❌');
    }
  });

  logoUpgradeBtn?.addEventListener('click', async () => {
    if (currentUser) {
      await showUpgradeModal(currentUser.uid, currentUser.email);
    }
  });

  // Remove branding checkbox handler
  removeBrandingCheckbox?.addEventListener('change', async () => {
    if (!sequenceId) return;

    const removeBranding = removeBrandingCheckbox.checked;

    try {
      await updateSequence(sequenceId, { removeBranding });

      // Update local sequence object
      if (sequence) {
        sequence.removeBranding = removeBranding;
      }

      console.log('[Branding] Remove branding:', removeBranding);
    } catch (error) {
      console.error('[Branding] Failed to update:', error);
      // Revert checkbox on error
      removeBrandingCheckbox.checked = !removeBranding;
      await showAlert('Failed to update branding setting. Please try again.', 'Error', '❌');
    }
  });

  // Remove footer branding checkbox handler
  removeFooterBrandingCheckbox?.addEventListener('change', async () => {
    if (!sequenceId) return;

    const removeFooterBranding = removeFooterBrandingCheckbox.checked;

    try {
      await updateSequence(sequenceId, { removeFooterBranding });

      // Update local sequence object
      if (sequence) {
        sequence.removeFooterBranding = removeFooterBranding;
      }

      console.log('[Branding] Remove footer branding:', removeFooterBranding);
    } catch (error) {
      console.error('[Branding] Failed to update footer branding:', error);
      // Revert checkbox on error
      removeFooterBrandingCheckbox.checked = !removeFooterBranding;
      await showAlert('Failed to update footer branding setting. Please try again.', 'Error', '❌');
    }
  });

  // Show floor arrow tooltips checkbox handler
  showFloorArrowTooltipsCheckbox?.addEventListener('change', () => {
    if (!config) return;

    config.showFloorArrowTooltips = showFloorArrowTooltipsCheckbox.checked;
    markUnsaved();
    console.log('[Settings] Show floor arrow tooltips:', config.showFloorArrowTooltips);
  });
}

/**
 * Handle file upload with progress tracking
 * For images, auto-generates optimized multi-resolution variants
 */
async function handleFileUpload(
  file: File,
  assetType: AssetType,
  urlInputId: string | null,  // Null for multi-variant image uploads
  progressContainerId: string,
  progressFillId: string,
  progressTextId: string
): Promise<void> {
  const progressContainer = document.getElementById(progressContainerId);
  const progressFill = document.getElementById(progressFillId);
  const progressText = document.getElementById(progressTextId);
  const urlInput = urlInputId ? document.getElementById(urlInputId) as HTMLInputElement : null;

  if (!progressContainer || !progressFill || !progressText) {
    console.error('Upload UI elements not found');
    return;
  }

  try {
    // Show progress
    progressContainer.style.display = 'block';

    // AUTO-OPTIMIZATION FOR IMAGES
    if (assetType === 'image') {
      // Capture original skybox URL before upload (for version history tracking)
      const currentStage = config?.stages?.[currentStageIndex];
      const originalSkyboxUrl = currentStage?.skybox?.url;

      console.log(`🎨 Auto-optimizing image: ${file.name} (${formatFileSize(file.size)})`);

      // Step 1: Generate all variants
      progressText.textContent = 'Generating optimized variants...';
      progressFill.style.width = '10%';

      // Check if KTX2 compression is enabled
      const enableKTX2Checkbox = document.getElementById('enableKTX2') as HTMLInputElement;
      const useKTX2 = enableKTX2Checkbox?.checked || false;

      const variants = await TextureOptimizer.generateAllVariants(
        file,
        useKTX2,  // Pass KTX2 preference
        (variantName, progress) => {
          if (progress === 100) {
            console.log(`  ✓ Generated ${variantName}`);
          }
        }
      );

      const savings = TextureOptimizer.calculateSavings(file.size, variants);
      console.log(`🎉 Optimization complete! Generated ${variants.length} variants`);
      console.log(`   Total size: ${formatFileSize(savings.totalSize)} (${savings.savingsPercent.toFixed(1)}% savings)`);

      progressFill.style.width = '20%';

      // Step 2: Upload all variants
      const uploadedVariants: Record<string, TextureVariant> = {};
      let uploadedCount = 0;

      for (const variant of variants) {
        const variantFile = new File([variant.blob], file.name.replace(/\.[^/.]+$/, `-${variant.name}.jpg`), {
          type: 'image/jpeg'
        });

        progressText.textContent = `Uploading ${variant.name} variant (${uploadedCount + 1}/${variants.length})...`;

        const result = await uploadFileWithQuotaCheck(
          variantFile,
          currentUser!.uid,
          sequenceId!,
          'image',
          currentUserProfile!,
          (progress) => {
            const baseProgress = 20 + (uploadedCount * 80 / variants.length);
            const variantProgress = (progress.progress / 100) * (80 / variants.length);
            progressFill.style.width = `${baseProgress + variantProgress}%`;
          }
        );

        // Use actual format from TextureOptimizer (currently JPG, will be KTX2 when encoder added)
        uploadedVariants[variant.name] = {
          url: result.url,
          format: variant.format,  // Use actual format instead of misleading map
          resolution: `${variant.width}x${variant.height}`,
          fileSize: variant.size
        };

        // Note: Quality field only relevant for KTX2-ETC1S (future implementation)

        uploadedCount++;
        console.log(`  ✓ Uploaded ${variant.name}: ${result.url}`);
      }

      // Step 3: Update config with variants
      const stage = config!.stages[currentStageIndex];
      if (!stage.skybox) {
        stage.skybox = { type: 'image' };
      }

      stage.skybox.variants = uploadedVariants as any;
      // Keep desktop variant in legacy url field for backward compatibility and preview
      stage.skybox.url = uploadedVariants.desktop?.url || uploadedVariants.fallback.url;

      progressText.textContent = '✅ All variants uploaded! (5 files optimized)';
      progressFill.style.width = '100%';

      console.log('✅ Multi-resolution upload complete!');
      console.log('   Variants:', uploadedVariants);

      // Update UI and preview
      loadStageData(stage);
      updatePreview();
      renderStageList();

      // Update performance metrics (floating panel only)
      updatePerformanceMetrics(true);  // Update floating panel if visible

      // Track upload in version history (so users can revert to original if needed)
      if (sequenceId && stage.id && stage.skybox?.url) {
        try {
          console.log('[Upload] Creating version history entry...');
          await AIAPI.createUploadVersion(
            sequenceId,
            stage.id,
            stage.skybox.url,
            currentStageIndex,
            undefined,  // thumbnailUrl - will be generated separately
            originalSkyboxUrl  // Track what was there before this upload
          );
          console.log('[Upload] Version history entry created');
        } catch (versionError) {
          // Don't block upload success if version tracking fails
          console.error('[Upload] Failed to create version entry:', versionError);
        }
      }

      // Auto-save after successful upload
      console.log('[Upload] Auto-saving after multi-variant upload...');
      await handleSave();

    } else if (assetType === 'video' && urlInputId === 'videoSkyboxUrl') {
      // VIDEO SKYBOX UPLOAD - Use Mux for HLS streaming
      console.log(`🎬 Uploading video skybox to Mux:`, file.name, formatFileSize(file.size));

      const uploader = getVideoUploader();

      // Phase 1: Upload to Mux
      progressText.textContent = `Uploading "${file.name}" to cloud...`;
      progressFill.style.width = '0%';

      const uploadResult = await uploader.upload(file, (progress) => {
        progressFill.style.width = `${progress.percent * 0.5}%`; // 0-50% for upload
        progressText.textContent = `Uploading to cloud... ${progress.percent}%`;
      });

      // Phase 2: Wait for Mux transcoding
      progressFill.style.width = '50%';
      progressText.textContent = '⏳ Converting to streaming format (1-2 min)...';

      const readyResult = await uploader.waitForReady(uploadResult.uploadId, (status) => {
        if (status === 'processing') {
          progressText.textContent = '⏳ Optimizing for all devices & network speeds...';
        }
      });

      // Phase 3: Save URLs and generate thumbnail
      if (!readyResult.hlsUrl) {
        throw new Error('Video processing completed but no streaming URL was returned');
      }

      progressFill.style.width = '75%';
      progressText.textContent = '📸 Generating thumbnail...';

      // Update URL inputs
      if (urlInput) {
        urlInput.value = readyResult.mp4Url || ''; // MP4 fallback in main URL input
      }
      const hlsInput = document.getElementById('videoSkyboxHlsUrl') as HTMLInputElement;
      if (hlsInput) {
        hlsInput.value = readyResult.hlsUrl;
      }

      // Update skybox config with both URLs
      const stage = config!.stages[currentStageIndex];
      if (!stage.skybox) {
        stage.skybox = { type: 'video' };
      }
      stage.skybox.hlsUrl = readyResult.hlsUrl;
      stage.skybox.url = readyResult.mp4Url || ''; // MP4 fallback (can be empty)

      // Use Mux thumbnail if available, otherwise generate from file
      if (readyResult.thumbnailUrl) {
        stage.skybox.thumbnailUrl = readyResult.thumbnailUrl;
        console.log('✅ Using Mux thumbnail:', readyResult.thumbnailUrl);
      } else {
        // Fallback: generate thumbnail from uploaded file
        try {
          const { VideoThumbnailGenerator } = await import('../src/utils/VideoThumbnailGenerator');
          const thumbnailBlob = await VideoThumbnailGenerator.extractThumbnail(file, {
            width: 1920,
            height: 1080,
            quality: 0.85,
            timeOffset: 0.1
          });

          const thumbnailFilename = VideoThumbnailGenerator.getThumbnailFilename(file.name);
          const thumbnailFile = new File([thumbnailBlob], thumbnailFilename, { type: 'image/jpeg' });

          const thumbnailResult = await uploadFileWithQuotaCheck(
            thumbnailFile,
            currentUser!.uid,
            sequenceId!,
            'image',
            currentUserProfile!,
            () => {}
          );

          stage.skybox.thumbnailUrl = thumbnailResult.url;
          console.log('✅ Generated and uploaded thumbnail:', thumbnailResult.url);
        } catch (error) {
          console.error('Failed to generate video thumbnail:', error);
        }
      }

      progressText.textContent = '✅ Video ready for streaming!';
      progressFill.style.width = '100%';

      console.log('✅ Video skybox upload complete!');
      console.log('   HLS URL:', readyResult.hlsUrl);
      console.log('   MP4 URL:', readyResult.mp4Url || '(none)');

      // Update UI and save
      handleSkyboxChange();
      console.log('[Upload] Auto-saving after video skybox upload...');
      await handleSave();

    } else {
      // STANDARD UPLOAD (audio, models, other assets)
      progressText.textContent = `Uploading ${file.name}...`;
      progressFill.style.width = '0%';

      console.log(`📤 Uploading ${assetType}:`, file.name, formatFileSize(file.size));

      const result = await uploadFileWithQuotaCheck(
        file,
        currentUser!.uid,
        sequenceId!,
        assetType,
        currentUserProfile!,
        (progress) => {
          progressFill.style.width = `${progress.progress}%`;
          progressText.textContent = `${progress.progress}% - ${formatFileSize(progress.bytesTransferred)} / ${formatFileSize(progress.totalBytes)}`;
        }
      );

      // Update URL input with uploaded file URL (if single URL mode)
      if (urlInput) {
        urlInput.value = result.url;
        // Dispatch change event so listeners (e.g. handleAudioChange) update the config
        urlInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      progressText.textContent = '✅ Upload complete!';
      progressFill.style.width = '100%';

      console.log('✅ Upload complete:', result.url);
    }

    // Hide progress after 2 seconds
    setTimeout(() => {
      progressContainer.style.display = 'none';
    }, 2000);

  } catch (error: any) {
    console.error('❌ Upload failed:', error);
    progressText.textContent = `❌ Upload failed: ${error.message}`;
    progressFill.style.width = '0%';

    // Hide progress after 5 seconds on error
    setTimeout(() => {
      progressContainer.style.display = 'none';
    }, 5000);
  }
}

/**
 * Wait for auth state to be ready
 */
function waitForAuth(): Promise<any> {
  return new Promise((resolve) => {
    // First check if already authenticated
    const user = getCurrentUser();
    if (user) {
      resolve(user);
      return;
    }

    // Otherwise wait for auth state change
    const unsubscribe = onAuthChange((user) => {
      unsubscribe();
      resolve(user);
    });
  });
}

/**
 * Setup event listeners
 */
function setupEventListeners() {
  // Tab switching
  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const tabName = tab.getAttribute('data-tab');
      switchTab(tabName!);
    });
  });

  // Header actions
  cancelBtn.addEventListener('click', handleCancel);
  // togglePreviewBtn removed
  previewBtn.addEventListener('click', handlePreview);
  saveBtn.addEventListener('click', handleSave);

  // Preview controls (docked panel uses previewPanelClose and resetCameraFloatBtn)
  // Note: closePreviewBtn and resetCameraBtn are legacy - now handled in setupPanelToggles

  // Stage management
  addStageBtn.addEventListener('click', handleAddStage);

  // Batch upload and scene map editor
  const batchUploadBtn = document.getElementById('batchUploadBtn');
  const sceneMapEditorBtn = document.getElementById('sceneMapEditorBtn');

  batchUploadBtn?.addEventListener('click', () => {
    if (!sequenceId || !currentUser || !currentUserProfile) return;

    const hasUploadAccess = hasFeatureAccess(currentUserProfile, 'uploads');

    openBatchUploadModal({
      userId: currentUser.uid,
      sequenceId,
      hasUploadAccess,
      showUpgradeModal: () => showUpgradeModal(currentUser.uid, currentUser.email),
      onComplete: (newScenes) => {
        if (!config) return;

        // Add the new scenes to the config
        config.stages = [...config.stages, ...newScenes];
        markUnsaved();
        renderStageList();

        // Select the first new scene
        if (newScenes.length > 0) {
          currentStageIndex = config.stages.length - newScenes.length;
          loadStageData(config.stages[currentStageIndex]);
        }

        // Show scene map editor for arranging the new scenes
        showConfirm(
          'Would you like to arrange scenes on a map and auto-create navigation hotspots?',
          'Open Scene Layout Editor',
          '🗺️',
          'Open Layout Editor',
          'Skip'
        ).then((confirmed) => {
          if (confirmed) {
            sceneMapEditorBtn?.click();
          }
        });
      },
      onCancel: () => {}
    });
  });

  sceneMapEditorBtn?.addEventListener('click', () => {
    if (!config || config.stages.length < 2) {
      showAlert('You need at least 2 scenes to use the map editor.', 'Not Enough Scenes', '');
      return;
    }

    openSceneMapEditor({
      scenes: config.stages,
      onUpdate: (updatedScenes) => {
        if (!config) return;

        config.stages = updatedScenes;
        markUnsaved();
        renderStageList();
        renderPortalsList(config.stages[currentStageIndex]?.hotspots?.filter(h => h.type === 'navigation') || []);
        const currentStage = config.stages[currentStageIndex];
        if (currentStage) {
          loadStageData(currentStage);
        }
        // Refresh 3D preview to show new/updated navigation portals
        updatePreview();
      },
      onClose: () => {}
    });
  });

  // Skybox (with null checks)
  if (skyboxTypeImage) skyboxTypeImage.addEventListener('change', updateSkyboxTypeUI);
  if (skyboxTypeVideo) skyboxTypeVideo.addEventListener('change', updateSkyboxTypeUI);
  if (skyboxTypeColor) skyboxTypeColor.addEventListener('change', updateSkyboxTypeUI);

  // Video URL input
  const videoSkyboxUrl = document.getElementById('videoSkyboxUrl') as HTMLInputElement;
  if (videoSkyboxUrl) videoSkyboxUrl.addEventListener('input', handleSkyboxChange);

  // HLS URL input (for fast streaming)
  const videoSkyboxHlsUrl = document.getElementById('videoSkyboxHlsUrl') as HTMLInputElement;
  if (videoSkyboxHlsUrl) videoSkyboxHlsUrl.addEventListener('input', handleSkyboxChange);

  // Color inputs
  if (skyboxColor) skyboxColor.addEventListener('input', handleSkyboxColorChange);
  if (skyboxColorHex) skyboxColorHex.addEventListener('input', handleSkyboxColorHexChange);

  // Rotation (separate handler for all skybox types) - sync slider and number input
  if (skyboxRotation) skyboxRotation.addEventListener('input', () => handleRotationChange('slider'));
  if (skyboxRotationNumber) skyboxRotationNumber.addEventListener('input', () => handleRotationChange('number'));

  // Video options
  if (skyboxAutoplay) skyboxAutoplay.addEventListener('change', handleSkyboxChange);
  if (skyboxLoop) skyboxLoop.addEventListener('change', handleSkyboxChange);
  if (clearSkyboxBtn) clearSkyboxBtn.addEventListener('click', handleClearSkybox);

  // Self-host help button (for free users)
  const selfHostHelpBtn = document.getElementById('selfHostHelpBtn');

  if (selfHostHelpBtn) {
    selfHostHelpBtn.addEventListener('click', () => {
      showCustomModal({
        title: 'How to Self-Host Your 360° Images',
        icon: '🌐',
        type: 'custom',
        customContent: `
          <div style="text-align: left; font-size: 14px; line-height: 1.7; color: rgba(255,255,255,0.9);">
            <p style="margin-bottom: 16px; font-weight: 600; color: #4A90E2;">Free hosting options:</p>
            <ul style="margin: 0 0 20px 0; padding-left: 20px; list-style: none;">
              <li style="margin-bottom: 10px; position: relative; padding-left: 20px;">
                <span style="position: absolute; left: 0;">📦</span>
                <strong>GitHub</strong> - Upload to public repo → Raw file URL
              </li>
              <li style="margin-bottom: 10px; position: relative; padding-left: 20px;">
                <span style="position: absolute; left: 0;">☁️</span>
                <strong>Cloudflare R2</strong> - 10GB free, fast CDN
              </li>
              <li style="margin-bottom: 10px; position: relative; padding-left: 20px;">
                <span style="position: absolute; left: 0;">🖼️</span>
                <strong>Imgur</strong> - Add <code style="background: rgba(255,255,255,0.1); padding: 2px 6px; border-radius: 4px;">.jpg</code> to share URL
              </li>
              <li style="margin-bottom: 10px; position: relative; padding-left: 20px;">
                <span style="position: absolute; left: 0;">📁</span>
                <strong>Google Drive</strong> - Share as "Anyone with link"
              </li>
            </ul>

            <p style="margin-bottom: 12px; font-weight: 600; color: #4A90E2;">Image requirements:</p>
            <ul style="margin: 0 0 20px 0; padding-left: 20px; color: rgba(255,255,255,0.8);">
              <li style="margin-bottom: 6px;">✓ Equirectangular projection (2:1 ratio)</li>
              <li style="margin-bottom: 6px;">✓ 4096×2048 or 8192×4096 pixels</li>
              <li style="margin-bottom: 6px;">✓ JPG, PNG, or WebP format</li>
              <li style="margin-bottom: 6px;">✓ HTTPS URL, publicly accessible</li>
            </ul>

            <div style="background: rgba(74, 144, 226, 0.15); padding: 12px 16px; border-radius: 8px; border-left: 3px solid #4A90E2;">
              <p style="margin: 0; font-size: 13px;"><strong>💡 Quick test:</strong> Paste your URL in a new browser tab. If the image loads directly (not a webpage), it will work!</p>
            </div>
          </div>
        `,
        confirmText: 'Got it',
        showCancel: false
      });
    });
  }

  // Video thumbnail regenerate button
  const regenerateThumbnailBtn = document.getElementById('regenerateThumbnailBtn');
  if (regenerateThumbnailBtn) regenerateThumbnailBtn.addEventListener('click', regenerateVideoThumbnail);

  // Thumbnail URL input - paste an external URL
  const thumbnailUrlInput = document.getElementById('thumbnailUrlInput') as HTMLInputElement;
  if (thumbnailUrlInput) {
    const handleThumbnailUrlChange = () => {
      const stage = config?.stages?.[currentStageIndex];
      if (stage?.skybox) {
        const url = thumbnailUrlInput.value.trim();
        stage.skybox.thumbnailUrl = url || undefined;
        updateVideoThumbnailDisplay();
        markUnsaved();
      }
    };
    thumbnailUrlInput.addEventListener('change', handleThumbnailUrlChange);
    thumbnailUrlInput.addEventListener('blur', handleThumbnailUrlChange);
  }

  // Upload thumbnail button - paid users can upload, free users see upgrade modal
  const uploadThumbnailBtn = document.getElementById('uploadThumbnailBtn');
  const thumbnailFileInput = document.getElementById('thumbnailFileInput') as HTMLInputElement;
  if (uploadThumbnailBtn) {
    uploadThumbnailBtn.addEventListener('click', async () => {
      if (!currentUserProfile || getStorageLimit(currentUserProfile) === 0) {
        if (currentUser) {
          await showUpgradeModal(currentUser.uid, currentUser.email);
        }
        return;
      }
      thumbnailFileInput?.click();
    });
  }
  if (thumbnailFileInput) {
    thumbnailFileInput.addEventListener('change', () => handleVideoThumbnailUpload());
  }

  // Manual variant URL inputs (with null checks)
  const variantUrlDesktop = document.getElementById('variantUrlDesktop') as HTMLInputElement;
  const variantUrlMobile = document.getElementById('variantUrlMobile') as HTMLInputElement;
  const variantUrlTablet = document.getElementById('variantUrlTablet') as HTMLInputElement;
  const variantUrlVR = document.getElementById('variantUrlVR') as HTMLInputElement;
  const variantUrlFallback = document.getElementById('variantUrlFallback') as HTMLInputElement;

  if (variantUrlDesktop) variantUrlDesktop.addEventListener('input', handleManualVariantChange);
  if (variantUrlMobile) variantUrlMobile.addEventListener('input', handleManualVariantChange);
  if (variantUrlTablet) variantUrlTablet.addEventListener('input', handleManualVariantChange);
  if (variantUrlVR) variantUrlVR.addEventListener('input', handleManualVariantChange);
  if (variantUrlFallback) variantUrlFallback.addEventListener('input', handleManualVariantChange);

  // Skybox Advanced Settings toggle
  const skyboxAdvancedToggle = document.getElementById('skyboxAdvancedToggle');
  const skyboxAdvancedContent = document.getElementById('skyboxAdvancedContent');
  const skyboxAdvancedChevron = document.getElementById('skyboxAdvancedChevron');
  if (skyboxAdvancedToggle && skyboxAdvancedContent) {
    skyboxAdvancedToggle.addEventListener('click', () => {
      const isHidden = skyboxAdvancedContent.style.display === 'none';
      skyboxAdvancedContent.style.display = isHidden ? 'block' : 'none';
      // Update toggle button border-radius to connect with content
      skyboxAdvancedToggle.style.borderRadius = isHidden ? 'var(--radius-md) var(--radius-md) 0 0' : 'var(--radius-md)';
      if (skyboxAdvancedChevron) {
        skyboxAdvancedChevron.style.transform = isHidden ? 'rotate(180deg)' : 'rotate(0deg)';
      }
    });
  }

  // 3D Tab collapsible sections toggles
  setupCollapsibleSection('modelsToggle', 'modelsContent', 'modelsChevron');
  setupCollapsibleSection('planesToggle', 'planesContent', 'planesChevron');
  setupCollapsibleSection('lightingToggle', 'lightingContent', 'lightingChevron');

  // Models
  addModelBtn.addEventListener('click', handleAddModel);
  uploadModelBtn?.addEventListener('click', () => modelFileInput?.click());
  modelFileInput?.addEventListener('change', handleModelFileUpload);

  // Planes
  addPlaneBtn.addEventListener('click', handleAddPlane);
  uploadPlaneBtn?.addEventListener('click', () => planeFileInput?.click());
  planeFileInput?.addEventListener('change', handlePlaneFileUpload);

  // Hotspots
  addHotspotClickBtn.addEventListener('click', handleAddHotspotClick);

  // Initial camera target (first scene only)
  setCameraTargetBtn?.addEventListener('click', handleSetCameraTargetClick);

  // Navigation (portals & floor arrows)
  addPortalClickBtn.addEventListener('click', handleAddPortalClick);
  addFloorArrowClickBtn.addEventListener('click', handleAddFloorArrowClick);

  // Lighting
  addLightBtn.addEventListener('click', handleAddLight);
  presetStudio.addEventListener('click', () => applyLightingPreset('studio'));
  presetOutdoor.addEventListener('click', () => applyLightingPreset('outdoor'));
  presetDramatic.addEventListener('click', () => applyLightingPreset('dramatic'));
  presetNight.addEventListener('click', () => applyLightingPreset('night'));
  presetClear.addEventListener('click', handleClearLights);

  // Audio (scene-specific)
  audioTitle.addEventListener('input', handleAudioTitleChange);
  audioTitle.addEventListener('change', handleAudioTitleChange);
  audioUrl.addEventListener('input', handleAudioChange);
  audioUrl.addEventListener('change', handleAudioChange);
  if (sceneAudioVolume) {
    sceneAudioVolume.addEventListener('input', handleSceneAudioVolumeChange);
    sceneAudioVolume.addEventListener('change', handleSceneAudioVolumeChange);
  }
  clearAudioBtn.addEventListener('click', handleClearAudio);
  addAudioHotspotClickBtn.addEventListener('click', handleAddAudioHotspotClick);
  initAudioPreviewControls();

  // Global Audio (persists across all scenes)
  if (globalAudioTitle) globalAudioTitle.addEventListener('input', handleGlobalAudioChange);
  if (globalAudioTitle) globalAudioTitle.addEventListener('change', handleGlobalAudioChange);
  if (globalAudioUrl) globalAudioUrl.addEventListener('input', handleGlobalAudioChange);
  if (globalAudioUrl) globalAudioUrl.addEventListener('change', handleGlobalAudioChange);
  if (globalAudioVolume) {
    globalAudioVolume.addEventListener('input', handleGlobalAudioVolumeChange);
    globalAudioVolume.addEventListener('change', handleGlobalAudioChange);
  }
  if (clearGlobalAudioBtn) clearGlobalAudioBtn.addEventListener('click', handleClearGlobalAudio);

  // Placement overlay cancel button - reset all creation mode states
  cancelPlacementBtn?.addEventListener('click', () => {
    // Reset all creation mode states using the dedicated reset functions
    // These functions handle both state flags and button UI
    if (isHotspotCreationMode) {
      resetHotspotCreationMode();
    }
    if (isPortalCreationMode) {
      resetPortalCreationMode();
    }
    if (isFloorArrowCreationMode) {
      resetFloorArrowCreationMode();
    }
    if (isAudioHotspotCreationMode) {
      resetAudioHotspotCreationMode();
    }
    // Ensure overlay is hidden even if no mode was active
    hidePlacementMode();
    editorPreview?.disableHotspotCreation();
  });

  // Settings
  stageName.addEventListener('input', handleStageNameChange);
  deleteStageBtn.addEventListener('click', handleDeleteStage);

  // Sequence settings (in floating panel) - these don't affect config directly, saved separately
  if (sequencePublicPanel) {
    sequencePublicPanel.addEventListener('change', () => {
      if (discoverableGroupPanel) {
        discoverableGroupPanel.style.display = sequencePublicPanel.checked ? 'block' : 'none';
      }
    });
  }

  // Global portal settings (for UI arrow transitions)
  // Sync color picker with hex input
  if (portalGlowColorPanel && portalGlowColorHexPanel) {
    portalGlowColorPanel.addEventListener('input', () => {
      portalGlowColorHexPanel.value = portalGlowColorPanel.value;
    });
    portalGlowColorHexPanel.addEventListener('input', () => {
      // Only update if valid hex color
      if (/^#[0-9A-Fa-f]{6}$/.test(portalGlowColorHexPanel.value)) {
        portalGlowColorPanel.value = portalGlowColorHexPanel.value;
      }
    });
  }
  // Sync edge opacity slider with number input
  if (portalEdgeOpacitySliderPanel && portalEdgeOpacityPanel) {
    portalEdgeOpacitySliderPanel.addEventListener('input', () => {
      portalEdgeOpacityPanel.value = portalEdgeOpacitySliderPanel.value;
    });
    portalEdgeOpacityPanel.addEventListener('input', () => {
      const value = Math.max(0, Math.min(1, parseFloat(portalEdgeOpacityPanel.value) || 0));
      portalEdgeOpacitySliderPanel.value = String(value);
      portalEdgeOpacityPanel.value = String(value);
    });
  }

  // Performance (floating panel only)
  if (refreshMetricsFloatBtn) {
    refreshMetricsFloatBtn.addEventListener('click', () => updatePerformanceMetrics(true));
  }

  // Warn on unsaved changes
  window.addEventListener('beforeunload', (e) => {
    if (hasUnsavedChanges && !isIntentionalNavigation) {
      e.preventDefault();
      e.returnValue = '';
    }
  });

  // Listen for tier updates from Pac-Man game
  window.addEventListener('tier-updated', async () => {
    console.log('🎮 Tier updated event received! Refreshing permissions...');
    await checkUserTier();
  });

  // Note: User dropdown toggle is setup via setupDropdownToggle() call below
  // Do NOT add inline handlers here as it causes duplicate event listeners

  if (logoutBtn) {
    logoutBtn.addEventListener('click', async () => {
      try {
        await logOut();
        window.location.href = '/dashboard/auth.html';
      } catch (error) {
        console.error('Logout error:', error);
        showAlert('Failed to sign out. Please try again.', 'Error', '❌');
      }
    });
  }

  if (userSettingsBtn) {
    userSettingsBtn.addEventListener('click', async () => {
      if (currentUserProfile && currentUser) {
        await showSettingsModal(currentUserProfile, currentUser.uid);
      } else {
        await showAlert('Profile not loaded yet. Please wait.', 'Not Ready', '⏳');
      }
    });
  }

  if (userUsageBtn) {
    userUsageBtn.addEventListener('click', async () => {
      if (currentUserProfile) {
        await showUsageModal(currentUserProfile);
      } else {
        await showAlert('Profile not loaded yet. Please wait.', 'Not Ready', '⏳');
      }
    });
  }

  // User dropdown toggle - using shared function
  setupDropdownToggle();
}

// showUsageModal moved to shared UserDropdown.ts

/**
 * Setup a collapsible section toggle
 */
function setupCollapsibleSection(toggleId: string, contentId: string, chevronId: string) {
  const toggle = document.getElementById(toggleId);
  const content = document.getElementById(contentId);
  const chevron = document.getElementById(chevronId);

  if (toggle && content) {
    toggle.addEventListener('click', () => {
      const isHidden = content.style.display === 'none';
      content.style.display = isHidden ? 'block' : 'none';
      if (chevron) {
        chevron.style.transform = isHidden ? 'rotate(180deg)' : 'rotate(0deg)';
      }
    });
  }
}

/**
 * Update 3D tab section count badges
 */
function update3DTabCounts() {
  const stage = config?.stages?.[currentStageIndex];
  if (!stage) return;

  // Models count
  const modelsCount = document.getElementById('modelsCount');
  const modelsLen = stage.models?.length || 0;
  if (modelsCount) {
    modelsCount.textContent = String(modelsLen);
    modelsCount.style.display = modelsLen > 0 ? 'inline-block' : 'none';
  }

  // Planes count
  const planesCount = document.getElementById('planesCount');
  const planesLen = stage.planes?.length || 0;
  if (planesCount) {
    planesCount.textContent = String(planesLen);
    planesCount.style.display = planesLen > 0 ? 'inline-block' : 'none';
  }

  // Lights count
  const lightsCount = document.getElementById('lightsCount');
  const lightsLen = stage.lights?.length || 0;
  if (lightsCount) {
    lightsCount.textContent = String(lightsLen);
    lightsCount.style.display = lightsLen > 0 ? 'inline-block' : 'none';
  }
}

/**
 * Switch tab
 */
function switchTab(tabName: string) {
  tabs.forEach((tab) => {
    if (tab.getAttribute('data-tab') === tabName) {
      tab.classList.add('active');
    } else {
      tab.classList.remove('active');
    }
  });

  tabContents.forEach((content) => {
    if (content.id === `${tabName}-tab`) {
      content.classList.add('active');
    } else {
      content.classList.remove('active');
    }
  });

  // Auto-show human scale reference, ground plane, and attach gizmo when VR tab is active
  if (tabName === 'vr' && editorPreview) {
    editorPreview.showHumanScaleReference();
    editorPreview.showGroundPlane(10, 10);
    editorPreview.attachGizmoToHumanScale(); // Attach position gizmo with Y locked to 0
    // Sync toggle UI
    const humanScaleToggle = document.getElementById('showHumanScale') as HTMLInputElement;
    const humanScaleGlobalToggle = document.getElementById('showHumanScaleGlobal') as HTMLInputElement;
    if (humanScaleToggle) humanScaleToggle.checked = true;
    if (humanScaleGlobalToggle) humanScaleGlobalToggle.checked = true;
  } else if (editorPreview) {
    // Hide VR helpers when leaving VR tab
    editorPreview.detachGizmoFromHumanScale();
    editorPreview.hideHumanScaleReference();
    editorPreview.hideGroundPlane();
    // Sync toggle UI
    const humanScaleToggle = document.getElementById('showHumanScale') as HTMLInputElement;
    const humanScaleGlobalToggle = document.getElementById('showHumanScaleGlobal') as HTMLInputElement;
    if (humanScaleToggle) humanScaleToggle.checked = false;
    if (humanScaleGlobalToggle) humanScaleGlobalToggle.checked = false;
  }

  // Performance tab removed - metrics only available in floating panel
}

/**
 * Render stage list with collapsible details
 */
function renderStageList() {
  if (!config || !config.stages) return;

  stageList.innerHTML = '';

  config.stages.forEach((stage, index) => {
    const isActive = index === currentStageIndex;

    // Get stage info for details
    const skyboxType = stage.skybox?.type || 'none';
    const hasModels = stage.models && stage.models.length > 0;
    const hasPortals = stage.hotspots?.filter(h => h.type === 'navigation').length || 0;
    const hasHotspots = stage.hotspots?.filter(h => h.type !== 'navigation').length || 0;

    const item = document.createElement('div');
    item.className = `stage-item ${isActive ? 'active' : ''}`;
    item.draggable = true;
    item.dataset.index = String(index);

    item.innerHTML = `
      <div class="stage-item-header">
        <div class="stage-drag-handle" style="cursor: grab; padding: 0 8px; margin-right: 8px; color: var(--ios-gray);">⋮⋮</div>
        <div style="flex: 1;">
          <div class="stage-number">Stage ${index + 1}</div>
          <div class="stage-name-row" style="display: flex; align-items: center; gap: 6px;">
            <span class="stage-name-text">${stage.name || 'Unnamed Stage'}</span>
            <button type="button" class="stage-edit-name-btn" data-stage-index="${index}" title="Edit name" style="background: none; border: none; cursor: pointer; padding: 2px; opacity: 0.5; transition: opacity 0.2s;">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
              </svg>
            </button>
            <button type="button" class="stage-duplicate-btn" data-stage-index="${index}" title="Duplicate stage" style="background: none; border: none; cursor: pointer; padding: 2px; opacity: 0.5; transition: opacity 0.2s;">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </button>
            <button type="button" class="stage-delete-btn" data-stage-index="${index}" title="Delete stage" style="background: none; border: none; cursor: pointer; padding: 2px; opacity: 0.5; transition: opacity 0.2s; color: #ff3b30;">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div class="stage-details">
        <div class="stage-detail-row">
          <span class="stage-detail-label">Skybox</span>
          <span class="stage-detail-value">${skyboxType}</span>
        </div>
        ${hasModels ? `
          <div class="stage-detail-row">
            <span class="stage-detail-label">Models</span>
            <span class="stage-detail-value">${stage.models?.length || 0}</span>
          </div>
        ` : ''}
        ${hasPortals ? `
          <div class="stage-detail-row">
            <span class="stage-detail-label">Portals</span>
            <span class="stage-detail-value">${hasPortals}</span>
          </div>
        ` : ''}
        ${hasHotspots ? `
          <div class="stage-detail-row">
            <span class="stage-detail-label">Hotspots</span>
            <span class="stage-detail-value">${hasHotspots}</span>
          </div>
        ` : ''}
      </div>
    `;

    // Click header to select stage
    const header = item.querySelector('.stage-item-header');
    header?.addEventListener('click', (e) => {
      e.stopPropagation();
      selectStage(index);
    });

    // Edit name button click handler
    const editNameBtn = item.querySelector('.stage-edit-name-btn');
    editNameBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      showInlineStageNameEdit(item, index);
    });

    // Duplicate stage button click handler
    const duplicateBtn = item.querySelector('.stage-duplicate-btn');
    duplicateBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      duplicateStage(index);
    });

    // Delete stage button click handler
    const deleteBtn = item.querySelector('.stage-delete-btn');
    deleteBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      deleteStage(index);
    });

    // Hover effect for action buttons
    item.addEventListener('mouseenter', () => {
      item.querySelectorAll('.stage-edit-name-btn, .stage-duplicate-btn, .stage-delete-btn').forEach(btn => {
        (btn as HTMLElement).style.opacity = '1';
      });
    });
    item.addEventListener('mouseleave', () => {
      item.querySelectorAll('.stage-edit-name-btn, .stage-duplicate-btn, .stage-delete-btn').forEach(btn => {
        (btn as HTMLElement).style.opacity = '0.5';
      });
    });

    // Drag and drop handlers
    item.addEventListener('dragstart', (e) => {
      e.dataTransfer!.effectAllowed = 'move';
      e.dataTransfer!.setData('text/plain', String(index));
      item.classList.add('dragging');
    });

    item.addEventListener('dragend', () => {
      item.classList.remove('dragging');
    });

    item.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.dataTransfer!.dropEffect = 'move';

      const draggingItem = document.querySelector('.stage-item.dragging');
      if (draggingItem && draggingItem !== item) {
        const rect = item.getBoundingClientRect();
        const midpoint = rect.top + rect.height / 2;

        if (e.clientY < midpoint) {
          item.classList.add('drag-over-top');
          item.classList.remove('drag-over-bottom');
        } else {
          item.classList.add('drag-over-bottom');
          item.classList.remove('drag-over-top');
        }
      }
    });

    item.addEventListener('dragleave', () => {
      item.classList.remove('drag-over-top', 'drag-over-bottom');
    });

    item.addEventListener('drop', (e) => {
      e.preventDefault();
      item.classList.remove('drag-over-top', 'drag-over-bottom');

      const fromIndex = parseInt(e.dataTransfer!.getData('text/plain'));
      const toIndex = index;

      if (fromIndex !== toIndex && config && config.stages) {
        handleStageReorder(fromIndex, toIndex);
      }
    });

    stageList.appendChild(item);
  });
}

/**
 * Show inline editor for stage name
 */
function showInlineStageNameEdit(stageItem: HTMLElement, stageIndex: number) {
  if (!config || !config.stages) return;

  const stage = config.stages[stageIndex];
  const nameRow = stageItem.querySelector('.stage-name-row');
  const nameText = stageItem.querySelector('.stage-name-text');
  const editBtn = stageItem.querySelector('.stage-edit-name-btn');

  if (!nameRow || !nameText || !editBtn) return;

  // Hide the name text and edit button
  (nameText as HTMLElement).style.display = 'none';
  (editBtn as HTMLElement).style.display = 'none';

  // Create inline input
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'ios-input stage-name-inline-input';
  input.style.cssText = 'flex: 1; font-size: 13px; padding: 4px 8px; margin: -4px 0;';
  input.value = stage.name || '';
  input.placeholder = 'Scene name...';

  // Create save button
  const saveBtn = document.createElement('button');
  saveBtn.className = 'btn-icon';
  saveBtn.style.cssText = 'padding: 4px; color: var(--ios-green);';
  saveBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M20 6L9 17l-5-5"/></svg>';
  saveBtn.title = 'Save';

  // Create cancel button
  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'btn-icon';
  cancelBtn.style.cssText = 'padding: 4px; color: var(--ios-red);';
  cancelBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>';
  cancelBtn.title = 'Cancel';

  // Wrapper for buttons
  const btnWrapper = document.createElement('div');
  btnWrapper.style.cssText = 'display: flex; gap: 2px;';
  btnWrapper.appendChild(saveBtn);
  btnWrapper.appendChild(cancelBtn);

  // Insert into row
  nameRow.appendChild(input);
  nameRow.appendChild(btnWrapper);

  // Focus and select
  input.focus();
  input.select();

  // Save function
  const saveEdit = () => {
    const newName = input.value.trim() || `Scene ${stageIndex + 1}`;
    stage.name = newName;

    // Update UI
    nameText.textContent = newName;

    // Cleanup
    input.remove();
    btnWrapper.remove();
    (nameText as HTMLElement).style.display = '';
    (editBtn as HTMLElement).style.display = '';

    // Also update the header if this is the current stage
    if (stageIndex === currentStageIndex) {
      currentStageTitle.textContent = newName;
      stageName.value = newName;
    }

    markUnsaved();
    showToast('Scene name updated', 'success');
  };

  // Cancel function
  const cancelEdit = () => {
    input.remove();
    btnWrapper.remove();
    (nameText as HTMLElement).style.display = '';
    (editBtn as HTMLElement).style.display = '';
  };

  // Event handlers
  saveBtn.onclick = (e) => {
    e.stopPropagation();
    saveEdit();
  };

  cancelBtn.onclick = (e) => {
    e.stopPropagation();
    cancelEdit();
  };

  input.addEventListener('keydown', (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  });

  input.addEventListener('click', (e) => {
    e.stopPropagation();
  });
}

/**
 * Handle stage reorder via drag and drop
 */
function handleStageReorder(fromIndex: number, toIndex: number) {
  if (!config || !config.stages) return;

  console.log(`[Reorder] Moving stage from ${fromIndex} to ${toIndex}`);

  // Remove item from old position
  const [movedStage] = config.stages.splice(fromIndex, 1);

  // Insert at new position
  config.stages.splice(toIndex, 0, movedStage);

  // Update current stage index if needed
  if (currentStageIndex === fromIndex) {
    currentStageIndex = toIndex;
  } else if (fromIndex < currentStageIndex && toIndex >= currentStageIndex) {
    currentStageIndex--;
  } else if (fromIndex > currentStageIndex && toIndex <= currentStageIndex) {
    currentStageIndex++;
  }

  // Re-render and update
  renderStageList();
  updatePreview();
  markUnsaved();

  console.log(`[Reorder] Stage moved successfully. New current index: ${currentStageIndex}`);
}

/**
 * Select stage
 */
function selectStage(index: number) {
  if (!config || !config.stages || index < 0 || index >= config.stages.length) return;

  currentStageIndex = index;
  const stage = config.stages[index];

  // Update header
  currentStageTitle.textContent = stage.name || `Scene ${index + 1}`;
  currentStageSubtitle.textContent = `Configure the content for this stage`;

  // Update toolbar indicator
  const stageIndicator = document.getElementById('stageIndicator');
  if (stageIndicator) {
    stageIndicator.textContent = `Stage ${index + 1} / ${config.stages.length}`;
  }

  // Update stage list
  renderStageList();

  // Load stage data into forms
  loadStageData(stage);

  // Update 3D preview (loading indicator is shown automatically by updateStage)
  updatePreview();

  // Clear and reload version history for the new stage
  clearVersionHistoryForStage();
}

/**
 * Display skybox variants if multi-resolution upload exists
 */
/**
 * Check if variants were from an actual upload (have fileSize metadata) vs manual URL entry
 */
function isUploadedVariants(variants: any): boolean {
  if (!variants) return false;
  // Check if any variant has fileSize - indicating it came from an actual upload
  const variantNames = ['desktop', 'mobile', 'tablet', 'vr', 'fallback'];
  return variantNames.some(name => variants[name]?.fileSize !== undefined);
}

/**
 * Copy URL to clipboard and show toast
 */
function copyVariantUrl(url: string, variantName: string) {
  navigator.clipboard.writeText(url).then(() => {
    showToast(`${variantName} URL copied to clipboard`, 'success');
  }).catch(() => {
    showToast('Failed to copy URL', 'error');
  });
}

/**
 * Handle variant URL edit - update the specific variant without auto-conversion
 */
function updateVariantUrl(variantName: string, newUrl: string) {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];

  if (!stage.skybox) {
    stage.skybox = { type: 'image', url: '' };
  }
  if (!stage.skybox.variants) {
    stage.skybox.variants = { fallback: { url: '', format: 'jpg' } };
  }

  // Update or create the specific variant (preserve existing metadata if present)
  // Use non-null assertion since we just ensured variants exists above
  const existingVariant = stage.skybox.variants![variantName as keyof typeof stage.skybox.variants];
  if (newUrl.trim()) {
    (stage.skybox.variants as any)[variantName] = {
      url: newUrl.trim(),
      format: existingVariant?.format || 'jpg',
      resolution: existingVariant?.resolution || 'unknown',
      ...(existingVariant?.fileSize && { fileSize: existingVariant.fileSize })
    };
  } else {
    // Remove variant if URL is empty
    delete (stage.skybox.variants as any)[variantName];
  }

  // Update legacy URL to desktop or fallback for backward compatibility
  const desktopUrl = stage.skybox.variants?.desktop?.url;
  const fallbackUrl = stage.skybox.variants?.fallback?.url;
  stage.skybox.url = desktopUrl || fallbackUrl || '';

  markUnsaved();
  updatePreview();
}

/**
 * Replace a variant with a new uploaded file
 */
function replaceVariantWithUpload(variantName: string) {
  // Check if user has upload access (Pro+ only)
  const hasUploadAccess = currentUserProfile && hasFeatureAccess(currentUserProfile, 'uploads');
  if (!hasUploadAccess) {
    showUpgradeModal('uploads', 'Individual device uploads require a Pro subscription');
    return;
  }

  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.onchange = async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file || !config || !config.stages) return;

    const stage = config.stages[currentStageIndex];

    // Upload the file to Firebase Storage
    try {
      showToast(`Uploading ${variantName} image...`, 'info');

      // Dynamic import Firebase Storage to avoid undefined errors
      const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
      const { storage } = await import('../src/firebase/config');

      const path = `uploads/${currentUser.uid}/${sequenceId}/images/${variantName}_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, path);

      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);

      // Update the variant
      if (!stage.skybox) {
        stage.skybox = { type: 'image', url: '' };
      }
      if (!stage.skybox.variants) {
        stage.skybox.variants = {} as any;
      }

      (stage.skybox.variants as any)[variantName] = {
        url: downloadUrl,
        format: file.type.includes('png') ? 'png' : 'jpg',
        resolution: 'uploaded',
        fileSize: file.size
      };

      // Update legacy URL
      const desktopUrl = stage.skybox.variants?.desktop?.url;
      const fallbackUrl = stage.skybox.variants?.fallback?.url;
      stage.skybox.url = desktopUrl || fallbackUrl || '';

      markUnsaved();
      updatePreview();
      displaySkyboxVariants(stage);

      showToast(`${variantName} image uploaded successfully`, 'success');
    } catch (error: any) {
      console.error('[Upload] Failed:', error);
      showToast(`Upload failed: ${error.message}`, 'error');
    }
  };
  input.click();
}

/**
 * Updates the skybox upload button text based on whether a skybox exists
 * Shows "Upload Skybox" when empty, "Replace Skybox" when one exists
 */
function updateSkyboxUploadButtonText() {
  const uploadBtnText = document.getElementById('uploadBtnText');
  if (!uploadBtnText) return;

  const stage = config.stages[currentStageIndex];
  const hasSkybox = stage?.skybox && (
    stage.skybox.url ||
    stage.skybox.hlsUrl ||
    stage.skybox.variants?.fallback?.url ||
    stage.skybox.color
  );

  uploadBtnText.textContent = hasSkybox ? 'Replace Skybox' : 'Upload Skybox';
}

function displaySkyboxVariants(stage: ExperienceStage) {
  const variantDisplay = document.getElementById('variantDisplay');
  const variantList = document.getElementById('variantList');
  const manualVariantInputs = document.getElementById('manualVariantInputs');

  if (!variantDisplay || !variantList) return;

  // Always show variant display with editable URLs (unified UI)
  if (manualVariantInputs) manualVariantInputs.style.display = 'none';
  variantDisplay.style.display = 'block';
  variantList.innerHTML = '';

  // Auto-expand Advanced Settings to show the variants
  const skyboxAdvancedContent = document.getElementById('skyboxAdvancedContent');
  const skyboxAdvancedChevron = document.getElementById('skyboxAdvancedChevron');
  const skyboxAdvancedToggle = document.getElementById('skyboxAdvancedToggle');
  if (skyboxAdvancedContent) {
    skyboxAdvancedContent.style.display = 'block';
    if (skyboxAdvancedToggle) {
      skyboxAdvancedToggle.style.borderRadius = 'var(--radius-md) var(--radius-md) 0 0';
    }
    if (skyboxAdvancedChevron) {
      skyboxAdvancedChevron.style.transform = 'rotate(180deg)';
    }
  }

  const variants = stage.skybox?.variants || {};
  const variantOrder: Array<'desktop' | 'mobile' | 'tablet' | 'vr' | 'fallback'> = ['desktop', 'mobile', 'tablet', 'vr', 'fallback'];

  // Create a compact grid layout
  const gridContainer = document.createElement('div');
  gridContainer.style.cssText = 'display: flex; flex-direction: column; gap: 6px;';

  variantOrder.forEach((name) => {
    const variant = (variants as any)[name];
    const url = variant?.url || (name === 'fallback' && stage.skybox?.url) || '';

    const row = document.createElement('div');
    row.style.cssText = 'display: flex; align-items: center; gap: 8px;';

    // Label with icon
    const label = document.createElement('div');
    label.style.cssText = 'width: 70px; font-size: 12px; font-weight: 500; color: var(--text-secondary); display: flex; align-items: center; gap: 4px;';
    const icon = name === 'desktop' ? '🖥️' : name === 'mobile' ? '📱' : name === 'tablet' ? '📲' : name === 'vr' ? '🥽' : '📁';
    label.innerHTML = `${icon} <span style="text-transform: capitalize;">${name}</span>`;

    // URL input
    const urlInput = document.createElement('input');
    urlInput.type = 'url';
    urlInput.className = 'ios-input';
    urlInput.style.cssText = 'flex: 1; font-size: 11px; padding: 6px 8px; height: 32px;';
    urlInput.placeholder = url ? '' : 'Not set';
    urlInput.value = url;
    urlInput.dataset.variant = name;
    urlInput.addEventListener('change', (e) => {
      updateVariantUrl(name, (e.target as HTMLInputElement).value);
    });

    // Action buttons container
    const actions = document.createElement('div');
    actions.style.cssText = 'display: flex; gap: 2px;';

    // Copy button
    const copyBtn = document.createElement('button');
    copyBtn.className = 'btn-icon';
    copyBtn.style.cssText = 'padding: 4px; width: 28px; height: 28px; opacity: ' + (url ? '1' : '0.3') + ';';
    copyBtn.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>';
    copyBtn.title = 'Copy URL';
    copyBtn.disabled = !url;
    copyBtn.onclick = (e) => { e.preventDefault(); if (url) copyVariantUrl(url, name); };

    // Upload button - check if user has upload access
    const uploadAccessEnabled = currentUserProfile && hasFeatureAccess(currentUserProfile, 'uploads');
    const uploadBtn = document.createElement('button');
    uploadBtn.className = 'btn-icon';
    uploadBtn.style.cssText = 'padding: 4px; width: 28px; height: 28px;' + (uploadAccessEnabled ? '' : ' opacity: 0.5;');
    uploadBtn.innerHTML = uploadAccessEnabled
      ? '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>'
      : '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>';
    uploadBtn.title = uploadAccessEnabled ? (url ? 'Replace' : 'Upload') : 'Pro feature - Upgrade to upload';
    uploadBtn.onclick = (e) => { e.preventDefault(); replaceVariantWithUpload(name); };

    // View button
    const viewBtn = document.createElement('a');
    viewBtn.className = 'btn-icon';
    viewBtn.style.cssText = 'padding: 4px; width: 28px; height: 28px; text-decoration: none; display: flex; align-items: center; justify-content: center; opacity: ' + (url ? '1' : '0.3') + ';';
    viewBtn.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"/></svg>';
    viewBtn.title = 'Open';
    if (url) {
      viewBtn.href = url;
      viewBtn.target = '_blank';
    }

    actions.appendChild(copyBtn);
    actions.appendChild(uploadBtn);
    actions.appendChild(viewBtn);

    row.appendChild(label);
    row.appendChild(urlInput);
    row.appendChild(actions);
    gridContainer.appendChild(row);
  });

  variantList.appendChild(gridContainer);
}

/**
 * Load stage data into forms
 */
function loadStageData(stage: ExperienceStage) {
  // Show/hide initial camera section (only visible for first scene)
  const initialCameraSectionEl = document.getElementById('initialCameraSection');
  if (initialCameraSectionEl) {
    initialCameraSectionEl.style.display = currentStageIndex === 0 ? 'block' : 'none';
  }

  // Skybox
  if (stage.skybox) {
    if (stage.skybox.type === 'video') {
      if (skyboxTypeVideo) skyboxTypeVideo.checked = true;
      if (skyboxVideoOptions) skyboxVideoOptions.style.display = 'block';

      const skyboxVideoUrlGroup = document.getElementById('skyboxVideoUrlGroup');
      if (skyboxVideoUrlGroup) skyboxVideoUrlGroup.style.display = 'block';

      // Show HLS URL group for video type
      const skyboxHlsUrlGroup = document.getElementById('skyboxHlsUrlGroup');
      if (skyboxHlsUrlGroup) skyboxHlsUrlGroup.style.display = 'block';

      // Show video preview group and update thumbnail display
      const skyboxVideoPreviewGroup = document.getElementById('skyboxVideoPreviewGroup');
      if (skyboxVideoPreviewGroup) skyboxVideoPreviewGroup.style.display = 'block';
      updateVideoThumbnailDisplay();

      if (skyboxColorGroup) skyboxColorGroup.style.display = 'none';
      if (skyboxRotationGroup) skyboxRotationGroup.style.display = 'block';

      const videoSkyboxUrl = document.getElementById('videoSkyboxUrl') as HTMLInputElement;
      if (videoSkyboxUrl) videoSkyboxUrl.value = stage.skybox.url || '';

      // Populate HLS URL field
      const videoSkyboxHlsUrl = document.getElementById('videoSkyboxHlsUrl') as HTMLInputElement;
      if (videoSkyboxHlsUrl) videoSkyboxHlsUrl.value = stage.skybox.hlsUrl || '';

      if (skyboxAutoplay) skyboxAutoplay.checked = stage.skybox.autoplay !== false;
      if (skyboxLoop) skyboxLoop.checked = stage.skybox.loop !== false;

      // Hide variant display and manual inputs for videos
      const variantDisplay = document.getElementById('variantDisplay');
      const manualVariantInputs = document.getElementById('manualVariantInputs');
      if (variantDisplay) variantDisplay.style.display = 'none';
      if (manualVariantInputs) manualVariantInputs.style.display = 'none';

    } else if (stage.skybox.type === 'color') {
      if (skyboxTypeColor) skyboxTypeColor.checked = true;
      if (skyboxVideoOptions) skyboxVideoOptions.style.display = 'none';

      const skyboxVideoUrlGroup = document.getElementById('skyboxVideoUrlGroup');
      if (skyboxVideoUrlGroup) skyboxVideoUrlGroup.style.display = 'none';

      // Hide HLS URL group for color type
      const skyboxHlsUrlGroup = document.getElementById('skyboxHlsUrlGroup');
      if (skyboxHlsUrlGroup) skyboxHlsUrlGroup.style.display = 'none';

      // Hide video preview group for color type
      const skyboxVideoPreviewGroup = document.getElementById('skyboxVideoPreviewGroup');
      if (skyboxVideoPreviewGroup) skyboxVideoPreviewGroup.style.display = 'none';

      if (skyboxColorGroup) skyboxColorGroup.style.display = 'block';
      if (skyboxRotationGroup) skyboxRotationGroup.style.display = 'none';

      const color = stage.skybox.color || '#000000';
      if (skyboxColor) skyboxColor.value = color;
      if (skyboxColorHex) skyboxColorHex.value = color;

    } else { // image
      if (skyboxTypeImage) skyboxTypeImage.checked = true;
      if (skyboxVideoOptions) skyboxVideoOptions.style.display = 'none';

      const skyboxVideoUrlGroup = document.getElementById('skyboxVideoUrlGroup');
      if (skyboxVideoUrlGroup) skyboxVideoUrlGroup.style.display = 'none';

      // Hide HLS URL group for image type
      const skyboxHlsUrlGroup = document.getElementById('skyboxHlsUrlGroup');
      if (skyboxHlsUrlGroup) skyboxHlsUrlGroup.style.display = 'none';

      // Hide video preview group for image type
      const skyboxVideoPreviewGroup = document.getElementById('skyboxVideoPreviewGroup');
      if (skyboxVideoPreviewGroup) skyboxVideoPreviewGroup.style.display = 'none';

      if (skyboxColorGroup) skyboxColorGroup.style.display = 'none';
      if (skyboxRotationGroup) skyboxRotationGroup.style.display = 'block';

      // Check if multi-variant upload exists
      displaySkyboxVariants(stage);
    }
    const rotationVal = String(stage.skybox.rotation || 0);
    if (skyboxRotation) skyboxRotation.value = rotationVal;
    if (skyboxRotationNumber) skyboxRotationNumber.value = rotationVal;
  } else {
    if (skyboxTypeImage) skyboxTypeImage.checked = true;
    if (skyboxRotation) skyboxRotation.value = '0';
    if (skyboxRotationNumber) skyboxRotationNumber.value = '0';
    if (skyboxVideoOptions) skyboxVideoOptions.style.display = 'none';

    const skyboxVideoUrlGroup = document.getElementById('skyboxVideoUrlGroup');
    if (skyboxVideoUrlGroup) skyboxVideoUrlGroup.style.display = 'none';

    // Hide HLS URL group when no skybox
    const skyboxHlsUrlGroup = document.getElementById('skyboxHlsUrlGroup');
    if (skyboxHlsUrlGroup) skyboxHlsUrlGroup.style.display = 'none';

    // Hide video preview group when no skybox
    const skyboxVideoPreviewGroup = document.getElementById('skyboxVideoPreviewGroup');
    if (skyboxVideoPreviewGroup) skyboxVideoPreviewGroup.style.display = 'none';

    if (skyboxColorGroup) skyboxColorGroup.style.display = 'none';
    if (skyboxRotationGroup) skyboxRotationGroup.style.display = 'block';

    // Show manual variant inputs and clear them (no skybox = empty form)
    const manualVariantInputs = document.getElementById('manualVariantInputs');
    if (manualVariantInputs) manualVariantInputs.style.display = 'block';

    const variantDisplay = document.getElementById('variantDisplay');
    if (variantDisplay) variantDisplay.style.display = 'none';

    // Clear all variant URL inputs when stage has no skybox
    const variantUrlDesktop = document.getElementById('variantUrlDesktop') as HTMLInputElement;
    const variantUrlMobile = document.getElementById('variantUrlMobile') as HTMLInputElement;
    const variantUrlTablet = document.getElementById('variantUrlTablet') as HTMLInputElement;
    const variantUrlVR = document.getElementById('variantUrlVR') as HTMLInputElement;
    const variantUrlFallback = document.getElementById('variantUrlFallback') as HTMLInputElement;
    if (variantUrlDesktop) variantUrlDesktop.value = '';
    if (variantUrlMobile) variantUrlMobile.value = '';
    if (variantUrlTablet) variantUrlTablet.value = '';
    if (variantUrlVR) variantUrlVR.value = '';
    if (variantUrlFallback) variantUrlFallback.value = '';
  }

  // Models
  renderModelsList(stage.models || []);

  // Planes
  renderPlanesList(stage.planes || []);

  // Hotspots (Info/Audio)
  renderInfoHotspotsList(stage.hotspots || []);

  // Portals (Navigation)
  renderPortalsList(stage.hotspots || []);

  // Incoming floor arrows from other stages
  renderIncomingArrows(stage);

  // Lighting
  renderLightsList(stage.lights || []);

  // Update 3D tab section count badges
  update3DTabCounts();

  // Audio
  audioTitle.value = stage.audioTitle || '';
  audioUrl.value = stage.audioUrl || '';
  // Load scene audio volume (default 80%)
  const volumePercent = Math.round((stage.audioVolume ?? 0.8) * 100);
  if (sceneAudioVolume) sceneAudioVolume.value = String(volumePercent);
  if (sceneAudioVolumeValue) sceneAudioVolumeValue.textContent = `${volumePercent}%`;
  renderAudioHotspotsList(stage.hotspots || []);

  // Show audio preview if there's an existing audio URL
  if (stage.audioUrl) {
    showAudioPreview(stage.audioUrl, stage.audioTitle || 'Background Audio');
  } else {
    hideAudioPreview();
  }

  // Settings
  stageName.value = stage.name || '';

  // Update skybox upload button text based on whether skybox exists
  updateSkyboxUploadButtonText();

  // Update AI edit sections visibility based on skybox presence
  updateAIEditSectionsVisibility();

  // Load Post-Processing (Effects tab) settings
  loadPostProcessingSettings(stage.postProcessing);

  // Load VR Settings
  loadVRSettings(stage.vrSettings);
}

/**
 * Render models list with improved transform controls
 */
function renderModelsList(models: ModelConfig[]) {
  if (models.length === 0) {
    modelsList.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12"/></svg></div>
        <div>No 3D models yet</div>
      </div>
    `;
    return;
  }

  modelsList.innerHTML = models
    .map((model, index) => `
      <div class="item-card model-card" data-index="${index}" style="border: 1px solid rgba(0,0,0,0.08); border-radius: var(--radius-md); margin-bottom: 12px; overflow: hidden;">
        <div class="item-header" style="display: flex; align-items: center; justify-content: space-between; padding: 12px; background: rgba(0,0,0,0.02); border-bottom: 1px solid rgba(0,0,0,0.05);">
          <span class="item-title" style="font-weight: 600; font-size: 14px;">Model ${index + 1}</span>
          <button type="button" class="btn-icon delete-model-btn" data-index="${index}" style="padding: 4px 8px; font-size: 14px;">🗑️</button>
        </div>
        <div class="model-gizmo-bar" data-index="${index}" style="display: flex; gap: 4px; padding: 8px 12px; background: rgba(74,144,226,0.05); border-bottom: 1px solid rgba(0,0,0,0.05);">
          <button type="button" class="gizmo-btn" data-index="${index}" data-mode="none" style="flex:1; padding:6px; font-size:11px; border:1px solid rgba(0,0,0,0.1); background:white; border-radius:4px; cursor:pointer;">Off</button>
          <button type="button" class="gizmo-btn" data-index="${index}" data-mode="position" style="flex:1; padding:6px; font-size:11px; border:1px solid rgba(0,0,0,0.1); background:transparent; border-radius:4px; cursor:pointer;">Move</button>
          <button type="button" class="gizmo-btn" data-index="${index}" data-mode="rotation" style="flex:1; padding:6px; font-size:11px; border:1px solid rgba(0,0,0,0.1); background:transparent; border-radius:4px; cursor:pointer;">Rotate</button>
          <button type="button" class="gizmo-btn" data-index="${index}" data-mode="scale" style="flex:1; padding:6px; font-size:11px; border:1px solid rgba(0,0,0,0.1); background:transparent; border-radius:4px; cursor:pointer;">Scale</button>
        </div>

        <div style="padding: 12px;">
          <!-- Model URL -->
          <div class="form-group" style="margin-bottom: 12px;">
            <label style="display: block; font-size: 12px; font-weight: 600; color: var(--ios-gray); margin-bottom: 6px;">Model File (glTF/GLB)</label>
            <input type="url" class="model-url" value="${model.url || ''}" data-index="${index}" data-field="url"
                   style="width: 100%; padding: 8px 10px; border: 1px solid rgba(0,0,0,0.1); border-radius: var(--radius-sm); font-size: 13px;">
          </div>

          <!-- Position - XYZ in a row -->
          <div class="form-group" style="margin-bottom: 8px;">
            <label style="display: block; font-size: 11px; font-weight: 600; color: var(--ios-gray); margin-bottom: 4px; text-transform: uppercase;">Position</label>
            <div style="display: flex; gap: 6px;">
              <div style="flex: 1; display: flex; align-items: center; gap: 4px;">
                <span style="font-size: 10px; font-weight: 600; color: #E53935;">X</span>
                <input type="number" step="0.1" value="${model.position?.x || 0}" data-index="${index}" data-field="position.x"
                       style="flex: 1; padding: 5px 6px; border: 1px solid rgba(0,0,0,0.1); border-radius: 4px; font-size: 11px; width: 50px;">
              </div>
              <div style="flex: 1; display: flex; align-items: center; gap: 4px;">
                <span style="font-size: 10px; font-weight: 600; color: #43A047;">Y</span>
                <input type="number" step="0.1" value="${model.position?.y || 0}" data-index="${index}" data-field="position.y"
                       style="flex: 1; padding: 5px 6px; border: 1px solid rgba(0,0,0,0.1); border-radius: 4px; font-size: 11px; width: 50px;">
              </div>
              <div style="flex: 1; display: flex; align-items: center; gap: 4px;">
                <span style="font-size: 10px; font-weight: 600; color: #1E88E5;">Z</span>
                <input type="number" step="0.1" value="${model.position?.z || 0}" data-index="${index}" data-field="position.z"
                       style="flex: 1; padding: 5px 6px; border: 1px solid rgba(0,0,0,0.1); border-radius: 4px; font-size: 11px; width: 50px;">
              </div>
            </div>
          </div>

          <!-- Rotation - XYZ in a row -->
          <div class="form-group" style="margin-bottom: 8px;">
            <label style="display: block; font-size: 11px; font-weight: 600; color: var(--ios-gray); margin-bottom: 4px; text-transform: uppercase;">Rotation (°)</label>
            <div style="display: flex; gap: 6px;">
              <div style="flex: 1; display: flex; align-items: center; gap: 4px;">
                <span style="font-size: 10px; font-weight: 600; color: #E53935;">X</span>
                <input type="number" step="5" value="${Math.round(radiansToDegrees(model.rotation?.x || 0))}" data-index="${index}" data-field="rotation.x"
                       style="flex: 1; padding: 5px 6px; border: 1px solid rgba(0,0,0,0.1); border-radius: 4px; font-size: 11px; width: 50px;">
              </div>
              <div style="flex: 1; display: flex; align-items: center; gap: 4px;">
                <span style="font-size: 10px; font-weight: 600; color: #43A047;">Y</span>
                <input type="number" step="5" value="${Math.round(radiansToDegrees(model.rotation?.y || 0))}" data-index="${index}" data-field="rotation.y"
                       style="flex: 1; padding: 5px 6px; border: 1px solid rgba(0,0,0,0.1); border-radius: 4px; font-size: 11px; width: 50px;">
              </div>
              <div style="flex: 1; display: flex; align-items: center; gap: 4px;">
                <span style="font-size: 10px; font-weight: 600; color: #1E88E5;">Z</span>
                <input type="number" step="5" value="${Math.round(radiansToDegrees(model.rotation?.z || 0))}" data-index="${index}" data-field="rotation.z"
                       style="flex: 1; padding: 5px 6px; border: 1px solid rgba(0,0,0,0.1); border-radius: 4px; font-size: 11px; width: 50px;">
              </div>
            </div>
          </div>

          <!-- Scale (full width below) -->
          <div class="form-group" style="margin-top: 12px; margin-bottom: 0;">
            <label style="display: block; font-size: 11px; font-weight: 600; color: var(--ios-gray); margin-bottom: 6px; text-transform: uppercase;">Scale (Uniform)</label>
            <div style="display: flex; align-items: center; gap: 8px;">
              <input type="range" min="0.1" max="10" step="0.1"
                     value="${typeof model.scale === 'number' ? model.scale : model.scale?.x || 1}"
                     data-index="${index}" data-field="scale" class="model-scale-slider"
                     style="flex: 1;">
              <input type="number" step="0.1" min="0.1"
                     value="${typeof model.scale === 'number' ? model.scale : model.scale?.x || 1}"
                     data-index="${index}" data-field="scale" class="model-scale-input"
                     style="width: 60px; padding: 6px 8px; border: 1px solid rgba(0,0,0,0.1); border-radius: var(--radius-sm); font-size: 12px; text-align: center;">
            </div>
          </div>
        </div>
      </div>
    `)
    .join('');

  // Attach event listeners for model inputs
  attachModelInputListeners();

  // Update count badge
  update3DTabCounts();
}

/**
 * Delete a model by index
 */
function deleteModel(index: number): void {
  if (!config?.stages?.[currentStageIndex]?.models) return;
  const stage = config.stages[currentStageIndex];
  stage.models!.splice(index, 1);
  markUnsaved();
  renderModelsList(stage.models || []);
  if (editorPreview) {
    editorPreview.updateStage(stage, currentStageIndex);
  }
}

/**
 * Update a model property by index and field path
 */
function updateModel(index: number, field: string, value: string): void {
  if (!config?.stages?.[currentStageIndex]?.models?.[index]) return;
  const stage = config.stages[currentStageIndex];
  const model = stage.models![index];

  // Handle nested fields like 'position.x'
  if (field.includes('.')) {
    const [parent, child] = field.split('.');
    if (parent === 'position' && model.position) {
      (model.position as any)[child] = parseFloat(value) || 0;
    } else if (parent === 'rotation' && model.rotation) {
      (model.rotation as any)[child] = parseFloat(value) || 0;
    }
  } else if (field === 'url') {
    model.url = value;
  } else if (field === 'scale') {
    model.scale = parseFloat(value) || 1;
  }

  markUnsaved();
  if (editorPreview) {
    // Use incremental update for transform changes, full reload for URL changes
    if (field === 'url') {
      editorPreview.updateStage(stage, currentStageIndex);
    } else if (model.url) {
      // Transform change - update in-place without reload
      const scaleVal = typeof model.scale === 'number' ? model.scale : (model.scale as any)?.x || 1;
      editorPreview.setModelTransform(
        model.url,
        model.position ? { x: model.position.x, y: model.position.y, z: model.position.z } : undefined,
        model.rotation ? { x: model.rotation.x, y: model.rotation.y, z: model.rotation.z } : undefined,
        scaleVal
      );
    }
  }
}

/**
 * Attach event listeners to model card inputs
 */
function attachModelInputListeners(): void {
  // Delete buttons
  document.querySelectorAll('.delete-model-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const index = parseInt((e.currentTarget as HTMLElement).dataset.index || '0');
      deleteModel(index);
    });
  });

  // Gizmo mode buttons (inline in each model card)
  document.querySelectorAll('.gizmo-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const el = e.currentTarget as HTMLElement;
      const index = parseInt(el.dataset.index || '0');
      const mode = el.dataset.mode as 'none' | 'position' | 'rotation' | 'scale';

      // Update button styles in this card
      const bar = el.parentElement;
      bar?.querySelectorAll('.gizmo-btn').forEach(b => {
        (b as HTMLElement).style.background = 'transparent';
      });
      el.style.background = 'white';

      // Activate gizmo for this model
      if (editorPreview && config?.stages?.[currentStageIndex]?.models?.[index]) {
        const model = config.stages[currentStageIndex].models![index];
        if (mode === 'none') {
          editorPreview.disableGizmos();
          editorPreview.detachGizmo();
          editingModelIndex = -1;
        } else {
          editingModelIndex = index;
          editorPreview.enableGizmos(mode);
          if (model.url) {
            editorPreview.attachGizmoToModel(model.url);
          }
        }
      }
    });
  });

  // Model URL inputs
  document.querySelectorAll('.model-url').forEach((input) => {
    input.addEventListener('change', (e) => {
      const el = e.target as HTMLInputElement;
      const index = parseInt(el.dataset.index || '0');
      updateModel(index, 'url', el.value);
    });
  });

  // Position/Rotation inputs
  document.querySelectorAll('.model-card input[data-field]').forEach((input) => {
    input.addEventListener('change', (e) => {
      const el = e.target as HTMLInputElement;
      const index = parseInt(el.dataset.index || '0');
      const field = el.dataset.field || '';
      if (field !== 'url') {
        updateModel(index, field, el.value);
      }
    });
  });

  // Scale slider/input sync
  document.querySelectorAll('.model-scale-slider').forEach((slider) => {
    slider.addEventListener('input', (e) => {
      const el = e.target as HTMLInputElement;
      const index = parseInt(el.dataset.index || '0');
      const value = el.value;
      // Sync with number input
      const numberInput = document.querySelector(`.model-scale-input[data-index="${index}"]`) as HTMLInputElement;
      if (numberInput) numberInput.value = value;
    });
    slider.addEventListener('change', (e) => {
      const el = e.target as HTMLInputElement;
      const index = parseInt(el.dataset.index || '0');
      updateModel(index, 'scale', el.value);
    });
  });

  document.querySelectorAll('.model-scale-input').forEach((input) => {
    input.addEventListener('input', (e) => {
      const el = e.target as HTMLInputElement;
      const index = parseInt(el.dataset.index || '0');
      // Sync with slider
      const slider = document.querySelector(`.model-scale-slider[data-index="${index}"]`) as HTMLInputElement;
      if (slider) slider.value = el.value;
    });
    input.addEventListener('change', (e) => {
      const el = e.target as HTMLInputElement;
      const index = parseInt(el.dataset.index || '0');
      updateModel(index, 'scale', el.value);
    });
  });
}

/** Currently editing model index (-1 = none) */
let editingModelIndex: number = -1;

/**
 * Show model editor tools for a specific model (now inline in card)
 */
function showModelEditor(modelIndex: number): void {
  if (!config?.stages?.[currentStageIndex]?.models?.[modelIndex]) {
    console.warn('Model not found at index', modelIndex);
    return;
  }

  // Close plane editor if open
  if (editingPlaneIndex >= 0) {
    hidePlaneEditor();
  }

  editingModelIndex = modelIndex;
  const model = config.stages[currentStageIndex].models[modelIndex];

  // Highlight the active model card
  document.querySelectorAll('.model-card').forEach((card, idx) => {
    if (idx === modelIndex) {
      (card as HTMLElement).style.borderColor = 'var(--red-primary)';
      (card as HTMLElement).style.boxShadow = '0 0 0 2px rgba(255, 45, 85, 0.2)';
    } else {
      (card as HTMLElement).style.borderColor = 'rgba(0,0,0,0.08)';
      (card as HTMLElement).style.boxShadow = 'none';
    }
  });

  // Attach gizmo to this model in preview
  if (editorPreview && model.url) {
    editorPreview.attachGizmoToModel(model.url);
    editorPreview.enableGizmos('position'); // Start with move tool

    // Select the 'position' button in this model's gizmo bar
    const gizmoBar = document.querySelector(`.model-gizmo-bar[data-index="${modelIndex}"]`);
    gizmoBar?.querySelectorAll('.gizmo-btn').forEach((btn) => {
      (btn as HTMLElement).style.background = 'transparent';
      if ((btn as HTMLElement).dataset.mode === 'position') {
        (btn as HTMLElement).style.background = 'white';
      }
    });
  }

  console.log(`[Editor] Opened model editor for Model ${modelIndex + 1}`);
}

/**
 * Hide model editor tools
 */
function hideModelEditor(): void {
  editingModelIndex = -1;

  // Remove highlight from all model cards
  document.querySelectorAll('.model-card').forEach((card) => {
    (card as HTMLElement).style.borderColor = 'rgba(0,0,0,0.08)';
    (card as HTMLElement).style.boxShadow = 'none';
  });

  // Disable gizmos
  editorPreview?.disableGizmos();
  editorPreview?.detachGizmo();

  // Reset all gizmo bars to 'Off'
  document.querySelectorAll('.gizmo-btn').forEach((btn) => {
    (btn as HTMLElement).style.background = 'transparent';
    if ((btn as HTMLElement).dataset.mode === 'none') {
      (btn as HTMLElement).style.background = 'white';
    }
  });

  console.log('[Editor] Closed model editor');
}

/** Currently editing plane index (-1 = none) */
let editingPlaneIndex: number = -1;

/**
 * Show plane editor tools for a specific plane
 */
function showPlaneEditor(planeIndex: number): void {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];
  if (!stage.planes || !stage.planes[planeIndex]) {
    console.warn('[Editor] Invalid plane index:', planeIndex);
    return;
  }

  // Close model editor if open
  if (editingModelIndex >= 0) {
    hideModelEditor();
  }

  editingPlaneIndex = planeIndex;
  const plane = stage.planes[planeIndex];

  // Show the editor tools panel (reuse model editor tools)
  const editorTools = document.getElementById('modelEditorTools');
  if (editorTools) {
    editorTools.style.display = 'flex';
  }

  // Highlight the current plane card
  document.querySelectorAll('.item-card[data-plane-index]').forEach((card, idx) => {
    if (idx === planeIndex) {
      (card as HTMLElement).style.borderColor = '#007aff';
      (card as HTMLElement).style.boxShadow = '0 0 0 2px rgba(0,122,255,0.2)';
    } else {
      (card as HTMLElement).style.borderColor = 'rgba(0,0,0,0.08)';
      (card as HTMLElement).style.boxShadow = 'none';
    }
  });

  // Attach gizmo to this plane in preview
  if (editorPreview) {
    editorPreview.attachGizmoToPlane(planeIndex);
    editorPreview.enableGizmos('position'); // Start with move tool

    // Select the 'position' button in gizmo selector
    document.querySelectorAll('#gizmoModeSelector button').forEach((btn) => {
      btn.classList.remove('active');
      (btn as HTMLElement).style.background = 'transparent';
      if ((btn as HTMLElement).dataset.mode === 'position') {
        btn.classList.add('active');
        (btn as HTMLElement).style.background = 'white';
      }
    });
  }

  console.log(`[Editor] Opened plane editor for Plane ${planeIndex + 1}`);
}

/**
 * Hide plane editor tools
 */
function hidePlaneEditor(): void {
  editingPlaneIndex = -1;

  // Hide the editor tools panel
  const editorTools = document.getElementById('modelEditorTools');
  if (editorTools) {
    editorTools.style.display = 'none';
  }

  // Remove highlight from all plane cards
  document.querySelectorAll('.item-card[data-plane-index]').forEach((card) => {
    (card as HTMLElement).style.borderColor = 'rgba(0,0,0,0.08)';
    (card as HTMLElement).style.boxShadow = 'none';
  });

  // Disable gizmos
  editorPreview?.disableGizmos();
  editorPreview?.detachPlaneGizmo();

  // Reset gizmo selector
  document.querySelectorAll('#gizmoModeSelector button').forEach((btn) => {
    btn.classList.remove('active');
    (btn as HTMLElement).style.background = 'transparent';
    if ((btn as HTMLElement).dataset.mode === 'none') {
      btn.classList.add('active');
      (btn as HTMLElement).style.background = 'white';
    }
  });

  console.log('[Editor] Closed plane editor');
}

/**
 * Update plane card inputs after gizmo transform
 */
function updatePlaneCardInputs(
  planeIndex: number,
  transform: { position: { x: number; y: number; z: number }; rotation: { x: number; y: number; z: number }; scale: { width: number; height: number } }
): void {
  const card = document.querySelector(`.item-card[data-plane-index="${planeIndex}"]`);
  if (!card) return;

  // Update position inputs
  const posInputs = card.querySelectorAll<HTMLInputElement>('input[type="number"]');
  if (posInputs.length >= 6) {
    // Position X, Y, Z
    posInputs[0].value = transform.position.x.toFixed(2);
    posInputs[1].value = transform.position.y.toFixed(2);
    posInputs[2].value = transform.position.z.toFixed(2);
    // Rotation X, Y, Z (convert to degrees)
    posInputs[3].value = Math.round(transform.rotation.x * 180 / Math.PI).toString();
    posInputs[4].value = Math.round(transform.rotation.y * 180 / Math.PI).toString();
    posInputs[5].value = Math.round(transform.rotation.z * 180 / Math.PI).toString();
    // Size Width, Height
    if (posInputs.length >= 8) {
      posInputs[6].value = transform.scale.width.toFixed(2);
      posInputs[7].value = transform.scale.height.toFixed(2);
    }
  }
}

// Export plane editor functions
(window as any).showPlaneEditor = showPlaneEditor;
(window as any).hidePlaneEditor = hidePlaneEditor;

/**
 * Render planes list
 */
function renderPlanesList(planes: PlaneConfig[]) {
  if (planes.length === 0) {
    planesList.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg></div>
        <div>No 2D planes yet</div>
      </div>
    `;
    return;
  }

  planesList.innerHTML = planes
    .map((plane, index) => `
      <div class="item-card" data-plane-index="${index}">
        <div class="item-header">
          <span class="item-title">${plane.type === 'video' ? 'Video' : 'Image'} Plane ${index + 1}</span>
          <div style="display: flex; gap: 4px;">
            <button class="ios-button ios-button-secondary ios-button-sm" onclick="showPlaneEditor(${index})" title="Edit with gizmo in 3D preview">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              Edit in Preview
            </button>
            <button class="btn-icon" onclick="deletePlane(${index})">🗑️</button>
          </div>
        </div>
        <div class="form-group">
          <label>Type</label>
          <select onchange="updatePlane(${index}, 'type', this.value)">
            <option value="image" ${plane.type === 'image' ? 'selected' : ''}>Image</option>
            <option value="video" ${plane.type === 'video' ? 'selected' : ''}>Video</option>
          </select>
        </div>
        <div class="form-group">
          <label>Media URL *</label>
          <input type="url" value="${plane.url || ''}"
                 onchange="updatePlane(${index}, 'url', this.value)">
          ${plane.url ? (plane.type === 'video' ?
            `<video src="${plane.url}" controls muted style="max-width: 100%; max-height: 100px; margin-top: 8px; border-radius: 8px; border: 1px solid var(--glass-border);"></video>` :
            `<img src="${plane.url}" style="max-width: 100%; max-height: 100px; margin-top: 8px; border-radius: 8px; object-fit: contain; border: 1px solid var(--glass-border);">`) : ''}
        </div>
        <div class="form-group">
          <label>Position</label>
          <div class="form-row">
            <input type="number" step="0.1" placeholder="X"
                   value="${plane.position?.x || 0}"
                   onchange="updatePlane(${index}, 'position.x', this.value)">
            <input type="number" step="0.1" placeholder="Y"
                   value="${plane.position?.y || 0}"
                   onchange="updatePlane(${index}, 'position.y', this.value)">
            <input type="number" step="0.1" placeholder="Z"
                   value="${plane.position?.z || -3}"
                   onchange="updatePlane(${index}, 'position.z', this.value)">
          </div>
        </div>
        <div class="form-group">
          <label>Rotation (degrees)</label>
          <div class="form-row">
            <input type="number" step="1" placeholder="X"
                   value="${radiansToDegrees(plane.rotation?.x || 0)}"
                   onchange="updatePlane(${index}, 'rotation.x', this.value)">
            <input type="number" step="1" placeholder="Y"
                   value="${radiansToDegrees(plane.rotation?.y || 0)}"
                   onchange="updatePlane(${index}, 'rotation.y', this.value)">
            <input type="number" step="1" placeholder="Z"
                   value="${radiansToDegrees(plane.rotation?.z || 0)}"
                   onchange="updatePlane(${index}, 'rotation.z', this.value)">
          </div>
        </div>
        <div class="form-group">
          <label>Size (Width × Height)</label>
          <div class="form-row">
            <input type="number" step="0.1" min="0.1" placeholder="Width"
                   value="${plane.scale?.width || 1.5}"
                   onchange="updatePlane(${index}, 'scale.width', this.value)">
            <input type="number" step="0.1" min="0.1" placeholder="Height"
                   value="${plane.scale?.height || 1}"
                   onchange="updatePlane(${index}, 'scale.height', this.value)">
          </div>
        </div>
        ${plane.type === 'video' ? `
          <div class="checkbox-group">
            <input type="checkbox" ${plane.autoplay !== false ? 'checked' : ''}
                   onchange="updatePlane(${index}, 'autoplay', this.checked)">
            <label>Autoplay</label>
          </div>
          <div class="checkbox-group">
            <input type="checkbox" ${plane.loop !== false ? 'checked' : ''}
                   onchange="updatePlane(${index}, 'loop', this.checked)">
            <label>Loop</label>
          </div>
          <div class="checkbox-group">
            <input type="checkbox" ${plane.muted !== false ? 'checked' : ''}
                   onchange="updatePlane(${index}, 'muted', this.checked)">
            <label>Muted</label>
          </div>
        ` : ''}
      </div>
    `)
    .join('');

  // Update count badge
  update3DTabCounts();
}

/**
 * Update logo preview display
 */
function updateLogoPreview(url: string | null): void {
  if (url) {
    // Custom logo is set - show preview and clear button
    if (logoPreviewContainer) {
      logoPreviewContainer.style.display = 'flex';
    }
    if (logoPreviewImg) {
      logoPreviewImg.src = url;
    }
    if (clearLogoBtn) {
      clearLogoBtn.style.display = 'block';
    }
  } else {
    // No custom logo - hide preview and clear button
    if (logoPreviewContainer) {
      logoPreviewContainer.style.display = 'none';
    }
    if (clearLogoBtn) {
      clearLogoBtn.style.display = 'none';
    }
  }
}

/**
 * Handle skybox type UI update
 */
function updateSkyboxTypeUI() {
  const manualVariantInputs = document.getElementById('manualVariantInputs');
  const skyboxVideoUrlGroup = document.getElementById('skyboxVideoUrlGroup');
  const skyboxHlsUrlGroup = document.getElementById('skyboxHlsUrlGroup');
  const skyboxVideoPreviewGroup = document.getElementById('skyboxVideoPreviewGroup');
  const variantDisplay = document.getElementById('variantDisplay');
  const uploadBtnText = document.getElementById('uploadBtnText');
  const uploadBtnHelper = document.getElementById('uploadBtnHelper');
  const ktx2ToggleSection = document.getElementById('ktx2ToggleSection');

  if (skyboxTypeVideo && skyboxTypeVideo.checked) {
    // Video type - show video URL input, HLS URL input, and preview
    if (skyboxVideoOptions) skyboxVideoOptions.style.display = 'block';
    if (skyboxVideoUrlGroup) skyboxVideoUrlGroup.style.display = 'block';
    if (skyboxHlsUrlGroup) skyboxHlsUrlGroup.style.display = 'block';
    if (skyboxVideoPreviewGroup) skyboxVideoPreviewGroup.style.display = 'block';
    if (skyboxColorGroup) skyboxColorGroup.style.display = 'none';
    if (skyboxRotationGroup) skyboxRotationGroup.style.display = 'block';
    if (manualVariantInputs) manualVariantInputs.style.display = 'none';
    if (variantDisplay) variantDisplay.style.display = 'none';
    if (ktx2ToggleSection) ktx2ToggleSection.style.display = 'none';

    // Update upload button text for video - check if video already exists
    const stage = config?.stages?.[currentStageIndex];
    const hasVideo = stage?.skybox?.hlsUrl || stage?.skybox?.url;
    if (uploadBtnText) uploadBtnText.textContent = hasVideo ? 'Replace Video' : 'Upload Video';
    if (uploadBtnHelper) uploadBtnHelper.textContent = 'Pro users: Upload videos directly to Firebase Storage';

    // Update video thumbnail display
    updateVideoThumbnailDisplay();

  } else if (skyboxTypeColor && skyboxTypeColor.checked) {
    // Color type - show color picker
    if (skyboxVideoOptions) skyboxVideoOptions.style.display = 'none';
    if (skyboxVideoUrlGroup) skyboxVideoUrlGroup.style.display = 'none';
    if (skyboxHlsUrlGroup) skyboxHlsUrlGroup.style.display = 'none';
    if (skyboxVideoPreviewGroup) skyboxVideoPreviewGroup.style.display = 'none';
    if (skyboxColorGroup) skyboxColorGroup.style.display = 'block';
    if (skyboxRotationGroup) skyboxRotationGroup.style.display = 'none';
    if (manualVariantInputs) manualVariantInputs.style.display = 'none';
    if (variantDisplay) variantDisplay.style.display = 'none';
    if (ktx2ToggleSection) ktx2ToggleSection.style.display = 'none';

    // Hide upload button for color type (not applicable)
    const uploadSkyboxBtn = document.getElementById('uploadSkyboxBtn');
    if (uploadSkyboxBtn) uploadSkyboxBtn.style.display = 'none';

  } else { // Image type - show variant inputs/display
    if (skyboxVideoOptions) skyboxVideoOptions.style.display = 'none';
    if (skyboxVideoUrlGroup) skyboxVideoUrlGroup.style.display = 'none';
    if (skyboxHlsUrlGroup) skyboxHlsUrlGroup.style.display = 'none';
    if (skyboxVideoPreviewGroup) skyboxVideoPreviewGroup.style.display = 'none';
    if (skyboxColorGroup) skyboxColorGroup.style.display = 'none';
    if (skyboxRotationGroup) skyboxRotationGroup.style.display = 'block';

    // Show upload button for images
    const uploadSkyboxBtn = document.getElementById('uploadSkyboxBtn');
    if (uploadSkyboxBtn && currentUserProfile && hasFeatureAccess(currentUserProfile, 'uploads')) {
      uploadSkyboxBtn.style.display = 'block';
      if (ktx2ToggleSection) ktx2ToggleSection.style.display = 'block';
    }

    // Update upload button text for images - use proper "Upload/Replace Skybox" text
    updateSkyboxUploadButtonText();
    if (uploadBtnHelper) uploadBtnHelper.innerHTML = 'Automatically optimized to 5 variants for all devices';

    // Use displaySkyboxVariants to properly show either variant cards OR manual inputs (not both!)
    const stage = config?.stages?.[currentStageIndex];
    if (stage) {
      displaySkyboxVariants(stage);
    } else {
      // Fallback: show manual inputs if no stage
      if (manualVariantInputs) manualVariantInputs.style.display = 'block';
      if (variantDisplay) variantDisplay.style.display = 'none';
    }
  }
  handleSkyboxChange();
}

/**
 * Handle manual variant URL changes
 */
function handleManualVariantChange() {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];

  if (!stage.skybox) {
    stage.skybox = { type: 'image' };
  }

  // Get all manual URL inputs
  const desktopUrl = (document.getElementById('variantUrlDesktop') as HTMLInputElement)?.value.trim();
  const mobileUrl = (document.getElementById('variantUrlMobile') as HTMLInputElement)?.value.trim();
  const tabletUrl = (document.getElementById('variantUrlTablet') as HTMLInputElement)?.value.trim();
  const vrUrl = (document.getElementById('variantUrlVR') as HTMLInputElement)?.value.trim();
  const fallbackUrl = (document.getElementById('variantUrlFallback') as HTMLInputElement)?.value.trim();

  // Ensure variants object exists
  if (!stage.skybox.variants) {
    stage.skybox.variants = {} as any;
  }

  // Update or remove each variant based on whether URL is filled
  if (desktopUrl) {
    stage.skybox!.variants!.desktop = {
      url: desktopUrl,
      format: 'jpg',
      resolution: '8192x4096'
    };
  } else {
    delete (stage.skybox.variants as any).desktop;
  }

  if (mobileUrl) {
    stage.skybox!.variants!.mobile = {
      url: mobileUrl,
      format: 'jpg',
      resolution: '4096x2048'
    };
  } else {
    delete (stage.skybox.variants as any).mobile;
  }

  if (tabletUrl) {
    stage.skybox!.variants!.tablet = {
      url: tabletUrl,
      format: 'jpg',
      resolution: '4096x2048'
    };
  } else {
    delete (stage.skybox.variants as any).tablet;
  }

  if (vrUrl) {
    stage.skybox!.variants!.vr = {
      url: vrUrl,
      format: 'jpg',
      resolution: '8192x4096'
    };
  } else {
    delete (stage.skybox.variants as any).vr;
  }

  if (fallbackUrl) {
    stage.skybox!.variants!.fallback = {
      url: fallbackUrl,
      format: 'jpg',
      resolution: '2048x1024'
    };
  } else {
    delete (stage.skybox.variants as any).fallback;
  }

  // Set legacy URL to desktop or fallback for backward compatibility
  // Clear it if both are empty
  stage.skybox.url = desktopUrl || fallbackUrl || '';

  markUnsaved();
  updatePreview();
}

/**
 * Handle rotation change (applies to all skybox types)
 */
function handleRotationChange(source: 'slider' | 'number' = 'slider') {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];

  if (stage.skybox) {
    // Get value from the source that triggered the change
    let rotationValue: number;
    if (source === 'slider' && skyboxRotation) {
      rotationValue = parseFloat(skyboxRotation.value) || 0;
      // Sync number input
      if (skyboxRotationNumber) skyboxRotationNumber.value = String(Math.round(rotationValue));
    } else if (source === 'number' && skyboxRotationNumber) {
      rotationValue = parseFloat(skyboxRotationNumber.value) || 0;
      // Clamp to valid range
      rotationValue = Math.max(0, Math.min(360, rotationValue));
      skyboxRotationNumber.value = String(Math.round(rotationValue));
      // Sync slider
      if (skyboxRotation) skyboxRotation.value = String(rotationValue);
    } else {
      return;
    }

    stage.skybox.rotation = rotationValue;
    console.log(`[Rotation] Updated rotation to ${rotationValue}° for stage ${stage.name || stage.id}`);

    markUnsaved();
    // Use incremental rotation update (no full reload needed)
    if (editorPreview) {
      editorPreview.setSkyboxRotation(rotationValue);
    }
  }
}

/**
 * Handle skybox change (for video type - images use manual variant inputs)
 * IMPORTANT: Preserves existing skybox data (variants, URLs) when switching types
 */
function handleSkyboxChange() {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];

  // Preserve existing skybox data - don't blow away variants when switching types
  const existingSkybox = (stage.skybox || {}) as SkyboxConfig;
  const existingVariants = existingSkybox.variants;
  const existingUrl = existingSkybox.url;
  const existingThumbnailUrl = existingSkybox.thumbnailUrl;
  const existingRotation = existingSkybox.rotation ?? (skyboxRotation ? parseFloat(skyboxRotation.value) || 0 : 0);

  if (skyboxTypeColor && skyboxTypeColor.checked) {
    // Color skybox - preserve variants/url for when user switches back to image
    const color = skyboxColor?.value || '#000000';
    stage.skybox = {
      type: 'color',
      color: color,
      // Preserve image data for when user switches back
      ...(existingVariants && { variants: existingVariants }),
      ...(existingUrl && { url: existingUrl }),
    };
  } else if (skyboxTypeVideo && skyboxTypeVideo.checked) {
    // Video skybox - preserve variants/url for when user switches back to image
    const videoUrlInput = document.getElementById('videoSkyboxUrl') as HTMLInputElement;
    const hlsUrlInput = document.getElementById('videoSkyboxHlsUrl') as HTMLInputElement;
    const thumbnailUrlInput = document.getElementById('thumbnailUrlInput') as HTMLInputElement;

    // Use input values directly - allow empty strings to clear the fields
    // Only fall back to existing values if the input element doesn't exist
    const videoUrl = videoUrlInput ? videoUrlInput.value.trim() : (existingSkybox.url || '');
    const hlsUrl = hlsUrlInput ? hlsUrlInput.value.trim() : (existingSkybox.hlsUrl || '');
    // Read thumbnail URL from input if available, otherwise preserve existing
    const thumbnailUrl = thumbnailUrlInput ? thumbnailUrlInput.value.trim() : (existingThumbnailUrl || '');

    const skyboxConfig: SkyboxConfig = {
      type: 'video',
      rotation: existingRotation,
      // Preserve image data for when user switches back
      ...(existingVariants && { variants: existingVariants }),
      // Only include URLs if they have values (allow clearing)
      ...(videoUrl && { url: videoUrl }),
      ...(hlsUrl && { hlsUrl }),
      // Include thumbnail URL if set (from input or existing)
      ...(thumbnailUrl && { thumbnailUrl }),
    };

    if (skyboxAutoplay) skyboxConfig.autoplay = skyboxAutoplay.checked;
    if (skyboxLoop) skyboxConfig.loop = skyboxLoop.checked;

    stage.skybox = skyboxConfig;
  } else {
    // Image type - ensure type is set and preserve all data
    if (!stage.skybox) {
      stage.skybox = { type: 'image' };
    } else {
      stage.skybox.type = 'image';
    }
    // Rotation is preserved automatically since we're not replacing the object
    if (skyboxRotation) {
      stage.skybox.rotation = parseFloat(skyboxRotation.value) || 0;
    }
  }

  // Update upload button text based on new skybox state
  updateSkyboxUploadButtonText();

  markUnsaved();
  updatePreview();
}

/**
 * Handle skybox color picker change
 */
function handleSkyboxColorChange() {
  const color = skyboxColor.value;
  skyboxColorHex.value = color;
  handleSkyboxChange();
}

/**
 * Handle skybox color hex input change
 */
function handleSkyboxColorHexChange() {
  const hex = skyboxColorHex.value.trim();
  // Validate hex color
  if (/^#[0-9A-F]{6}$/i.test(hex)) {
    skyboxColor.value = hex;
    handleSkyboxChange();
  }
}

/**
 * Update video thumbnail display based on current skybox config
 */
function updateVideoThumbnailDisplay() {
  const thumbnailImg = document.getElementById('videoThumbnailImg') as HTMLImageElement;
  const thumbnailPlaceholder = document.getElementById('videoThumbnailPlaceholder');
  const thumbnailUrlInput = document.getElementById('thumbnailUrlInput') as HTMLInputElement;

  if (!thumbnailImg || !thumbnailPlaceholder) return;

  const stage = config?.stages?.[currentStageIndex];
  const thumbnailUrl = stage?.skybox?.thumbnailUrl;

  // Sync the URL input field
  if (thumbnailUrlInput) {
    thumbnailUrlInput.value = thumbnailUrl || '';
  }

  if (thumbnailUrl) {
    thumbnailImg.src = thumbnailUrl;
    thumbnailImg.style.display = 'block';
    thumbnailPlaceholder.style.display = 'none';
  } else {
    thumbnailImg.style.display = 'none';
    thumbnailPlaceholder.style.display = 'block';
  }
}

/**
 * Regenerate video thumbnail from the video URL
 */
async function regenerateVideoThumbnail() {
  const stage = config?.stages?.[currentStageIndex];
  const videoUrl = stage?.skybox?.url;

  if (!videoUrl) {
    showAlert('Please enter a video URL first.', 'Missing URL', '🎬');
    return;
  }

  if (!currentUser || !sequenceId || !currentUserProfile) {
    showAlert('You must be logged in to regenerate thumbnails.', 'Sign In Required', '🔐');
    return;
  }

  const progressContainer = document.getElementById('thumbnailProgress');
  const progressFill = document.getElementById('thumbnailProgressFill');
  const progressText = document.getElementById('thumbnailProgressText');
  const regenerateBtn = document.getElementById('regenerateThumbnailBtn') as HTMLButtonElement;
  const uploadThumbnailBtn = document.getElementById('uploadThumbnailBtn') as HTMLButtonElement;
  const thumbnailImg = document.getElementById('videoThumbnailImg') as HTMLImageElement;

  if (!progressContainer || !progressFill || !progressText) return;

  let localPreviewUrl: string | null = null;

  try {
    // Show progress and disable buttons
    progressContainer.style.display = 'block';
    if (regenerateBtn) regenerateBtn.disabled = true;
    if (uploadThumbnailBtn) uploadThumbnailBtn.disabled = true;
    progressFill.style.width = '10%';
    progressText.textContent = 'Loading video...';

    // Import the VideoThumbnailGenerator
    const { VideoThumbnailGenerator } = await import('../src/utils/VideoThumbnailGenerator');

    progressFill.style.width = '30%';
    progressText.textContent = 'Extracting first frame...';

    // Generate thumbnail from video URL
    const thumbnailBlob = await VideoThumbnailGenerator.extractThumbnailFromUrl(videoUrl, {
      width: 1920,
      height: 960,
      seekTime: 0.1
    });

    // Show local preview of the generated frame regardless of tier
    localPreviewUrl = URL.createObjectURL(thumbnailBlob);
    if (thumbnailImg) {
      thumbnailImg.src = localPreviewUrl;
      thumbnailImg.style.display = 'block';
      const placeholder = document.getElementById('videoThumbnailPlaceholder');
      if (placeholder) placeholder.style.display = 'none';
    }

    // Check if user has cloud storage (paid tier); -1 means unlimited
    const hasStorage = getStorageLimit(currentUserProfile) !== 0;

    if (hasStorage) {
      // Paid user: upload to Firebase Storage
      progressFill.style.width = '60%';
      progressText.textContent = 'Uploading thumbnail...';

      const thumbnailFile = new File([thumbnailBlob], 'video-thumbnail.jpg', { type: 'image/jpeg' });

      const thumbnailResult = await uploadFileWithQuotaCheck(
        thumbnailFile,
        currentUser.uid,
        sequenceId,
        'image',
        currentUserProfile,
        () => {}
      );

      progressFill.style.width = '90%';
      progressText.textContent = 'Saving...';

      if (stage?.skybox) {
        stage.skybox.thumbnailUrl = thumbnailResult.url;
        console.log('Video thumbnail uploaded:', thumbnailResult.url);
      }

      progressFill.style.width = '100%';
      progressText.textContent = 'Thumbnail uploaded!';

      // Update display first (sets img src to remote URL), then revoke blob
      updateVideoThumbnailDisplay();
      URL.revokeObjectURL(localPreviewUrl);
      localPreviewUrl = null;
      markUnsaved();

      setTimeout(() => {
        progressContainer.style.display = 'none';
        progressFill.style.width = '0%';
      }, 2000);
    } else {
      // Free user: download the generated thumbnail (keep localPreviewUrl alive for preview)
      progressFill.style.width = '80%';
      progressText.textContent = 'Preparing download...';

      const a = document.createElement('a');
      a.href = localPreviewUrl;
      a.download = 'video-thumbnail.jpg';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      progressFill.style.width = '100%';
      progressText.textContent = 'Thumbnail downloaded! Host the file and paste the URL above.';

      setTimeout(() => {
        progressContainer.style.display = 'none';
        progressFill.style.width = '0%';
      }, 6000);
    }

  } catch (error) {
    // Revoke blob URL on error
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
    }
    console.error('Failed to regenerate video thumbnail:', error);
    const msg = error instanceof Error ? error.message : String(error);
    const videoUrl = stage?.skybox?.url || '';

    let userMessage: string;
    if (msg.includes('tainted') || msg.includes('SecurityError') || msg.includes('cross-origin')) {
      userMessage = 'CORS error — the video server blocks cross-origin access. Paste a thumbnail URL instead.';
    } else if (msg.includes('Timeout') || msg.includes('timeout')) {
      userMessage = 'Timeout — the server is slow or unreachable. Check the URL or paste a thumbnail URL instead.';
    } else if (msg.includes('MEDIA_ERR') && videoUrl.includes('.m3u8')) {
      userMessage = 'HLS streams cannot generate thumbnails in the browser. Paste a thumbnail URL instead.';
    } else if (msg.includes('MEDIA_ERR')) {
      userMessage = 'Could not load video. The URL may be invalid or the format is unsupported.';
    } else if (msg.includes('quota') || msg.includes('storage')) {
      userMessage = 'Storage quota exceeded.';
    } else {
      userMessage = 'Failed to generate thumbnail. Try pasting a thumbnail URL instead.';
    }

    progressText.textContent = userMessage;
    progressFill.style.width = '0%';

    setTimeout(() => {
      progressContainer.style.display = 'none';
    }, 6000);
  } finally {
    if (regenerateBtn) regenerateBtn.disabled = false;
    if (uploadThumbnailBtn) uploadThumbnailBtn.disabled = false;
  }
}

/**
 * Handle thumbnail image upload from file input
 */
async function handleVideoThumbnailUpload() {
  const fileInput = document.getElementById('thumbnailFileInput') as HTMLInputElement;
  if (!fileInput?.files?.length) return;

  const file = fileInput.files[0];

  // Validate it's an image
  if (!file.type.startsWith('image/')) {
    showAlert('Please select an image file (JPG, PNG, WebP, etc.).', 'Invalid File', '🖼️');
    fileInput.value = '';
    return;
  }

  if (!currentUser || !sequenceId || !currentUserProfile) {
    showAlert('You must be logged in to upload thumbnails.', 'Sign In Required', '🔐');
    fileInput.value = '';
    return;
  }

  const stage = config?.stages?.[currentStageIndex];
  if (!stage?.skybox) {
    fileInput.value = '';
    return;
  }

  const progressContainer = document.getElementById('thumbnailProgress');
  const progressFill = document.getElementById('thumbnailProgressFill');
  const progressText = document.getElementById('thumbnailProgressText');
  const regenerateBtn = document.getElementById('regenerateThumbnailBtn') as HTMLButtonElement;
  const uploadThumbnailBtn = document.getElementById('uploadThumbnailBtn') as HTMLButtonElement;

  if (!progressContainer || !progressFill || !progressText) return;

  try {
    progressContainer.style.display = 'block';
    if (regenerateBtn) regenerateBtn.disabled = true;
    if (uploadThumbnailBtn) uploadThumbnailBtn.disabled = true;
    progressFill.style.width = '10%';
    progressText.textContent = 'Uploading thumbnail...';

    const result = await uploadFileWithQuotaCheck(
      file,
      currentUser.uid,
      sequenceId,
      'image',
      currentUserProfile,
      (progress) => {
        const pct = Math.round(10 + progress.progress * 80);
        progressFill.style.width = `${pct}%`;
        progressText.textContent = `Uploading... ${pct}%`;
      }
    );

    progressFill.style.width = '100%';
    progressText.textContent = 'Thumbnail uploaded!';

    stage.skybox.thumbnailUrl = result.url;
    updateVideoThumbnailDisplay();
    markUnsaved();

    setTimeout(() => {
      progressContainer.style.display = 'none';
      progressFill.style.width = '0%';
    }, 2000);

  } catch (error) {
    console.error('Failed to upload thumbnail:', error);
    const msg = error instanceof Error ? error.message : String(error);
    progressText.textContent = msg.includes('quota') || msg.includes('storage')
      ? 'Storage quota exceeded.'
      : 'Failed to upload thumbnail.';
    progressFill.style.width = '0%';

    setTimeout(() => {
      progressContainer.style.display = 'none';
    }, 4000);
  } finally {
    if (regenerateBtn) regenerateBtn.disabled = false;
    if (uploadThumbnailBtn) uploadThumbnailBtn.disabled = false;
    fileInput.value = '';
  }
}

/**
 * Handle clear skybox
 */
function handleClearSkybox() {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];

  delete stage.skybox;

  // Reset form fields (with null checks)
  if (skyboxRotation) skyboxRotation.value = '0';
  if (skyboxRotationNumber) skyboxRotationNumber.value = '0';
  if (skyboxColor) skyboxColor.value = '#000000';
  if (skyboxColorHex) skyboxColorHex.value = '#000000';

  // Clear all variant URL inputs (image skybox)
  const variantUrlDesktop = document.getElementById('variantUrlDesktop') as HTMLInputElement;
  const variantUrlMobile = document.getElementById('variantUrlMobile') as HTMLInputElement;
  const variantUrlTablet = document.getElementById('variantUrlTablet') as HTMLInputElement;
  const variantUrlVR = document.getElementById('variantUrlVR') as HTMLInputElement;
  const variantUrlFallback = document.getElementById('variantUrlFallback') as HTMLInputElement;
  if (variantUrlDesktop) variantUrlDesktop.value = '';
  if (variantUrlMobile) variantUrlMobile.value = '';
  if (variantUrlTablet) variantUrlTablet.value = '';
  if (variantUrlVR) variantUrlVR.value = '';
  if (variantUrlFallback) variantUrlFallback.value = '';

  // Clear video URL inputs
  const videoSkyboxUrl = document.getElementById('videoSkyboxUrl') as HTMLInputElement;
  const videoSkyboxHlsUrl = document.getElementById('videoSkyboxHlsUrl') as HTMLInputElement;
  if (videoSkyboxUrl) videoSkyboxUrl.value = '';
  if (videoSkyboxHlsUrl) videoSkyboxHlsUrl.value = '';

  // Reset video options
  if (skyboxAutoplay) skyboxAutoplay.checked = true;
  if (skyboxLoop) skyboxLoop.checked = true;

  // Hide video preview/thumbnail
  const skyboxVideoPreviewGroup = document.getElementById('skyboxVideoPreviewGroup');
  if (skyboxVideoPreviewGroup) skyboxVideoPreviewGroup.style.display = 'none';

  // Reset the variant display UI
  displaySkyboxVariants(stage);

  // Update UI visibility based on currently selected type (don't force to image)
  updateSkyboxTypeUI();

  // Update upload button text to reflect empty state
  updateSkyboxUploadButtonText();

  markUnsaved();
  updatePreview();
}

/**
 * Handle add model
 */
function handleAddModel() {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];

  if (!stage.models) stage.models = [];

  stage.models.push({
    url: '',
    position: { x: 0, y: 0, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    scale: { x: 1, y: 1, z: 1 }
  });

  renderModelsList(stage.models);
  markUnsaved();
}

/**
 * Handle 3D model file upload
 */
async function handleModelFileUpload(e: Event) {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (!file || !sequenceId || !currentUser || !config || !currentUserProfile) return;

  // Validate file type
  const validExtensions = ['.glb', '.gltf'];
  const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
  if (!validExtensions.includes(ext)) {
    showAlert('Please upload a .glb or .gltf file', 'Invalid File Type', '⚠️');
    return;
  }

  try {
    showAlert('Uploading 3D model...', 'Please Wait', '⏳');

    const result = await uploadFileWithQuotaCheck(
      file,
      currentUser.uid,
      sequenceId,
      'model',
      currentUserProfile
    );

    const stage = config.stages[currentStageIndex];
    if (!stage.models) stage.models = [];

    stage.models.push({
      url: result.url,
      position: { x: 0, y: 0, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
      scale: { x: 1, y: 1, z: 1 }
    });

    renderModelsList(stage.models);
    markUnsaved();
    updatePreview();
    showAlert('3D model uploaded successfully!', 'Success', '✓');
  } catch (error: any) {
    console.error('Model upload error:', error);
    showAlert(error.message || 'Failed to upload model', 'Error', '✕');
  }

  // Reset file input
  if (modelFileInput) modelFileInput.value = '';
}

/**
 * Update model
 * When editing a model (gizmo attached), uses direct transform update instead of full reload.
 * This keeps the gizmo attached and provides instant visual feedback.
 */
(window as any).updateModel = function (index: number, field: string, value: any) {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];
  if (!stage.models || !stage.models[index]) return;

  const model = stage.models[index];
  let isTransformChange = false;

  // IMPORTANT: Before modifying config, get current transform from 3D preview
  // This ensures we preserve the actual current values when changing only one property
  // (e.g., changing scale shouldn't reset position that was moved via gizmo)
  let currentTransform: { position: { x: number; y: number; z: number }; rotation: { x: number; y: number; z: number }; scale: { x: number; y: number; z: number } } | null = null;
  if (model.url && editorPreview) {
    currentTransform = editorPreview.getModelTransform(model.url);
    if (currentTransform) {
      // Sync config with current preview state before applying new change
      model.position = currentTransform.position;
      model.rotation = currentTransform.rotation;
      model.scale = currentTransform.scale;
      console.log('[Editor] Synced config with current preview transform:', currentTransform);
    }
  }

  if (field === 'url') {
    model.url = value;
  } else if (field.startsWith('position.')) {
    const axis = field.split('.')[1] as 'x' | 'y' | 'z';
    if (!model.position) model.position = { x: 0, y: 0, z: 0 };
    if (axis) {
      (model.position as any)[axis] = parseFloat(value) || 0;
      isTransformChange = true;
    }
  } else if (field.startsWith('rotation.')) {
    const axis = field.split('.')[1] as 'x' | 'y' | 'z';
    if (!model.rotation) model.rotation = { x: 0, y: 0, z: 0 };
    if (axis) {
      const degrees = parseFloat(value) || 0;
      (model.rotation as any)[axis] = (degrees * Math.PI) / 180;
      isTransformChange = true;
    }
  } else if (field === 'scale') {
    const scaleVal = parseFloat(value) || 1;
    model.scale = { x: scaleVal, y: scaleVal, z: scaleVal };
    isTransformChange = true;
  }

  markUnsaved();

  // For transform changes, always try direct update first (more efficient and keeps gizmo attached)
  // This works whether or not "Edit in Preview" is active
  if (isTransformChange && model.url && editorPreview) {
    const success = editorPreview.setModelTransform(
      model.url,
      model.position,
      model.rotation,
      model.scale
    );
    if (success) {
      console.log('[Editor] Direct transform update applied for model:', index);
      return; // Skip full preview reload - transforms applied directly
    }
    // If direct transform failed (model not loaded yet), fall through to full reload
  }

  // Full preview update for non-transform changes or when direct transform isn't possible
  updatePreview();
};

/**
 * Delete model
 */
(window as any).deleteModel = async function (index: number) {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];
  if (!stage.models || !stage.models[index]) return;

  const confirmed = await showConfirm('Are you sure you want to delete this model?', 'Delete Model', '🗑️');
  if (confirmed) {
    const modelUrl = stage.models[index].url;

    // If this model was being edited, close the editor first
    if (editingModelIndex === index) {
      hideModelEditor();
    } else if (editingModelIndex > index) {
      // Adjust editing index if we deleted a model before it
      editingModelIndex--;
    }

    // Remove from 3D preview
    if (modelUrl && editorPreview) {
      editorPreview.unloadModel(modelUrl);
    }

    // Remove from config
    stage.models.splice(index, 1);
    renderModelsList(stage.models);
    markUnsaved();
  }
};

/**
 * Handle add plane
 */
function handleAddPlane() {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];

  if (!stage.planes) stage.planes = [];

  stage.planes.push({
    type: 'image',
    url: '',
    position: { x: 0, y: 1.5, z: -3 },
    rotation: { x: 0, y: 0, z: 0 },
    scale: { width: 1.5, height: 1 }
  });

  renderPlanesList(stage.planes);
  markUnsaved();
}

/**
 * Handle 2D plane file upload (image or video)
 */
async function handlePlaneFileUpload(e: Event) {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (!file || !sequenceId || !currentUser || !config || !currentUserProfile) return;

  // Determine if it's an image or video
  const isVideo = file.type.startsWith('video/');
  const assetType = isVideo ? 'video' : 'image';

  try {
    showAlert(`Uploading ${isVideo ? 'video' : 'image'}...`, 'Please Wait', '⏳');

    const result = await uploadFileWithQuotaCheck(
      file,
      currentUser.uid,
      sequenceId,
      assetType,
      currentUserProfile
    );

    const stage = config.stages[currentStageIndex];
    if (!stage.planes) stage.planes = [];

    stage.planes.push({
      type: isVideo ? 'video' : 'image',
      url: result.url,
      position: { x: 0, y: 1.5, z: -3 },
      rotation: { x: 0, y: 0, z: 0 },
      scale: { width: 1.5, height: 1 }
    });

    renderPlanesList(stage.planes);
    markUnsaved();
    updatePreview();
    showAlert(`${isVideo ? 'Video' : 'Image'} plane added successfully!`, 'Success', '✓');
  } catch (error: any) {
    console.error('Plane upload error:', error);
    showAlert(error.message || 'Failed to upload file', 'Error', '✕');
  }

  // Reset file input
  if (planeFileInput) planeFileInput.value = '';
}

/**
 * Update plane
 */
(window as any).updatePlane = function (index: number, field: string, value: any) {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];
  if (!stage.planes || !stage.planes[index]) return;

  const plane = stage.planes[index];

  // IMPORTANT: Get current transform from 3D preview BEFORE modifying config
  // This ensures we preserve the actual state of the plane and don't reset other values
  if (editorPreview) {
    const currentTransform = editorPreview.getPlaneTransform(index);
    if (currentTransform) {
      plane.position = currentTransform.position;
      plane.rotation = currentTransform.rotation;
      plane.scale = currentTransform.scale;
    }
  }

  // Track if we need direct transform update vs full reload
  let useDirectTransform = false;

  if (field === 'type') {
    plane.type = value as 'image' | 'video';
    renderPlanesList(stage.planes);
    // Type change requires reload
  } else if (field === 'url') {
    plane.url = value;
    // URL change requires reload
  } else if (field.startsWith('position.')) {
    const axis = field.split('.')[1] as 'x' | 'y' | 'z';
    if (!plane.position) plane.position = { x: 0, y: 1.5, z: -3 };
    plane.position[axis] = parseFloat(value) || 0;
    useDirectTransform = true;
  } else if (field.startsWith('rotation.')) {
    const axis = field.split('.')[1] as 'x' | 'y' | 'z';
    if (!plane.rotation) plane.rotation = { x: 0, y: 0, z: 0 };
    plane.rotation[axis] = degreesToRadians(parseFloat(value) || 0);
    useDirectTransform = true;
  } else if (field.startsWith('scale.')) {
    const dimension = field.split('.')[1] as 'width' | 'height';
    if (!plane.scale) plane.scale = { width: 1.5, height: 1 };
    plane.scale[dimension] = parseFloat(value) || 1;
    useDirectTransform = true;
  } else if (field === 'autoplay' || field === 'loop' || field === 'muted') {
    (plane as any)[field] = value;
    // These require reload to take effect
  }

  markUnsaved();

  // Use direct transform for position/rotation/scale changes (preserves gizmo state)
  if (useDirectTransform && editorPreview) {
    editorPreview.setPlaneTransform(index, plane.position, plane.rotation, plane.scale);
  } else {
    updatePreview();
  }
};

/**
 * Delete plane
 */
(window as any).deletePlane = async function (index: number) {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];
  if (!stage.planes) return;

  const confirmed = await showConfirm('Are you sure you want to delete this plane?', 'Delete Plane', '🗑️');
  if (confirmed) {
    // If this plane was being edited, close the editor first
    if (editingPlaneIndex === index) {
      hidePlaneEditor();
    } else if (editingPlaneIndex > index) {
      // Adjust editing index if we deleted a plane before it
      editingPlaneIndex--;
    }

    stage.planes.splice(index, 1);
    renderPlanesList(stage.planes);
    markUnsaved();
    updatePreview(); // Reload to remove deleted plane from 3D
  }
};

/**
 * Create custom icon upload UI for hotspots
 * Reusable across info hotspots, audio hotspots, and portals
 */
function createCustomIconUploadUI(
  hotspot: HotspotConfig,
  index: number,
  container: HTMLElement,
  rerenderFn: () => void
): void {
  const iconGroup = document.createElement('div');
  iconGroup.className = 'form-group';
  iconGroup.style.marginTop = '12px';
  iconGroup.style.marginBottom = '12px';
  iconGroup.innerHTML = `
    <label style="font-size: 12px;">Custom Icon (replaces default)</label>
    <div style="display: flex; gap: 8px; align-items: center;">
      <input type="text" class="ios-input custom-icon-url" value="${hotspot.customIconUrl || ''}" placeholder="PNG image URL" style="flex: 1; font-size: 12px;">
      <button class="ios-button ios-button-secondary upload-icon-btn" style="padding: 6px 10px; font-size: 11px;">Upload</button>
      <input type="file" class="icon-file-input" accept="image/png" style="display: none;">
    </div>
    ${hotspot.customIconUrl ? `
      <div style="margin-top: 8px; display: flex; align-items: center; gap: 8px;">
        <div style="width: 40px; height: 40px; background: rgba(0,0,0,0.1); border-radius: 8px; display: flex; align-items: center; justify-content: center; overflow: hidden;">
          <img src="${hotspot.customIconUrl}" style="max-width: 100%; max-height: 100%;">
        </div>
        <button class="ios-button ios-button-secondary remove-icon-btn" style="padding: 4px 8px; font-size: 11px; color: #ff3b30;">Remove</button>
      </div>
    ` : ''}
    <div class="upload-progress icon-progress" style="display: none; margin-top: 4px;">
      <div class="progress-bar"><div class="progress-fill"></div></div>
      <div class="progress-text" style="font-size: 10px; color: var(--ios-gray); margin-top: 2px;">Uploading...</div>
    </div>
    <div style="font-size: 10px; color: var(--ios-gray); margin-top: 4px;">PNG with transparency (256x256 or 512x512)</div>
  `;
  container.appendChild(iconGroup);

  // URL input handler
  const iconUrlInput = iconGroup.querySelector('.custom-icon-url') as HTMLInputElement;
  iconUrlInput.addEventListener('change', (e) => {
    const value = (e.target as HTMLInputElement).value;
    updateHotspot(index, 'customIconUrl', value || undefined);
  });

  // Upload button handler
  const iconUploadBtn = iconGroup.querySelector('.upload-icon-btn') as HTMLButtonElement;
  const iconFileInput = iconGroup.querySelector('.icon-file-input') as HTMLInputElement;
  const iconProgress = iconGroup.querySelector('.icon-progress') as HTMLElement;
  const iconProgressFill = iconProgress.querySelector('.progress-fill') as HTMLElement;
  const iconProgressText = iconProgress.querySelector('.progress-text') as HTMLElement;

  iconUploadBtn.addEventListener('click', () => {
    const hasUploadAccess = currentUserProfile && hasFeatureAccess(currentUserProfile, 'uploads');
    if (!hasUploadAccess) {
      if (currentUser) showUpgradeModal(currentUser.uid, currentUser.email);
      return;
    }
    iconFileInput.click();
  });

  iconFileInput.addEventListener('change', async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file || !currentUser || !sequenceId) return;

    // Validate PNG
    if (!file.type.includes('png')) {
      showAlert('Please upload a PNG image for best transparency support.', 'Wrong Format', '🖼️');
      return;
    }

    // CRITICAL FIX: Validate file size (2MB limit)
    const MAX_ICON_SIZE = 2 * 1024 * 1024; // 2MB
    if (file.size > MAX_ICON_SIZE) {
      showAlert('Icon must be under 2MB. Please optimize your image or use a smaller resolution (e.g., 512x512).', 'File Too Large', '📏');
      return;
    }

    try {
      iconProgress.style.display = 'block';
      iconProgressText.textContent = `Uploading ${file.name}...`;
      iconProgressFill.style.width = '0%';

      const result = await uploadFileWithQuotaCheck(
        file,
        currentUser.uid,
        sequenceId,
        'image',
        currentUserProfile!,
        (progress) => {
          iconProgressFill.style.width = `${progress.progress}%`;
        }
      );

      iconUrlInput.value = result.url;
      updateHotspot(index, 'customIconUrl', result.url);
      iconProgressText.textContent = '✅ Upload complete!';
      setTimeout(() => {
        iconProgress.style.display = 'none';
        rerenderFn(); // Re-render to show preview
      }, 1000);
    } catch (error: any) {
      console.error('Icon upload failed:', error);
      iconProgressText.textContent = `❌ Error: ${error.message}`;
    }
    iconFileInput.value = '';
  });

  // Remove icon button handler
  const removeIconBtn = iconGroup.querySelector('.remove-icon-btn');
  if (removeIconBtn) {
    removeIconBtn.addEventListener('click', () => {
      updateHotspot(index, 'customIconUrl', undefined);
      rerenderFn(); // Re-render to update UI
    });
  }
}

/**
 * Render info hotspots list (Info/Audio)
 */
function renderInfoHotspotsList(hotspots: HotspotConfig[]) {
  hotspotsList.innerHTML = ''; // Clear previous list

  // Filter for info/content types (exclude navigation and pure audio)
  // Includes: info, both (info+audio), and lead_capture
  console.log('[Editor] renderInfoHotspotsList - all hotspots:', hotspots.map(h => ({ id: h.id, type: h.type })));
  const infoHotspots = hotspots
    .map((h, originalIndex) => ({ ...h, originalIndex }))
    .filter(h => h.type === 'info' || h.type === 'both' || h.type === 'lead_capture');
  console.log('[Editor] renderInfoHotspotsList - filtered hotspots:', infoHotspots.map(h => ({ id: h.id, type: h.type })));

  if (infoHotspots.length === 0) {
    hotspotsList.innerHTML = `
      <div class="empty-state">

      </div>
    `;
    return;
  }

  infoHotspots.forEach((hotspot) => {
    const index = hotspot.originalIndex; // Use original index for updates
    const item = document.createElement('div');
    item.className = 'item-card';
    item.dataset.index = index.toString();
    item.dataset.id = hotspot.id; // For highlight-on-click feature

    // Header
    const header = document.createElement('div');
    header.className = 'item-header';
    header.innerHTML = `
      <span class="item-title">Hotspot ${index + 1}</span>
      <button class="btn-icon delete-hotspot-btn">🗑️</button>
    `;
    
    // Delete button handler
    header.querySelector('.delete-hotspot-btn')!.addEventListener('click', () => {
      deleteHotspot(index);
    });

    // Content
    const content = document.createElement('div');
    
    // Type Selector
    const typeGroup = document.createElement('div');
    typeGroup.className = 'form-group';
    typeGroup.innerHTML = `
      <label>Type</label>
      <select class="ios-input hotspot-type">
        <option value="info" ${hotspot.type === 'info' ? 'selected' : ''}>📄 Info</option>
        <option value="both" ${hotspot.type === 'both' ? 'selected' : ''}>🔊 Info + Audio</option>
        <option value="lead_capture" ${hotspot.type === 'lead_capture' ? 'selected' : ''}>📋 Lead Capture</option>
      </select>
    `;
    content.appendChild(typeGroup);

    // Position Inputs
    const posGroup = document.createElement('div');
    posGroup.className = 'form-group';
    posGroup.innerHTML = `
      <label>Position</label>
      <div class="form-row" style="display: flex; gap: 8px;">
        <input type="number" step="0.1" placeholder="X" class="ios-input pos-x" value="${hotspot.position.x || 0}" style="flex: 1;">
        <input type="number" step="0.1" placeholder="Y" class="ios-input pos-y" value="${hotspot.position.y || 0}" style="flex: 1;">
        <input type="number" step="0.1" placeholder="Z" class="ios-input pos-z" value="${hotspot.position.z || 0}" style="flex: 1;">
      </div>
    `;
    posGroup.querySelector('.pos-x')!.addEventListener('change', (e) => updateHotspot(index, 'position.x', (e.target as HTMLInputElement).value));
    posGroup.querySelector('.pos-y')!.addEventListener('change', (e) => updateHotspot(index, 'position.y', (e.target as HTMLInputElement).value));
    posGroup.querySelector('.pos-z')!.addEventListener('change', (e) => updateHotspot(index, 'position.z', (e.target as HTMLInputElement).value));
    content.appendChild(posGroup);

    // Info Fields (hidden when type is lead_capture)
    const infoGroup = document.createElement('div');
    infoGroup.className = 'form-group info-fields-section';
    infoGroup.style.display = hotspot.type === 'lead_capture' ? 'none' : 'block';
    infoGroup.innerHTML = `
      <label>Info Title</label>
      <input type="text" class="ios-input info-title" value="${hotspot.infoTitle || ''}" placeholder="Title">

      <label style="margin-top: 12px;">Description</label>
      <textarea class="ios-input info-desc" placeholder="Description" style="min-height: 80px; resize: vertical;">${hotspot.infoDescription || ''}</textarea>
      
      <label style="margin-top: 8px;">Content Type</label>
      <div class="ios-segmented-control" style="margin-bottom: 12px; display: flex; background: rgba(118, 118, 128, 0.24); padding: 2px; border-radius: 8px;">
        <label style="flex: 1; text-align: center; padding: 6px; cursor: pointer; border-radius: 6px; margin: 0; font-size: 13px;" class="segment-option ${(!hotspot.videoUrl && !hotspot.iframeUrl) ? 'active-segment' : ''}">
          <input type="radio" name="contentType-${index}" value="image" ${(!hotspot.videoUrl && !hotspot.iframeUrl) ? 'checked' : ''} style="display: none;"> Image
        </label>
        <label style="flex: 1; text-align: center; padding: 6px; cursor: pointer; border-radius: 6px; margin: 0; font-size: 13px;" class="segment-option ${hotspot.videoUrl ? 'active-segment' : ''}">
          <input type="radio" name="contentType-${index}" value="video" ${hotspot.videoUrl ? 'checked' : ''} style="display: none;"> Video
        </label>
        <label style="flex: 1; text-align: center; padding: 6px; cursor: pointer; border-radius: 6px; margin: 0; font-size: 13px;" class="segment-option ${hotspot.iframeUrl ? 'active-segment' : ''}">
          <input type="radio" name="contentType-${index}" value="iframe" ${hotspot.iframeUrl ? 'checked' : ''} style="display: none;"> Iframe
        </label>
      </div>

      <div class="content-section section-image" style="display: ${(!hotspot.videoUrl && !hotspot.iframeUrl) ? 'block' : 'none'};">
        <label>Image URL</label>
        <div style="display: flex; gap: 8px;">
          <input type="text" class="ios-input info-image" value="${hotspot.infoImageUrl || ''}" placeholder="https://example.com/image.jpg" style="flex: 1;">
          <button class="ios-button ios-button-secondary upload-image-btn" style="padding: 8px 12px; font-size: 12px;">Upload</button>
          <input type="file" class="image-file-input" accept="image/*" style="display: none;">
        </div>
        ${hotspot.infoImageUrl ? `<div style="margin-top: 8px; width: 100%; height: 120px; background: rgba(0,0,0,0.2); border-radius: 8px; display: flex; align-items: center; justify-content: center; overflow: hidden; border: 1px solid var(--glass-border);"><img src="${hotspot.infoImageUrl}" style="width: 100%; height: 100%; object-fit: contain;"></div>` : ''}
        <div class="upload-progress image-progress" style="display: none; margin-top: 4px;">
          <div class="progress-bar"><div class="progress-fill"></div></div>
          <div class="progress-text" style="font-size: 10px; color: var(--ios-gray); margin-top: 2px;">Uploading...</div>
        </div>
      </div>

      <div class="content-section section-video" style="display: ${hotspot.videoUrl || hotspot.videoMp4Url ? 'block' : 'none'};">
        <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;">
          <label style="margin: 0;">Video</label>
          <div style="display: flex; align-items: center; gap: 8px;">
            <button class="ios-button ios-button-secondary upload-video-btn" style="padding: 6px 12px; font-size: 11px;">Upload Video</button>
            <input type="file" class="video-file-input" accept="video/*" style="display: none;">
          </div>
        </div>

        <!-- HLS Streaming URL (primary) -->
        <div style="margin-bottom: 8px;">
          <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 4px;">
            <label style="margin: 0; font-size: 11px; color: var(--ios-gray);">Streaming URL (HLS)</label>
            <span style="font-size: 9px; padding: 1px 6px; background: #34c759; color: white; border-radius: 8px;">Recommended</span>
          </div>
          <input type="text" class="ios-input info-video-hls" value="${hotspot.videoUrl || ''}" placeholder=".m3u8 URL for adaptive streaming" style="font-size: 12px;">
        </div>

        <!-- MP4 Fallback URL -->
        <div style="margin-bottom: 8px;">
          <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 4px;">
            <label style="margin: 0; font-size: 11px; color: var(--ios-gray);">Direct URL (MP4)</label>
            <span style="font-size: 9px; padding: 1px 6px; background: var(--ios-light-gray); color: var(--ios-gray); border-radius: 8px;">Fallback</span>
          </div>
          <input type="text" class="ios-input info-video-mp4" value="${hotspot.videoMp4Url || ''}" placeholder=".mp4 URL for direct playback" style="font-size: 12px;">
        </div>

        <p style="font-size: 10px; color: var(--ios-gray); margin: 0 0 8px 0;">
          💡 Upload fills both automatically. Or paste URLs directly - use HLS for streaming, MP4 for simple playback.
        </p>

        ${hotspot.videoUrl || hotspot.videoMp4Url ? `<video src="${hotspot.videoUrl || hotspot.videoMp4Url}" controls style="width: 100%; max-height: 150px; border-radius: 8px; border: 1px solid var(--glass-border);"></video>` : ''}
        <div class="upload-progress video-progress" style="display: none; margin-top: 8px;">
          <div class="video-upload-steps" style="display: flex; gap: 4px; margin-bottom: 6px; font-size: 10px;">
            <span class="step step-upload" style="padding: 2px 8px; border-radius: 10px; background: var(--ios-light-gray); color: var(--ios-gray);">① Upload</span>
            <span class="step step-process" style="padding: 2px 8px; border-radius: 10px; background: var(--ios-light-gray); color: var(--ios-gray);">② Processing</span>
            <span class="step step-ready" style="padding: 2px 8px; border-radius: 10px; background: var(--ios-light-gray); color: var(--ios-gray);">③ Ready</span>
          </div>
          <div class="progress-bar"><div class="progress-fill"></div></div>
          <div class="progress-text" style="font-size: 11px; color: var(--ios-gray); margin-top: 4px;">Uploading...</div>
        </div>
      </div>

      <div class="content-section section-iframe" style="display: ${hotspot.iframeUrl ? 'block' : 'none'};">
        <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px;">
          <label style="margin: 0;">Iframe URL</label>
          <span style="font-size: 10px; padding: 2px 8px; background: linear-gradient(135deg, #667eea, #764ba2); color: white; border-radius: 10px; font-weight: 500;"><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style="vertical-align: middle; margin-right: 2px;"><path d="M20 6H4c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h3.5l1.71-3h5.58l1.71 3H20c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zM8 13c-.83 0-1.5-.67-1.5-1.5S7.17 10 8 10s1.5.67 1.5 1.5S8.83 13 8 13zm8 0c-.83 0-1.5-.67-1.5-1.5S15.17 10 16 10s1.5.67 1.5 1.5S16.83 13 16 13z"/></svg>VR: Coming Soon</span>
        </div>
        <input type="text" class="ios-input info-iframe" value="${hotspot.iframeUrl || ''}" placeholder="https://example.com/embed">
        ${hotspot.iframeUrl ? `<iframe src="${hotspot.iframeUrl}" style="width: 100%; height: 150px; margin-top: 8px; border-radius: 8px; border: 1px solid var(--glass-border);"></iframe>` : ''}
      </div>

      <div style="display: flex; gap: 12px; margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--glass-border);">
        <div style="flex: 1;">
          <label>Link Text</label>
          <input type="text" class="ios-input link-text" value="${hotspot.linkText || ''}" placeholder="Learn More">
        </div>
        <div style="flex: 1;">
          <label>Link URL</label>
          <input type="text" class="ios-input link-url" value="${hotspot.linkUrl || ''}" placeholder="https://example.com">
        </div>
      </div>
    `;

    // Lead Capture Form Section (SEPARATE from infoGroup - shown when type is lead_capture)
    const leadFormGroup = document.createElement('div');
    leadFormGroup.className = 'lead-form-section';
    leadFormGroup.style.display = hotspot.type === 'lead_capture' ? 'block' : 'none';
    leadFormGroup.style.marginTop = '16px';
    leadFormGroup.style.paddingTop = '16px';
    leadFormGroup.style.borderTop = '2px solid var(--glass-border)';
    leadFormGroup.innerHTML = `
      <h4 style="margin: 0 0 12px 0; font-size: 14px; font-weight: 600; color: var(--ios-black);">📋 Lead Capture Form</h4>

      <div class="lead-form-config">
        <!-- Form Type Preset -->
        <div class="form-group" style="margin-bottom: 12px;">
          <label>Form Template</label>
          <select class="ios-input lead-form-type">
            <option value="email_capture" ${(hotspot.leadForm?.formType || 'email_capture') === 'email_capture' ? 'selected' : ''}>📧 Email Capture</option>
            <option value="contact" ${hotspot.leadForm?.formType === 'contact' ? 'selected' : ''}>💬 Contact Form</option>
            <option value="inquiry" ${hotspot.leadForm?.formType === 'inquiry' ? 'selected' : ''}>📝 Inquiry Form</option>
            <option value="custom" ${hotspot.leadForm?.formType === 'custom' ? 'selected' : ''}>🔧 Custom</option>
          </select>
        </div>

        <!-- Form Title -->
        <div class="form-group" style="margin-bottom: 12px;">
          <label>Form Title</label>
          <input type="text" class="ios-input lead-form-title" value="${hotspot.leadForm?.title || ''}" placeholder="Stay Updated">
        </div>

        <!-- Form Description -->
        <div class="form-group" style="margin-bottom: 12px;">
          <label>Description</label>
          <textarea class="ios-input lead-form-description" placeholder="Optional description..." style="min-height: 60px; resize: vertical;">${hotspot.leadForm?.description || ''}</textarea>
        </div>

        <!-- Fields Builder -->
        <div class="form-group" style="margin-bottom: 12px;">
          <label style="display: flex; align-items: center; justify-content: space-between;">
            Form Fields
            <button type="button" class="ios-button ios-button-secondary add-form-field-btn" style="padding: 4px 8px; font-size: 11px;">+ Add Field</button>
          </label>
          <div class="form-fields-list" style="background: rgba(0,0,0,0.05); border-radius: 8px; padding: 8px; margin-top: 8px;">
            <!-- Fields will be rendered here -->
          </div>
        </div>

        <!-- Submit Button Text -->
        <div class="form-group" style="margin-bottom: 12px;">
          <label>Submit Button Text</label>
          <input type="text" class="ios-input lead-form-submit-text" value="${hotspot.leadForm?.submitButtonText || 'Submit'}" placeholder="Submit">
        </div>

        <!-- Success Message -->
        <div class="form-group" style="margin-bottom: 12px;">
          <label>Success Message</label>
          <input type="text" class="ios-input lead-form-success-msg" value="${hotspot.leadForm?.successMessage || ''}" placeholder="Thank you for your submission!">
        </div>

        <!-- Delivery Options -->
        <div style="margin-top: 16px; padding-top: 12px; border-top: 1px dashed var(--glass-border);">
          <label style="font-size: 12px; color: var(--ios-gray); text-transform: uppercase; letter-spacing: 0.5px;">Delivery Options</label>

          <div class="form-group" style="margin-top: 8px; margin-bottom: 12px;">
            <label>Notification Email</label>
            <input type="email" class="ios-input lead-form-notify-email" value="${hotspot.leadForm?.notificationEmail || ''}" placeholder="you@example.com">
          </div>

          <div class="form-group" style="margin-bottom: 12px;">
            <label>Webhook URL <span style="font-size: 10px; color: var(--ios-gray);">(Zapier, HubSpot, etc.)</span></label>
            <input type="url" class="ios-input lead-form-webhook" value="${hotspot.leadForm?.webhookUrl || ''}" placeholder="https://hooks.zapier.com/...">
          </div>

          <div class="form-group" style="margin-bottom: 12px;">
            <label>Redirect URL <span style="font-size: 10px; color: var(--ios-gray);">(after submit)</span></label>
            <input type="url" class="ios-input lead-form-redirect" value="${hotspot.leadForm?.redirectUrl || ''}" placeholder="https://example.com/thank-you">
          </div>
        </div>
      </div>
    `;
    
    // Toggle Logic
    const segmentOptions = infoGroup.querySelectorAll('.segment-option');
    const sections = {
      'image': infoGroup.querySelector('.section-image') as HTMLElement,
      'video': infoGroup.querySelector('.section-video') as HTMLElement,
      'iframe': infoGroup.querySelector('.section-iframe') as HTMLElement
    };

    segmentOptions.forEach(option => {
      option.addEventListener('click', (e) => {
        // Update UI
        segmentOptions.forEach(opt => {
          opt.classList.remove('active-segment');
          (opt as HTMLElement).style.background = 'transparent';
          (opt as HTMLElement).style.boxShadow = 'none';
        });
        const target = e.currentTarget as HTMLElement;
        target.classList.add('active-segment');
        target.style.background = 'white';
        target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)';
        
        // Show/Hide Sections
        const input = target.querySelector('input') as HTMLInputElement;
        const value = input.value as 'image' | 'video' | 'iframe';

        Object.values(sections).forEach(s => s.style.display = 'none');
        if (value === 'image') sections.image.style.display = 'block';
        if (value === 'video') sections.video.style.display = 'block';
        if (value === 'iframe') sections.iframe.style.display = 'block';

        // Save the selected content type so viewer knows which to display
        updateHotspot(index, 'infoContentType', value);
      });
      
      // Initialize styles for active segment
      if (option.classList.contains('active-segment')) {
        (option as HTMLElement).style.background = 'white';
        (option as HTMLElement).style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)';
      }
    });

    infoGroup.querySelector('.info-title')!.addEventListener('change', (e) => updateHotspot(index, 'infoTitle', (e.target as HTMLInputElement).value));
    infoGroup.querySelector('.info-desc')!.addEventListener('change', (e) => updateHotspot(index, 'infoDescription', (e.target as HTMLTextAreaElement).value));
    
    // Image URL input
    const imageInput = infoGroup.querySelector('.info-image') as HTMLInputElement;
    imageInput.addEventListener('change', (e) => updateHotspot(index, 'infoImageUrl', (e.target as HTMLInputElement).value));
    
    // Image Upload
    const imageUploadBtn = infoGroup.querySelector('.upload-image-btn') as HTMLButtonElement;
    const imageFileInput = infoGroup.querySelector('.image-file-input') as HTMLInputElement;
    const imageProgress = infoGroup.querySelector('.image-progress') as HTMLElement;
    const imageProgressFill = imageProgress.querySelector('.progress-fill') as HTMLElement;
    const imageProgressText = imageProgress.querySelector('.progress-text') as HTMLElement;

    imageUploadBtn.addEventListener('click', () => {
      const hasUploadAccess = currentUserProfile && hasFeatureAccess(currentUserProfile, 'uploads');
      if (!hasUploadAccess) {
        if (currentUser) showUpgradeModal(currentUser.uid, currentUser.email);
        return;
      }
      imageFileInput.click();
    });
    imageFileInput.addEventListener('change', async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file || !currentUser || !sequenceId) return;

      try {
        imageProgress.style.display = 'block';
        imageProgressText.textContent = `Uploading ${file.name}...`;
        imageProgressFill.style.width = '0%';

        const result = await uploadFileWithQuotaCheck(
          file,
          currentUser.uid,
          sequenceId,
          'image',
          currentUserProfile!,
          (progress) => {
            imageProgressFill.style.width = `${progress.progress}%`;
          }
        );

        imageInput.value = result.url;
        updateHotspot(index, 'infoImageUrl', result.url);
        imageProgressText.textContent = '✅ Upload complete!';
        setTimeout(() => { imageProgress.style.display = 'none'; }, 2000);
      } catch (error: any) {
        console.error('Upload failed:', error);
        imageProgressText.textContent = `❌ Error: ${error.message}`;
      }
      imageFileInput.value = '';
    });

    // Video URL inputs (HLS streaming + MP4 fallback)
    const videoHlsInput = infoGroup.querySelector('.info-video-hls') as HTMLInputElement;
    const videoMp4Input = infoGroup.querySelector('.info-video-mp4') as HTMLInputElement;
    videoHlsInput.addEventListener('change', (e) => updateHotspot(index, 'videoUrl', (e.target as HTMLInputElement).value));
    videoMp4Input.addEventListener('change', (e) => updateHotspot(index, 'videoMp4Url', (e.target as HTMLInputElement).value));

    // Video Upload
    const videoUploadBtn = infoGroup.querySelector('.upload-video-btn') as HTMLButtonElement;
    const videoFileInput = infoGroup.querySelector('.video-file-input') as HTMLInputElement;
    const videoProgress = infoGroup.querySelector('.video-progress') as HTMLElement;
    const videoProgressFill = videoProgress.querySelector('.progress-fill') as HTMLElement;
    const videoProgressText = videoProgress.querySelector('.progress-text') as HTMLElement;

    videoUploadBtn.addEventListener('click', () => {
      const hasUploadAccess = currentUserProfile && hasFeatureAccess(currentUserProfile, 'uploads');
      if (!hasUploadAccess) {
        if (currentUser) showUpgradeModal(currentUser.uid, currentUser.email);
        return;
      }
      videoFileInput.click();
    });
    videoFileInput.addEventListener('change', async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file || !currentUser || !sequenceId) return;

      // Check Mux video quota before uploading
      if (currentUserProfile) {
        const limit = getMuxMinutesLimit(currentUserProfile);
        if (limit === 0) {
          showToast('Video uploads are not available on your plan. Upgrade to Basic or Pro.', 'error');
          videoFileInput.value = '';
          return;
        }
        if (!canUploadMuxVideo(currentUserProfile)) {
          const used = getMuxMinutesUsed(currentUserProfile);
          showToast(`Video quota exceeded. You've used ${formatMuxMinutes(used)} of ${formatMuxMinutes(limit)}. Please upgrade for more.`, 'error');
          videoFileInput.value = '';
          return;
        }
      }

      const uploader = getVideoUploader();

      // Get step indicator elements
      const stepUpload = videoProgress.querySelector('.step-upload') as HTMLElement;
      const stepProcess = videoProgress.querySelector('.step-process') as HTMLElement;
      const stepReady = videoProgress.querySelector('.step-ready') as HTMLElement;

      const setActiveStep = (step: 'upload' | 'process' | 'ready' | 'error') => {
        const activeStyle = 'background: var(--ios-blue); color: white;';
        const completedStyle = 'background: #34c759; color: white;';
        const pendingStyle = 'background: var(--ios-light-gray); color: var(--ios-gray);';
        const errorStyle = 'background: #ff3b30; color: white;';

        if (step === 'upload') {
          stepUpload.style.cssText = `padding: 2px 8px; border-radius: 10px; ${activeStyle}`;
          stepProcess.style.cssText = `padding: 2px 8px; border-radius: 10px; ${pendingStyle}`;
          stepReady.style.cssText = `padding: 2px 8px; border-radius: 10px; ${pendingStyle}`;
        } else if (step === 'process') {
          stepUpload.style.cssText = `padding: 2px 8px; border-radius: 10px; ${completedStyle}`;
          stepProcess.style.cssText = `padding: 2px 8px; border-radius: 10px; ${activeStyle}`;
          stepReady.style.cssText = `padding: 2px 8px; border-radius: 10px; ${pendingStyle}`;
        } else if (step === 'ready') {
          stepUpload.style.cssText = `padding: 2px 8px; border-radius: 10px; ${completedStyle}`;
          stepProcess.style.cssText = `padding: 2px 8px; border-radius: 10px; ${completedStyle}`;
          stepReady.style.cssText = `padding: 2px 8px; border-radius: 10px; ${completedStyle}`;
        } else if (step === 'error') {
          stepUpload.style.cssText = `padding: 2px 8px; border-radius: 10px; ${errorStyle}`;
          stepProcess.style.cssText = `padding: 2px 8px; border-radius: 10px; ${pendingStyle}`;
          stepReady.style.cssText = `padding: 2px 8px; border-radius: 10px; ${pendingStyle}`;
        }
      };

      try {
        // Disable button to prevent double uploads
        videoUploadBtn.disabled = true;
        videoUploadBtn.textContent = 'Uploading...';

        videoProgress.style.display = 'block';
        videoProgressFill.style.width = '0%';
        setActiveStep('upload');
        videoProgressText.textContent = `Uploading "${file.name}" to cloud...`;

        // Phase 1: Upload to Mux with progress tracking
        const uploadResult = await uploader.upload(file, (progress) => {
          videoProgressFill.style.width = `${progress.percent}%`;
          videoProgressText.textContent = `Uploading to cloud... ${progress.percent}%`;
        });

        // Phase 2: Wait for Mux transcoding to complete
        setActiveStep('process');
        videoProgressFill.style.width = '100%';
        videoProgressText.textContent = 'Converting video for streaming (1-2 min)...';

        const readyResult = await uploader.waitForReady(uploadResult.uploadId, (status) => {
          if (status === 'processing') {
            videoProgressText.textContent = 'Optimizing for all devices & network speeds...';
          }
        });

        // Phase 3: Save both HLS and MP4 URLs
        if (readyResult.hlsUrl) {
          // Save HLS URL (primary streaming format)
          videoHlsInput.value = readyResult.hlsUrl;
          updateHotspot(index, 'videoUrl', readyResult.hlsUrl);

          // Save MP4 URL if available (fallback format)
          if (readyResult.mp4Url) {
            videoMp4Input.value = readyResult.mp4Url;
            updateHotspot(index, 'videoMp4Url', readyResult.mp4Url);
          }

          setActiveStep('ready');
          videoProgressText.textContent = '✅ Video ready! Both streaming and direct playback URLs saved.';
        } else {
          throw new Error('Video processing completed but no streaming URL was returned');
        }

        setTimeout(() => { videoProgress.style.display = 'none'; }, 3000);
      } catch (error: any) {
        console.error('Video upload failed:', error);
        setActiveStep('error');
        videoProgressText.textContent = `❌ ${error.message}`;
        uploader.dispose(); // Clean up any pending watchers
      } finally {
        // Re-enable button
        videoUploadBtn.disabled = false;
        videoUploadBtn.textContent = 'Upload Video';
      }
      videoFileInput.value = '';
    });

    infoGroup.querySelector('.info-iframe')!.addEventListener('change', (e) => updateHotspot(index, 'iframeUrl', (e.target as HTMLInputElement).value));
    infoGroup.querySelector('.link-text')!.addEventListener('change', (e) => updateHotspot(index, 'linkText', (e.target as HTMLInputElement).value));
    infoGroup.querySelector('.link-url')!.addEventListener('change', (e) => updateHotspot(index, 'linkUrl', (e.target as HTMLInputElement).value));

    // ========================================================================
    // Lead Capture Form Event Handlers
    // ========================================================================
    // leadFormGroup is a SEPARATE element from infoGroup (created above at line ~2800)
    // This allows showing leadFormGroup while hiding infoGroup for lead_capture type
    const leadFormSection = leadFormGroup; // Use the separate element directly
    const leadFormTypeSelect = leadFormSection.querySelector('.lead-form-type') as HTMLSelectElement;
    const leadFormFieldsList = leadFormSection.querySelector('.form-fields-list') as HTMLElement;
    const addFormFieldBtn = leadFormSection.querySelector('.add-form-field-btn') as HTMLButtonElement;

    // Initialize leadForm object if not present (auto-enabled for lead_capture type)
    const initLeadForm = () => {
      if (!hotspot.leadForm) {
        hotspot.leadForm = {
          enabled: hotspot.type === 'lead_capture',
          formType: 'email_capture',
          title: '',
          fields: [],
          submitButtonText: 'Submit',
          successMessage: 'Thank you for your submission!',
          honeypotEnabled: true,
          allowDismiss: true
        };
      } else if (hotspot.type === 'lead_capture') {
        hotspot.leadForm.enabled = true;
      }
    };

    // Type change handler - show/hide sections based on type
    const hotspotTypeSelect = typeGroup.querySelector('.hotspot-type') as HTMLSelectElement;
    hotspotTypeSelect.addEventListener('change', (e) => {
      const newType = (e.target as HTMLSelectElement).value as 'info' | 'both' | 'lead_capture';
      const isLeadCapture = newType === 'lead_capture';

      // Check tier limits before allowing lead_capture type
      if (isLeadCapture && currentUserProfile) {
        const tier = currentUserProfile.tier || 'free';
        if (!canCreateForms(tier)) {
          (e.target as HTMLSelectElement).value = hotspot.type; // Revert
          showUpgradeModal(currentUser!.uid, currentUser!.email);
          return;
        }

        // Count existing forms in this sequence (excluding this one if already lead_capture)
        const existingFormCount = countFormsInSequence() - (hotspot.type === 'lead_capture' ? 1 : 0);
        if (isFormLimitReached(tier, existingFormCount)) {
          (e.target as HTMLSelectElement).value = hotspot.type; // Revert
          showAlert('Form limit reached', `Your ${tier} plan allows ${FORM_LIMITS_BY_TIER[tier]} form(s) per sequence. Upgrade to Pro for unlimited forms.`);
          return;
        }
      }

      updateHotspot(index, 'type', newType);

      // Show/hide sections based on type
      infoGroup.style.display = isLeadCapture ? 'none' : 'block';
      leadFormSection.style.display = isLeadCapture ? 'block' : 'none';

      // Auto-enable leadForm when switching to lead_capture
      if (isLeadCapture) {
        initLeadForm();
        // Load default preset if no fields
        if (!hotspot.leadForm!.fields || hotspot.leadForm!.fields.length === 0) {
          const preset = FORM_PRESETS[hotspot.leadForm!.formType || 'email_capture'];
          hotspot.leadForm = { ...hotspot.leadForm, ...preset, enabled: true };
          (leadFormSection.querySelector('.lead-form-title') as HTMLInputElement).value = preset.title;
          (leadFormSection.querySelector('.lead-form-description') as HTMLTextAreaElement).value = preset.description || '';
          (leadFormSection.querySelector('.lead-form-submit-text') as HTMLInputElement).value = preset.submitButtonText;
          (leadFormSection.querySelector('.lead-form-success-msg') as HTMLInputElement).value = preset.successMessage;
          renderFormFields();
        }
        updateHotspot(index, 'leadForm', hotspot.leadForm);
      } else {
        // Disable leadForm when switching away from lead_capture
        if (hotspot.leadForm) {
          hotspot.leadForm.enabled = false;
          updateHotspot(index, 'leadForm', hotspot.leadForm);
        }
      }
    });

    // Render form fields list
    const renderFormFields = () => {
      initLeadForm();
      const fields = hotspot.leadForm!.fields || [];

      if (fields.length === 0) {
        leadFormFieldsList.innerHTML = '<div style="text-align: center; padding: 16px; color: var(--ios-gray); font-size: 12px;">No fields added. Add a field or select a template.</div>';
        return;
      }

      leadFormFieldsList.innerHTML = fields.map((field, fieldIndex) => `
        <div class="form-field-item" data-field-index="${fieldIndex}" style="background: white; border-radius: 6px; padding: 8px; margin-bottom: 6px; border: 1px solid var(--glass-border);">
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px;">
            <select class="ios-input field-type" style="flex: 0 0 100px; font-size: 12px; padding: 4px;">
              <option value="text" ${field.type === 'text' ? 'selected' : ''}>Text</option>
              <option value="email" ${field.type === 'email' ? 'selected' : ''}>Email</option>
              <option value="tel" ${field.type === 'tel' ? 'selected' : ''}>Phone</option>
              <option value="textarea" ${field.type === 'textarea' ? 'selected' : ''}>Textarea</option>
              <option value="select" ${field.type === 'select' ? 'selected' : ''}>Dropdown</option>
              <option value="checkbox" ${field.type === 'checkbox' ? 'selected' : ''}>Checkbox</option>
            </select>
            <input type="text" class="ios-input field-label" value="${field.label || ''}" placeholder="Label" style="flex: 1; font-size: 12px; padding: 4px;">
            <label style="display: flex; align-items: center; gap: 4px; font-size: 11px; white-space: nowrap;">
              <input type="checkbox" class="field-required" ${field.required ? 'checked' : ''} style="width: auto;">
              Req
            </label>
            <button type="button" class="remove-field-btn" style="background: none; border: none; cursor: pointer; font-size: 14px; color: #ff3b30;">×</button>
          </div>
          <input type="text" class="ios-input field-placeholder" value="${field.placeholder || ''}" placeholder="Placeholder text..." style="width: 100%; font-size: 11px; padding: 4px;">
        </div>
      `).join('');

      // Add event listeners to field items
      leadFormFieldsList.querySelectorAll('.form-field-item').forEach((fieldItem, fieldIndex) => {
        // Field type change
        fieldItem.querySelector('.field-type')!.addEventListener('change', (e) => {
          const newType = (e.target as HTMLSelectElement).value as FormFieldType;
          hotspot.leadForm!.fields[fieldIndex].type = newType;
          updateHotspot(index, 'leadForm', hotspot.leadForm);
        });

        // Field label change
        fieldItem.querySelector('.field-label')!.addEventListener('change', (e) => {
          const label = (e.target as HTMLInputElement).value;
          hotspot.leadForm!.fields[fieldIndex].label = label;
          hotspot.leadForm!.fields[fieldIndex].name = label.toLowerCase().replace(/[^a-z0-9]/g, '_');
          updateHotspot(index, 'leadForm', hotspot.leadForm);
        });

        // Field required change
        fieldItem.querySelector('.field-required')!.addEventListener('change', (e) => {
          hotspot.leadForm!.fields[fieldIndex].required = (e.target as HTMLInputElement).checked;
          updateHotspot(index, 'leadForm', hotspot.leadForm);
        });

        // Field placeholder change
        fieldItem.querySelector('.field-placeholder')!.addEventListener('change', (e) => {
          hotspot.leadForm!.fields[fieldIndex].placeholder = (e.target as HTMLInputElement).value;
          updateHotspot(index, 'leadForm', hotspot.leadForm);
        });

        // Remove field button
        fieldItem.querySelector('.remove-field-btn')!.addEventListener('click', () => {
          hotspot.leadForm!.fields.splice(fieldIndex, 1);
          updateHotspot(index, 'leadForm', hotspot.leadForm);
          renderFormFields();
        });
      });
    };

    // Form type (preset) change handler
    leadFormTypeSelect.addEventListener('change', (e) => {
      const formType = (e.target as HTMLSelectElement).value as LeadFormType;
      initLeadForm();

      const preset = FORM_PRESETS[formType];
      hotspot.leadForm = {
        ...hotspot.leadForm!,
        formType,
        title: preset.title,
        description: preset.description,
        fields: [...preset.fields],
        submitButtonText: preset.submitButtonText,
        successMessage: preset.successMessage
      };

      // Update UI
      (leadFormSection.querySelector('.lead-form-title') as HTMLInputElement).value = preset.title;
      (leadFormSection.querySelector('.lead-form-description') as HTMLTextAreaElement).value = preset.description || '';
      (leadFormSection.querySelector('.lead-form-submit-text') as HTMLInputElement).value = preset.submitButtonText;
      (leadFormSection.querySelector('.lead-form-success-msg') as HTMLInputElement).value = preset.successMessage;
      renderFormFields();

      updateHotspot(index, 'leadForm', hotspot.leadForm);
    });

    // Add field button handler
    addFormFieldBtn.addEventListener('click', () => {
      initLeadForm();
      const newField: FormField = {
        id: `field_${Date.now()}`,
        name: `field_${hotspot.leadForm!.fields.length + 1}`,
        type: 'text',
        label: `Field ${hotspot.leadForm!.fields.length + 1}`,
        placeholder: '',
        required: false
      };
      hotspot.leadForm!.fields.push(newField);
      updateHotspot(index, 'leadForm', hotspot.leadForm);
      renderFormFields();
    });

    // Other input handlers
    leadFormSection.querySelector('.lead-form-title')!.addEventListener('change', (e) => {
      initLeadForm();
      hotspot.leadForm!.title = (e.target as HTMLInputElement).value;
      updateHotspot(index, 'leadForm', hotspot.leadForm);
    });

    leadFormSection.querySelector('.lead-form-description')!.addEventListener('change', (e) => {
      initLeadForm();
      hotspot.leadForm!.description = (e.target as HTMLTextAreaElement).value;
      updateHotspot(index, 'leadForm', hotspot.leadForm);
    });

    leadFormSection.querySelector('.lead-form-submit-text')!.addEventListener('change', (e) => {
      initLeadForm();
      hotspot.leadForm!.submitButtonText = (e.target as HTMLInputElement).value;
      updateHotspot(index, 'leadForm', hotspot.leadForm);
    });

    leadFormSection.querySelector('.lead-form-success-msg')!.addEventListener('change', (e) => {
      initLeadForm();
      hotspot.leadForm!.successMessage = (e.target as HTMLInputElement).value;
      updateHotspot(index, 'leadForm', hotspot.leadForm);
    });

    leadFormSection.querySelector('.lead-form-notify-email')!.addEventListener('change', (e) => {
      initLeadForm();
      hotspot.leadForm!.notificationEmail = (e.target as HTMLInputElement).value;
      updateHotspot(index, 'leadForm', hotspot.leadForm);
    });

    leadFormSection.querySelector('.lead-form-webhook')!.addEventListener('change', (e) => {
      initLeadForm();
      hotspot.leadForm!.webhookUrl = (e.target as HTMLInputElement).value;
      updateHotspot(index, 'leadForm', hotspot.leadForm);
    });

    leadFormSection.querySelector('.lead-form-redirect')!.addEventListener('change', (e) => {
      initLeadForm();
      hotspot.leadForm!.redirectUrl = (e.target as HTMLInputElement).value;
      updateHotspot(index, 'leadForm', hotspot.leadForm);
    });

    // Initial render of form fields (for lead_capture type hotspots)
    if (hotspot.type === 'lead_capture') {
      initLeadForm();
      renderFormFields();
    }
    // ========================================================================

    content.appendChild(infoGroup);
    content.appendChild(leadFormGroup); // Lead form section is SEPARATE from infoGroup

    // Audio Fields (for both type only here - pure audio is in Audio tab)
    if (hotspot.type === 'both') {
      const audioGroup = document.createElement('div');
      audioGroup.className = 'form-group';
      audioGroup.style.marginTop = '16px';
      audioGroup.style.paddingTop = '16px';
      audioGroup.style.borderTop = '2px solid var(--glass-border)';
      audioGroup.innerHTML = `
        <h4 style="margin: 0 0 12px 0; font-size: 14px; font-weight: 600; color: var(--ios-black);">🔊 Audio Settings</h4>

        <label>Audio Title</label>
        <input type="text" class="ios-input audio-title" value="${hotspot.audioTitle || ''}" placeholder="Display name for audio" style="width: 100%; margin-bottom: 12px;">

        <label>Audio URL</label>
        <div style="display: flex; gap: 8px;">
          <input type="text" class="ios-input audio-url" value="${hotspot.audioUrl || ''}" placeholder="https://example.com/audio.mp3" style="flex: 1;">
          <button class="ios-button ios-button-secondary upload-audio-btn" style="padding: 8px 12px; font-size: 12px;">Upload</button>
          <input type="file" class="audio-file-input" accept="audio/*" style="display: none;">
        </div>
        <div class="upload-progress audio-progress" style="display: none; margin-top: 4px;">
          <div class="progress-bar"><div class="progress-fill"></div></div>
          <div class="progress-text" style="font-size: 10px; color: var(--ios-gray); margin-top: 2px;">Uploading...</div>
        </div>

        <div style="display: flex; gap: 12px; margin-top: 12px;">
          <div style="flex: 1;">
            <label>Volume</label>
            <input type="number" class="ios-input audio-volume" min="0" max="1" step="0.1" value="${hotspot.audioVolume !== undefined ? hotspot.audioVolume : 1.0}" placeholder="1.0">
          </div>
          <div style="display: flex; align-items: flex-end; padding-bottom: 8px; gap: 12px;">
            <label style="display: flex; align-items: center; gap: 6px; margin: 0;">
              <input type="checkbox" class="audio-loop" ${hotspot.audioLoop ? 'checked' : ''} style="width: auto;">
              <span style="font-size: 13px;">Loop</span>
            </label>
            <label style="display: flex; align-items: center; gap: 6px; margin: 0;" title="Play automatically when scene loads">
              <input type="checkbox" class="audio-autoplay" ${hotspot.audioAutoplay ? 'checked' : ''} style="width: auto;">
              <span style="font-size: 13px;">Autoplay</span>
            </label>
          </div>
        </div>
      `;

      // Audio Title input
      const audioTitleInput = audioGroup.querySelector('.audio-title') as HTMLInputElement;
      audioTitleInput.addEventListener('change', (e) => updateHotspot(index, 'audioTitle', (e.target as HTMLInputElement).value));

      // Audio URL input
      const audioInput = audioGroup.querySelector('.audio-url') as HTMLInputElement;
      audioInput.addEventListener('change', (e) => updateHotspot(index, 'audioUrl', (e.target as HTMLInputElement).value));

      // Audio Upload
      const audioUploadBtn = audioGroup.querySelector('.upload-audio-btn') as HTMLButtonElement;
      const audioFileInput = audioGroup.querySelector('.audio-file-input') as HTMLInputElement;
      const audioProgress = audioGroup.querySelector('.audio-progress') as HTMLElement;
      const audioProgressFill = audioProgress.querySelector('.progress-fill') as HTMLElement;
      const audioProgressText = audioProgress.querySelector('.progress-text') as HTMLElement;

      audioUploadBtn.addEventListener('click', () => {
        const hasUploadAccess = currentUserProfile && hasFeatureAccess(currentUserProfile, 'uploads');
        if (!hasUploadAccess) {
          if (currentUser) showUpgradeModal(currentUser.uid, currentUser.email);
          return;
        }
        audioFileInput.click();
      });

      audioFileInput.addEventListener('change', async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file || !currentUser || !sequenceId) return;

        try {
          audioProgress.style.display = 'block';
          audioProgressText.textContent = `Uploading ${file.name}...`;
          audioProgressFill.style.width = '0%';

          const result = await uploadFileWithQuotaCheck(
            file,
            currentUser.uid,
            sequenceId,
            'audio',
            currentUserProfile!,
            (progress) => {
              audioProgressFill.style.width = `${progress.progress}%`;
            }
          );

          audioInput.value = result.url;
          updateHotspot(index, 'audioUrl', result.url);
          audioProgressText.textContent = '✅ Upload complete!';
          setTimeout(() => { audioProgress.style.display = 'none'; }, 2000);
        } catch (error: any) {
          console.error('Upload failed:', error);
          audioProgressText.textContent = `❌ Error: ${error.message}`;
        }
        audioFileInput.value = '';
      });

      // Audio settings
      audioGroup.querySelector('.audio-volume')!.addEventListener('change', (e) => {
        updateHotspot(index, 'audioVolume', parseFloat((e.target as HTMLInputElement).value));
      });
      audioGroup.querySelector('.audio-loop')!.addEventListener('change', (e) => {
        updateHotspot(index, 'audioLoop', (e.target as HTMLInputElement).checked);
      });
      audioGroup.querySelector('.audio-autoplay')!.addEventListener('change', (e) => {
        updateHotspot(index, 'audioAutoplay', (e.target as HTMLInputElement).checked);
      });

      content.appendChild(audioGroup);
    }

    // Add customization controls
    const customizationContainer = document.createElement('div');
    customizationContainer.className = 'hotspot-customization';
    customizationContainer.style.marginTop = '12px';
    customizationContainer.style.paddingTop = '12px';
    customizationContainer.style.borderTop = '1px solid var(--glass-border)';
    
    // Color Picker
    const colorGroup = document.createElement('div');
    colorGroup.className = 'form-group';
    colorGroup.style.marginBottom = '12px';
    colorGroup.innerHTML = `
      <label style="font-size: 12px;">Color</label>
      <div style="display: flex; align-items: center; gap: 8px;">
        <input type="color" class="hotspot-color" value="${hotspot.color || '#ff0000'}" style="width: 40px; height: 30px; border: none; border-radius: 4px; cursor: pointer;">
        <span style="font-size: 12px; color: var(--ios-gray); font-family: monospace;">${hotspot.color || '#ff0000'}</span>
      </div>
    `;
    customizationContainer.appendChild(colorGroup);

    // Scale Slider (converts to size, base 8)
    const currentSize = hotspot.size || 8;
    const currentScale = (currentSize / 8).toFixed(1);
    
    const sizeGroup = document.createElement('div');
    sizeGroup.className = 'form-group';
    sizeGroup.style.marginBottom = '12px';
    sizeGroup.innerHTML = `
      <label style="font-size: 12px;">Scale: <span class="size-value">${currentScale}</span>x</label>
      <input type="range" class="hotspot-scale" min="0.5" max="5" step="0.1" value="${currentScale}" style="width: 100%;">
    `;
    
    sizeGroup.querySelector('.hotspot-scale')!.addEventListener('input', (e) => {
      const scale = parseFloat((e.target as HTMLInputElement).value);
      sizeGroup.querySelector('.size-value')!.textContent = scale.toFixed(1);
      // Update size based on scale * 8
      updateHotspot(index, 'size', scale * 8);
    });
    customizationContainer.appendChild(sizeGroup);

    // Opacity Slider
    const opacityGroup = document.createElement('div');
    opacityGroup.className = 'form-group';
    opacityGroup.style.marginBottom = '12px';
    opacityGroup.innerHTML = `
      <label style="font-size: 12px;">Opacity: <span class="opacity-value">${hotspot.opacity !== undefined ? hotspot.opacity : 0.9}</span></label>
      <input type="range" class="hotspot-opacity" min="0.1" max="1" step="0.1" value="${hotspot.opacity !== undefined ? hotspot.opacity : 0.9}" style="width: 100%;">
    `;
    customizationContainer.appendChild(opacityGroup);

    // Reposition Button
    const repositionBtn = document.createElement('button');
    repositionBtn.className = 'ios-button ios-button-secondary';
    repositionBtn.style.width = '100%';
    repositionBtn.style.fontSize = '13px';
    repositionBtn.style.padding = '8px';
    repositionBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="vertical-align: middle; margin-right: 4px;"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>Reposition';
    repositionBtn.onclick = () => handleRepositionHotspot(hotspot.id);
    customizationContainer.appendChild(repositionBtn);

    // Custom Icon Upload
    createCustomIconUploadUI(hotspot, index, customizationContainer, renderInfoHotspotsList.bind(null, config!.stages[currentStageIndex].hotspots || []));

    content.appendChild(customizationContainer);

    // Add event listeners for customization
    const colorInput = customizationContainer.querySelector('.hotspot-color') as HTMLInputElement;
    colorInput.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      const val = target.value;
      target.nextElementSibling!.textContent = val;
      updateHotspot(index, 'color', val);
    });

    const opacityInput = customizationContainer.querySelector('.hotspot-opacity') as HTMLInputElement;
    opacityInput.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      const val = parseFloat(target.value);
      customizationContainer.querySelector('.opacity-value')!.textContent = val.toString();
      updateHotspot(index, 'opacity', val);
    });

    item.appendChild(header);
    item.appendChild(content);
    hotspotsList.appendChild(item);
  });
}

// Track currently expanded portal and incoming arrow
let expandedPortalId: string | null = null;
let expandedIncomingArrowIndex: number | null = null;

/**
 * Get icon for portal effect style
 */
function getPortalStyleIcon(style: string | undefined): string {
  switch (style) {
    case 'floor-arrow': return '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-8 8h5v8h6v-8h5z"/></svg>';
    case 'alien': return '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
    case 'enhanced': return '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>';
    case 'minimal': return '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="8"/></svg>';
    default: return '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="10"/></svg>';
  }
}

function getPortalTypeLabel(style: string | undefined): string {
  switch (style) {
    case 'floor-arrow': return 'Floor Arrow';
    case 'alien': return 'Alien';
    case 'enhanced': return 'Enhanced';
    case 'minimal': return 'Minimal';
    default: return 'Portal';
  }
}

/**
 * Get target stage name for a hotspot
 */
function getTargetStageName(hotspot: HotspotConfig): string {
  const targetId = hotspot.targetSceneId || hotspot.targetStageId;
  if (!targetId || !config?.stages) return 'No target';
  const target = config.stages.find(s => s.id === targetId);
  return target?.name || target?.id || 'Unknown';
}

/**
 * Create compact portal card (collapsed state)
 */
function createCompactPortalCard(hotspot: HotspotConfig & { originalIndex: number }, isSelected = false): HTMLDivElement {
  const item = document.createElement('div');
  const isFloorArrow = hotspot.portalEffectStyle === 'floor-arrow';
  item.className = `item-card portal-compact${isFloorArrow ? ' floor-arrow' : ''}${isSelected ? ' selected' : ''}`;
  item.dataset.index = hotspot.originalIndex.toString();
  item.dataset.id = hotspot.id;

  const icon = getPortalStyleIcon(hotspot.portalEffectStyle);
  const typeLabel = getPortalTypeLabel(hotspot.portalEffectStyle);
  const targetName = getTargetStageName(hotspot);

  item.innerHTML = `
    <div class="portal-compact-icon">${icon}</div>
    <div class="portal-compact-type">${typeLabel}</div>
    <div class="portal-compact-target">To: ${targetName}</div>
  `;

  item.addEventListener('click', () => {
    // Toggle: if already selected, collapse; otherwise expand
    expandedPortalId = isSelected ? null : hotspot.id;
    renderPortalsList(config!.stages[currentStageIndex].hotspots || []);
  });

  return item;
}

/**
 * Create expanded portal card (full form)
 */
function createExpandedPortalCard(hotspot: HotspotConfig & { originalIndex: number }): HTMLDivElement {
  const index = hotspot.originalIndex;
  const item = document.createElement('div');
  item.className = 'item-card portal-expanded';
  item.dataset.index = index.toString();
  item.dataset.id = hotspot.id;

  const isFloorArrow = hotspot.portalEffectStyle === 'floor-arrow';
  const targetId = hotspot.targetSceneId || hotspot.targetStageId;
  const targetName = getTargetStageName(hotspot);

  // Build target scene options
  let targetOptions = '<option value="">Select destination...</option>';
  if (config && config.stages) {
    targetOptions += config.stages.map((s, idx) => {
      const stageName = s.name || `Scene ${idx + 1}`;
      return `<option value="${s.id}" ${targetId === s.id ? 'selected' : ''}>${stageName}</option>`;
    }).join('');
  }

  // Portal style options
  const styles = [
    { value: 'clean', label: 'Clean', icon: '<circle cx="12" cy="12" r="8"/>' },
    { value: 'enhanced', label: 'Glow', icon: '<path d="M12 2L9 9l-7 3 7 3 3 7 3-7 7-3-7-3z"/>' },
    { value: 'minimal', label: 'Simple', icon: '<circle cx="12" cy="12" r="6" fill="none" stroke="currentColor" stroke-width="2"/>' },
    { value: 'alien', label: 'Alien', icon: '<path d="M12 2C8 2 5 5.5 5 10c0 3 1.5 5.5 3.5 7 .5.5 1 1.5 1 2.5V21h5v-1.5c0-1 .5-2 1-2.5 2-1.5 3.5-4 3.5-7 0-4.5-3-8-7-8z"/>' },
    { value: 'floor-arrow', label: 'Arrow', icon: '<path d="M12 4l-8 8h5v8h6v-8h5z"/>' },
  ];

  const currentStyle = hotspot.portalEffectStyle || 'clean';
  const directionValue = hotspot.arrowDirection ?? 0;

  item.innerHTML = `
    <div class="portal-card-header">
      <div class="portal-card-title">
        <div class="portal-card-icon">${getPortalStyleIcon(hotspot.portalEffectStyle)}</div>
        <div>
          <div style="font-size: 14px; font-weight: 600;">${isFloorArrow ? 'Floor Arrow' : 'Portal'}</div>
          <div style="font-size: 11px; color: var(--text-tertiary); margin-top: 1px;">To: ${targetName}</div>
        </div>
      </div>
      <div class="portal-card-actions">
        <button class="portal-collapse-btn" title="Collapse">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M7 14l5-5 5 5z"/></svg>
        </button>
      </div>
    </div>
    <div class="portal-card-body">
      <!-- Destination Section -->
      <div class="portal-form-section">
        <div class="portal-form-section-title">
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/></svg>
          Destination
        </div>
        <div class="portal-form-field">
          <select class="portal-input target-stage">${targetOptions}</select>
        </div>
      </div>

      <!-- Style Section -->
      <div class="portal-form-section">
        <div class="portal-form-section-title">
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8z"/></svg>
          Style
        </div>
        <div class="portal-style-grid">
          ${styles.map(s => `
            <div class="portal-style-option ${currentStyle === s.value ? 'selected' : ''}" data-style="${s.value}">
              <div class="style-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">${s.icon}</svg></div>
              <div class="style-label">${s.label}</div>
            </div>
          `).join('')}
        </div>
      </div>

      <!-- Appearance Section -->
      <div class="portal-form-section portal-appearance-section">
        <div class="portal-form-section-title">
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.66 7.93L12 2.27 6.34 7.93c-3.12 3.12-3.12 8.19 0 11.31C7.9 20.8 9.95 21.58 12 21.58s4.1-.78 5.66-2.34c3.12-3.12 3.12-8.19 0-11.31z"/></svg>
          Appearance
        </div>
        <div class="portal-form-row">
          <div class="portal-form-field">
            <label>Glow Color</label>
            <div class="portal-color-picker">
              <div class="portal-color-swatch">
                <input type="color" class="portal-color" value="${hotspot.portalColor || '#00bfff'}">
              </div>
              <input type="text" class="portal-input portal-color-hex" value="${hotspot.portalColor || '#00bfff'}" placeholder="#00bfff">
            </div>
          </div>
        </div>
        <div class="portal-form-row">
          <div class="portal-form-field">
            <label>Rest Size</label>
            <input type="number" step="0.1" min="0.1" max="5" class="portal-input portal-rest-size" value="${hotspot.portalRestSize || 1.0}">
          </div>
          <div class="portal-form-field">
            <label>Hover Size</label>
            <input type="number" step="0.1" min="0.1" max="5" class="portal-input portal-hover-size" value="${hotspot.portalHoverSize || 1.0}">
          </div>
        </div>
        <div class="portal-form-row">
          <div class="portal-form-field">
            <label>Edge Opacity</label>
            <input type="range" min="0" max="1" step="0.1" class="portal-edge-opacity-slider" value="${hotspot.portalEdgeOpacity !== undefined ? hotspot.portalEdgeOpacity : 1.0}" style="flex: 1;">
            <input type="number" step="0.1" min="0" max="1" class="portal-input portal-edge-opacity" value="${hotspot.portalEdgeOpacity !== undefined ? hotspot.portalEdgeOpacity : 1.0}" style="width: 60px; margin-left: 8px;">
          </div>
        </div>
      </div>

      <!-- Arrow Direction (only for floor arrows) -->
      <div class="portal-form-section portal-direction-section" style="${isFloorArrow ? '' : 'display: none;'}">
        <div class="portal-form-section-title">
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/></svg>
          Direction
        </div>
        <div class="portal-direction-control">
          <div class="portal-direction-dial" style="--rotation: ${directionValue}deg;">
            <div style="position: absolute; top: 50%; left: 50%; width: 2px; height: 18px; background: var(--primary); border-radius: 1px; transform-origin: center bottom; transform: translateX(-50%) translateY(-100%) rotate(${directionValue}deg);"></div>
          </div>
          <input type="range" min="-180" max="180" step="5" class="portal-direction-slider arrow-direction-slider" value="${directionValue}">
          <div style="display: flex; align-items: center; gap: 4px;">
            <input type="number" min="-180" max="180" step="5" class="portal-input arrow-direction-input" value="${directionValue}" style="width: 60px; text-align: center;">
            <span style="font-size: 12px; color: var(--text-tertiary);">°</span>
          </div>
        </div>
      </div>

      <!-- Position Section (collapsible/advanced) -->
      <div class="portal-form-section" style="opacity: 0.7;">
        <div class="portal-form-section-title">
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/></svg>
          Position
        </div>
        <div class="portal-form-row">
          <div class="portal-form-field">
            <label>X</label>
            <input type="number" step="0.1" class="portal-input pos-x" value="${hotspot.position.x || 0}">
          </div>
          <div class="portal-form-field">
            <label>Y</label>
            <input type="number" step="0.1" class="portal-input pos-y" value="${hotspot.position.y || 0}">
          </div>
          <div class="portal-form-field">
            <label>Z</label>
            <input type="number" step="0.1" class="portal-input pos-z" value="${hotspot.position.z || 0}">
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="portal-actions">
        <button class="portal-action-btn reposition-btn">
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
          Reposition
        </button>
        <button class="portal-action-btn danger delete-btn">
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
          Delete
        </button>
      </div>
    </div>
  `;

  // Event listeners
  item.querySelector('.portal-collapse-btn')!.addEventListener('click', (e) => {
    e.stopPropagation();
    expandedPortalId = null;
    renderPortalsList(config!.stages[currentStageIndex].hotspots || []);
  });

  item.querySelector('.target-stage')!.addEventListener('change', (e) => {
    updateHotspot(index, 'targetSceneId', (e.target as HTMLSelectElement).value);
  });

  // Style selector
  item.querySelectorAll('.portal-style-option').forEach(opt => {
    opt.addEventListener('click', () => {
      const style = (opt as HTMLElement).dataset.style;
      updateHotspot(index, 'portalEffectStyle', style);
      renderPortalsList(config!.stages[currentStageIndex].hotspots || []);
    });
  });

  // Color picker
  const colorInput = item.querySelector('.portal-color') as HTMLInputElement;
  const colorHex = item.querySelector('.portal-color-hex') as HTMLInputElement;
  colorInput?.addEventListener('input', (e) => {
    const color = (e.target as HTMLInputElement).value;
    colorHex.value = color;
    updateHotspot(index, 'portalColor', color);
  });
  colorHex?.addEventListener('input', (e) => {
    const color = (e.target as HTMLInputElement).value;
    if (/^#[0-9A-F]{6}$/i.test(color)) {
      colorInput.value = color;
      updateHotspot(index, 'portalColor', color);
    }
  });

  // Sizes
  item.querySelector('.portal-rest-size')?.addEventListener('change', (e) => {
    updateHotspot(index, 'portalRestSize', parseFloat((e.target as HTMLInputElement).value));
  });
  item.querySelector('.portal-hover-size')?.addEventListener('change', (e) => {
    updateHotspot(index, 'portalHoverSize', parseFloat((e.target as HTMLInputElement).value));
  });

  // Edge opacity (slider and number input stay in sync)
  const edgeOpacitySlider = item.querySelector('.portal-edge-opacity-slider') as HTMLInputElement;
  const edgeOpacityInput = item.querySelector('.portal-edge-opacity') as HTMLInputElement;
  edgeOpacitySlider?.addEventListener('input', (e) => {
    const value = parseFloat((e.target as HTMLInputElement).value);
    edgeOpacityInput.value = value.toString();
    updateHotspot(index, 'portalEdgeOpacity', value);
  });
  edgeOpacityInput?.addEventListener('change', (e) => {
    const value = Math.max(0, Math.min(1, parseFloat((e.target as HTMLInputElement).value) || 0));
    edgeOpacitySlider.value = value.toString();
    edgeOpacityInput.value = value.toString();
    updateHotspot(index, 'portalEdgeOpacity', value);
  });

  // Arrow direction
  const dirSlider = item.querySelector('.arrow-direction-slider') as HTMLInputElement;
  const dirInput = item.querySelector('.arrow-direction-input') as HTMLInputElement;
  const dirDial = item.querySelector('.portal-direction-dial > div') as HTMLElement;

  dirSlider?.addEventListener('input', (e) => {
    const value = parseInt((e.target as HTMLInputElement).value);
    dirInput.value = value.toString();
    if (dirDial) dirDial.style.transform = `translateX(-50%) translateY(-100%) rotate(${value}deg)`;
    updateHotspot(index, 'arrowDirection', value);
  });
  dirInput?.addEventListener('change', (e) => {
    const value = parseInt((e.target as HTMLInputElement).value) || 0;
    const clampedValue = Math.max(-180, Math.min(180, value));
    dirSlider.value = clampedValue.toString();
    dirInput.value = clampedValue.toString();
    if (dirDial) dirDial.style.transform = `translateX(-50%) translateY(-100%) rotate(${clampedValue}deg)`;
    updateHotspot(index, 'arrowDirection', clampedValue);
  });

  // Position
  item.querySelector('.pos-x')!.addEventListener('change', (e) => updateHotspot(index, 'position.x', (e.target as HTMLInputElement).value));
  item.querySelector('.pos-y')!.addEventListener('change', (e) => updateHotspot(index, 'position.y', (e.target as HTMLInputElement).value));
  item.querySelector('.pos-z')!.addEventListener('change', (e) => updateHotspot(index, 'position.z', (e.target as HTMLInputElement).value));

  // Action buttons
  item.querySelector('.reposition-btn')!.addEventListener('click', () => handleRepositionHotspot(hotspot.id));
  item.querySelector('.delete-btn')!.addEventListener('click', () => deleteHotspot(index));

  return item;
}

/**
 * Render portals list (Navigation) - compact grid with expanded card below
 */
function renderPortalsList(hotspots: HotspotConfig[]) {
  portalsList.innerHTML = '';
  portalsList.classList.add('portal-grid');

  const portals = hotspots
    .map((h, originalIndex) => ({ ...h, originalIndex }))
    .filter(h => h.type === 'navigation');

  if (portals.length === 0) {
    portalsList.classList.remove('portal-grid');
    portalsList.innerHTML = `<div class="empty-state"></div>`;
    return;
  }

  // All compact cards in grid first
  portals.forEach((hotspot) => {
    const isSelected = hotspot.id === expandedPortalId;
    portalsList.appendChild(createCompactPortalCard(hotspot, isSelected));
  });

  // If one is expanded, add the expanded card below the grid
  if (expandedPortalId) {
    const expandedHotspot = portals.find(h => h.id === expandedPortalId);
    if (expandedHotspot) {
      portalsList.appendChild(createExpandedPortalCard(expandedHotspot));
    }
  }
}

/**
 * Select a portal in the UI (called when clicked in 3D preview)
 */
function selectPortalInUI(hotspotId: string): void {
  // Clear previous selection
  document.querySelectorAll('.item-card.portal-selected').forEach(el => {
    el.classList.remove('portal-selected');
  });

  // Find and highlight the portal card
  const card = portalsList.querySelector(`.item-card[data-id="${hotspotId}"]`);
  if (card) {
    card.classList.add('portal-selected');
    card.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

/**
 * Get the current camera look direction as a scene-local unit vector
 * Converts camera alpha/beta to a 3D direction, then un-rotates by skybox rotation
 */
function getCameraLookDirection(): { x: number; y: number; z: number } | null {
  if (!editorPreview) return null;

  const alpha = editorPreview.getCameraAlpha();
  const beta = editorPreview.getCameraBeta();

  // Camera look direction in world space (from orientCameraToTarget inverse)
  const sinBeta = Math.sin(beta);
  const worldX = -Math.cos(alpha) * sinBeta;
  const worldY = -Math.cos(beta);
  const worldZ = -Math.sin(alpha) * sinBeta;

  // Un-rotate by skybox rotation to get scene-local direction
  const rotY = (editorPreview.getSkyboxRotation() * Math.PI) / 180;
  const cos = Math.cos(-rotY);
  const sin = Math.sin(-rotY);
  const localX = worldX * cos - worldZ * sin;
  const localZ = worldX * sin + worldZ * cos;

  return { x: localX, y: worldY, z: localZ };
}

/**
 * Incoming arrow color palette
 */
const INCOMING_ARROW_COLORS = ['#007AFF', '#FF9500', '#34C759', '#AF52DE', '#FF2D55', '#5AC8FA'];

/**
 * Render incoming floor arrows panel (arrows from other stages pointing to this stage)
 */
function renderIncomingArrows(currentStage: ExperienceStage) {
  const container = document.getElementById('incomingArrowsContainer');
  const list = document.getElementById('incomingArrowsList');
  if (!container || !list || !config || !config.stages) {
    if (editorPreview) editorPreview.clearViewDirectionIndicators();
    return;
  }

  // Find all arrows from other stages pointing to this stage
  const incomingArrows: Array<{
    sourceStageIndex: number;
    sourceStageName: string;
    hotspotIndex: number;
    hotspot: HotspotConfig;
    color: string;
  }> = [];

  // Track seen arrows to prevent duplicates (by source stage + hotspot id)
  const seenArrows = new Set<string>();

  console.log(`[Editor] Looking for incoming arrows to stage: "${currentStage.id}", total stages: ${config.stages.length}`);

  // Check for duplicate stage IDs (which would cause duplicate arrows)
  const stageIdCounts = new Map<string, number>();
  config.stages.forEach(s => stageIdCounts.set(s.id, (stageIdCounts.get(s.id) || 0) + 1));
  stageIdCounts.forEach((count, id) => {
    if (count > 1) console.warn(`[Editor] WARNING: Stage ID "${id}" appears ${count} times in config!`);
  });

  config.stages.forEach((stage, stageIndex) => {
    if (stage.id === currentStage.id || !stage.hotspots) return;
    stage.hotspots.forEach((hotspot, hotspotIndex) => {
      // Only check floor-arrow navigation hotspots
      if (hotspot.type !== 'navigation' || hotspot.portalEffectStyle !== 'floor-arrow') return;

      // Use targetSceneId (new field) preferentially, fall back to targetStageId (legacy)
      // Don't use OR - if both are set, only check the primary one to avoid matching multiple stages
      const targetId = hotspot.targetSceneId ?? hotspot.targetStageId;
      const isMatch = targetId === currentStage.id;

      console.log(`[Editor] Checking hotspot: source=${stage.id}, hotspot=${hotspot.id}, targetId="${targetId}", currentStage="${currentStage.id}", isMatch=${isMatch}`);

      if (!isMatch) return;

      {
        // Create unique key for this arrow
        const arrowKey = `${stage.id}-${hotspot.id}`;
        console.log(`[Editor] Found incoming arrow: stage=${stage.id}, hotspot=${hotspot.id}, key=${arrowKey}`);

        if (seenArrows.has(arrowKey)) {
          console.warn(`[Editor] Duplicate incoming arrow detected: ${arrowKey}`);
          return;
        }
        seenArrows.add(arrowKey);

        incomingArrows.push({
          sourceStageIndex: stageIndex,
          sourceStageName: stage.name || stage.id || `Stage ${stageIndex + 1}`,
          hotspotIndex,
          hotspot,
          color: INCOMING_ARROW_COLORS[incomingArrows.length % INCOMING_ARROW_COLORS.length],
        });
      }
    });
  });

  console.log(`[Editor] Total incoming arrows found: ${incomingArrows.length}`);

  if (incomingArrows.length === 0) {
    container.style.display = 'none';
    if (editorPreview) editorPreview.clearViewDirectionIndicators();
    return;
  }

  container.style.display = 'block';

  // Default direction (front of image) for arrows without a configured target
  const defaultPos = { x: 0, y: 0, z: 1 };

  // Show draggable direction targets in 3D preview
  if (editorPreview) {
    editorPreview.showViewDirectionIndicators(
      incomingArrows.map(arrow => ({
        position: arrow.hotspot.targetViewDirection ?? defaultPos,
        color: arrow.color,
        label: `From: ${arrow.sourceStageName}`,
      }))
    );

    // Connect drag callback - update config when target is dragged
    editorPreview.onViewDirectionTargetMoved = (indicatorIndex: number, position: { x: number; y: number; z: number }) => {
      if (!config || !config.stages) return;
      const arrow = incomingArrows[indicatorIndex];
      if (!arrow) return;

      const sourceStage = config.stages[arrow.sourceStageIndex];
      if (!sourceStage?.hotspots?.[arrow.hotspotIndex]) return;

      sourceStage.hotspots[arrow.hotspotIndex].targetViewDirection = position;
      markUnsaved();

      // Update the status text in the panel
      const listEl = document.getElementById('incomingArrowsList');
      if (listEl) {
        const statuses = listEl.querySelectorAll('.incoming-arrow-status');
        if (statuses[indicatorIndex]) {
          statuses[indicatorIndex].textContent = 'Custom view set';
        }
      }
    };
  }

  // Render compact grid first, then expanded card below
  list.classList.add('navigation-grid');

  // All compact cards in grid
  let html = incomingArrows.map((arrow, idx) => {
    const isSelected = expandedIncomingArrowIndex === idx;
    const hasCustomView = arrow.hotspot.targetViewDirection ? ' ✓' : '';
    return `
      <div class="nav-card-compact incoming-arrow-compact${isSelected ? ' selected' : ''}" data-arrow-index="${idx}" onclick="expandIncomingArrow(${idx})">
        <div class="nav-icon" style="background: ${arrow.color};">
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-8 8h5v8h6v-8h5z"/></svg>
        </div>
        <div class="nav-type">Incoming</div>
        <div class="nav-target">${arrow.sourceStageName}${hasCustomView}</div>
      </div>
    `;
  }).join('');

  // If one is expanded, add the expanded card below the grid
  if (expandedIncomingArrowIndex !== null && incomingArrows[expandedIncomingArrowIndex]) {
    const arrow = incomingArrows[expandedIncomingArrowIndex];
    const idx = expandedIncomingArrowIndex;
    html += `
      <div class="incoming-arrow-card" data-arrow-index="${idx}">
        <div class="arrow-header">
          <div class="arrow-header-left">
            <div class="arrow-icon" style="background: ${arrow.color};">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-8 8h5v8h6v-8h5z"/></svg>
            </div>
            <div class="arrow-source">
              <span class="arrow-source-label">Arriving from</span>
              ${arrow.sourceStageName}
            </div>
          </div>
          <button class="arrow-collapse-btn" onclick="collapseIncomingArrow()">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
        <div class="arrow-body">
          <div class="arrow-direction-section">
            <div class="arrow-direction-label">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>
              Camera Direction
            </div>
            <div class="arrow-status">
              <span class="incoming-arrow-status">${arrow.hotspot.targetViewDirection ? 'Custom view direction set' : 'Default view (front of panorama)'}</span>
            </div>
          </div>
          <div class="arrow-actions">
            <button onclick="useCurrentViewForArrow(${arrow.sourceStageIndex}, ${arrow.hotspotIndex}, ${idx})">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>
              Use Current View
            </button>
            ${arrow.hotspot.targetViewDirection ? `<button onclick="clearArrowViewDirection(${arrow.sourceStageIndex}, ${arrow.hotspotIndex}, ${idx})" style="color: var(--primary);">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              Reset
            </button>` : ''}
          </div>
        </div>
      </div>
    `;
  }

  list.innerHTML = html;
}

/**
 * Toggle expand/collapse an incoming arrow card
 */
(window as any).expandIncomingArrow = function (idx: number) {
  // Toggle: if already expanded, collapse; otherwise expand
  expandedIncomingArrowIndex = expandedIncomingArrowIndex === idx ? null : idx;
  const currentStage = config?.stages?.[currentStageIndex];
  if (currentStage) {
    renderIncomingArrows(currentStage);
  }
};

/**
 * Collapse the expanded incoming arrow card
 */
(window as any).collapseIncomingArrow = function () {
  expandedIncomingArrowIndex = null;
  const currentStage = config?.stages?.[currentStageIndex];
  if (currentStage) {
    renderIncomingArrows(currentStage);
  }
};

/**
 * Capture current camera look direction and set as targetViewDirection
 */
(window as any).useCurrentViewForArrow = function (sourceStageIndex: number, hotspotIndex: number, indicatorIndex: number) {
  if (!config || !config.stages || !editorPreview) return;
  const sourceStage = config.stages[sourceStageIndex];
  if (!sourceStage?.hotspots?.[hotspotIndex]) return;

  const pos = getCameraLookDirection();
  if (!pos) return;

  sourceStage.hotspots[hotspotIndex].targetViewDirection = pos;
  markUnsaved();

  // Update the 3D target
  editorPreview.updateViewDirectionIndicator(indicatorIndex, pos);

  // Update status text
  const listEl = document.getElementById('incomingArrowsList');
  if (listEl) {
    const statuses = listEl.querySelectorAll('.incoming-arrow-status');
    if (statuses[indicatorIndex]) {
      statuses[indicatorIndex].textContent = 'Custom view set';
    }
  }
};

/**
 * Clear targetViewDirection for an incoming arrow
 */
(window as any).clearArrowViewDirection = function (sourceStageIndex: number, hotspotIndex: number, indicatorIndex: number) {
  if (!config || !config.stages) return;
  const sourceStage = config.stages[sourceStageIndex];
  if (!sourceStage?.hotspots?.[hotspotIndex]) return;

  delete sourceStage.hotspots[hotspotIndex].targetViewDirection;
  markUnsaved();

  // Reset 3D target to default (front)
  if (editorPreview) {
    editorPreview.updateViewDirectionIndicator(indicatorIndex, { x: 0, y: 0, z: 1 });
  }

  // Update status text
  const listEl = document.getElementById('incomingArrowsList');
  if (listEl) {
    const statuses = listEl.querySelectorAll('.incoming-arrow-status');
    if (statuses[indicatorIndex]) {
      statuses[indicatorIndex].textContent = 'Default (front)';
    }
  }
};

/**
 * Render audio hotspots list (Spatialized Audio)
 */
function renderAudioHotspotsList(hotspots: HotspotConfig[]) {
  if (!audioHotspotsList) return;
  audioHotspotsList.innerHTML = ''; // Clear previous list

  // Filter for audio types only
  const audioHotspots = hotspots
    .map((h, originalIndex) => ({ ...h, originalIndex }))
    .filter(h => h.type === 'audio');

  if (audioHotspots.length === 0) {
    audioHotspotsList.innerHTML = `
      <div class="empty-state">
      </div>
    `;
    return;
  }

  audioHotspots.forEach((hotspot) => {
    const index = hotspot.originalIndex; // Use original index for updates
    const item = document.createElement('div');
    item.className = 'item-card';
    item.dataset.index = index.toString();
    item.dataset.id = hotspot.id; // For highlight-on-click feature

    // Header
    const header = document.createElement('div');
    header.className = 'item-header';
    header.innerHTML = `
      <span class="item-title">Audio Source ${index + 1}</span>
      <button class="btn-icon delete-hotspot-btn">🗑️</button>
    `;
    
    // Delete button handler
    header.querySelector('.delete-hotspot-btn')!.addEventListener('click', () => {
      deleteHotspot(index);
    });

    // Content
    const content = document.createElement('div');
    
    // Position Inputs
    const posGroup = document.createElement('div');
    posGroup.className = 'form-group';
    posGroup.innerHTML = `
      <label>Position</label>
      <div class="form-row" style="display: flex; gap: 8px;">
        <input type="number" step="0.1" placeholder="X" class="ios-input pos-x" value="${hotspot.position.x || 0}" style="flex: 1;">
        <input type="number" step="0.1" placeholder="Y" class="ios-input pos-y" value="${hotspot.position.y || 0}" style="flex: 1;">
        <input type="number" step="0.1" placeholder="Z" class="ios-input pos-z" value="${hotspot.position.z || 0}" style="flex: 1;">
      </div>
    `;
    posGroup.querySelector('.pos-x')!.addEventListener('change', (e) => updateHotspot(index, 'position.x', (e.target as HTMLInputElement).value));
    posGroup.querySelector('.pos-y')!.addEventListener('change', (e) => updateHotspot(index, 'position.y', (e.target as HTMLInputElement).value));
    posGroup.querySelector('.pos-z')!.addEventListener('change', (e) => updateHotspot(index, 'position.z', (e.target as HTMLInputElement).value));
    content.appendChild(posGroup);

    // Audio Fields
    const audioGroup = document.createElement('div');
    audioGroup.className = 'form-group';
    audioGroup.innerHTML = `
      <label>Audio Title</label>
      <input type="text" class="ios-input audio-title" value="${hotspot.audioTitle || ''}" placeholder="Display name for audio" style="width: 100%; margin-bottom: 12px;">

      <label>Audio URL</label>
      <div style="display: flex; gap: 8px;">
        <input type="text" class="ios-input audio-url" value="${hotspot.audioUrl || ''}" placeholder="https://example.com/audio.mp3" style="flex: 1;">
        <button class="ios-button ios-button-secondary upload-audio-btn" style="padding: 8px 12px; font-size: 12px;">Upload</button>
        <input type="file" class="audio-file-input" accept="audio/*" style="display: none;">
      </div>
      <div class="upload-progress audio-progress" style="display: none; margin-top: 4px;">
        <div class="progress-bar"><div class="progress-fill"></div></div>
        <div class="progress-text" style="font-size: 10px; color: var(--ios-gray); margin-top: 2px;">Uploading...</div>
      </div>

      <!-- Audio Preview Player (matches background audio style) -->
      <div class="spatial-audio-preview" style="display: ${hotspot.audioUrl ? 'block' : 'none'}; margin-top: 12px; padding: 16px; background: rgba(0,0,0,0.03); border-radius: var(--radius-md); border: 1px solid var(--glass-border);">
        <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px;">
          <button class="ios-button ios-button-primary spatial-preview-play-btn" style="width: 44px; height: 44px; border-radius: 50%; padding: 0; display: flex; align-items: center; justify-content: center; font-size: 18px;">▶</button>
          <div style="flex: 1;">
            <div class="spatial-preview-title" style="font-weight: 600; font-size: 14px; color: var(--ios-black); margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${hotspot.audioTitle || 'Audio Preview'}</div>
            <div class="spatial-preview-time" style="font-size: 12px; color: var(--ios-gray);">0:00 / --:--</div>
          </div>
          <div style="display: flex; align-items: center; gap: 6px;">
            <span style="font-size: 14px;">🔊</span>
            <input type="range" class="spatial-preview-volume" min="0" max="100" value="80" style="width: 60px; accent-color: var(--red-primary);">
          </div>
        </div>
        <div class="spatial-preview-progress" style="position: relative; height: 6px; background: rgba(0,0,0,0.1); border-radius: 3px; cursor: pointer;">
          <div class="spatial-preview-progress-fill" style="position: absolute; left: 0; top: 0; height: 100%; width: 0%; background: var(--red-primary); border-radius: 3px; transition: width 0.1s;"></div>
        </div>
        <audio class="spatial-preview-audio" preload="metadata" style="display: none;"></audio>
      </div>

      <div style="display: flex; gap: 12px; margin-top: 12px;">
        <div style="flex: 1;">
          <label>Volume</label>
          <input type="number" class="ios-input audio-volume" min="0" max="1" step="0.1" value="${hotspot.audioVolume !== undefined ? hotspot.audioVolume : 1.0}" placeholder="1.0">
        </div>
        <div style="display: flex; align-items: flex-end; padding-bottom: 8px; gap: 12px;">
          <label style="display: flex; align-items: center; gap: 6px; margin: 0;">
            <input type="checkbox" class="audio-loop" ${hotspot.audioLoop ? 'checked' : ''} style="width: auto;">
            <span style="font-size: 13px;">Loop</span>
          </label>
          <label style="display: flex; align-items: center; gap: 6px; margin: 0;" title="Play automatically when scene loads">
            <input type="checkbox" class="audio-autoplay" ${hotspot.audioAutoplay ? 'checked' : ''} style="width: auto;">
            <span style="font-size: 13px;">Autoplay</span>
          </label>
        </div>
      </div>
    `;

    // Audio Title input
    const audioTitleInput = audioGroup.querySelector('.audio-title') as HTMLInputElement;
    audioTitleInput.addEventListener('change', (e) => updateHotspot(index, 'audioTitle', (e.target as HTMLInputElement).value));

    // Audio URL input
    const audioInput = audioGroup.querySelector('.audio-url') as HTMLInputElement;
    audioInput.addEventListener('change', (e) => updateHotspot(index, 'audioUrl', (e.target as HTMLInputElement).value));

    // Audio Upload
    const audioUploadBtn = audioGroup.querySelector('.upload-audio-btn') as HTMLButtonElement;
    const audioFileInput = audioGroup.querySelector('.audio-file-input') as HTMLInputElement;
    const audioProgress = audioGroup.querySelector('.audio-progress') as HTMLElement;
    const audioProgressFill = audioProgress.querySelector('.progress-fill') as HTMLElement;
    const audioProgressText = audioProgress.querySelector('.progress-text') as HTMLElement;

    audioUploadBtn.addEventListener('click', () => {
      const hasUploadAccess = currentUserProfile && hasFeatureAccess(currentUserProfile, 'uploads');
      if (!hasUploadAccess) {
        if (currentUser) showUpgradeModal(currentUser.uid, currentUser.email);
        return;
      }
      audioFileInput.click();
    });

    audioFileInput.addEventListener('change', async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file || !currentUser || !sequenceId) return;

      try {
        audioProgress.style.display = 'block';
        audioProgressText.textContent = `Uploading ${file.name}...`;
        audioProgressFill.style.width = '0%';

        const result = await uploadFileWithQuotaCheck(
          file,
          currentUser.uid,
          sequenceId,
          'audio',
          currentUserProfile!,
          (progress) => {
            audioProgressFill.style.width = `${progress.progress}%`;
          }
        );

        audioInput.value = result.url;
        updateHotspot(index, 'audioUrl', result.url);
        audioProgressText.textContent = '✅ Upload complete!';
        setTimeout(() => { audioProgress.style.display = 'none'; }, 2000);
      } catch (error: any) {
        console.error('Upload failed:', error);
        audioProgressText.textContent = `❌ Error: ${error.message}`;
      }
      audioFileInput.value = '';
    });

    // Audio settings
    audioGroup.querySelector('.audio-volume')!.addEventListener('change', (e) => {
      updateHotspot(index, 'audioVolume', parseFloat((e.target as HTMLInputElement).value));
    });
    audioGroup.querySelector('.audio-loop')!.addEventListener('change', (e) => {
      updateHotspot(index, 'audioLoop', (e.target as HTMLInputElement).checked);
    });
    audioGroup.querySelector('.audio-autoplay')!.addEventListener('change', (e) => {
      updateHotspot(index, 'audioAutoplay', (e.target as HTMLInputElement).checked);
    });

    // Spatial Audio Preview Player Controls
    const spatialPreview = audioGroup.querySelector('.spatial-audio-preview') as HTMLElement;
    const spatialPlayBtn = audioGroup.querySelector('.spatial-preview-play-btn') as HTMLButtonElement;
    const spatialPreviewAudio = audioGroup.querySelector('.spatial-preview-audio') as HTMLAudioElement;
    const spatialPreviewTime = audioGroup.querySelector('.spatial-preview-time') as HTMLElement;
    const spatialPreviewProgressFill = audioGroup.querySelector('.spatial-preview-progress-fill') as HTMLElement;
    const spatialPreviewProgress = audioGroup.querySelector('.spatial-preview-progress') as HTMLElement;
    const spatialPreviewVolume = audioGroup.querySelector('.spatial-preview-volume') as HTMLInputElement;
    const spatialPreviewTitle = audioGroup.querySelector('.spatial-preview-title') as HTMLElement;

    // Set initial audio source if available
    if (hotspot.audioUrl) {
      spatialPreviewAudio.src = hotspot.audioUrl;
    }

    // Play/Pause
    spatialPlayBtn.addEventListener('click', () => {
      if (spatialPreviewAudio.paused) {
        spatialPreviewAudio.play();
        spatialPlayBtn.textContent = '⏸';
      } else {
        spatialPreviewAudio.pause();
        spatialPlayBtn.textContent = '▶';
      }
    });

    // Time update
    spatialPreviewAudio.addEventListener('timeupdate', () => {
      const current = formatAudioTime(spatialPreviewAudio.currentTime);
      const duration = formatAudioTime(spatialPreviewAudio.duration);
      spatialPreviewTime.textContent = `${current} / ${duration}`;
      const progress = (spatialPreviewAudio.currentTime / spatialPreviewAudio.duration) * 100;
      spatialPreviewProgressFill.style.width = `${progress}%`;
    });

    // Load metadata
    spatialPreviewAudio.addEventListener('loadedmetadata', () => {
      const duration = formatAudioTime(spatialPreviewAudio.duration);
      spatialPreviewTime.textContent = `0:00 / ${duration}`;
    });

    // Ended
    spatialPreviewAudio.addEventListener('ended', () => {
      spatialPlayBtn.textContent = '▶';
      spatialPreviewProgressFill.style.width = '0%';
    });

    // Volume control
    spatialPreviewVolume.addEventListener('input', (e) => {
      spatialPreviewAudio.volume = parseInt((e.target as HTMLInputElement).value) / 100;
    });

    // Click on progress bar to seek
    spatialPreviewProgress.addEventListener('click', (e) => {
      const rect = spatialPreviewProgress.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      spatialPreviewAudio.currentTime = percent * spatialPreviewAudio.duration;
    });

    // Update preview when URL changes
    audioInput.addEventListener('change', () => {
      const url = audioInput.value;
      if (url) {
        spatialPreview.style.display = 'block';
        spatialPreviewAudio.src = url;
        spatialPlayBtn.textContent = '▶';
        spatialPreviewTime.textContent = '0:00 / --:--';
        spatialPreviewProgressFill.style.width = '0%';
      } else {
        spatialPreview.style.display = 'none';
        spatialPreviewAudio.pause();
        spatialPreviewAudio.src = '';
      }
    });

    // Update title when title input changes
    audioTitleInput.addEventListener('input', () => {
      spatialPreviewTitle.textContent = audioTitleInput.value || 'Audio Preview';
    });

    content.appendChild(audioGroup);

    // Add customization controls (Color, Size, Opacity)
    const customizationContainer = document.createElement('div');
    customizationContainer.className = 'hotspot-customization';
    customizationContainer.style.marginTop = '12px';
    customizationContainer.style.paddingTop = '12px';
    customizationContainer.style.borderTop = '1px solid var(--glass-border)';
    
    // Color Picker
    const colorGroup = document.createElement('div');
    colorGroup.className = 'form-group';
    colorGroup.style.marginBottom = '12px';
    colorGroup.innerHTML = `
      <label style="font-size: 12px;">Visual Color</label>
      <div style="display: flex; align-items: center; gap: 8px;">
        <input type="color" class="hotspot-color" value="${hotspot.color || '#ff0000'}" style="width: 40px; height: 30px; border: none; border-radius: 4px; cursor: pointer;">
        <span style="font-size: 12px; color: var(--ios-gray); font-family: monospace;">${hotspot.color || '#ff0000'}</span>
      </div>
    `;
    customizationContainer.appendChild(colorGroup);

    // Scale Slider (converts to size, base 8)
    const currentSize = hotspot.size || 8;
    const currentScale = (currentSize / 8).toFixed(1);
    
    const sizeGroup = document.createElement('div');
    sizeGroup.className = 'form-group';
    sizeGroup.style.marginBottom = '12px';
    sizeGroup.innerHTML = `
      <label style="font-size: 12px;">Scale: <span class="size-value">${currentScale}</span>x</label>
      <input type="range" class="hotspot-scale" min="0.5" max="5" step="0.1" value="${currentScale}" style="width: 100%;">
    `;
    
    sizeGroup.querySelector('.hotspot-scale')!.addEventListener('input', (e) => {
      const scale = parseFloat((e.target as HTMLInputElement).value);
      sizeGroup.querySelector('.size-value')!.textContent = scale.toFixed(1);
      // Update size based on scale * 8
      updateHotspot(index, 'size', scale * 8);
    });
    customizationContainer.appendChild(sizeGroup);

    // Opacity Slider
    const opacityGroup = document.createElement('div');
    opacityGroup.className = 'form-group';
    opacityGroup.style.marginBottom = '12px';
    opacityGroup.innerHTML = `
      <label style="font-size: 12px;">Opacity: <span class="opacity-value">${hotspot.opacity !== undefined ? hotspot.opacity : 0.9}</span></label>
      <input type="range" class="hotspot-opacity" min="0.1" max="1" step="0.1" value="${hotspot.opacity !== undefined ? hotspot.opacity : 0.9}" style="width: 100%;">
    `;
    customizationContainer.appendChild(opacityGroup);

    // Reposition Button
    const repositionBtn = document.createElement('button');
    repositionBtn.className = 'ios-button ios-button-secondary';
    repositionBtn.style.width = '100%';
    repositionBtn.style.fontSize = '13px';
    repositionBtn.style.padding = '8px';
    repositionBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="vertical-align: middle; margin-right: 4px;"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>Reposition';
    repositionBtn.onclick = () => handleRepositionHotspot(hotspot.id);
    customizationContainer.appendChild(repositionBtn);

    // Custom Icon Upload UI
    createCustomIconUploadUI(hotspot, index, customizationContainer, renderAudioHotspotsList.bind(null, config!.stages[currentStageIndex].hotspots || []));

    content.appendChild(customizationContainer);

    // Add event listeners for customization
    const colorInput = customizationContainer.querySelector('.hotspot-color') as HTMLInputElement;
    colorInput.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      const val = target.value;
      target.nextElementSibling!.textContent = val;
      updateHotspot(index, 'color', val);
    });

    const opacityInput = customizationContainer.querySelector('.hotspot-opacity') as HTMLInputElement;
    opacityInput.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      const val = parseFloat(target.value);
      customizationContainer.querySelector('.opacity-value')!.textContent = val.toString();
      updateHotspot(index, 'opacity', val);
    });

    item.appendChild(header);
    item.appendChild(content);
    audioHotspotsList.appendChild(item);
  });
}

/**
 * Handle add hotspot manually (Info/Audio)
 */
function handleAddHotspot() {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];

  if (!stage.hotspots) stage.hotspots = [];

  stage.hotspots.push({
    id: `hotspot-${Date.now()}`,
    type: 'info', // Default to info
    position: { x: 0, y: 1.5, z: -3 },
    infoTitle: 'New Hotspot',
    infoDescription: 'Description here...'
  });

  renderInfoHotspotsList(stage.hotspots);
  markUnsaved();
}

/**
 * Handle add portal manually (Navigation)
 */
function handleAddPortal() {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];

  if (!stage.hotspots) stage.hotspots = [];

  stage.hotspots.push({
    id: `portal-${Date.now()}`,
    type: 'navigation', // Default to navigation
    position: { x: 0, y: 1.5, z: -3 }
  });

  renderPortalsList(stage.hotspots);
  markUnsaved();
}

/**
 * Count the number of lead capture forms in the entire sequence
 * Used for tier-based form limits
 */
function countFormsInSequence(): number {
  if (!config || !config.stages) return 0;

  let count = 0;
  for (const stage of config.stages) {
    if (!stage.hotspots) continue;
    for (const hotspot of stage.hotspots) {
      if (hotspot.type === 'lead_capture') {
        count++;
      }
    }
  }
  return count;
}

/**
 * Delete a hotspot
 */
function deleteHotspot(index: number) {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];
  if (!stage.hotspots) return;

  stage.hotspots.splice(index, 1);
  markUnsaved();
  renderInfoHotspotsList(stage.hotspots);
  renderPortalsList(stage.hotspots);
  renderAudioHotspotsList(stage.hotspots);
  updatePreview();
}

/**
 * Update a hotspot property
 */
function updateHotspot(index: number, field: string, value: any) {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];
  if (!stage.hotspots) return;

  const hotspot = stage.hotspots[index];

  if (field.includes('.')) {
    const parts = field.split('.');
    // @ts-ignore
    hotspot[parts[0]][parts[1]] = parseFloat(value);
  } else {
    // @ts-ignore
    hotspot[field] = value;
  }

  markUnsaved();

  // If type changed, re-render all lists to show/hide relevant fields and move items
  if (field === 'type') {
    renderInfoHotspotsList(stage.hotspots);
    renderPortalsList(stage.hotspots);
    renderAudioHotspotsList(stage.hotspots);
  }

  // Use incremental updates for transform/direction changes, full reload for structural changes
  if (editorPreview && field === 'arrowDirection') {
    editorPreview.setFloorArrowDirection(hotspot.id, parseFloat(value) || 0);
  } else if (editorPreview && field === 'portalColor') {
    editorPreview.setFloorArrowColor(hotspot.id, value);
  } else if (editorPreview && field === 'portalRestSize') {
    editorPreview.setFloorArrowRestScale(hotspot.id, parseFloat(value) || 1.0);
  } else if (editorPreview && field === 'portalHoverSize') {
    editorPreview.setFloorArrowHoverScale(hotspot.id, parseFloat(value) || 1.2);
  } else if (editorPreview && (field === 'position.x' || field === 'position.y' || field === 'position.z')) {
    editorPreview.setHotspotPosition(hotspot.id, hotspot.position);
  } else if (field === 'targetViewDirection') {
    // No preview update needed - this only affects runtime camera on arrival
  } else {
    updatePreview();
  }
}

/**
 * Handle reposition hotspot
 */
function handleRepositionHotspot(hotspotId: string) {
  if (!editorPreview) return;

  // Show toast or instruction
  const toast = document.createElement('div');
  toast.className = 'success-message show'; // Reuse success message style
  toast.style.background = 'rgba(0, 0, 0, 0.8)';
  toast.style.color = 'white';
  toast.style.border = 'none';
  toast.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style="vertical-align: middle; margin-right: 6px;"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg> Click anywhere on the background to move the hotspot...';
  document.body.appendChild(toast);

  // Enable reposition mode
  editorPreview.enableHotspotRepositioning(hotspotId);

  // Listen for reposition event
  editorPreview.onHotspotRepositioned = (id, position) => {
    if (!config || !config.stages) return;
    const stage = config.stages[currentStageIndex];
    if (!stage.hotspots) return;

    const hotspot = stage.hotspots.find(h => h.id === id);
    if (hotspot) {
      hotspot.position = position;
      markUnsaved();
      updatePreview();
      markUnsaved();
      updatePreview();
      renderInfoHotspotsList(stage.hotspots); // Refresh UI to show new coords
      renderPortalsList(stage.hotspots);
      renderAudioHotspotsList(stage.hotspots);
      
      // Remove toast
      toast.remove();
      
      // Show success
      const success = document.getElementById('successMessage');
      if (success) {
        success.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style="vertical-align: middle; margin-right: 6px;"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg> Hotspot moved!';
        success.classList.add('show');
        setTimeout(() => success.classList.remove('show'), 2000);
      }
    }
  };
}

/**
 * Ensure preview panel is open and show placement overlay
 */
function showPlacementMode(type: 'hotspot' | 'portal' | 'floor-arrow' | 'audio' | 'camera-target') {
  // Ensure docked preview panel is open
  const previewPanelDocked = document.getElementById('previewPanelDocked');
  const isHidden = previewPanelDocked?.classList.contains('hidden');

  if (isHidden) {
    previewPanelDocked?.classList.remove('hidden');
    if (togglePreviewPanel) togglePreviewPanel.checked = true;
    previewVisible = true;

    // Give preview time to initialize if needed
    setTimeout(() => {
      editorPreview?.resize();
      updatePreview();
    }, 100);
  }

  // Update overlay text based on type
  const subtitles: Record<string, string> = {
    'hotspot': 'Click anywhere in the 360° view to place an info hotspot',
    'portal': 'Click anywhere in the 360° view to place a navigation portal',
    'floor-arrow': 'Click anywhere in the 360° view to place a floor arrow',
    'audio': 'Click anywhere in the 360° view to place a 3D audio source',
    'camera-target': 'Click anywhere in the 360° view to set the initial camera direction'
  };

  if (placementOverlaySubtitle) {
    placementOverlaySubtitle.textContent = subtitles[type];
  }

  // Show overlay
  if (placementOverlay) {
    placementOverlay.style.display = 'flex';
  }
}

/**
 * Hide placement overlay
 */
function hidePlacementMode() {
  if (placementOverlay) {
    placementOverlay.style.display = 'none';
  }
}

/**
 * Reset hotspot creation mode UI and state
 */
function resetHotspotCreationMode() {
  isHotspotCreationMode = false;
  editorPreview?.disableHotspotCreation();
  addHotspotClickBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="vertical-align: middle; margin-right: 4px;"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>Place Hotspot by Clicking';
  addHotspotClickBtn.classList.remove('btn-danger');
  hidePlacementMode();
}

/**
 * Handle add hotspot via click in preview (Info/Audio)
 * Uses state-based toggle to fix second creation bug
 */
function handleAddHotspotClick() {
  if (!editorPreview) {
    showAlert('Preview is not available for this content.', 'Preview Unavailable', '👁️');
    return;
  }

  // If already in creation mode, cancel it
  if (isHotspotCreationMode) {
    resetHotspotCreationMode();
    return;
  }

  // Enter creation mode
  isHotspotCreationMode = true;

  // Show preview and overlay
  showPlacementMode('hotspot');

  // Enable hotspot creation mode
  editorPreview.enableHotspotCreation();

  // Update button state to show cancel option
  addHotspotClickBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="vertical-align: middle; margin-right: 4px;"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>Cancel Hotspot Placement';
  addHotspotClickBtn.classList.add('btn-danger');

  // Handle creation event
  editorPreview.onHotspotCreated = (position: { x: number; y: number; z: number }) => {
    if (!config || !config.stages) return;
    const stage = config.stages[currentStageIndex];

    if (!stage.hotspots) stage.hotspots = [];

    stage.hotspots.push({
      id: `hotspot-${Date.now()}`,
      type: 'info', // Default to info
      position,
      infoTitle: 'New Hotspot',
      infoDescription: 'Description here...'
    });

    renderInfoHotspotsList(stage.hotspots);
    markUnsaved();
    updatePreview();

    // Reset mode after successful creation
    resetHotspotCreationMode();
  };
}

/**
 * Reset portal creation mode UI and state
 */
function resetPortalCreationMode() {
  isPortalCreationMode = false;
  editorPreview?.disableHotspotCreation();
  addPortalClickBtn.textContent = 'Place Portal';
  addPortalClickBtn.classList.remove('btn-danger');
  hidePlacementMode();
}

function resetFloorArrowCreationMode() {
  isFloorArrowCreationMode = false;
  editorPreview?.disableHotspotCreation();
  addFloorArrowClickBtn.textContent = 'Place Floor Arrow';
  addFloorArrowClickBtn.classList.remove('btn-danger');
  hidePlacementMode();
}

/**
 * Reset camera target placement mode
 */
function resetCameraTargetMode() {
  isCameraTargetMode = false;
  editorPreview?.disableCameraTargetPlacement();
  const btn = document.getElementById('setCameraTargetBtn');
  if (btn) {
    btn.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align: middle; margin-right: 6px;">
        <circle cx="12" cy="12" r="10"/>
        <circle cx="12" cy="12" r="3"/>
        <path d="M12 2v4M12 18v4M2 12h4M18 12h4"/>
      </svg>
      Set Initial Camera View
    `;
    btn.classList.remove('btn-danger');
  }
  hidePlacementMode();
}

/**
 * Handle set camera target via click in preview
 * Only available for the first scene
 */
function handleSetCameraTargetClick() {
  if (!editorPreview) {
    showAlert('Preview is not available for this content.', 'Preview Unavailable', '👁️');
    return;
  }

  // Only allowed for first scene
  if (currentStageIndex !== 0) {
    showAlert('Initial camera rotation can only be set for the first scene.', 'Scene 1 Only', '📷');
    return;
  }

  // If already in camera target mode, cancel it
  if (isCameraTargetMode) {
    resetCameraTargetMode();
    return;
  }

  // Cancel any other creation modes
  resetHotspotCreationMode();
  resetPortalCreationMode();
  resetFloorArrowCreationMode();
  resetAudioHotspotCreationMode();

  // Enter camera target mode
  isCameraTargetMode = true;

  // Show placement overlay with custom message
  showPlacementMode('camera-target');

  // Enable camera target placement in preview
  editorPreview.enableCameraTargetPlacement();

  // Update button to show cancel state
  const btn = document.getElementById('setCameraTargetBtn');
  if (btn) {
    btn.innerHTML = `
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="vertical-align: middle; margin-right: 4px;">
        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
      </svg>
      Cancel Camera Placement
    `;
    btn.classList.add('btn-danger');
  }

  // Handle camera target set event
  editorPreview.onCameraTargetMoved = (position: { x: number; y: number; z: number }) => {
    if (!config || !config.stages) return;
    const stage = config.stages[0]; // Always first stage

    // Save camera target to config
    stage.initialCameraTarget = position;
    markUnsaved();

    console.log('[Editor] Initial camera target set:', position);

    // Reset mode after setting
    resetCameraTargetMode();
  };
}

/**
 * Handle add portal via click in preview (Navigation)
 * Uses state-based toggle to fix second creation bug
 */
function handleAddPortalClick() {
  if (!editorPreview) {
    showAlert('Preview is not available for this content.', 'Preview Unavailable', '👁️');
    return;
  }

  // If already in creation mode, cancel it
  if (isPortalCreationMode) {
    resetPortalCreationMode();
    return;
  }

  // Enter creation mode
  isPortalCreationMode = true;

  // Show preview and overlay
  showPlacementMode('portal');

  // Enable hotspot creation mode
  editorPreview.enableHotspotCreation();

  // Update button state to show cancel option
  addPortalClickBtn.textContent = 'Cancel';
  addPortalClickBtn.classList.add('btn-danger');

  // Handle creation event
  editorPreview.onHotspotCreated = (position: { x: number; y: number; z: number }) => {
    if (!config || !config.stages) return;
    const stage = config.stages[currentStageIndex];

    if (!stage.hotspots) stage.hotspots = [];

    stage.hotspots.push({
      id: `portal-${Date.now()}`,
      type: 'navigation', // Default to navigation
      position
    });

    renderPortalsList(stage.hotspots);
    markUnsaved();
    updatePreview();

    // Reset mode after successful creation
    resetPortalCreationMode();
  };
}

/**
 * Handle add floor arrow via click in preview (Navigation)
 * Creates a navigation hotspot with floor-arrow style
 */
function handleAddFloorArrowClick() {
  if (!editorPreview) {
    showAlert('Preview is not available for this content.', 'Preview Unavailable', '👁️');
    return;
  }

  // If already in creation mode, cancel it
  if (isFloorArrowCreationMode) {
    resetFloorArrowCreationMode();
    return;
  }

  // Enter creation mode
  isFloorArrowCreationMode = true;

  // Show preview and overlay
  showPlacementMode('floor-arrow');

  // Enable hotspot creation mode
  editorPreview.enableHotspotCreation();

  // Update button state to show cancel option
  addFloorArrowClickBtn.textContent = 'Cancel';
  addFloorArrowClickBtn.classList.add('btn-danger');

  // Handle creation event
  editorPreview.onHotspotCreated = (position: { x: number; y: number; z: number }) => {
    if (!config || !config.stages) return;
    const stage = config.stages[currentStageIndex];

    if (!stage.hotspots) stage.hotspots = [];

    // Create floor arrow navigation hotspot with default 0° direction (natural outward)
    stage.hotspots.push({
      id: `floor-arrow-${Date.now()}`,
      type: 'navigation',
      position,
      portalEffectStyle: 'floor-arrow',
      arrowDirection: 0 // Default to natural outward direction
    });

    renderPortalsList(stage.hotspots);
    markUnsaved();
    updatePreview();

    // Reset mode after successful creation
    resetFloorArrowCreationMode();
  };
}

/**
 * Update hotspot
 */
(window as any).updateHotspot = function (index: number, field: string, value: any) {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];
  if (!stage.hotspots || !stage.hotspots[index]) return;

  const hotspot = stage.hotspots[index];

  if (field === 'type') {
    hotspot.type = value as 'navigation' | 'info' | 'both';
    renderInfoHotspotsList(stage.hotspots);
    renderPortalsList(stage.hotspots);
  } else if (field.startsWith('position.')) {
    const axis = field.split('.')[1] as 'x' | 'y' | 'z';
    hotspot.position[axis] = parseFloat(value) || 0;
  } else if (field === 'targetStageId') {
    hotspot.targetStageId = value || undefined;
  } else if (field === 'infoTitle') {
    hotspot.infoTitle = value || undefined;
  } else if (field === 'infoDescription') {
    hotspot.infoDescription = value || undefined;
  } else if (field === 'infoImageUrl') {
    hotspot.infoImageUrl = value || undefined;
  }

  markUnsaved();
  updatePreview();
};

/**
 * Delete hotspot
 */
(window as any).deleteHotspot = async function (index: number) {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];
  if (!stage.hotspots) return;

  const confirmed = await showConfirm('Are you sure you want to delete this hotspot?', 'Delete Hotspot', '🗑️');
  if (confirmed) {
    stage.hotspots.splice(index, 1);
    renderInfoHotspotsList(stage.hotspots);
    renderPortalsList(stage.hotspots);
    markUnsaved();
    updatePreview();
  }
};

/**
 * Handle audio title change
 */
function handleAudioTitleChange() {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];

  const title = audioTitle.value.trim();
  if (!title) {
    delete stage.audioTitle;
  } else {
    stage.audioTitle = title;
  }

  markUnsaved();
}

/**
 * Handle audio change (scene-specific)
 */
function handleAudioChange() {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];

  const url = audioUrl.value.trim();
  if (!url) {
    delete stage.audioUrl;
    delete stage.audioVolume;
    hideAudioPreview();
  } else {
    stage.audioUrl = url;
    // Save volume (convert from 0-100 to 0-1)
    const volume = sceneAudioVolume ? parseInt(sceneAudioVolume.value) / 100 : 0.8;
    stage.audioVolume = volume;
    showAudioPreview(url, audioTitle.value || 'Background Audio');
  }

  markUnsaved();
}

/**
 * Handle scene audio volume change
 */
function handleSceneAudioVolumeChange() {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];

  if (sceneAudioVolume && sceneAudioVolumeValue) {
    sceneAudioVolumeValue.textContent = `${sceneAudioVolume.value}%`;
    // Save volume (convert from 0-100 to 0-1)
    stage.audioVolume = parseInt(sceneAudioVolume.value) / 100;
    markUnsaved();
  }
}

/**
 * Handle global audio change (persists across all scenes)
 */
function handleGlobalAudioChange() {
  if (!config) return;

  const url = globalAudioUrl?.value.trim() || '';
  const title = globalAudioTitle?.value.trim() || '';
  const volume = globalAudioVolume ? parseInt(globalAudioVolume.value) / 100 : 0.8;

  if (!url) {
    delete config.globalAudio;
  } else {
    config.globalAudio = {
      url,
      ...(title ? { title } : {}),
      volume,
      loop: true
    };
  }

  markUnsaved();
}

/**
 * Handle global audio volume slider change (live update display)
 */
function handleGlobalAudioVolumeChange() {
  if (globalAudioVolumeValue && globalAudioVolume) {
    globalAudioVolumeValue.textContent = `${globalAudioVolume.value}%`;
  }
}

/**
 * Handle clear global audio
 */
function handleClearGlobalAudio() {
  if (!config) return;

  delete config.globalAudio;

  if (globalAudioTitle) globalAudioTitle.value = '';
  if (globalAudioUrl) globalAudioUrl.value = '';
  if (globalAudioVolume) globalAudioVolume.value = '80';
  if (globalAudioVolumeValue) globalAudioVolumeValue.textContent = '80%';

  markUnsaved();
  showToast('Global audio cleared', 'success');
}

/**
 * Load global audio settings into form
 */
function loadGlobalAudioSettings() {
  if (!config) return;

  const globalAudio = config.globalAudio;

  if (globalAudioTitle) globalAudioTitle.value = globalAudio?.title || '';
  if (globalAudioUrl) globalAudioUrl.value = globalAudio?.url || '';
  if (globalAudioVolume) {
    const volumePercent = Math.round((globalAudio?.volume ?? 0.8) * 100);
    globalAudioVolume.value = String(volumePercent);
    if (globalAudioVolumeValue) globalAudioVolumeValue.textContent = `${volumePercent}%`;
  }
}

/**
 * Show audio preview player
 */
function showAudioPreview(url: string, title: string) {
  if (!audioPreviewContainer || !audioPreviewElement) return;

  audioPreviewContainer.style.display = 'block';
  audioPreviewTitle.textContent = title;
  audioPreviewElement.src = url;
  audioPreviewElement.volume = parseInt(audioPreviewVolumeSlider.value) / 100;
  audioPreviewTime.textContent = '0:00 / --:--';
  audioPreviewProgressFill.style.width = '0%';
  audioPreviewPlayBtn.textContent = '▶';
}

/**
 * Hide audio preview player
 */
function hideAudioPreview() {
  if (!audioPreviewContainer || !audioPreviewElement) return;

  audioPreviewContainer.style.display = 'none';
  audioPreviewElement.pause();
  audioPreviewElement.src = '';
}

/**
 * Format time in mm:ss
 */
function formatAudioTime(seconds: number): string {
  if (isNaN(seconds) || !isFinite(seconds)) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Initialize audio preview controls
 */
function initAudioPreviewControls() {
  if (!audioPreviewElement || !audioPreviewPlayBtn) return;

  // Play/Pause button
  audioPreviewPlayBtn.addEventListener('click', () => {
    if (audioPreviewElement.paused) {
      audioPreviewElement.play();
      audioPreviewPlayBtn.textContent = '⏸';
    } else {
      audioPreviewElement.pause();
      audioPreviewPlayBtn.textContent = '▶';
    }
  });

  // Time update
  audioPreviewElement.addEventListener('timeupdate', () => {
    const current = formatAudioTime(audioPreviewElement.currentTime);
    const duration = formatAudioTime(audioPreviewElement.duration);
    audioPreviewTime.textContent = `${current} / ${duration}`;

    const progress = (audioPreviewElement.currentTime / audioPreviewElement.duration) * 100;
    audioPreviewProgressFill.style.width = `${progress}%`;
  });

  // Load metadata
  audioPreviewElement.addEventListener('loadedmetadata', () => {
    const duration = formatAudioTime(audioPreviewElement.duration);
    audioPreviewTime.textContent = `0:00 / ${duration}`;
  });

  // Ended
  audioPreviewElement.addEventListener('ended', () => {
    audioPreviewPlayBtn.textContent = '▶';
    audioPreviewProgressFill.style.width = '0%';
  });

  // Volume slider
  audioPreviewVolumeSlider.addEventListener('input', () => {
    audioPreviewElement.volume = parseInt(audioPreviewVolumeSlider.value) / 100;
  });

  // Progress bar click to seek
  audioPreviewProgress.addEventListener('click', (e) => {
    const rect = audioPreviewProgress.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    audioPreviewElement.currentTime = percent * audioPreviewElement.duration;
  });
}

/**
 * Handle clear audio
 */
function handleClearAudio() {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];

  delete stage.audioUrl;
  delete stage.audioTitle;
  delete stage.audioVolume;
  audioUrl.value = '';
  audioTitle.value = '';
  // Reset volume slider to default
  if (sceneAudioVolume) sceneAudioVolume.value = '80';
  if (sceneAudioVolumeValue) sceneAudioVolumeValue.textContent = '80%';
  hideAudioPreview();

  markUnsaved();
}

/**
 * Render lights list
 */
function renderLightsList(lights: LightConfig[]) {
  if (lights.length === 0) {
    lightsList.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">💡</div>
        <div>No lights configured</div>
        <div style="font-size: 12px; color: #999; margin-top: 4px;">Use presets or add custom lights</div>
      </div>
    `;
    update3DTabCounts();
    return;
  }

  lightsList.innerHTML = lights
    .map((light, index) => `
      <div class="item-card" data-index="${index}">
        <div class="item-header">
          <span class="item-title">${light.type.charAt(0).toUpperCase() + light.type.slice(1)} Light ${index + 1}</span>
          <button class="btn-icon" onclick="deleteLight(${index})">🗑️</button>
        </div>

        <div class="form-group">
          <label>Light Type</label>
          <select onchange="updateLight(${index}, 'type', this.value)">
            <option value="hemispheric" ${light.type === 'hemispheric' ? 'selected' : ''}>Hemispheric</option>
            <option value="directional" ${light.type === 'directional' ? 'selected' : ''}>Directional</option>
            <option value="point" ${light.type === 'point' ? 'selected' : ''}>Point</option>
            <option value="spot" ${light.type === 'spot' ? 'selected' : ''}>Spot</option>
          </select>
        </div>

        <div class="form-group">
          <label>Intensity</label>
          <input type="number" step="0.1" min="0" max="5"
                 value="${light.intensity ?? 1}"
                 onchange="updateLight(${index}, 'intensity', this.value)">
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>Diffuse Color</label>
            <input type="color"
                   value="${light.diffuse || '#ffffff'}"
                   onchange="updateLight(${index}, 'diffuse', this.value)">
          </div>
          <div class="form-group">
            <label>Specular Color</label>
            <input type="color"
                   value="${light.specular || '#ffffff'}"
                   onchange="updateLight(${index}, 'specular', this.value)">
          </div>
        </div>

        ${light.type === 'hemispheric' ? `
          <div class="form-group">
            <label>Direction (Y-axis)</label>
            <div class="form-row">
              <input type="number" step="0.1" placeholder="X"
                     value="${light.direction?.x ?? 0}"
                     onchange="updateLight(${index}, 'direction.x', this.value)">
              <input type="number" step="0.1" placeholder="Y"
                     value="${light.direction?.y ?? 1}"
                     onchange="updateLight(${index}, 'direction.y', this.value)">
              <input type="number" step="0.1" placeholder="Z"
                     value="${light.direction?.z ?? 0}"
                     onchange="updateLight(${index}, 'direction.z', this.value)">
            </div>
          </div>
          <div class="form-group">
            <label>Ground Color</label>
            <input type="color"
                   value="${light.groundColor || '#000000'}"
                   onchange="updateLight(${index}, 'groundColor', this.value)">
          </div>
        ` : ''}

        ${light.type === 'directional' ? `
          <div class="form-group">
            <label>Direction</label>
            <div class="form-row">
              <input type="number" step="0.1" placeholder="X"
                     value="${light.direction?.x ?? 0}"
                     onchange="updateLight(${index}, 'direction.x', this.value)">
              <input type="number" step="0.1" placeholder="Y"
                     value="${light.direction?.y ?? -1}"
                     onchange="updateLight(${index}, 'direction.y', this.value)">
              <input type="number" step="0.1" placeholder="Z"
                     value="${light.direction?.z ?? 0}"
                     onchange="updateLight(${index}, 'direction.z', this.value)">
            </div>
          </div>
        ` : ''}

        ${light.type === 'point' || light.type === 'spot' ? `
          <div class="form-group">
            <label>Position</label>
            <div class="form-row">
              <input type="number" step="0.1" placeholder="X"
                     value="${light.position?.x ?? 0}"
                     onchange="updateLight(${index}, 'position.x', this.value)">
              <input type="number" step="0.1" placeholder="Y"
                     value="${light.position?.y ?? 5}"
                     onchange="updateLight(${index}, 'position.y', this.value)">
              <input type="number" step="0.1" placeholder="Z"
                     value="${light.position?.z ?? 0}"
                     onchange="updateLight(${index}, 'position.z', this.value)">
            </div>
          </div>
        ` : ''}

        ${light.type === 'spot' ? `
          <div class="form-group">
            <label>Direction</label>
            <div class="form-row">
              <input type="number" step="0.1" placeholder="X"
                     value="${light.direction?.x ?? 0}"
                     onchange="updateLight(${index}, 'direction.x', this.value)">
              <input type="number" step="0.1" placeholder="Y"
                     value="${light.direction?.y ?? -1}"
                     onchange="updateLight(${index}, 'direction.y', this.value)">
              <input type="number" step="0.1" placeholder="Z"
                     value="${light.direction?.z ?? 0}"
                     onchange="updateLight(${index}, 'direction.z', this.value)">
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Angle (degrees)</label>
              <input type="number" step="1" min="0" max="180"
                     value="${light.angle ? (light.angle * 180 / Math.PI) : 45}"
                     onchange="updateLight(${index}, 'angle', this.value)">
            </div>
            <div class="form-group">
              <label>Exponent</label>
              <input type="number" step="0.1" min="0"
                     value="${light.exponent ?? 2}"
                     onchange="updateLight(${index}, 'exponent', this.value)">
            </div>
          </div>
        ` : ''}
      </div>
    `)
    .join('');

  // Update count badge
  update3DTabCounts();
}

/**
 * Handle add light
 */
function handleAddLight() {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];

  if (!stage.lights) stage.lights = [];

  stage.lights.push({
    type: 'hemispheric',
    intensity: 1,
    diffuse: '#ffffff',
    specular: '#ffffff',
    direction: { x: 0, y: 1, z: 0 }
  });

  renderLightsList(stage.lights);
  markUnsaved();
  updatePreview();
}

/**
 * Update light
 */
(window as any).updateLight = function (index: number, field: string, value: any) {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];
  if (!stage.lights || !stage.lights[index]) return;

  const light = stage.lights[index];

  if (field === 'type') {
    light.type = value as 'hemispheric' | 'directional' | 'point' | 'spot';
    // Reset type-specific fields
    if (value === 'hemispheric') {
      light.direction = { x: 0, y: 1, z: 0 };
      delete light.position;
      delete light.angle;
      delete light.exponent;
    } else if (value === 'directional') {
      light.direction = { x: 0, y: -1, z: 0 };
      delete light.position;
      delete light.angle;
      delete light.exponent;
      delete light.groundColor;
    } else if (value === 'point') {
      light.position = { x: 0, y: 5, z: 0 };
      delete light.direction;
      delete light.angle;
      delete light.exponent;
      delete light.groundColor;
    } else if (value === 'spot') {
      light.position = { x: 0, y: 5, z: 0 };
      light.direction = { x: 0, y: -1, z: 0 };
      light.angle = Math.PI / 4;
      light.exponent = 2;
      delete light.groundColor;
    }
    renderLightsList(stage.lights);
  } else if (field === 'intensity') {
    light.intensity = parseFloat(value) || 1;
  } else if (field === 'diffuse' || field === 'specular' || field === 'groundColor') {
    (light as any)[field] = value;
  } else if (field.startsWith('direction.')) {
    const axis = field.split('.')[1] as 'x' | 'y' | 'z';
    if (!light.direction) light.direction = { x: 0, y: 1, z: 0 };
    light.direction[axis] = parseFloat(value) || 0;
  } else if (field.startsWith('position.')) {
    const axis = field.split('.')[1] as 'x' | 'y' | 'z';
    if (!light.position) light.position = { x: 0, y: 5, z: 0 };
    light.position[axis] = parseFloat(value) || 0;
  } else if (field === 'angle') {
    light.angle = (parseFloat(value) || 45) * Math.PI / 180;
  } else if (field === 'exponent') {
    light.exponent = parseFloat(value) || 2;
  }

  markUnsaved();

  // Use incremental update for simple property changes, full reload for structural changes
  if (editorPreview && (field === 'intensity' || field === 'diffuse' || field === 'specular')) {
    editorPreview.setLightProperty(index, field, field === 'intensity' ? (parseFloat(value) || 1) : value);
  } else {
    updatePreview();
  }
};

/**
 * Delete light
 */
(window as any).deleteLight = async function (index: number) {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];
  if (!stage.lights) return;

  const confirmed = await showConfirm('Are you sure you want to delete this light?', 'Delete Light', '🗑️');
  if (confirmed) {
    stage.lights.splice(index, 1);
    renderLightsList(stage.lights);
    markUnsaved();
    updatePreview();
  }
};

/**
 * Apply lighting preset
 */
function applyLightingPreset(preset: string) {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];

  const presets: { [key: string]: LightConfig[] } = {
    studio: [
      {
        type: 'hemispheric',
        intensity: 0.7,
        diffuse: '#ffffff',
        specular: '#ffffff',
        direction: { x: 0, y: 1, z: 0 },
        groundColor: '#444444'
      },
      {
        type: 'directional',
        intensity: 0.5,
        diffuse: '#ffffff',
        specular: '#ffffff',
        direction: { x: 1, y: -1, z: 1 }
      }
    ],
    outdoor: [
      {
        type: 'directional',
        intensity: 1.2,
        diffuse: '#ffffee',
        specular: '#ffffff',
        direction: { x: 0.5, y: -1, z: 0.3 }
      },
      {
        type: 'hemispheric',
        intensity: 0.5,
        diffuse: '#87ceeb',
        specular: '#ffffff',
        direction: { x: 0, y: 1, z: 0 },
        groundColor: '#8b7355'
      }
    ],
    dramatic: [
      {
        type: 'spot',
        intensity: 2,
        diffuse: '#ffffff',
        specular: '#ffffff',
        position: { x: 0, y: 10, z: 0 },
        direction: { x: 0, y: -1, z: 0 },
        angle: Math.PI / 6,
        exponent: 4
      },
      {
        type: 'point',
        intensity: 0.3,
        diffuse: '#4444ff',
        specular: '#ffffff',
        position: { x: -5, y: 2, z: 0 }
      }
    ],
    night: [
      {
        type: 'hemispheric',
        intensity: 0.2,
        diffuse: '#2244aa',
        specular: '#ffffff',
        direction: { x: 0, y: 1, z: 0 },
        groundColor: '#000000'
      },
      {
        type: 'point',
        intensity: 0.5,
        diffuse: '#ffaa44',
        specular: '#ffaa44',
        position: { x: 0, y: 3, z: 0 }
      }
    ]
  };

  stage.lights = presets[preset] || [];
  renderLightsList(stage.lights);
  markUnsaved();
  updatePreview();
}

/**
 * Handle clear lights
 */
async function handleClearLights() {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];

  const confirmed = await showConfirm('Are you sure you want to clear all lights from this stage?', 'Clear All Lights', '💡');
  if (confirmed) {
    stage.lights = [];
    renderLightsList(stage.lights);
    markUnsaved();
    updatePreview();
  }
}

/**
 * Handle stage name change
 */
function handleStageNameChange() {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];

  stage.name = stageName.value.trim() || undefined;
  renderStageList();
  currentStageTitle.textContent = stage.name || `Scene ${currentStageIndex + 1}`;

  markUnsaved();
}

// Portal config moved to per-portal basis (handled in individual portal editors)

// ============================================================================
// CUSTOM URL SLUG FUNCTIONS
// ============================================================================

let userSlugCheckTimeout: ReturnType<typeof setTimeout> | null = null;

/**
 * Debounced user slug availability check
 * Updates both panel and sidebar elements
 */
async function checkUserSlugAvailability(slug: string): Promise<void> {
  // Helper to update both panel and sidebar elements
  const updateStatus = (icon: string, text: string, color: string) => {
    if (userSlugStatus) userSlugStatus.textContent = icon;
    if (userSlugHelperText) {
      userSlugHelperText.textContent = text;
      userSlugHelperText.style.color = color;
    }
    if (userSlugStatusSidebar) userSlugStatusSidebar.textContent = icon;
    if (userSlugHelperTextSidebar) {
      userSlugHelperTextSidebar.textContent = text;
      userSlugHelperTextSidebar.style.color = color;
    }
  };

  // Clear previous timeout
  if (userSlugCheckTimeout) {
    clearTimeout(userSlugCheckTimeout);
  }

  // Don't check if empty
  if (!slug.trim()) {
    updateStatus('', 'URL under your username', '');
    return;
  }

  // Show checking indicator
  updateStatus('⏳', 'Checking availability...', '');

  // Debounce: wait 300ms before actually checking
  userSlugCheckTimeout = setTimeout(async () => {
    try {
      const functions = getFunctions();
      const checkAvailability = httpsCallable(functions, 'checkSlugAvailability');
      const seqId = new URLSearchParams(window.location.search).get('id');

      const result = await checkAvailability({
        slug: slug.toLowerCase(),
        type: 'user',
        sequenceId: seqId,
      });

      const data = result.data as { available: boolean; currentOwner?: boolean; error?: string };

      if (data.available) {
        const text = data.currentOwner ? 'This is your current URL' : 'This URL is available!';
        updateStatus('✅', text, 'var(--ios-green)');
        updateSlugPreview();
      } else {
        updateStatus('❌', data.error || 'This URL is already used by another of your tours', 'var(--ios-red)');
      }
    } catch (error: any) {
      updateStatus('⚠️', error.message || 'Error checking availability', 'var(--ios-red)');
    }
  }, 300);
}

/**
 * Update the URL preview section
 * Shows the custom URL when username and userSlug are available
 */
function updateSlugPreview(): void {
  // Get values from sidebar (primary) or panel (fallback)
  const userSlug = (userSlugInputSidebar?.value || userSlugInput?.value || '').trim().toLowerCase();
  const username = tourOwnerUsername;

  // Build URL
  const userUrl = (username && userSlug) ? `https://xrgallery.online/${username}/${userSlug}` : null;

  // Helper to build URL preview content using safe DOM methods
  function buildPreviewContent(container: HTMLElement) {
    container.textContent = ''; // Clear existing content

    if (userUrl) {
      const userDiv = document.createElement('div');

      const userLabel = document.createElement('span');
      userLabel.style.cssText = 'font-size: 10px; color: var(--ios-gray); display: block; margin-bottom: 2px;';
      userLabel.textContent = 'Your Custom URL';
      userDiv.appendChild(userLabel);

      const userLink = document.createElement('span');
      userLink.style.cssText = 'color: var(--ios-blue); word-break: break-all;';
      userLink.textContent = userUrl;
      userDiv.appendChild(userLink);

      container.appendChild(userDiv);
    }
  }

  // Update panel preview (if exists)
  if (slugUrlPreview && slugUrlPreviewText) {
    if (userUrl) {
      slugUrlPreview.style.display = 'block';
      buildPreviewContent(slugUrlPreviewText);
    } else {
      slugUrlPreview.style.display = 'none';
    }
  }

  // Update sidebar preview
  if (slugUrlPreviewSidebar && slugUrlPreviewTextSidebar) {
    if (userUrl) {
      slugUrlPreviewSidebar.style.display = 'block';
      buildPreviewContent(slugUrlPreviewTextSidebar);
    } else {
      slugUrlPreviewSidebar.style.display = 'none';
    }
  }
}

/**
 * Handle username claiming
 */
async function handleClaimUsername(): Promise<void> {
  if (!usernameSetupInput || !claimUsernameBtn || !usernameSetupError) return;

  const username = usernameSetupInput.value.trim().toLowerCase();

  if (!username) {
    usernameSetupError.textContent = 'Please enter a username';
    usernameSetupError.style.display = 'block';
    return;
  }

  claimUsernameBtn.disabled = true;
  claimUsernameBtn.textContent = 'Claiming...';
  usernameSetupError.style.display = 'none';

  try {
    const functions = getFunctions();
    const claimUsernameFn = httpsCallable(functions, 'claimUsername');
    const result = await claimUsernameFn({ username });

    const data = result.data as { success: boolean; username: string };

    if (data.success) {
      // Update local state
      if (currentUserProfile) {
        currentUserProfile.username = data.username;
      }

      // Update UI
      if (usernameDisplay) {
        usernameDisplay.textContent = data.username;
      }
      if (usernameSetupPrompt) {
        usernameSetupPrompt.style.display = 'none';
      }
      if (userSlugInput) {
        userSlugInput.disabled = false;
      }

      showToast(`Username @${data.username} claimed!`, 'success');
      updateSlugPreview();
    }
  } catch (error: any) {
    usernameSetupError.textContent = error.message || 'Failed to claim username';
    usernameSetupError.style.display = 'block';
  } finally {
    claimUsernameBtn.disabled = false;
    claimUsernameBtn.textContent = 'Claim';
  }
}

/**
 * Handle username claiming from sidebar
 */
async function handleClaimUsernameSidebar(): Promise<void> {
  if (!usernameSetupInputSidebar || !claimUsernameBtnSidebar) return;

  const username = usernameSetupInputSidebar.value.trim().toLowerCase();

  if (!username) {
    if (usernameSetupErrorSidebar) {
      usernameSetupErrorSidebar.textContent = 'Please enter a username';
      usernameSetupErrorSidebar.style.display = 'block';
    }
    return;
  }

  claimUsernameBtnSidebar.disabled = true;
  claimUsernameBtnSidebar.textContent = 'Claiming...';
  if (usernameSetupErrorSidebar) usernameSetupErrorSidebar.style.display = 'none';

  try {
    const functions = getFunctions();
    const claimUsernameFn = httpsCallable(functions, 'claimUsername');
    const result = await claimUsernameFn({ username });

    const data = result.data as { success: boolean; username: string };

    if (data.success) {
      // Update local state
      if (currentUserProfile) {
        currentUserProfile.username = data.username;
      }

      // Update UI - both panel and sidebar
      if (usernameDisplay) usernameDisplay.textContent = data.username;
      if (usernameDisplaySidebar) usernameDisplaySidebar.textContent = data.username;
      if (usernameSetupPrompt) usernameSetupPrompt.style.display = 'none';
      if (usernameSetupPromptSidebar) usernameSetupPromptSidebar.style.display = 'none';
      if (userSlugInput) userSlugInput.disabled = false;
      if (userSlugInputSidebar) userSlugInputSidebar.disabled = false;

      showToast(`Username @${data.username} claimed!`, 'success');
      updateSlugPreview();
    }
  } catch (error: any) {
    if (usernameSetupErrorSidebar) {
      usernameSetupErrorSidebar.textContent = error.message || 'Failed to claim username';
      usernameSetupErrorSidebar.style.display = 'block';
    }
  } finally {
    claimUsernameBtnSidebar.disabled = false;
    claimUsernameBtnSidebar.textContent = 'Claim';
  }
}

/**
 * Handle changing an existing username
 */
async function handleChangeUsername(): Promise<void> {
  if (!usernameChangeInputSidebar || !confirmUsernameChangeBtnSidebar) return;

  const newUsername = usernameChangeInputSidebar.value.trim().toLowerCase();

  if (!newUsername) {
    if (usernameChangeErrorSidebar) {
      usernameChangeErrorSidebar.textContent = 'Please enter a username';
      usernameChangeErrorSidebar.style.display = 'block';
    }
    return;
  }

  // Don't do anything if username hasn't changed
  if (newUsername === currentUserProfile?.username) {
    if (usernameChangeFormSidebar) usernameChangeFormSidebar.style.display = 'none';
    return;
  }

  confirmUsernameChangeBtnSidebar.disabled = true;
  confirmUsernameChangeBtnSidebar.textContent = 'Saving...';
  if (usernameChangeErrorSidebar) usernameChangeErrorSidebar.style.display = 'none';

  try {
    const functions = getFunctions();
    const claimUsernameFn = httpsCallable(functions, 'claimUsername');
    const result = await claimUsernameFn({ username: newUsername });

    const data = result.data as { success: boolean; username: string };

    if (data.success) {
      // Update local state
      if (currentUserProfile) {
        currentUserProfile.username = data.username;
      }

      // Update UI - both panel and sidebar
      if (usernameDisplay) usernameDisplay.textContent = data.username;
      if (usernameDisplaySidebar) usernameDisplaySidebar.textContent = data.username;

      // Hide the change form
      if (usernameChangeFormSidebar) usernameChangeFormSidebar.style.display = 'none';

      showToast(`Username changed to @${data.username}!`, 'success');
      updateSlugPreview();
    }
  } catch (error: any) {
    if (usernameChangeErrorSidebar) {
      usernameChangeErrorSidebar.textContent = error.message || 'Failed to change username';
      usernameChangeErrorSidebar.style.display = 'block';
    }
  } finally {
    confirmUsernameChangeBtnSidebar.disabled = false;
    confirmUsernameChangeBtnSidebar.textContent = 'Save';
  }
}

/**
 * Initialize slug UI based on tour owner's username
 * Handles both panel and sidebar elements
 * Uses tourOwnerUsername (which is the owner's username, not the admin's when admin-editing)
 */
function initializeSlugUI(): void {
  const username = tourOwnerUsername;
  const isAdminEditingOtherUser = sequence && currentUser && sequence.userId !== currentUser.uid;

  // User slug section - show username setup if needed (Panel)
  if (username) {
    if (usernameDisplay) usernameDisplay.textContent = username;
    if (usernameSetupPrompt) usernameSetupPrompt.style.display = 'none';
    if (userSlugInput) userSlugInput.disabled = false;
  } else {
    if (usernameDisplay) usernameDisplay.textContent = 'username';
    // Only show username setup prompt if user is editing their own tour
    if (usernameSetupPrompt) usernameSetupPrompt.style.display = isAdminEditingOtherUser ? 'none' : 'block';
    if (userSlugInput) userSlugInput.disabled = true;
  }

  // User slug section - Sidebar
  if (username) {
    if (usernameDisplaySidebar) usernameDisplaySidebar.textContent = username;
    if (usernameSetupPromptSidebar) usernameSetupPromptSidebar.style.display = 'none';
    // Hide username change section when admin is editing another user's tour
    if (usernameChangeSectionSidebar) usernameChangeSectionSidebar.style.display = isAdminEditingOtherUser ? 'none' : 'block';
    if (usernameChangeFormSidebar) usernameChangeFormSidebar.style.display = 'none'; // Hide form by default
    if (userSlugInputSidebar) userSlugInputSidebar.disabled = false;
  } else {
    if (usernameDisplaySidebar) usernameDisplaySidebar.textContent = 'username';
    // Only show username setup prompt if user is editing their own tour
    if (usernameSetupPromptSidebar) usernameSetupPromptSidebar.style.display = isAdminEditingOtherUser ? 'none' : 'block';
    if (usernameChangeSectionSidebar) usernameChangeSectionSidebar.style.display = 'none';
    if (userSlugInputSidebar) userSlugInputSidebar.disabled = true;
  }
}

/**
 * Load sequence settings into panel UI
 */
async function loadSequenceSettings() {
  if (!config) return;

  // Load sequence metadata (from Firestore sequence object)
  const sequenceId = new URLSearchParams(window.location.search).get('id');
  if (sequenceId) {
    try {
      const sequence = await getSequence(sequenceId);

      if (sequence) {
        // Basic info
        if (sequenceNamePanel) sequenceNamePanel.value = sequence.name || '';
        if (sequenceDescriptionPanel) sequenceDescriptionPanel.value = sequence.description || '';

        // Privacy & Access
        if (sequencePublicPanel) sequencePublicPanel.checked = sequence.isPublic || false;
        if (sequenceDiscoverablePanel) sequenceDiscoverablePanel.checked = sequence.isDiscoverable || false;
        if (discoverableGroupPanel) {
          discoverableGroupPanel.style.display = sequence.isPublic ? 'block' : 'none';
        }

        // Note: Password is hashed, so we don't load it (user can set new password if needed)
        if (sequencePasswordPanel) sequencePasswordPanel.value = '';
        if (sequencePasswordPanel) sequencePasswordPanel.placeholder = sequence.password ? 'Enter new password to change' : 'Leave empty for no password';

        // Analytics
        if (sequenceAnalyticsPanel) sequenceAnalyticsPanel.checked = sequence.analyticsEnabled !== false;

        // Viewer settings (inverted: Enable checkbox = not disabled)
        if (enableTransitionsPanel) enableTransitionsPanel.checked = sequence.disableTransitions !== true;
        if (enableGazeNavigationPanel) enableGazeNavigationPanel.checked = sequence.disableGazeNavigation !== true;

        // Portal transition settings
        const portalSettings = sequence.portalSettings || {};
        if (portalGlowColorPanel) portalGlowColorPanel.value = portalSettings.glowColor || '#00bfff';
        if (portalGlowColorHexPanel) portalGlowColorHexPanel.value = portalSettings.glowColor || '#00bfff';
        if (portalEdgeOpacitySliderPanel) portalEdgeOpacitySliderPanel.value = String(portalSettings.edgeOpacity ?? 1);
        if (portalEdgeOpacityPanel) portalEdgeOpacityPanel.value = String(portalSettings.edgeOpacity ?? 1);
        if (portalEffectStylePanel) portalEffectStylePanel.value = portalSettings.effectStyle || 'clean';

        // White-label branding
        if (removeBrandingCheckbox) {
          removeBrandingCheckbox.checked = sequence.removeBranding || false;
        }
        if (removeFooterBrandingCheckbox) {
          removeFooterBrandingCheckbox.checked = sequence.removeFooterBranding || false;
        }
        if (sequence.logoUrl) {
          updateLogoPreview(sequence.logoUrl);
        }

        // Custom URL slugs
        if (userSlugInput) {
          userSlugInput.value = sequence.userSlug || '';
        }

        // Initialize slug UI based on user tier
        initializeSlugUI();
        updateSlugPreview();

        // Load preview image
        updatePreviewThumbnailUI();
      }
    } catch (error) {
      console.error('[Sequence Settings] Failed to load sequence metadata:', error);
    }
  }

  // Initialize slug UI even if sequence loading failed
  initializeSlugUI();

  // Update preview thumbnail UI
  updatePreviewThumbnailUI();

  // Portal config now per-portal (no sequence-level config)
  console.log('[Sequence Settings] Loaded successfully');
}

/**
 * Handle save sequence settings
 */
async function handleSaveSequenceSettings() {
  if (!saveSequenceSettingsBtn) return;

  const sequenceId = new URLSearchParams(window.location.search).get('id');
  if (!sequenceId) {
    showAlert('No sequence ID found. Please try again.', 'Error', '❌');
    return;
  }

  saveSequenceSettingsBtn.disabled = true;
  saveSequenceSettingsBtn.textContent = 'Saving...';

  try {
    // Always preserve existing values for critical fields to prevent data loss
    // Only update from UI if element exists, otherwise keep existing sequence value
    const updates: any = {
      name: sequenceNamePanel?.value.trim() || sequence?.name || 'Untitled',
      description: sequenceDescriptionPanel?.value.trim() ?? sequence?.description ?? '',
      isPublic: sequencePublicPanel ? sequencePublicPanel.checked : (sequence?.isPublic ?? false),
      isDiscoverable: sequenceDiscoverablePanel ? sequenceDiscoverablePanel.checked : (sequence?.isDiscoverable ?? false),
      analyticsEnabled: sequenceAnalyticsPanel ? sequenceAnalyticsPanel.checked : (sequence?.analyticsEnabled ?? true),
      // Invert: Enable checkbox checked = disable false
      disableTransitions: enableTransitionsPanel ? !enableTransitionsPanel.checked : (sequence?.disableTransitions ?? false),
      disableGazeNavigation: enableGazeNavigationPanel ? !enableGazeNavigationPanel.checked : (sequence?.disableGazeNavigation ?? false),
      portalSettings: {
        glowColor: portalGlowColorPanel?.value || '#00bfff',
        edgeOpacity: portalEdgeOpacityPanel ? parseFloat(portalEdgeOpacityPanel.value) : 1,
        effectStyle: portalEffectStylePanel?.value || 'clean',
      },
    };

    // Handle password
    const newPassword = sequencePasswordPanel?.value.trim();
    if (newPassword) {
      // Hash password before saving
      const bcrypt = await import('bcryptjs');
      updates.password = await bcrypt.hash(newPassword, 10);
    }

    // Save to Firestore (basic settings)
    await updateSequence(sequenceId, updates);

    // Handle slug claiming via Cloud Functions (atomic operations)
    const functions = getFunctions();

    // User slug (all users, requires tour owner's username)
    const newUserSlug = userSlugInput?.value.trim().toLowerCase();
    const currentUserSlug = sequence?.userSlug;
    if (tourOwnerUsername && newUserSlug && newUserSlug !== currentUserSlug) {
      try {
        const claimUserSlugFn = httpsCallable(functions, 'claimUserSlug');
        await claimUserSlugFn({ sequenceId, slug: newUserSlug });
        if (sequence) sequence.userSlug = newUserSlug;
        console.log('[Sequence Settings] User slug claimed:', newUserSlug);
      } catch (slugError: any) {
        console.error('[Sequence Settings] Failed to claim user slug:', slugError);
        showAlert(slugError.message || 'Failed to set tour URL', 'URL Error', '⚠️');
      }
    }

    // Sync sidebar name field with panel value before calling handleSave
    // This prevents handleSave from overwriting with a stale sidebar value
    const sequenceNameSidebar = document.getElementById('sequenceNameSidebar') as HTMLInputElement;
    if (sequenceNameSidebar && sequenceNamePanel) {
      sequenceNameSidebar.value = sequenceNamePanel.value.trim() || 'Untitled';
    }

    // Update local sequence state with all saved values
    if (sequence) {
      sequence.name = updates.name;
      sequence.description = updates.description;
      sequence.isPublic = updates.isPublic;
      sequence.isDiscoverable = updates.isDiscoverable;
      sequence.analyticsEnabled = updates.analyticsEnabled;
      sequence.disableTransitions = updates.disableTransitions;
      sequence.disableGazeNavigation = updates.disableGazeNavigation;
      sequence.portalSettings = updates.portalSettings;
    }

    // Also save config (with portal settings)
    await handleSave();

    // Update header title
    if (headerTitle) {
      headerTitle.textContent = `Edit: ${updates.name}`;
    }

    showToast('Sequence settings saved successfully', 'success');
    console.log('[Sequence Settings] Saved successfully');

  } catch (error) {
    console.error('[Sequence Settings] Failed to save:', error);
    showAlert('Failed to save sequence settings. Please try again.', 'Save Failed', '❌');
  } finally {
    saveSequenceSettingsBtn.disabled = false;
    saveSequenceSettingsBtn.textContent = 'Save Sequence Settings';
  }
}

/**
 * Handle add stage
 */
function handleAddStage() {
  if (!config || !config.stages) return;

  const newStage: ExperienceStage = {
    id: generateId(),
    name: `Scene ${config.stages.length + 1}`
  };

  config.stages.push(newStage);
  renderStageList();
  selectStage(config.stages.length - 1);

  markUnsaved();
}

/**
 * Handle delete stage (from legacy button)
 */
function handleDeleteStage() {
  deleteStage(currentStageIndex);
}

/**
 * Delete a stage with confirmation modal
 */
async function deleteStage(stageIndex: number) {
  if (!config || !config.stages) return;

  if (config.stages.length <= 1) {
    await showAlert('Cannot delete the last stage. An experience must have at least one stage.', 'Cannot Delete', '⚠️');
    return;
  }

  const stage = config.stages[stageIndex];
  const stageName = stage.name || `Scene ${stageIndex + 1}`;

  const confirmed = await showConfirm(
    `Are you sure you want to delete "${stageName}"? This cannot be undone.`,
    'Delete Stage',
    '🗑️',
    'Delete',
    'Cancel'
  );

  if (!confirmed) return;

  config.stages.splice(stageIndex, 1);
  renderStageList();

  // Select appropriate stage after deletion
  if (stageIndex <= currentStageIndex) {
    selectStage(Math.max(0, currentStageIndex - 1));
  } else {
    selectStage(currentStageIndex);
  }

  markUnsaved();
  showToast(`Deleted "${stageName}"`, 'success');
}

/**
 * Duplicate a stage (including version history)
 * Shows a popup letting user choose current version or pick from history
 */
async function duplicateStage(stageIndex: number) {
  if (!config || !config.stages || !sequenceId) return;

  const sourceStage = config.stages[stageIndex];
  const sourceStageId = sourceStage.id;

  // Show version selection popup
  showCustomModal({
    title: 'Duplicate Stage',
    icon: '📋',
    message: 'Which version do you want to use for the duplicated stage?',
    customContent: `
      <div style="display: flex; gap: 12px; justify-content: center; margin-top: 16px;">
        <button type="button" id="duplicate-current-btn" class="modal-btn modal-btn-primary" style="flex: 1; max-width: 180px;">
          Current Version
        </button>
        <button type="button" id="duplicate-choose-btn" class="modal-btn modal-btn-secondary" style="flex: 1; max-width: 180px;">
          Choose from History
        </button>
      </div>
    `,
    confirmText: '', // Hide default confirm button
    showCancel: true,
    cancelText: 'Cancel'
  });

  // Wait for DOM to update then attach handlers
  setTimeout(() => {
    const currentBtn = document.getElementById('duplicate-current-btn');
    const chooseBtn = document.getElementById('duplicate-choose-btn');

    currentBtn?.addEventListener('click', () => {
      closeModal();
      performDuplication(stageIndex, sourceStage, sourceStageId);
    });

    chooseBtn?.addEventListener('click', async () => {
      closeModal();
      await showVersionPicker(stageIndex, sourceStage, sourceStageId);
    });
  }, 50);
}

/**
 * Show version history picker for stage duplication
 */
async function showVersionPicker(stageIndex: number, sourceStage: ExperienceStage, sourceStageId: string | undefined) {
  if (!sequenceId || !sourceStageId) {
    // No version history available, just duplicate with current
    performDuplication(stageIndex, sourceStage, sourceStageId);
    return;
  }

  // Show loading state
  showCustomModal({
    title: 'Loading Versions...',
    icon: '⏳',
    message: 'Fetching version history...',
    confirmText: '',
    showCancel: false,
    lockClose: true
  });

  try {
    const { versions } = await AIAPI.getStageVersionHistory(sequenceId, sourceStageId, stageIndex);

    if (!versions || versions.length === 0) {
      closeModal();
      showToast('No version history found. Using current version.', 'info');
      performDuplication(stageIndex, sourceStage, sourceStageId);
      return;
    }

    // Build thumbnail grid HTML
    const thumbnailsHtml = versions.map((v, idx) => `
      <div class="version-thumb-item" data-version-idx="${idx}" style="
        cursor: pointer;
        border: 2px solid transparent;
        border-radius: 8px;
        overflow: hidden;
        transition: all 0.2s;
      ">
        <img src="${v.thumbnailUrl || v.imageUrl}" alt="Version ${v.versionNumber}" style="
          width: 100%;
          aspect-ratio: 2/1;
          object-fit: cover;
          display: block;
        ">
        <div style="
          padding: 6px 8px;
          font-size: 11px;
          background: #f5f5f5;
          text-align: center;
        ">
          v${v.versionNumber}${v.isActive ? ' (Current)' : ''}
        </div>
      </div>
    `).join('');

    closeModal();

    // Show version picker modal
    showCustomModal({
      title: 'Select Version',
      icon: '🖼️',
      message: 'Click a version to use for the duplicated stage:',
      customContent: `
        <div id="version-picker-grid" style="
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-top: 16px;
          max-height: 400px;
          overflow-y: auto;
          padding: 4px;
        ">
          ${thumbnailsHtml}
        </div>
        <style>
          .version-thumb-item:hover {
            border-color: #c62828 !important;
            transform: scale(1.02);
          }
        </style>
      `,
      confirmText: '',
      showCancel: true,
      cancelText: 'Cancel'
    });

    // Attach click handlers to thumbnails
    setTimeout(() => {
      const grid = document.getElementById('version-picker-grid');
      grid?.querySelectorAll('.version-thumb-item').forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const idx = parseInt(thumb.getAttribute('data-version-idx') || '0');
          const selectedVersion = versions[idx];
          closeModal();
          performDuplication(stageIndex, sourceStage, sourceStageId, selectedVersion.imageUrl);
        });
      });
    }, 50);

  } catch (error) {
    console.error('[Stage] Failed to fetch version history:', error);
    closeModal();
    showToast('Failed to load versions. Using current version.', 'error');
    performDuplication(stageIndex, sourceStage, sourceStageId);
  }
}

/**
 * Perform the actual stage duplication with optional custom skybox URL
 */
async function performDuplication(
  stageIndex: number,
  sourceStage: ExperienceStage,
  sourceStageId: string | undefined,
  customSkyboxUrl?: string
) {
  if (!config || !config.stages || !sequenceId) return;

  // Deep clone the stage
  const duplicatedStage: ExperienceStage = JSON.parse(JSON.stringify(sourceStage));

  // Generate new unique ID and update name
  duplicatedStage.id = generateId();
  duplicatedStage.name = `${sourceStage.name || 'Scene'} (Copy)`;

  // Also generate new IDs for hotspots to avoid conflicts
  if (duplicatedStage.hotspots) {
    duplicatedStage.hotspots = duplicatedStage.hotspots.map(h => ({
      ...h,
      id: generateId()
    }));
  }

  // If a specific version was selected, update the skybox URL
  if (customSkyboxUrl && duplicatedStage.skybox) {
    duplicatedStage.skybox.url = customSkyboxUrl;
    // Update all variants to use the selected version
    if (duplicatedStage.skybox.variants) {
      const variant = { url: customSkyboxUrl, format: 'jpg' as const };
      duplicatedStage.skybox.variants = {
        fallback: variant,
        desktop: variant,
        mobile: variant,
        tablet: variant,
        vr: variant,
        original: variant,
      };
    }
  }

  // Insert after the source stage
  config.stages.splice(stageIndex + 1, 0, duplicatedStage);

  renderStageList();
  selectStage(stageIndex + 1);
  markUnsaved();

  showToast(`Duplicated "${sourceStage.name || 'Scene'}"`, 'success');

  // Copy version history in the background (if source stage has an ID)
  if (sourceStageId) {
    try {
      const result = await AIAPI.duplicateStageVersions(sequenceId, sourceStageId, duplicatedStage.id);
      if (result.versionsCopied > 0) {
        console.log(`[Stage] Copied ${result.versionsCopied} versions to duplicated stage`);
      }
    } catch (error) {
      console.error('[Stage] Failed to copy version history:', error);
      // Don't show error to user - version history copy is non-critical
    }
  }
}

/**
 * Drag and drop handlers
 */
let draggedIndex: number | null = null;

function handleDragStart(e: Event) {
  const target = e.target as HTMLElement;
  draggedIndex = parseInt(target.getAttribute('data-index')!);
  target.classList.add('dragging');
}

function handleDragOver(e: Event) {
  e.preventDefault();
}

function handleDrop(e: Event) {
  e.preventDefault();
  const target = (e.target as HTMLElement).closest('.stage-item') as HTMLElement;
  if (!target || draggedIndex === null) return;

  const dropIndex = parseInt(target.getAttribute('data-index')!);

  if (draggedIndex !== dropIndex && config && config.stages) {
    // Reorder stages
    const [removed] = config.stages.splice(draggedIndex, 1);
    config.stages.splice(dropIndex, 0, removed);

    // Update current stage index if needed
    if (currentStageIndex === draggedIndex) {
      currentStageIndex = dropIndex;
    } else if (draggedIndex < currentStageIndex && dropIndex >= currentStageIndex) {
      currentStageIndex--;
    } else if (draggedIndex > currentStageIndex && dropIndex <= currentStageIndex) {
      currentStageIndex++;
    }

    renderStageList();
    markUnsaved();
  }
}

function handleDragEnd(e: Event) {
  const target = e.target as HTMLElement;
  target.classList.remove('dragging');
  draggedIndex = null;
}

/**
 * Handle cancel
 */
async function handleCancel() {
  if (hasUnsavedChanges) {
    const confirmed = await showConfirm('You have unsaved changes. Are you sure you want to leave?', 'Unsaved Changes', '⚠️');
    if (!confirmed) {
      return;
    }
  }
  // Clear both flags to bypass beforeunload warning
  clearUnsaved();
  isIntentionalNavigation = true;

  // Use setTimeout to ensure flags are cleared before navigation
  setTimeout(() => {
    window.location.href = '/dashboard/';
  }, 0);
}

/**
 * Handle preview - opens the tour using the best available URL
 */
function handlePreview() {
  if (!sequenceId) return;
  // Use getShareableLink to get the preferred URL (slug if available, using tour owner's username)
  const shareUrl = getShareableLink(sequenceId, sequence, tourOwnerUsername);
  // Extract just the path from the full URL
  const url = new URL(shareUrl);
  window.open(url.pathname, '_blank');
}

/**
 * Handle save
 */
async function handleSave() {
  if (!sequenceId || !config) return;

  try {
    saveBtn.disabled = true;
    saveBtnText.innerHTML = '<div class="loading-spinner"></div>';

    // Generate thumbnail from preview canvas ONLY if:
    // - No thumbnail exists yet
    // - User hasn't uploaded a custom thumbnail (isCustomThumbnail)
    let thumbnailUrl: string | undefined;
    const shouldAutoCapture = editorPreview &&
      config.stages.length > 0 &&
      !sequence?.thumbnailUrl &&
      !sequence?.isCustomThumbnail;

    if (shouldAutoCapture) {
      try {
        console.log('📸 Auto-generating initial thumbnail...');

        // Store current preview visibility state
        const wasPreviewVisible = previewVisible;
        const previewPanelDocked = document.getElementById('previewPanelDocked');

        // Temporarily show preview to ensure canvas renders properly
        if (!previewVisible) {
          console.log('📸 Temporarily showing preview for thumbnail capture...');
          previewVisible = true;
          previewPanelDocked?.classList.remove('hidden');
          editorPreview.resize();
          updatePreview();
          // Give the scene extra time to render when preview was hidden
          await waitForSceneSettle(10);
        } else {
          // Preview already visible, just wait for scene to settle
          await waitForSceneSettle(5);
        }

        // Hide editor overlays (camera targets, etc.) for clean capture
        const restoreOverlays = editorPreview.hideEditorOverlaysForCapture();

        try {
          const canvas = editorPreview.getCanvas();

          // Get tour name for branding
          const tourName = sequenceNameSidebar?.value || sequenceNamePanel?.value || sequence?.name;

          // Capture and upload thumbnail with branding
          thumbnailUrl = await generateAndUploadThumbnail(canvas, sequenceId, {
            addBranding: true,
            tourName: tourName
          });
          console.log('✅ Thumbnail generated:', thumbnailUrl);
        } finally {
          // Always restore overlays
          restoreOverlays();
        }

        // Restore previous preview visibility state
        if (!wasPreviewVisible) {
          console.log('📸 Restoring preview hidden state...');
          previewVisible = false;
          previewPanelDocked?.classList.add('hidden');
        }
      } catch (thumbError) {
        console.warn('⚠️ Thumbnail generation failed:', thumbError);
        // Continue saving even if thumbnail fails
      }
    }

    // Collect sidebar settings to save alongside config
    const sequenceNameSidebar = document.getElementById('sequenceNameSidebar') as HTMLInputElement;
    const sequenceDescriptionSidebar = document.getElementById('sequenceDescriptionSidebar') as HTMLTextAreaElement;
    const sequencePublicSidebar = document.getElementById('sequencePublicSidebar') as HTMLInputElement;
    const sequenceDiscoverableSidebar = document.getElementById('sequenceDiscoverableSidebar') as HTMLInputElement;
    const sequenceAnalyticsSidebar = document.getElementById('sequenceAnalyticsSidebar') as HTMLInputElement;
    const enableTransitionsSidebar = document.getElementById('enableTransitionsSidebar') as HTMLInputElement;
    const enableGazeNavigationSidebar = document.getElementById('enableGazeNavigationSidebar') as HTMLInputElement;

    // Portal transition settings (sidebar)
    const portalGlowColorSidebar = document.getElementById('portalGlowColorSidebar') as HTMLInputElement;
    const portalEdgeOpacitySidebar = document.getElementById('portalEdgeOpacitySidebar') as HTMLInputElement;
    const portalEffectStyleSidebar = document.getElementById('portalEffectStyleSidebar') as HTMLSelectElement;

    // Update sequence in Firestore (config + metadata settings)
    // Always include name to prevent title loss - check sidebar, then panel, then existing value
    const currentName = sequenceNameSidebar?.value || sequenceNamePanel?.value || sequence?.name || 'Untitled';

    // Always preserve existing values for critical fields to prevent data loss
    // Only update from UI if element exists, otherwise keep existing sequence value
    const currentDescription = sequenceDescriptionSidebar?.value ?? sequence?.description ?? '';
    const currentIsPublic = sequencePublicSidebar ? sequencePublicSidebar.checked : (sequence?.isPublic ?? false);
    const currentIsDiscoverable = sequenceDiscoverableSidebar ? sequenceDiscoverableSidebar.checked : (sequence?.isDiscoverable ?? false);
    const currentAnalyticsEnabled = sequenceAnalyticsSidebar ? sequenceAnalyticsSidebar.checked : (sequence?.analyticsEnabled ?? true);
    // Invert: "Enable" checkbox checked means disableTransitions = false
    const currentDisableTransitions = enableTransitionsSidebar ? !enableTransitionsSidebar.checked : (sequence?.disableTransitions ?? false);
    const currentDisableGazeNavigation = enableGazeNavigationSidebar ? !enableGazeNavigationSidebar.checked : (sequence?.disableGazeNavigation ?? false);

    // Update default FOV in config (convert degrees to radians)
    const defaultFOVSidebar = document.getElementById('defaultFOVSidebar') as HTMLInputElement;
    if (defaultFOVSidebar && config?.experience) {
      const fovDegrees = parseInt(defaultFOVSidebar.value) || 90;
      config.experience.defaultFOV = fovDegrees * Math.PI / 180;
    }

    // Collect navigation settings from sidebar form into config
    collectNavigationSettings();

    // Build portal settings from sidebar inputs
    const currentPortalSettings = {
      glowColor: portalGlowColorSidebar?.value || sequence?.portalSettings?.glowColor || '#00bfff',
      edgeOpacity: portalEdgeOpacitySidebar ? parseFloat(portalEdgeOpacitySidebar.value) : (sequence?.portalSettings?.edgeOpacity ?? 1),
      effectStyle: portalEffectStyleSidebar?.value || sequence?.portalSettings?.effectStyle || 'clean',
    };

    await updateSequence(sequenceId, {
      config,
      name: currentName,
      description: currentDescription,
      isPublic: currentIsPublic,
      isDiscoverable: currentIsDiscoverable,
      analyticsEnabled: currentAnalyticsEnabled,
      disableTransitions: currentDisableTransitions,
      disableGazeNavigation: currentDisableGazeNavigation,
      portalSettings: currentPortalSettings,
      ...(thumbnailUrl && { thumbnailUrl }),
    });

    // Save custom URL slugs if changed (use tour owner's username, not admin's)
    const newUserSlug = userSlugInputSidebar?.value?.trim().toLowerCase() || null;
    const userSlugChanged = newUserSlug !== (sequence?.userSlug || null);

    // Handle user slug change
    if (userSlugChanged && tourOwnerUsername) {
      if (newUserSlug) {
        try {
          const functions = getFunctions();
          const claimUserSlugFn = httpsCallable(functions, 'claimUserSlug');
          await claimUserSlugFn({ sequenceId, slug: newUserSlug });
          if (sequence) sequence.userSlug = newUserSlug;
          console.log('[Save] User slug claimed:', newUserSlug);
        } catch (error: any) {
          console.error('Failed to claim user slug:', error);
          showToast(error.message || 'Failed to claim tour URL', 'error');
        }
      } else if (sequence?.userSlug) {
        // Release user slug
        try {
          const functions = getFunctions();
          const releaseSlugFn = httpsCallable(functions, 'releaseSlug');
          await releaseSlugFn({ sequenceId, type: 'user' });
          if (sequence) sequence.userSlug = undefined;
          console.log('[Save] User slug released');
        } catch (error: any) {
          console.error('Failed to release user slug:', error);
        }
      }
    }

    // Update slug preview after saving
    updateSlugPreview();

    // Update local sequence state with the name we just saved
    if (sequence) {
      sequence.name = currentName;
      if (sequenceNameSidebar) sequenceNameSidebar.value = currentName; // Keep UI in sync
      if (sequenceDescriptionSidebar) sequence.description = sequenceDescriptionSidebar.value;
      if (sequencePublicSidebar) sequence.isPublic = sequencePublicSidebar.checked;
      if (sequenceDiscoverableSidebar) sequence.isDiscoverable = sequenceDiscoverableSidebar.checked;
      if (sequenceAnalyticsSidebar) sequence.analyticsEnabled = sequenceAnalyticsSidebar.checked;
      if (enableTransitionsSidebar) sequence.disableTransitions = !enableTransitionsSidebar.checked;
      if (enableGazeNavigationSidebar) sequence.disableGazeNavigation = !enableGazeNavigationSidebar.checked;
      sequence.portalSettings = currentPortalSettings;
    }

    // Show success message
    successMessage.classList.add('show');
    setTimeout(() => {
      successMessage.classList.remove('show');
    }, 3000);

    clearUnsaved();
    console.log('✅ Changes saved');
  } catch (error) {
    console.error('Failed to save:', error);
    await showAlert('Failed to save changes. Please try again.', 'Save Failed', '❌');
  } finally {
    saveBtn.disabled = false;
    saveBtnText.textContent = 'Save Changes';
  }
}


/**
 * Mark unsaved changes and highlight save button
 */
function markUnsaved() {
  hasUnsavedChanges = true;
  // Re-query in case DOM wasn't ready at module load
  const btn = document.getElementById('saveBtn');
  btn?.classList.add('has-unsaved');
}

/**
 * Clear unsaved state and remove save button highlight
 */
function clearUnsaved() {
  hasUnsavedChanges = false;
  const btn = document.getElementById('saveBtn');
  btn?.classList.remove('has-unsaved');
}

/**
 * Utility functions
 */
function generateId(): string {
  return `stage-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

function degreesToRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

function radiansToDegrees(radians: number): number {
  return radians * (180 / Math.PI);
}

/**
 * Initialize 3D preview
 * Uses double requestAnimationFrame to ensure proper browser reflow after panel becomes visible
 */
function initializePreview() {
  try {
    // Show the docked preview panel FIRST before initializing the engine
    // This ensures the canvas has valid dimensions when Babylon.js creates the engine
    const previewPanelDocked = document.getElementById('previewPanelDocked');
    const resizeHandle = document.getElementById('previewResizeHandle');
    const canvas = document.getElementById('previewCanvasFloat') as HTMLCanvasElement;

    if (previewPanelDocked) {
      previewPanelDocked.classList.remove('hidden');
      resizeHandle?.classList.add('visible');

      // Force synchronous reflow by reading offsetHeight
      // This ensures the browser computes layout before we read canvas dimensions
      void previewPanelDocked.offsetHeight;
    }

    // CRITICAL: Wait for double requestAnimationFrame to ensure browser has fully laid out the panel
    // First RAF: scheduled after current task completes
    // Second RAF: ensures layout computation is complete
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Now the canvas should have valid dimensions
        if (canvas) {
          console.log('[Editor] Canvas dimensions at init:', canvas.clientWidth, 'x', canvas.clientHeight);
        }

        // Initialize with the docked canvas (still uses same ID)
        editorPreview = new EditorPreview('previewCanvasFloat');
        editorPreview.startRendering();

        // Expose for debugging
        (window as any).editorPreview = editorPreview;

        // Setup hotspot click handler
        setupPreviewHotspotHandler();

        // Initial update IMMEDIATELY if we have stage data
        if (config && config.stages && config.stages.length > 0) {
          console.log('[Editor] Triggering initial preview load for stage:', config.stages[currentStageIndex]?.id);
          updatePreview();
        }

        // Debug: Log scene state after a short delay
        setTimeout(() => {
          const scene = editorPreview?.getScene();
          if (scene) {
            const dome = scene.getMeshByName('dome');
            console.log('[Editor] DEBUG Scene state:', {
              totalMeshes: scene.meshes.length,
              meshNames: scene.meshes.map(m => m.name),
              domeExists: !!dome,
              domeVisible: dome?.isVisible,
              domeMaterial: dome?.material?.name
            });
          }
        }, 1000);

        console.log('[Editor] Preview fully initialized');
      });
    });

    // Preview is now visible by default
    previewVisible = true;

    // Mark the toggle button as active since panel is shown
    if (togglePreviewPanel) togglePreviewPanel.checked = true;

    // Handle window resize
    window.addEventListener('resize', () => {
      if (editorPreview && previewVisible) {
        editorPreview.resize();
      }
    });

    console.log('✅ 3D Preview initialization started (waiting for canvas layout)');
  } catch (error) {
    console.error('Failed to initialize 3D preview:', error);
  }
}

/**
 * Setup hotspot click handler for the preview
 * Called after editorPreview is created in the async initialization
 */
function setupPreviewHotspotHandler() {
  if (!editorPreview) return;

  editorPreview.onHotspotClick = (hotspotId: string) => {
    console.log('[Editor] Hotspot clicked in preview:', hotspotId);

    if (!config || !config.stages) return;
    const stage = config.stages[currentStageIndex];
    if (!stage.hotspots) return;

    // Find hotspot to determine type
    const hotspot = stage.hotspots.find(h => h.id === hotspotId);
    if (!hotspot) {
      console.warn('[Editor] Hotspot not found for highlight:', hotspotId);
      return;
    }

    // Determine target tab and list based on hotspot type
    let targetTab: string;
    let targetList: HTMLElement;

    if (hotspot.type === 'navigation') {
      targetTab = 'portals';
      targetList = portalsList;
    } else if (hotspot.type === 'audio') {
      targetTab = 'audio';
      targetList = audioHotspotsList;
    } else {
      // info, both, lead_capture all go to hotspots tab
      targetTab = 'hotspots';
      targetList = hotspotsList;
    }

    // Switch to correct tab if not active
    const activeTab = document.querySelector('.tab.active');
    if (activeTab?.getAttribute('data-tab') !== targetTab) {
      switchTab(targetTab);
    }

    // Use requestAnimationFrame for more reliable timing after tab switch
    requestAnimationFrame(() => {
      // Clear previous selections
      document.querySelectorAll('.item-card.portal-selected').forEach(el => {
        el.classList.remove('portal-selected');
      });

      // Find DOM element by data-id (more reliable than index)
      const element = targetList.querySelector(`.item-card[data-id="${hotspotId}"]`);
      if (element) {
        // Scroll into view
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Add glow effect
        element.classList.add('portal-selected');

        // Remove glow after 3 seconds
        setTimeout(() => {
          element.classList.remove('portal-selected');
        }, 3000);

        console.log('[Editor] Highlighted hotspot:', hotspotId);
      } else {
        console.warn('[Editor] Could not find DOM element for hotspot:', hotspotId);
      }
    });
  };

  // Camera target moved handler - update config when target is dragged
  editorPreview.onCameraTargetMoved = (position) => {
    console.log('[Editor] Camera target moved:', position);

    if (!config || !config.stages) return;

    // Camera target only applies to the first stage (index 0)
    // Other stages use portal's initialViewDirection
    if (currentStageIndex !== 0) {
      console.log('[Editor] Camera target only applies to first stage, ignoring');
      return;
    }

    const stage = config.stages[currentStageIndex];

    // Update stage config
    stage.initialCameraTarget = position;
    markUnsaved();

    console.log('[Editor] Camera target saved to config for first stage');
  };

  // Hotspot dragged handler - update position when dragged
  editorPreview.onHotspotDragged = (id, position) => {
    console.log('[Editor] Hotspot dragged:', id, position);

    if (!config || !config.stages) return;
    const stage = config.stages[currentStageIndex];
    if (!stage.hotspots) return;

    // Find and update the hotspot position
    const hotspot = stage.hotspots.find(h => h.id === id);
    if (hotspot) {
      hotspot.position = position;
      markUnsaved();

      // Refresh the preview to show updated position
      if (editorPreview) {
        editorPreview.updateStage(stage, currentStageIndex);
      }

      // Update the UI to show new coordinates
      renderInfoHotspotsList(stage.hotspots || []);
      renderAudioHotspotsList(stage.hotspots || []);
      renderPortalsList(stage.hotspots || []);

      console.log('[Editor] Hotspot position updated');
    }
  };

  console.log('✅ 3D Preview hotspot handler attached');
  console.log('✅ Camera target and drag handlers attached');
}

/**
 * Handle toggle preview (docked panel)
 */
function handleTogglePreview() {
  const previewPanelDocked = document.getElementById('previewPanelDocked');
  previewVisible = !previewVisible;

  if (previewVisible) {
    previewPanelDocked?.classList.remove('hidden');
    if (togglePreviewPanel) togglePreviewPanel.checked = true;

    if (editorPreview) {
      // Force resize after a short delay to allow transition/layout
      requestAnimationFrame(() => {
        editorPreview?.resize();
        updatePreview();
      });
    }
  } else {
    previewPanelDocked?.classList.add('hidden');
    if (togglePreviewPanel) togglePreviewPanel.checked = false;
  }
}

/**
 * Handle close preview (docked panel)
 */
function handleClosePreview() {
  const previewPanelDocked = document.getElementById('previewPanelDocked');
  previewVisible = false;
  previewPanelDocked?.classList.add('hidden');
  if (togglePreviewPanel) togglePreviewPanel.checked = false;
}

/**
 * Handle reset camera
 */
function handleResetCamera() {
  if (editorPreview) {
    editorPreview.resetCamera();
  }
}

/**
 * Update 3D preview with current stage
 */
function updatePreview() {
  // Bail early if preview not ready
  if (!editorPreview) {
    console.log('[Editor] Preview not ready, skipping update');
    return;
  }

  // If no config or stages, clear loading and return
  if (!config || !config.stages || config.stages.length === 0) {
    console.log('[Editor] No stage data, clearing loading indicator');
    editorPreview.forceHideLoading();
    return;
  }

  // Always force resize before updating to ensure valid viewport
  editorPreview.resize();

  const stage = config.stages[currentStageIndex];
  if (stage) {
    console.log('[Editor] Updating preview for stage:', stage.id || stage.name, 'index:', currentStageIndex);
    editorPreview.updateStage(stage, currentStageIndex);
  } else {
    // No stage at current index, clear loading
    console.log('[Editor] No stage at index', currentStageIndex, ', clearing loading');
    editorPreview.forceHideLoading();
  }
}

/**
 * Setup panel toggle buttons
 */
function setupPanelToggles() {
  const previewPanelDocked = document.getElementById('previewPanelDocked');

  const resizeHandle = document.getElementById('previewResizeHandle');

  // Preview Panel Toggle - now uses docked panel (not floating)
  // Uses checkbox change event for toggle switch UI
  togglePreviewPanel?.addEventListener('change', () => {
    const shouldShow = togglePreviewPanel.checked;

    if (shouldShow) {
      // Show the docked preview and resize handle
      previewPanelDocked?.classList.remove('hidden');
      resizeHandle?.classList.add('visible');
      previewVisible = true;

      // Use requestAnimationFrame to ensure DOM layout is complete before resizing
      requestAnimationFrame(() => {
        editorPreview?.resize();
        updatePreview();
      });
    } else {
      // Hide the docked preview and resize handle
      previewPanelDocked?.classList.add('hidden');
      resizeHandle?.classList.remove('visible');
      previewVisible = false;
    }
  });

  // Performance Panel Toggle (still uses floating PanelManager)
  togglePerformancePanel?.addEventListener('click', () => {
    if (!panelManager) return;
    const isVisible = panelManager.togglePanel('performancePanel');
    togglePerformancePanel.classList.toggle('active', isVisible);

    if (isVisible) {
      updatePerformanceMetrics(true); // true = floating panel
    }
  });

  // Close buttons
  previewPanelClose?.addEventListener('click', () => {
    previewPanelDocked?.classList.add('hidden');
    resizeHandle?.classList.remove('visible');
    if (togglePreviewPanel) togglePreviewPanel.checked = false;
    previewVisible = false;
  });

  performancePanelClose?.addEventListener('click', () => {
    panelManager?.hidePanel('performancePanel');
    togglePerformancePanel.classList.remove('active');
  });

  // Custom URL slug event listeners
  userSlugInput?.addEventListener('input', () => {
    const slug = userSlugInput.value.trim().toLowerCase();
    if (slug) {
      checkUserSlugAvailability(slug);
    } else {
      if (userSlugStatus) userSlugStatus.textContent = '';
      if (userSlugHelperText) {
        userSlugHelperText.textContent = 'Custom URL under your username';
        userSlugHelperText.style.color = '';
      }
    }
    updateSlugPreview();
  });

  claimUsernameBtn?.addEventListener('click', handleClaimUsername);

  copySlugUrlBtn?.addEventListener('click', () => {
    const url = slugUrlPreviewText?.textContent;
    if (url) {
      navigator.clipboard.writeText(url).then(() => {
        showToast('URL copied to clipboard!', 'success');
      }).catch(() => {
        showToast('Failed to copy URL', 'error');
      });
    }
  });

  // Custom URL slug event listeners (Sidebar)
  userSlugInputSidebar?.addEventListener('input', () => {
    const slug = userSlugInputSidebar.value.trim().toLowerCase();
    if (slug) {
      checkUserSlugAvailability(slug);
    } else {
      if (userSlugStatusSidebar) userSlugStatusSidebar.textContent = '';
      if (userSlugHelperTextSidebar) {
        userSlugHelperTextSidebar.textContent = 'URL under your username';
        userSlugHelperTextSidebar.style.color = '';
      }
    }
    // Sync panel input if exists
    if (userSlugInput) userSlugInput.value = userSlugInputSidebar.value;
    updateSlugPreview();
  });

  claimUsernameBtnSidebar?.addEventListener('click', handleClaimUsernameSidebar);

  // Change username event listeners
  changeUsernameBtnSidebar?.addEventListener('click', () => {
    if (usernameChangeFormSidebar) {
      usernameChangeFormSidebar.style.display = 'block';
      if (usernameChangeInputSidebar) {
        usernameChangeInputSidebar.value = currentUserProfile?.username || '';
        usernameChangeInputSidebar.focus();
      }
    }
  });

  cancelUsernameChangeBtnSidebar?.addEventListener('click', () => {
    if (usernameChangeFormSidebar) usernameChangeFormSidebar.style.display = 'none';
    if (usernameChangeErrorSidebar) usernameChangeErrorSidebar.style.display = 'none';
  });

  confirmUsernameChangeBtnSidebar?.addEventListener('click', handleChangeUsername);

  // Aspect ratio toggles
  aspectHorizontal?.addEventListener('click', () => setPreviewAspectRatio('horizontal'));
  aspectSquare?.addEventListener('click', () => setPreviewAspectRatio('square'));
  aspectVertical?.addEventListener('click', () => setPreviewAspectRatio('vertical'));

  // Reset camera button in docked preview
  resetCameraFloatBtn?.addEventListener('click', handleResetCamera);

  // Setup preview panel resize
  setupPreviewResize(previewPanelDocked, resizeHandle);

  // Setup sidebar resize
  const sidebar = document.querySelector('.sidebar') as HTMLElement;
  const sidebarResizeHandle = document.getElementById('sidebarResizeHandle');
  setupSidebarResize(sidebar, sidebarResizeHandle);
}

/**
 * Setup draggable resize handle between main panel and preview panel
 */
function setupPreviewResize(previewPanel: HTMLElement | null, resizeHandle: HTMLElement | null) {
  if (!resizeHandle || !previewPanel) return;

  const editorLayout = document.querySelector('.editor-layout') as HTMLElement;
  const mainPanel = document.querySelector('.main-panel') as HTMLElement;
  if (!mainPanel) return;

  const sidebar = document.querySelector('.sidebar') as HTMLElement;
  const minMainWidth = 320;

  // Calculate equal split: (window - sidebar) / 2
  const getEqualWidth = () => {
    const sidebarWidth = sidebar?.offsetWidth || 420;
    return Math.floor((window.innerWidth - sidebarWidth - 20) / 2); // 20 for resize handles
  };

  // Max width = window width - (sidebar width * 2) - some buffer for resize handles
  const getMaxMainWidth = () => {
    const sidebarWidth = sidebar?.offsetWidth || 420;
    return Math.max(400, window.innerWidth - (sidebarWidth * 2) - 50);
  };

  // Set initial width to equal split
  const initialWidth = Math.max(minMainWidth, Math.min(getMaxMainWidth(), getEqualWidth()));
  mainPanel.style.flex = `0 0 ${initialWidth}px`;

  let isResizing = false;
  let startX = 0;
  let startMainWidth = 0;

  resizeHandle.addEventListener('mousedown', (e: MouseEvent) => {
    e.preventDefault();
    isResizing = true;
    startX = e.clientX;
    startMainWidth = mainPanel.offsetWidth;

    resizeHandle.classList.add('dragging');
    editorLayout?.classList.add('resizing');

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  });

  function handleMouseMove(e: MouseEvent) {
    if (!isResizing) return;

    // Calculate new main panel width (dragging right increases, left decreases)
    const deltaX = e.clientX - startX;
    let newWidth = startMainWidth + deltaX;

    // Clamp to min/max (max is dynamic based on window and sidebar size)
    newWidth = Math.max(minMainWidth, Math.min(getMaxMainWidth(), newWidth));

    // Set main panel to fixed width
    mainPanel!.style.flex = `0 0 ${newWidth}px`;

    // Resize the Babylon engine to match
    editorPreview?.resize();
  }

  function handleMouseUp() {
    if (!isResizing) return;

    isResizing = false;
    resizeHandle!.classList.remove('dragging');
    editorLayout?.classList.remove('resizing');

    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);

    // Final resize of the Babylon engine
    requestAnimationFrame(() => {
      editorPreview?.resize();
    });
  }
}

/**
 * Setup draggable resize handle for sidebar
 */
function setupSidebarResize(sidebar: HTMLElement | null, resizeHandle: HTMLElement | null) {
  if (!resizeHandle || !sidebar) return;

  const editorLayout = document.querySelector('.editor-layout') as HTMLElement;
  const minWidth = 280;
  const maxWidth = 600;

  let isResizing = false;
  let startX = 0;
  let startWidth = 0;

  resizeHandle.addEventListener('mousedown', (e: MouseEvent) => {
    e.preventDefault();
    isResizing = true;
    startX = e.clientX;
    startWidth = sidebar.offsetWidth;

    resizeHandle.classList.add('dragging');
    editorLayout?.classList.add('resizing');

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  });

  function handleMouseMove(e: MouseEvent) {
    if (!isResizing) return;

    // Calculate new width (dragging right increases width)
    const deltaX = e.clientX - startX;
    let newWidth = startWidth + deltaX;

    // Clamp to min/max
    newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

    sidebar!.style.width = `${newWidth}px`;
  }

  function handleMouseUp() {
    if (!isResizing) return;

    isResizing = false;
    resizeHandle!.classList.remove('dragging');
    editorLayout?.classList.remove('resizing');

    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }
}

/**
 * Setup sidebar tab switching (Stages / Performance / Settings)
 */
function setupSidebarTabs() {
  const sidebarTabs = document.querySelectorAll('.sidebar-tab');
  const sidebarContents = document.querySelectorAll('.sidebar-content');

  sidebarTabs.forEach(tab => {
    tab.addEventListener('click', () => {
      const targetTab = tab.getAttribute('data-sidebar-tab');
      if (!targetTab) return;

      // Update active tab
      sidebarTabs.forEach(t => t.classList.remove('active'));
      tab.classList.add('active');

      // Show target content
      sidebarContents.forEach(content => {
        content.classList.remove('active');
        if (content.id === `sidebar-${targetTab}`) {
          content.classList.add('active');

          // Load data when switching to specific tabs
          if (targetTab === 'performance') {
            updatePerformanceMetrics(false, true); // use sidebar
          } else if (targetTab === 'settings') {
            loadSidebarSequenceSettings();
          } else if (targetTab === 'navigation') {
            loadNavigationSettings();
          } else if (targetTab === 'leads') {
            loadLeadsSubmissions();
          }
        }
      });
    });
  });

  // Setup sidebar refresh button
  const refreshBtn = document.getElementById('refreshMetricsSidebarBtn');
  refreshBtn?.addEventListener('click', () => updatePerformanceMetrics(false, true));

  // Note: Sidebar settings are saved via the main Save button (no separate save button)
  // The handleSave function pulls from both panel and sidebar inputs

  // Setup sidebar settings inputs
  setupSidebarSettingsInputs();

  // Setup navigation inputs
  setupNavigationInputs();
}

/**
 * Setup sidebar settings input handlers
 */
function setupSidebarSettingsInputs() {
  const sequenceNameSidebar = document.getElementById('sequenceNameSidebar') as HTMLInputElement;
  const sequenceDescriptionSidebar = document.getElementById('sequenceDescriptionSidebar') as HTMLTextAreaElement;
  const sequencePublicSidebar = document.getElementById('sequencePublicSidebar') as HTMLInputElement;
  const sequenceDiscoverableSidebar = document.getElementById('sequenceDiscoverableSidebar') as HTMLInputElement;
  const sequencePasswordSidebar = document.getElementById('sequencePasswordSidebar') as HTMLInputElement;
  const sequenceAnalyticsSidebar = document.getElementById('sequenceAnalyticsSidebar') as HTMLInputElement;

  // Visibility toggle logic
  sequencePublicSidebar?.addEventListener('change', () => {
    const discoverableGroup = document.getElementById('discoverableGroupSidebar');
    if (discoverableGroup) {
      discoverableGroup.style.display = sequencePublicSidebar.checked ? 'flex' : 'none';
    }
  });

  // FOV slider real-time display update and preview sync
  const defaultFOVSidebar = document.getElementById('defaultFOVSidebar') as HTMLInputElement;
  const defaultFOVValueSidebar = document.getElementById('defaultFOVValueSidebar') as HTMLElement;
  defaultFOVSidebar?.addEventListener('input', () => {
    if (defaultFOVValueSidebar) {
      defaultFOVValueSidebar.textContent = `${defaultFOVSidebar.value}°`;
    }
    // Update 3D preview FOV in real-time
    if (editorPreview) {
      const fovDegrees = parseInt(defaultFOVSidebar.value) || 90;
      const fovRadians = fovDegrees * Math.PI / 180;
      editorPreview.setFOV(fovRadians);
    }
    markUnsaved();
  });

  // Portal settings event listeners (sync color picker with hex, slider with number)
  const portalGlowColorSidebar = document.getElementById('portalGlowColorSidebar') as HTMLInputElement;
  const portalGlowColorHexSidebar = document.getElementById('portalGlowColorHexSidebar') as HTMLInputElement;
  const portalEdgeOpacitySliderSidebar = document.getElementById('portalEdgeOpacitySliderSidebar') as HTMLInputElement;
  const portalEdgeOpacitySidebar = document.getElementById('portalEdgeOpacitySidebar') as HTMLInputElement;

  portalGlowColorSidebar?.addEventListener('input', () => {
    if (portalGlowColorHexSidebar) portalGlowColorHexSidebar.value = portalGlowColorSidebar.value;
    markUnsaved();
  });
  portalGlowColorHexSidebar?.addEventListener('input', () => {
    if (/^#[0-9A-Fa-f]{6}$/.test(portalGlowColorHexSidebar.value) && portalGlowColorSidebar) {
      portalGlowColorSidebar.value = portalGlowColorHexSidebar.value;
    }
    markUnsaved();
  });
  portalEdgeOpacitySliderSidebar?.addEventListener('input', () => {
    if (portalEdgeOpacitySidebar) portalEdgeOpacitySidebar.value = portalEdgeOpacitySliderSidebar.value;
    markUnsaved();
  });
  portalEdgeOpacitySidebar?.addEventListener('input', () => {
    const value = Math.max(0, Math.min(1, parseFloat(portalEdgeOpacitySidebar.value) || 0));
    if (portalEdgeOpacitySliderSidebar) portalEdgeOpacitySliderSidebar.value = String(value);
    portalEdgeOpacitySidebar.value = String(value);
    markUnsaved();
  });

  // Preview image capture and upload handlers
  setupPreviewImageHandlers();
}

/**
 * Setup preview image capture and upload handlers
 * Sets up handlers for both sidebar and panel versions
 */
function setupPreviewImageHandlers() {
  // Sidebar version
  const capturePreviewBtn = document.getElementById('capturePreviewBtn') as HTMLButtonElement;
  const uploadPreviewBtn = document.getElementById('uploadPreviewBtn') as HTMLButtonElement;
  const previewImageUpload = document.getElementById('previewImageUpload') as HTMLInputElement;

  // Panel version
  const capturePreviewBtnPanel = document.getElementById('capturePreviewBtnPanel') as HTMLButtonElement;
  const uploadPreviewBtnPanel = document.getElementById('uploadPreviewBtnPanel') as HTMLButtonElement;
  const previewImageUploadPanel = document.getElementById('previewImageUploadPanel') as HTMLInputElement;

  // Capture preview from scene (both versions use same handler)
  capturePreviewBtn?.addEventListener('click', handleCapturePreview);
  capturePreviewBtnPanel?.addEventListener('click', handleCapturePreview);

  // Open file picker for custom upload (sidebar)
  uploadPreviewBtn?.addEventListener('click', () => {
    previewImageUpload?.click();
  });

  // Open file picker for custom upload (panel)
  uploadPreviewBtnPanel?.addEventListener('click', () => {
    previewImageUploadPanel?.click();
  });

  // Handle file selection for custom upload (both versions)
  previewImageUpload?.addEventListener('change', handleUploadPreviewImage);
  previewImageUploadPanel?.addEventListener('change', handleUploadPreviewImage);
}

/**
 * Handle manual capture of preview image from the 3D scene
 */
async function handleCapturePreview() {
  if (!editorPreview || !sequenceId) {
    console.warn('Cannot capture preview: editor preview or sequence ID not available');
    return;
  }

  // Get both sidebar and panel buttons
  const capturePreviewBtn = document.getElementById('capturePreviewBtn') as HTMLButtonElement;
  const capturePreviewBtnPanel = document.getElementById('capturePreviewBtnPanel') as HTMLButtonElement;

  const originalText = 'Capture from Scene';

  // Disable both buttons
  if (capturePreviewBtn) {
    capturePreviewBtn.disabled = true;
    capturePreviewBtn.textContent = 'Capturing...';
  }
  if (capturePreviewBtnPanel) {
    capturePreviewBtnPanel.disabled = true;
    capturePreviewBtnPanel.textContent = 'Capturing...';
  }

  try {
    // Store current preview visibility state
    const wasPreviewVisible = previewVisible;
    const previewPanelDocked = document.getElementById('previewPanelDocked');

    // Temporarily show preview if hidden
    if (!previewVisible) {
      previewVisible = true;
      previewPanelDocked?.classList.remove('hidden');
      editorPreview.resize();
      updatePreview();
      await waitForSceneSettle(10);
    } else {
      await waitForSceneSettle(5);
    }

    // Hide editor overlays for clean capture
    const restoreOverlays = editorPreview.hideEditorOverlaysForCapture();

    try {
      const canvas = editorPreview.getCanvas();
      const tourName = (document.getElementById('sequenceNameSidebar') as HTMLInputElement)?.value || sequence?.name;

      // Capture and upload thumbnail with branding
      const thumbnailUrl = await generateAndUploadThumbnail(canvas, sequenceId, {
        addBranding: true,
        tourName: tourName
      });

      // Update sequence with new thumbnail (NOT a custom thumbnail, so auto-capture can still update it)
      await updateSequence(sequenceId, {
        thumbnailUrl,
        isCustomThumbnail: false
      });

      // Update local state
      if (sequence) {
        sequence.thumbnailUrl = thumbnailUrl;
        sequence.isCustomThumbnail = false;
      }

      // Update UI
      updatePreviewThumbnailUI();

      console.log('Preview captured successfully:', thumbnailUrl);

      // Show success on both buttons
      if (capturePreviewBtn) capturePreviewBtn.textContent = 'Captured!';
      if (capturePreviewBtnPanel) capturePreviewBtnPanel.textContent = 'Captured!';
      setTimeout(() => {
        if (capturePreviewBtn) capturePreviewBtn.textContent = originalText;
        if (capturePreviewBtnPanel) capturePreviewBtnPanel.textContent = originalText;
      }, 2000);

    } finally {
      restoreOverlays();
    }

    // Restore preview visibility
    if (!wasPreviewVisible) {
      previewVisible = false;
      previewPanelDocked?.classList.add('hidden');
    }

  } catch (error) {
    console.error('Failed to capture preview:', error);
    if (capturePreviewBtn) capturePreviewBtn.textContent = 'Capture Failed';
    if (capturePreviewBtnPanel) capturePreviewBtnPanel.textContent = 'Capture Failed';
    setTimeout(() => {
      if (capturePreviewBtn) capturePreviewBtn.textContent = originalText;
      if (capturePreviewBtnPanel) capturePreviewBtnPanel.textContent = originalText;
    }, 2000);
  } finally {
    if (capturePreviewBtn) capturePreviewBtn.disabled = false;
    if (capturePreviewBtnPanel) capturePreviewBtnPanel.disabled = false;
  }
}

/**
 * Handle upload of custom preview image
 */
async function handleUploadPreviewImage(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input?.files?.[0];

  if (!file || !sequenceId) {
    console.warn('No file selected or sequence ID not available');
    return;
  }

  // Get both sidebar and panel buttons
  const uploadPreviewBtn = document.getElementById('uploadPreviewBtn') as HTMLButtonElement;
  const uploadPreviewBtnPanel = document.getElementById('uploadPreviewBtnPanel') as HTMLButtonElement;

  const originalText = 'Upload Custom';

  // Disable both buttons
  if (uploadPreviewBtn) {
    uploadPreviewBtn.disabled = true;
    uploadPreviewBtn.textContent = 'Uploading...';
  }
  if (uploadPreviewBtnPanel) {
    uploadPreviewBtnPanel.disabled = true;
    uploadPreviewBtnPanel.textContent = 'Uploading...';
  }

  try {
    // Upload custom thumbnail
    const thumbnailUrl = await uploadCustomThumbnail(sequenceId, file);

    // Update sequence with custom thumbnail (marked as custom to prevent auto-capture overwriting)
    await updateSequence(sequenceId, {
      thumbnailUrl,
      isCustomThumbnail: true
    });

    // Update local state
    if (sequence) {
      sequence.thumbnailUrl = thumbnailUrl;
      sequence.isCustomThumbnail = true;
    }

    // Update UI
    updatePreviewThumbnailUI();

    console.log('Custom preview uploaded successfully:', thumbnailUrl);

    // Show success on both buttons
    if (uploadPreviewBtn) uploadPreviewBtn.textContent = 'Uploaded!';
    if (uploadPreviewBtnPanel) uploadPreviewBtnPanel.textContent = 'Uploaded!';
    setTimeout(() => {
      if (uploadPreviewBtn) uploadPreviewBtn.textContent = originalText;
      if (uploadPreviewBtnPanel) uploadPreviewBtnPanel.textContent = originalText;
    }, 2000);

  } catch (error) {
    console.error('Failed to upload custom preview:', error);
    if (uploadPreviewBtn) uploadPreviewBtn.textContent = 'Upload Failed';
    if (uploadPreviewBtnPanel) uploadPreviewBtnPanel.textContent = 'Upload Failed';
    setTimeout(() => {
      if (uploadPreviewBtn) uploadPreviewBtn.textContent = originalText;
      if (uploadPreviewBtnPanel) uploadPreviewBtnPanel.textContent = originalText;
    }, 2000);
  } finally {
    if (uploadPreviewBtn) uploadPreviewBtn.disabled = false;
    if (uploadPreviewBtnPanel) uploadPreviewBtnPanel.disabled = false;
    // Reset file input
    input.value = '';
  }
}

/**
 * Load sequence settings into sidebar
 */
async function loadSidebarSequenceSettings() {
  if (!sequence) return;

  const sequenceNameSidebar = document.getElementById('sequenceNameSidebar') as HTMLInputElement;
  const sequenceDescriptionSidebar = document.getElementById('sequenceDescriptionSidebar') as HTMLTextAreaElement;
  const sequencePublicSidebar = document.getElementById('sequencePublicSidebar') as HTMLInputElement;
  const sequenceDiscoverableSidebar = document.getElementById('sequenceDiscoverableSidebar') as HTMLInputElement;
  const sequencePasswordSidebar = document.getElementById('sequencePasswordSidebar') as HTMLInputElement;
  const sequenceAnalyticsSidebar = document.getElementById('sequenceAnalyticsSidebar') as HTMLInputElement;
  const enableTransitionsSidebar = document.getElementById('enableTransitionsSidebar') as HTMLInputElement;
  const enableGazeNavigationSidebar = document.getElementById('enableGazeNavigationSidebar') as HTMLInputElement;

  if (sequenceNameSidebar) sequenceNameSidebar.value = sequence.name || '';
  if (sequenceDescriptionSidebar) sequenceDescriptionSidebar.value = sequence.description || '';
  if (sequencePublicSidebar) sequencePublicSidebar.checked = sequence.isPublic || false;
  if (sequenceDiscoverableSidebar) sequenceDiscoverableSidebar.checked = sequence.isDiscoverable || false;
  if (sequencePasswordSidebar) sequencePasswordSidebar.value = '';  // Don't populate hashed password
  if (sequenceAnalyticsSidebar) sequenceAnalyticsSidebar.checked = sequence.analyticsEnabled !== false;
  // Invert: "Enable" checkbox should be checked when disableTransitions is false (default ON)
  if (enableTransitionsSidebar) {
    enableTransitionsSidebar.checked = sequence.disableTransitions !== true;
    console.log('[Settings] Loading enableTransitions:', !sequence.disableTransitions, '-> checkbox:', enableTransitionsSidebar.checked);
  }
  if (enableGazeNavigationSidebar) {
    enableGazeNavigationSidebar.checked = sequence.disableGazeNavigation !== true;
    console.log('[Settings] Loading enableGazeNavigation:', !sequence.disableGazeNavigation, '-> checkbox:', enableGazeNavigationSidebar.checked);
  }

  // Load default FOV setting (stored in radians, displayed in degrees)
  const defaultFOVSidebar = document.getElementById('defaultFOVSidebar') as HTMLInputElement;
  const defaultFOVValueSidebar = document.getElementById('defaultFOVValueSidebar') as HTMLElement;
  if (defaultFOVSidebar) {
    // Convert radians to degrees, default to 90° if not set
    const fovRadians = config?.experience?.defaultFOV ?? (90 * Math.PI / 180);
    const fovDegrees = Math.round(fovRadians * 180 / Math.PI);
    defaultFOVSidebar.value = String(fovDegrees);
    if (defaultFOVValueSidebar) {
      defaultFOVValueSidebar.textContent = `${fovDegrees}°`;
    }
    // Sync preview FOV with loaded setting
    if (editorPreview) {
      editorPreview.setFOV(fovRadians);
    }
    console.log('[Settings] Loading defaultFOV:', fovRadians, 'radians ->', fovDegrees, 'degrees');
  }

  // Load portal transition settings (sidebar)
  const portalSettings = sequence.portalSettings || {};
  const portalGlowColorSidebar = document.getElementById('portalGlowColorSidebar') as HTMLInputElement;
  const portalGlowColorHexSidebar = document.getElementById('portalGlowColorHexSidebar') as HTMLInputElement;
  const portalEdgeOpacitySliderSidebar = document.getElementById('portalEdgeOpacitySliderSidebar') as HTMLInputElement;
  const portalEdgeOpacitySidebar = document.getElementById('portalEdgeOpacitySidebar') as HTMLInputElement;
  const portalEffectStyleSidebar = document.getElementById('portalEffectStyleSidebar') as HTMLSelectElement;

  if (portalGlowColorSidebar) portalGlowColorSidebar.value = portalSettings.glowColor || '#00bfff';
  if (portalGlowColorHexSidebar) portalGlowColorHexSidebar.value = portalSettings.glowColor || '#00bfff';
  if (portalEdgeOpacitySliderSidebar) portalEdgeOpacitySliderSidebar.value = String(portalSettings.edgeOpacity ?? 1);
  if (portalEdgeOpacitySidebar) portalEdgeOpacitySidebar.value = String(portalSettings.edgeOpacity ?? 1);
  if (portalEffectStyleSidebar) portalEffectStyleSidebar.value = portalSettings.effectStyle || 'clean';

  // Toggle discoverable visibility
  const discoverableGroup = document.getElementById('discoverableGroupSidebar');
  if (discoverableGroup) {
    discoverableGroup.style.display = sequencePublicSidebar?.checked ? 'flex' : 'none';
  }

  // Check if user has Pro access for branding
  if (currentUserProfile) {
    const hasWhiteLabel = hasFeatureAccess(currentUserProfile, 'white_label');
    const logoLockedState = document.getElementById('logoLockedStateSidebar');
    const logoProState = document.getElementById('logoProStateSidebar');

    if (logoLockedState && logoProState) {
      logoLockedState.style.display = hasWhiteLabel ? 'none' : 'block';
      logoProState.style.display = hasWhiteLabel ? 'block' : 'none';
    }

    // Load branding settings
    if (hasWhiteLabel) {
      const removeBrandingCheckbox = document.getElementById('removeBrandingCheckboxSidebar') as HTMLInputElement;
      const removeFooterBrandingCheckbox = document.getElementById('removeFooterBrandingCheckboxSidebar') as HTMLInputElement;

      if (removeBrandingCheckbox) removeBrandingCheckbox.checked = config?.disableLogo || false;
      if (removeFooterBrandingCheckbox) removeFooterBrandingCheckbox.checked = config?.disableFooterBranding || false;
    }
  }

  // Load floor arrow tooltip setting (available to all users)
  const showFloorArrowTooltipsCheckbox = document.getElementById('showFloorArrowTooltipsCheckbox') as HTMLInputElement;
  if (showFloorArrowTooltipsCheckbox) {
    showFloorArrowTooltipsCheckbox.checked = config?.showFloorArrowTooltips || false;
  }

  // Load custom URL slugs
  if (userSlugInputSidebar) {
    userSlugInputSidebar.value = sequence.userSlug || '';
  }

  // Initialize slug UI based on user tier and update preview
  initializeSlugUI();
  updateSlugPreview();

  // Load preview image
  updatePreviewThumbnailUI();
}

/**
 * Update the preview thumbnail UI based on sequence state
 * Updates both sidebar and panel versions
 */
function updatePreviewThumbnailUI() {
  // Update sidebar version
  updatePreviewThumbnailElement('previewThumbnailImg', 'previewThumbnailPlaceholder');
  // Update panel version
  updatePreviewThumbnailElement('previewThumbnailImgPanel', 'previewThumbnailPlaceholderPanel');
}

function updatePreviewThumbnailElement(imgId: string, placeholderId: string) {
  const img = document.getElementById(imgId) as HTMLImageElement;
  const placeholder = document.getElementById(placeholderId) as HTMLElement;

  if (!img || !placeholder) return;

  if (sequence?.thumbnailUrl) {
    img.src = sequence.thumbnailUrl;
    img.style.display = 'block';
    placeholder.style.display = 'none';
  } else {
    img.style.display = 'none';
    placeholder.style.display = 'flex';
  }
}

/**
 * Save sidebar sequence settings
 */
async function handleSaveSidebarSequenceSettings() {
  if (!sequence || !sequenceId) return;

  const sequenceNameSidebar = document.getElementById('sequenceNameSidebar') as HTMLInputElement;
  const sequenceDescriptionSidebar = document.getElementById('sequenceDescriptionSidebar') as HTMLTextAreaElement;
  const sequencePublicSidebar = document.getElementById('sequencePublicSidebar') as HTMLInputElement;
  const sequenceDiscoverableSidebar = document.getElementById('sequenceDiscoverableSidebar') as HTMLInputElement;
  const sequencePasswordSidebar = document.getElementById('sequencePasswordSidebar') as HTMLInputElement;
  const sequenceAnalyticsSidebar = document.getElementById('sequenceAnalyticsSidebar') as HTMLInputElement;
  const enableTransitionsSidebar = document.getElementById('enableTransitionsSidebar') as HTMLInputElement;
  const enableGazeNavigationSidebar = document.getElementById('enableGazeNavigationSidebar') as HTMLInputElement;

  const updates: any = {
    name: sequenceNameSidebar?.value || sequence.name,
    description: sequenceDescriptionSidebar?.value || '',
    // Only update visibility if checkbox elements exist, otherwise preserve existing values
    ...(sequencePublicSidebar && { isPublic: sequencePublicSidebar.checked }),
    ...(sequenceDiscoverableSidebar && { isDiscoverable: sequenceDiscoverableSidebar.checked }),
    analyticsEnabled: sequenceAnalyticsSidebar?.checked !== false,
    // Invert: Enable checkbox checked = disableTransitions false
    disableTransitions: enableTransitionsSidebar?.checked === false,
    disableGazeNavigation: enableGazeNavigationSidebar?.checked === false
  };

  // Only update password if a new one was entered
  const newPassword = sequencePasswordSidebar?.value?.trim();
  if (newPassword) {
    updates.password = newPassword; // Will be hashed by updateSequence or handled elsewhere
  }

  console.log('[Settings] Saving enableTransitions:', enableTransitionsSidebar?.checked, '-> disableTransitions:', updates.disableTransitions);

  // Update default FOV setting (convert degrees to radians)
  const defaultFOVSidebar = document.getElementById('defaultFOVSidebar') as HTMLInputElement;
  if (defaultFOVSidebar && config?.experience) {
    const fovDegrees = parseInt(defaultFOVSidebar.value) || 90;
    const fovRadians = fovDegrees * Math.PI / 180;
    config.experience.defaultFOV = fovRadians;
    console.log('[Settings] Saving defaultFOV:', fovDegrees, 'degrees ->', fovRadians, 'radians');
  }

  // Update branding if Pro
  if (currentUserProfile && hasFeatureAccess(currentUserProfile, 'white_label')) {
    const removeBrandingCheckbox = document.getElementById('removeBrandingCheckboxSidebar') as HTMLInputElement;
    const removeFooterBrandingCheckbox = document.getElementById('removeFooterBrandingCheckboxSidebar') as HTMLInputElement;

    if (config) {
      config.disableLogo = removeBrandingCheckbox?.checked || false;
      config.disableFooterBranding = removeFooterBrandingCheckbox?.checked || false;
    }
  }

  // Always include config in updates if it exists (for FOV and branding)
  if (config) {
    updates.config = config;
  }

  // Save custom URL slugs if changed (use tour owner's username, not admin's)
  const newUserSlug = userSlugInputSidebar?.value?.trim().toLowerCase() || null;
  const userSlugChanged = newUserSlug !== (sequence.userSlug || null);

  try {
    // Handle user slug change
    if (userSlugChanged && tourOwnerUsername) {
      if (newUserSlug) {
        try {
          const functions = getFunctions();
          const claimUserSlugFn = httpsCallable(functions, 'claimUserSlug');
          await claimUserSlugFn({ sequenceId, slug: newUserSlug });
          sequence.userSlug = newUserSlug;
          console.log('[Settings] User slug claimed:', newUserSlug);
        } catch (error: any) {
          console.error('Failed to claim user slug:', error);
          showToast(error.message || 'Failed to claim user slug', 'error');
        }
      } else {
        // Release user slug
        try {
          const functions = getFunctions();
          const releaseSlugFn = httpsCallable(functions, 'releaseSlug');
          await releaseSlugFn({ sequenceId, type: 'user' });
          sequence.userSlug = null;
          console.log('[Settings] User slug released');
        } catch (error: any) {
          console.error('Failed to release user slug:', error);
        }
      }
    }

    await updateSequence(sequenceId, updates);

    // Update local state (only update fields that were actually changed)
    sequence.name = updates.name;
    sequence.description = updates.description;
    if ('isPublic' in updates) sequence.isPublic = updates.isPublic;
    if ('isDiscoverable' in updates) sequence.isDiscoverable = updates.isDiscoverable;
    if (updates.password) sequence.password = updates.password;
    sequence.analyticsEnabled = updates.analyticsEnabled;
    sequence.disableTransitions = updates.disableTransitions;

    // Update header title
    headerTitle.textContent = `Edit: ${updates.name}`;

    await showAlert('Settings saved successfully!', 'Success', '✅');
  } catch (error) {
    console.error('Failed to save settings:', error);
    await showAlert('Failed to save settings. Please try again.', 'Error', '❌');
  }
}

/**
 * Set preview panel aspect ratio
 * Panel stays fixed, canvas container changes aspect ratio class
 */
function setPreviewAspectRatio(ratio: 'horizontal' | 'square' | 'vertical') {
  const canvasContainer = document.getElementById('previewCanvasContainer');
  if (!canvasContainer) return;

  // Update active button
  [aspectHorizontal, aspectSquare, aspectVertical].forEach(btn => {
    btn?.classList.remove('active');
  });

  // Remove all aspect classes
  canvasContainer.classList.remove('aspect-horizontal', 'aspect-square', 'aspect-vertical');

  // Add the selected aspect class
  if (ratio === 'horizontal') {
    canvasContainer.classList.add('aspect-horizontal');
    aspectHorizontal?.classList.add('active');
  } else if (ratio === 'square') {
    canvasContainer.classList.add('aspect-square');
    aspectSquare?.classList.add('active');
  } else if (ratio === 'vertical') {
    canvasContainer.classList.add('aspect-vertical');
    aspectVertical?.classList.add('active');
  }

  // Resize Babylon engine to match new canvas size
  requestAnimationFrame(() => {
    if (editorPreview) {
      editorPreview.resize();
    }
  });
}


/**
 * Calculate and update performance metrics for entire sequence
 * @param useFloatingPanel If true, updates the floating panel instead of tab
 * @param useSidebar If true, updates the sidebar panel
 */
function updatePerformanceMetrics(useFloatingPanel: boolean = false, useSidebar: boolean = false) {
  if (!config || !config.stages) return;

  console.log('[Performance] Calculating metrics...');

  let totalFileSize = 0;
  let totalMemory = 0;
  const formatCounts: Record<string, number> = {
    'ktx2-uastc': 0,
    'ktx2-etc1s': 0,
    'jpg': 0,
    'png': 0,
    'video-hls': 0,
    'video-mp4': 0
  };
  const stageMetrics: any[] = [];
  const recommendations: string[] = [];

  // Analyze each stage
  for (const stage of config.stages) {
    let stageSize = 0;
    let stageMemory = 0;
    let stageFormat = 'none';
    let stageResolution = 'none';
    let isVideo = false;

    // Analyze skybox
    if (stage.skybox) {
      // Check if it's a video skybox
      if (stage.skybox.type === 'video') {
        isVideo = true;

        // Check for HLS streaming
        if (stage.skybox.hlsUrl) {
          stageFormat = 'video-hls';
          formatCounts['video-hls']++;
          stageResolution = 'adaptive';
          // HLS streams, so no fixed file size - estimate based on typical segment
          stageSize = 0; // Streaming, not downloaded
          stageMemory = 3840 * 2160 * 4; // Assume 4K video frame buffer
        } else if (stage.skybox.videoUrl || stage.skybox.url) {
          stageFormat = 'video-mp4';
          formatCounts['video-mp4']++;
          stageResolution = 'varies';
          // MP4 files can be large - estimate ~50MB for a 4K 360 video
          stageSize = 50 * 1024 * 1024; // Rough estimate
          stageMemory = 3840 * 2160 * 4; // 4K frame buffer
        }
      } else if (stage.skybox.type !== 'color') {
        // Image skybox
        if (stage.skybox.variants) {
          // Multi-resolution config - use desktop variant for analysis
          const variant = stage.skybox.variants.desktop || stage.skybox.variants.fallback;
          if (variant) {
            stageSize = variant.fileSize || 0;
            stageFormat = variant.format;
            stageResolution = variant.resolution || 'unknown';

            // Count format
            if (variant.format.startsWith('ktx2-uastc')) {
              formatCounts['ktx2-uastc']++;
            } else if (variant.format.startsWith('ktx2-etc1s')) {
              formatCounts['ktx2-etc1s']++;
            } else if (variant.format === 'jpg') {
              formatCounts['jpg']++;
            } else if (variant.format === 'png') {
              formatCounts['png']++;
            }

            // Calculate memory
            const [width, height] = (variant.resolution || '2048x1024').split('x').map(Number);
            stageMemory = width * height * 4 * 1.33; // RGBA + mipmaps
          }
        } else if (stage.skybox.url) {
          // Legacy single URL
          stageFormat = 'legacy';
          recommendations.push(`"${stage.name || stage.id}" using legacy format - upload to auto-optimize`);
        }
      }
    }

    totalFileSize += stageSize;
    totalMemory += stageMemory;

    stageMetrics.push({
      name: stage.name || stage.id,
      size: stageSize,
      memory: stageMemory,
      format: stageFormat,
      resolution: stageResolution,
      isVideo
    });
  }

  // Count video vs image stages
  const videoStages = formatCounts['video-hls'] + formatCounts['video-mp4'];
  const imageStages = formatCounts['ktx2-uastc'] + formatCounts['ktx2-etc1s'] + formatCounts['jpg'] + formatCounts['png'];

  // Additional recommendations
  if (formatCounts.jpg + formatCounts.png > 0) {
    recommendations.push(`${formatCounts.jpg + formatCounts.png} image texture(s) not using KTX2 compression`);
  }

  if (formatCounts['video-mp4'] > 0 && formatCounts['video-hls'] === 0) {
    recommendations.push(`${formatCounts['video-mp4']} video(s) using MP4 - consider HLS for faster streaming`);
  }

  if (videoStages > 0) {
    recommendations.push(`💡 ${videoStages} video stage(s) - load times depend on network streaming`);
  }

  const avgLoadTime = imageStages > 0
    ? estimateLoadTime(totalFileSize / Math.max(imageStages, 1), 10)
    : 0;

  if (recommendations.length === 0) {
    recommendations.push('✅ All textures optimized with multi-resolution variants!');
  }

  // Render metrics
  renderPerformanceMetrics({
    totalFileSize,
    totalMemory,
    stageCount: config.stages.length,
    formatCounts,
    avgLoadTime,
    stageMetrics,
    recommendations,
    videoStages,
    imageStages
  }, useFloatingPanel, useSidebar);
}

/**
 * Render performance metrics to the UI
 * @param useFloat If true, renders to floating panel
 * @param useSidebar If true, renders to sidebar panel
 */
function renderPerformanceMetrics(metrics: any, useFloat: boolean = false, useSidebar: boolean = false) {
  // Determine which element IDs to use
  const suffix = useSidebar ? 'Sidebar' : (useFloat ? 'Float' : '');

  // Update summary cards
  const perfTotalSize = document.getElementById(`perfTotalSize${suffix}`);
  const perfMemory = document.getElementById(`perfMemory${suffix}`);
  const perfLoadTime = document.getElementById(`perfLoadTime${suffix}`);
  const perfStageCount = document.getElementById(`perfStageCount${suffix}`);

  // For video stages, show "Streaming" instead of file size
  if (metrics.videoStages > 0 && metrics.imageStages === 0) {
    if (perfTotalSize) perfTotalSize.textContent = 'Streaming';
  } else {
    if (perfTotalSize) perfTotalSize.textContent = formatBytes(metrics.totalFileSize);
  }

  if (perfMemory) perfMemory.textContent = formatBytes(metrics.totalMemory);

  // Show different load time label for video stages
  if (metrics.videoStages > 0 && metrics.imageStages === 0) {
    if (perfLoadTime) perfLoadTime.textContent = '~2s';
  } else {
    if (perfLoadTime) perfLoadTime.textContent = metrics.avgLoadTime.toFixed(1) + 's';
  }

  // Stage count with video indicator
  if (perfStageCount) {
    if (metrics.videoStages > 0) {
      perfStageCount.textContent = `${metrics.stageCount} stages (${metrics.videoStages} video)`;
    } else {
      perfStageCount.textContent = `${metrics.stageCount} stages`;
    }
  }

  // Format distribution
  const formatChart = document.getElementById(`perfFormatChart${suffix}`);
  if (formatChart) {
    formatChart.innerHTML = '';

    // Labels and CSS class mapping for all formats including video
    const labels: Record<string, string> = {
      'ktx2-uastc': 'KTX2 UASTC',
      'ktx2-etc1s': 'KTX2 ETC1S',
      'jpg': 'JPG',
      'png': 'PNG',
      'video-hls': '🎬 HLS Video',
      'video-mp4': '🎬 MP4 Video'
    };

    Object.entries(metrics.formatCounts).forEach(([format, count]: [string, any]) => {
      if (count > 0) {
        const percent = (count / metrics.stageCount * 100).toFixed(0);

        const bar = document.createElement('div');
        bar.className = 'perf-format-bar';
        bar.innerHTML = `
          <div class="perf-format-fill ${format}" style="width: ${percent}%"></div>
          <span class="perf-format-label">${labels[format] || format}</span>
          <span class="perf-format-count">${count}</span>
        `;
        formatChart.appendChild(bar);
      }
    });
  }

  // Stage breakdown
  const stageList = document.getElementById(`perfStageList${suffix}`);
  if (stageList) {
    stageList.innerHTML = '';

    metrics.stageMetrics.forEach((stage: any) => {
      const item = document.createElement('div');
      item.className = 'perf-stage-item';

      // Format display for video stages
      let formatDisplay = stage.format;
      let sizeDisplay = formatBytes(stage.size);

      if (stage.isVideo) {
        formatDisplay = stage.format === 'video-hls' ? '🎬 HLS' : '🎬 MP4';
        sizeDisplay = stage.format === 'video-hls' ? 'Streaming' : '~50 MB';
      }

      item.innerHTML = `
        <span class="perf-stage-name">${stage.name}</span>
        <div class="perf-stage-details">
          <span class="perf-stage-format">${formatDisplay}</span>
          <span class="perf-stage-size">${sizeDisplay}</span>
        </div>
      `;
      stageList.appendChild(item);
    });
  }

  // Recommendations
  const recommendationsSection = document.getElementById(`perfRecommendations${suffix}`);
  const recommendationsList = document.getElementById(`perfRecommendationsList${suffix}`);

  if (recommendationsSection && recommendationsList) {
    if (metrics.recommendations.length > 0) {
      recommendationsSection.style.display = 'block';
      recommendationsList.innerHTML = '';

      metrics.recommendations.forEach((rec: string) => {
        const item = document.createElement('div');
        item.className = 'perf-recommendation';
        item.textContent = rec;
        recommendationsList.appendChild(item);
      });
    } else {
      recommendationsSection.style.display = 'none';
    }
  }
}

/**
 * Format bytes helper
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (bytes / Math.pow(k, i)).toFixed(1) + ' ' + sizes[i];
}

/**
 * Estimate load time
 */
function estimateLoadTime(bytes: number, mbps: number): number {
  const bits = bytes * 8;
  const megabits = bits / (1024 * 1024);
  return megabits / mbps;
}

/**
 * Handle add audio hotspot manually
 */
function handleAddAudioHotspot() {
  if (!config || !config.stages) return;
  const stage = config.stages[currentStageIndex];

  if (!stage.hotspots) stage.hotspots = [];

  stage.hotspots.push({
    id: `audio-${Date.now()}`,
    type: 'audio',
    position: { x: 0, y: 1.5, z: -3 },
    audioUrl: '',
    audioLoop: true,
    audioVolume: 1.0,
    color: '#4A90E2',
    size: 24 // Default 3x size (8*3)
  });

  renderAudioHotspotsList(stage.hotspots);
  markUnsaved();
}

/**
 * Reset audio hotspot creation mode UI and state
 */
function resetAudioHotspotCreationMode() {
  isAudioHotspotCreationMode = false;
  editorPreview?.disableHotspotCreation();
  addAudioHotspotClickBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="vertical-align: middle; margin-right: 4px;"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>Place Audio Source by Clicking';
  addAudioHotspotClickBtn.classList.remove('btn-danger');
  hidePlacementMode();
}

/**
 * Handle add audio hotspot via click in preview
 * Uses state-based toggle to fix second creation bug
 */
function handleAddAudioHotspotClick() {
  if (!editorPreview) {
    showAlert('Preview is not available for this content.', 'Preview Unavailable', '👁️');
    return;
  }

  // If already in creation mode, cancel it
  if (isAudioHotspotCreationMode) {
    resetAudioHotspotCreationMode();
    return;
  }

  // Enter creation mode
  isAudioHotspotCreationMode = true;

  // Show preview and overlay
  showPlacementMode('audio');

  // Enable hotspot creation mode with type 'audio'
  editorPreview.enableHotspotCreation();

  // Update button state to show cancel option
  addAudioHotspotClickBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="vertical-align: middle; margin-right: 4px;"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>Cancel Audio Placement';
  addAudioHotspotClickBtn.classList.add('btn-danger');

  // Handle creation event
  editorPreview.onHotspotCreated = (position: { x: number, y: number, z: number }) => {
    if (!config || !config.stages) return;
    const stage = config.stages[currentStageIndex];

    if (!stage.hotspots) stage.hotspots = [];

    stage.hotspots.push({
      id: `audio-${Date.now()}`,
      type: 'audio',
      position: position,
      audioUrl: '',
      audioLoop: true,
      audioVolume: 1.0,
      color: '#4A90E2',
      size: 24
    });

    renderAudioHotspotsList(stage.hotspots);
    markUnsaved();
    updatePreview();

    // Show success
    const success = document.getElementById('successMessage');
    if (success) {
      success.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style="vertical-align: middle; margin-right: 6px;"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg> Audio source added!';
      success.classList.add('show');
      setTimeout(() => success.classList.remove('show'), 2000);
    }

    // Reset mode after successful creation
    resetAudioHotspotCreationMode();
  };
}

/**
 * Show admin editing banner when admin is editing another user's tour
 */
function showAdminEditingBanner(ownerId: string) {
  // Create banner element
  const banner = document.createElement('div');
  banner.id = 'admin-editing-banner';
  banner.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: linear-gradient(135deg, #FF9500 0%, #FF3B30 100%);
    color: white;
    padding: 10px 20px;
    text-align: center;
    font-size: 13px;
    font-weight: 600;
    z-index: 10001;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.3);
  `;

  banner.innerHTML = `
    <span style="font-size: 16px;">🔧</span>
    <span>ADMIN MODE: You are editing another user's tour (Owner ID: ${ownerId.substring(0, 8)}...)</span>
  `;

  document.body.prepend(banner);

  // Adjust editor layout to account for banner
  const editorWrapper = document.querySelector('.editor-wrapper') as HTMLElement;
  if (editorWrapper) {
    editorWrapper.style.marginTop = '40px';
  }
}

/**
 * Show toast message
 * SECURITY: Uses textContent instead of innerHTML to prevent XSS
 */
function showToast(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') {
  const toast = document.createElement('div');
  toast.className = `success-message show`;
  if (type === 'error') {
    toast.style.background = 'rgba(255, 59, 48, 0.9)'; // Red for error
  } else if (type === 'warning') {
    toast.style.background = 'rgba(255, 149, 0, 0.9)'; // Orange for warning
  } else if (type === 'info') {
    toast.style.background = 'rgba(0, 122, 255, 0.9)'; // Blue for info
  } else {
    toast.style.background = 'rgba(0, 0, 0, 0.8)';
  }
  toast.style.color = 'white';
  toast.style.border = 'none';

  // Create icon span
  const icon = document.createElement('span');
  const icons: Record<string, string> = { success: '✅', error: '❌', warning: '⚠️', info: 'ℹ️' };
  icon.textContent = icons[type] || '✅';
  toast.appendChild(icon);

  // Add space
  toast.appendChild(document.createTextNode(' '));

  // Add message text (safely escaped)
  const messageText = document.createTextNode(message);
  toast.appendChild(messageText);

  document.body.appendChild(toast);

  setTimeout(() => {
    toast.classList.remove('show');
    setTimeout(() => toast.remove(), 300);
  }, 3000);
}

// ============================================
// NAVIGATION SETTINGS
// ============================================

/**
 * Setup navigation input handlers
 */
function setupNavigationInputs() {
  const navType = document.getElementById('navType') as HTMLSelectElement;
  const mapZoom = document.getElementById('mapZoom') as HTMLInputElement;
  const mapZoomValue = document.getElementById('mapZoomValue');
  // Navigation type change handler
  navType?.addEventListener('change', () => {
    const type = navType.value as 'none' | 'floorplan' | 'map';
    updateNavigationTypeVisibility(type);
  });

  // Map zoom slider update
  mapZoom?.addEventListener('input', () => {
    if (mapZoomValue) {
      mapZoomValue.textContent = mapZoom.value;
    }
  });

  // Floor plan upload handlers
  const uploadFloorPlanBtn = document.getElementById('uploadFloorPlanBtn');
  const floorPlanFileInput = document.getElementById('floorPlanFileInput') as HTMLInputElement;
  const clearFloorPlanBtn = document.getElementById('clearFloorPlanBtn');

  uploadFloorPlanBtn?.addEventListener('click', () => {
    floorPlanFileInput?.click();
  });

  floorPlanFileInput?.addEventListener('change', async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file && sequenceId && currentUser) {
      await handleFloorPlanUpload(file);
    }
    floorPlanFileInput.value = '';
  });

  clearFloorPlanBtn?.addEventListener('click', () => {
    const previewContainer = document.getElementById('floorPlanPreviewContainer');
    const floorPlanUrl = document.getElementById('floorPlanUrl') as HTMLInputElement;
    if (previewContainer) previewContainer.style.display = 'none';
    if (floorPlanUrl) floorPlanUrl.value = '';
    markUnsaved();
  });

  // Auto-set navigation type to 'floorplan' when URL is manually entered
  const floorPlanUrlInput = document.getElementById('floorPlanUrl') as HTMLInputElement;
  floorPlanUrlInput?.addEventListener('input', () => {
    const navType = document.getElementById('navType') as HTMLSelectElement;
    if (floorPlanUrlInput.value && navType && navType.value === 'none') {
      navType.value = 'floorplan';
      updateNavigationTypeVisibility('floorplan');
      markUnsaved();
    }

    // Show preview when URL is manually entered
    const previewContainer = document.getElementById('floorPlanPreviewContainer');
    const previewImg = document.getElementById('floorPlanPreviewImg') as HTMLImageElement;
    if (floorPlanUrlInput.value) {
      if (previewImg) previewImg.src = floorPlanUrlInput.value;
      if (previewContainer) previewContainer.style.display = 'block';
    } else {
      if (previewContainer) previewContainer.style.display = 'none';
    }
  });
}

/**
 * Handle floor plan image upload with validation and progress tracking
 */
async function handleFloorPlanUpload(file: File) {
  if (!sequenceId || !currentUser) return;

  // Allowed file types for floor plans
  const allowedTypes = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
    'image/svg+xml'
  ];

  // File type validation
  if (!allowedTypes.includes(file.type)) {
    showToast(`Invalid file type: ${file.type || 'unknown'}. Please upload a JPG, PNG, WebP, GIF, or SVG image.`, 'error');
    return;
  }

  // File size validation (10MB max)
  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) {
    showToast('File too large. Maximum size is 10MB.', 'error');
    return;
  }

  // Get progress UI elements
  const progressContainer = document.getElementById('floorPlanUploadProgress');
  const progressFill = document.getElementById('floorPlanProgressFill');
  const progressText = document.getElementById('floorPlanProgressText');
  const uploadBtn = document.getElementById('uploadFloorPlanBtn') as HTMLButtonElement;

  // Show progress and disable upload button
  if (progressContainer) progressContainer.style.display = 'block';
  if (uploadBtn) uploadBtn.disabled = true;

  try {
    const { ref, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
    const { storage } = await import('../src/firebase/config');

    // Generate unique filename - use uploads/ path to match storage.rules
    const timestamp = Date.now();
    const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
    const path = `uploads/${currentUser.uid}/${sequenceId}/images/floorplan_${timestamp}.${ext}`;

    const storageRef = ref(storage, path);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on('state_changed',
      (snapshot) => {
        // Progress tracking
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        if (progressFill) progressFill.style.width = `${progress}%`;
        if (progressText) progressText.textContent = `Uploading... ${Math.round(progress)}%`;
      },
      (error: any) => {
        console.error('Floor plan upload error:', error);

        // Provide meaningful error messages based on Firebase Storage error codes
        let message = 'Failed to upload floor plan';
        if (error.code) {
          switch (error.code) {
            case 'storage/unauthorized':
              message = 'Upload failed: You do not have permission to upload files. Please check your subscription.';
              break;
            case 'storage/canceled':
              message = 'Upload was canceled.';
              break;
            case 'storage/quota-exceeded':
              message = 'Storage quota exceeded. Please upgrade your plan or free up space.';
              break;
            case 'storage/retry-limit-exceeded':
              message = 'Upload failed due to network issues. Please try again.';
              break;
            case 'storage/invalid-checksum':
              message = 'File was corrupted during upload. Please try again.';
              break;
            default:
              message = `Upload failed: ${error.message || error.code}`;
          }
        }
        showToast(message, 'error');

        // Hide progress UI
        if (progressContainer) progressContainer.style.display = 'none';
        if (uploadBtn) uploadBtn.disabled = false;
      },
      async () => {
        try {
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);

          // Update URL input and preview
          const floorPlanUrl = document.getElementById('floorPlanUrl') as HTMLInputElement;
          const previewContainer = document.getElementById('floorPlanPreviewContainer');
          const previewImg = document.getElementById('floorPlanPreviewImg') as HTMLImageElement;

          if (floorPlanUrl) floorPlanUrl.value = downloadUrl;
          if (previewImg) previewImg.src = downloadUrl;
          if (previewContainer) previewContainer.style.display = 'block';

          // Auto-set navigation type to 'floorplan' when floor plan is uploaded
          const navType = document.getElementById('navType') as HTMLSelectElement;
          if (navType && navType.value === 'none') {
            navType.value = 'floorplan';
            updateNavigationTypeVisibility('floorplan');
          }

          markUnsaved();
          showToast('Floor plan uploaded successfully');
        } catch (urlError) {
          console.error('Error getting download URL:', urlError);
          showToast('Upload completed but failed to get URL. Please try again.', 'error');
        } finally {
          // Hide progress UI and re-enable button
          if (progressContainer) progressContainer.style.display = 'none';
          if (uploadBtn) uploadBtn.disabled = false;
        }
      }
    );
  } catch (error) {
    console.error('Floor plan upload error:', error);
    const errorMsg = error instanceof Error ? error.message : 'Unknown error';
    showToast(`Failed to upload floor plan: ${errorMsg}`, 'error');

    // Hide progress UI and re-enable button
    if (progressContainer) progressContainer.style.display = 'none';
    if (uploadBtn) uploadBtn.disabled = false;
  }
}

/**
 * Update navigation type visibility based on selected type
 */
function updateNavigationTypeVisibility(type: 'none' | 'floorplan' | 'map') {
  const floorPlanSettings = document.getElementById('floorPlanSettings');
  const mapSettings = document.getElementById('mapSettings');
  const displaySettings = document.getElementById('displaySettings');
  const markerSettings = document.getElementById('markerSettings');

  // Hide all first
  if (floorPlanSettings) floorPlanSettings.style.display = 'none';
  if (mapSettings) mapSettings.style.display = 'none';
  if (displaySettings) displaySettings.style.display = 'none';
  if (markerSettings) markerSettings.style.display = 'none';

  if (type === 'none') {
    return;
  }

  // Show relevant settings
  if (type === 'floorplan') {
    if (floorPlanSettings) floorPlanSettings.style.display = 'block';
  } else if (type === 'map') {
    if (mapSettings) mapSettings.style.display = 'block';
  }

  // Show common settings for both types
  if (displaySettings) displaySettings.style.display = 'block';
  if (markerSettings) markerSettings.style.display = 'block';

  // Render markers for stages
  renderNavigationMarkers();
}

/**
 * Load navigation settings into the form
 */
function loadNavigationSettings() {
  if (!config) return;

  const navType = document.getElementById('navType') as HTMLSelectElement;
  const floorPlanUrl = document.getElementById('floorPlanUrl') as HTMLInputElement;
  const floorLabel = document.getElementById('floorLabel') as HTMLInputElement;
  const mapStyle = document.getElementById('mapStyle') as HTMLSelectElement;
  const mapZoom = document.getElementById('mapZoom') as HTMLInputElement;
  const mapZoomValue = document.getElementById('mapZoomValue');
  const showMinimap = document.getElementById('showMinimap') as HTMLInputElement;

  const nav = config.navigation;

  if (nav) {
    // Navigation type
    if (navType) navType.value = nav.type || 'none';

    // Floor plan settings
    if (nav.floorPlan) {
      if (floorPlanUrl) floorPlanUrl.value = nav.floorPlan.imageUrl || '';
      if (floorLabel) floorLabel.value = nav.floorPlan.floorLabel || '';

      // Show preview if URL exists
      const previewContainer = document.getElementById('floorPlanPreviewContainer');
      const previewImg = document.getElementById('floorPlanPreviewImg') as HTMLImageElement;
      if (nav.floorPlan.imageUrl) {
        if (previewImg) previewImg.src = nav.floorPlan.imageUrl;
        if (previewContainer) previewContainer.style.display = 'block';
      }
    }

    // Map settings
    if (nav.map) {
      if (mapStyle) mapStyle.value = nav.map.style || 'streets';
      if (mapZoom) mapZoom.value = String(nav.map.defaultZoom || 12);
      if (mapZoomValue) mapZoomValue.textContent = String(nav.map.defaultZoom || 12);
    }

    // Display settings - use showMinimap, fall back to legacy displayMode
    if (showMinimap) {
      // If showMinimap is explicitly set, use it; otherwise derive from legacy displayMode
      if (nav.showMinimap !== undefined) {
        showMinimap.checked = nav.showMinimap;
      } else {
        // Legacy: 'both' and 'minimap' show minimap, 'fullscreen' and 'none' hide it
        showMinimap.checked = nav.displayMode !== 'fullscreen' && nav.displayMode !== 'none';
      }
    }

    // Update visibility based on type
    updateNavigationTypeVisibility(nav.type || 'none');
  } else {
    // No navigation config, reset to defaults
    if (navType) navType.value = 'none';
    updateNavigationTypeVisibility('none');
  }

  // Render stage markers
  renderNavigationMarkers();
}

/**
 * Render navigation markers for each stage
 */
function renderNavigationMarkers() {
  const markerList = document.getElementById('markerList');
  const noMarkersMessage = document.getElementById('noMarkersMessage');
  const navType = document.getElementById('navType') as HTMLSelectElement;

  if (!markerList || !config?.stages || config.stages.length === 0) {
    if (noMarkersMessage) noMarkersMessage.style.display = 'block';
    if (markerList) markerList.innerHTML = '';
    return;
  }

  if (noMarkersMessage) noMarkersMessage.style.display = 'none';

  const isFloorPlan = navType?.value === 'floorplan';
  const isMap = navType?.value === 'map';

  markerList.innerHTML = config.stages.map((stage, index) => {
    const marker = config?.navigation?.markers?.find(m => m.stageId === stage.id);

    if (isFloorPlan) {
      const xPos = marker?.floorPlanPosition?.x ?? 0.5;
      const yPos = marker?.floorPlanPosition?.y ?? 0.5;
      const hasPosition = marker?.floorPlanPosition !== undefined;
      return `
        <div class="marker-item" data-stage-id="${stage.id}" style="background: rgba(0,0,0,0.03); padding: 12px; border-radius: 8px; margin-bottom: 8px;">
          <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;">
            <span style="font-weight: 500; font-size: 13px;">${stage.name || `Scene ${index + 1}`}</span>
            <button class="ios-button ios-button-ghost floorplan-edit-btn" data-stage-id="${stage.id}" style="padding: 6px 10px; min-width: auto; font-size: 12px;" title="Click on floor plan to position">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="margin-right: 4px;">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
              Place
            </button>
          </div>
          <div class="marker-position-info" style="font-size: 12px; color: var(--ios-gray);">
            ${hasPosition
              ? `<span style="font-family: 'SF Mono', Monaco, monospace; font-size: 11px;">Position: ${Math.round(xPos * 100)}%, ${Math.round(yPos * 100)}%</span>`
              : '<span style="font-style: italic; color: var(--ios-orange);">Not positioned - click Place to set</span>'
            }
          </div>
          <input type="hidden" class="marker-x" data-stage-id="${stage.id}" value="${xPos}">
          <input type="hidden" class="marker-y" data-stage-id="${stage.id}" value="${yPos}">
        </div>
      `;
    } else if (isMap) {
      const lat = stage.location?.lat ?? marker?.geoPosition?.lat ?? 0;
      const lng = stage.location?.lng ?? marker?.geoPosition?.lng ?? 0;
      const placeName = marker?.placeName || '';
      const hasLocation = lat !== 0 || lng !== 0;

      return `
        <div class="marker-item" data-stage-id="${stage.id}" style="background: rgba(0,0,0,0.03); padding: 12px; border-radius: 8px; margin-bottom: 8px;">
          <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;">
            <span style="font-weight: 500; font-size: 13px;">${stage.name || `Scene ${index + 1}`}</span>
            <button class="ios-button ios-button-ghost marker-edit-btn" data-stage-id="${stage.id}" style="padding: 6px 10px; min-width: auto; font-size: 12px;" title="Edit location on map">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="margin-right: 4px;">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
              Edit
            </button>
          </div>
          <div class="marker-location-info" style="font-size: 12px;">
            ${placeName ? `<div class="marker-place-name" style="color: #333; margin-bottom: 4px; font-weight: 500;">${placeName}</div>` : ''}
            <div class="marker-coords" style="color: var(--ios-gray); font-family: 'SF Mono', Monaco, monospace; font-size: 11px;">
              ${hasLocation
                ? `<span class="coord-display">${lat.toFixed(6)}, ${lng.toFixed(6)}</span>`
                : '<span style="font-style: italic; font-family: inherit; color: var(--ios-orange);">No location set - click Edit to add</span>'
              }
            </div>
          </div>
          <input type="hidden" class="marker-lat" data-stage-id="${stage.id}" value="${lat}">
          <input type="hidden" class="marker-lng" data-stage-id="${stage.id}" value="${lng}">
          <input type="hidden" class="marker-place-name" data-stage-id="${stage.id}" value="${placeName}">
        </div>
      `;
    }
    return '';
  }).join('');

  // Add change listeners for marker inputs (floor plan x/y)
  markerList.querySelectorAll('input.marker-x, input.marker-y').forEach(input => {
    input.addEventListener('change', () => markUnsaved());
  });

  // Add click handlers for map marker edit buttons
  markerList.querySelectorAll('.marker-edit-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const stageId = btn.getAttribute('data-stage-id');
      if (stageId) {
        openMapLocationPicker(stageId);
      }
    });
  });

  // Add click handlers for floor plan marker edit buttons
  markerList.querySelectorAll('.floorplan-edit-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const stageId = btn.getAttribute('data-stage-id');
      if (stageId) {
        openFloorPlanPicker(stageId);
      }
    });
  });
}

/**
 * Open the map location picker modal for a stage
 */
function openMapLocationPicker(stageId: string) {
  if (!config?.stages) return;

  const stage = config.stages.find(s => s.id === stageId);
  if (!stage) return;

  const marker = config.navigation?.markers?.find(m => m.stageId === stageId);
  const currentLat = stage.location?.lat ?? marker?.geoPosition?.lat ?? 0;
  const currentLng = stage.location?.lng ?? marker?.geoPosition?.lng ?? 0;
  const hasPosition = currentLat !== 0 || currentLng !== 0;

  // Get map style from config
  const mapStyle = config.navigation?.map?.style || 'streets';

  openLocationPicker({
    initialPosition: hasPosition ? { lat: currentLat, lng: currentLng } : undefined,
    initialZoom: hasPosition ? 15 : 4,
    mapStyle: mapStyle as 'streets' | 'satellite' | 'terrain' | 'dark' | 'light',
    stageName: stage.name || `Stage`,
    onConfirm: (position, placeName) => {
      updateStageMarkerLocation(stageId, position.lat, position.lng, placeName);
      markUnsaved();
    },
    onCancel: () => {
      // Nothing to do on cancel
    }
  });
}

/**
 * Update a stage's marker location in the UI (without saving)
 */
function updateStageMarkerLocation(stageId: string, lat: number, lng: number, placeName?: string) {
  const markerList = document.getElementById('markerList');
  if (!markerList) return;

  // Update the in-memory config object to keep it in sync
  // This ensures re-renders don't lose the data
  if (config) {
    // Update stage location
    const stage = config.stages?.find(s => s.id === stageId);
    if (stage) {
      stage.location = { lat, lng };
    }

    // Update or create navigation marker
    if (!config.navigation) {
      config.navigation = {
        type: 'map',
        displayMode: 'both',
        markers: []
      };
    }
    if (!config.navigation.markers) {
      config.navigation.markers = [];
    }

    const existingMarker = config.navigation.markers.find(m => m.stageId === stageId);
    if (existingMarker) {
      existingMarker.geoPosition = { lat, lng };
      existingMarker.placeName = placeName;
    } else {
      config.navigation.markers.push({
        stageId,
        geoPosition: { lat, lng },
        placeName
      });
    }
  }

  // Update hidden inputs
  const latInput = markerList.querySelector(`.marker-lat[data-stage-id="${stageId}"]`) as HTMLInputElement;
  const lngInput = markerList.querySelector(`.marker-lng[data-stage-id="${stageId}"]`) as HTMLInputElement;
  const placeInput = markerList.querySelector(`.marker-place-name[data-stage-id="${stageId}"]`) as HTMLInputElement;

  if (latInput) latInput.value = String(lat);
  if (lngInput) lngInput.value = String(lng);
  if (placeInput) placeInput.value = placeName || '';

  // Update visible display
  const markerItem = markerList.querySelector(`.marker-item[data-stage-id="${stageId}"]`);
  if (markerItem) {
    const locationInfo = markerItem.querySelector('.marker-location-info');
    if (locationInfo) {
      locationInfo.innerHTML = `
        ${placeName ? `<div class="marker-place-name" style="color: #333; margin-bottom: 4px; font-weight: 500;">${placeName}</div>` : ''}
        <div class="marker-coords" style="color: var(--ios-gray); font-family: 'SF Mono', Monaco, monospace; font-size: 11px;">
          <span class="coord-display">${lat.toFixed(6)}, ${lng.toFixed(6)}</span>
        </div>
      `;
    }
  }
}

/**
 * Open the floor plan picker modal for a stage
 */
function openFloorPlanPicker(stageId: string) {
  if (!config?.stages) return;

  const stage = config.stages.find(s => s.id === stageId);
  if (!stage) return;

  // Get floor plan URL
  const floorPlanUrl = config.navigation?.floorPlan?.imageUrl ||
    (document.getElementById('floorPlanUrl') as HTMLInputElement)?.value;

  if (!floorPlanUrl) {
    showAlert('Please upload or enter a floor plan image URL first.', 'No Floor Plan', '🖼️');
    return;
  }

  const marker = config.navigation?.markers?.find(m => m.stageId === stageId);
  const currentX = marker?.floorPlanPosition?.x ?? 0.5;
  const currentY = marker?.floorPlanPosition?.y ?? 0.5;

  // Collect all other markers for display
  const otherMarkers = config.stages
    .filter(s => s.id !== stageId)
    .map(s => {
      const m = config?.navigation?.markers?.find(mk => mk.stageId === s.id);
      return {
        id: s.id,
        name: s.name || 'Scene',
        x: m?.floorPlanPosition?.x ?? 0.5,
        y: m?.floorPlanPosition?.y ?? 0.5,
        hasPosition: m?.floorPlanPosition !== undefined
      };
    });

  // Create modal
  const modal = document.createElement('div');
  modal.id = 'floorPlanPickerModal';
  modal.innerHTML = `
    <style>
      #floorPlanPickerModal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 100001;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.3s ease;
      }
      #floorPlanPickerModal.visible {
        opacity: 1;
      }
      .fpp-backdrop {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.7);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
      }
      .fpp-content {
        position: relative;
        width: 90vw;
        max-width: 800px;
        max-height: 85vh;
        background: rgba(30, 30, 40, 0.98);
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 20px;
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }
      .fpp-header {
        padding: 16px 20px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
      .fpp-header h3 {
        margin: 0;
        font-size: 17px;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.95);
      }
      .fpp-close-btn {
        background: none;
        border: none;
        font-size: 24px;
        line-height: 1;
        cursor: pointer;
        padding: 4px 8px;
        color: rgba(255, 255, 255, 0.7);
        border-radius: 6px;
      }
      .fpp-close-btn:hover {
        background: rgba(255, 255, 255, 0.1);
      }
      .fpp-body {
        flex: 1;
        padding: 20px;
        overflow: auto;
        display: flex;
        flex-direction: column;
        align-items: center;
      }
      .fpp-instructions {
        font-size: 14px;
        color: rgba(255, 255, 255, 0.7);
        margin-bottom: 16px;
        text-align: center;
      }
      .fpp-zoom-controls {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;
        background: rgba(255, 255, 255, 0.1);
        padding: 8px 12px;
        border-radius: 8px;
      }
      .fpp-zoom-btn {
        width: 32px;
        height: 32px;
        border-radius: 6px;
        border: none;
        background: rgba(255, 255, 255, 0.15);
        color: white;
        font-size: 18px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s;
      }
      .fpp-zoom-btn:hover {
        background: rgba(255, 255, 255, 0.25);
      }
      .fpp-zoom-btn:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
      .fpp-zoom-level {
        font-size: 13px;
        color: rgba(255, 255, 255, 0.7);
        min-width: 50px;
        text-align: center;
      }
      .fpp-zoom-reset {
        font-size: 12px;
        padding: 4px 10px;
        border-radius: 4px;
        border: none;
        background: rgba(255, 255, 255, 0.1);
        color: rgba(255, 255, 255, 0.7);
        cursor: pointer;
        margin-left: 8px;
      }
      .fpp-zoom-reset:hover {
        background: rgba(255, 255, 255, 0.2);
      }
      .fpp-image-wrapper {
        overflow: auto;
        max-height: 55vh;
        border-radius: 8px;
        border: 2px solid rgba(255, 255, 255, 0.2);
      }
      .fpp-image-container {
        position: relative;
        display: inline-block;
        cursor: crosshair;
        user-select: none;
        transform-origin: center center;
        transition: transform 0.2s ease;
      }
      .fpp-image {
        display: block;
        pointer-events: none;
      }
      .fpp-pin {
        position: absolute;
        width: 24px;
        height: 32px;
        transform: translate(-50%, -100%);
        filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));
        cursor: grab;
        transition: transform 0.1s ease;
      }
      .fpp-pin.active {
        cursor: grabbing;
        transform: translate(-50%, -100%) scale(1.1);
      }
      .fpp-pin.dragging {
        cursor: grabbing;
      }
      .fpp-pin-other {
        position: absolute;
        width: 16px;
        height: 22px;
        transform: translate(-50%, -100%);
        opacity: 0.5;
        pointer-events: none;
      }
      .fpp-pin-label {
        position: absolute;
        transform: translate(-50%, 4px);
        font-size: 10px;
        color: rgba(255, 255, 255, 0.6);
        white-space: nowrap;
        text-shadow: 0 1px 2px rgba(0,0,0,0.8);
        pointer-events: none;
      }
      .fpp-pin-label.active {
        color: #FF3B30;
        font-weight: 600;
      }
      .fpp-footer {
        padding: 16px 20px;
        border-top: 1px solid rgba(255, 255, 255, 0.1);
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
      }
      .fpp-position-display {
        font-size: 13px;
        color: rgba(255, 255, 255, 0.6);
        font-family: 'SF Mono', Monaco, monospace;
      }
      .fpp-actions {
        display: flex;
        gap: 12px;
      }
      .fpp-btn {
        padding: 10px 20px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        border: none;
        transition: all 0.2s ease;
      }
      .fpp-btn-secondary {
        background: rgba(255, 255, 255, 0.1);
        color: rgba(255, 255, 255, 0.9);
        border: 1px solid rgba(255, 255, 255, 0.15);
      }
      .fpp-btn-secondary:hover {
        background: rgba(255, 255, 255, 0.15);
      }
      .fpp-btn-primary {
        background: #007AFF;
        color: white;
      }
      .fpp-btn-primary:hover {
        background: #0066DD;
      }
    </style>
    <div class="fpp-backdrop"></div>
    <div class="fpp-content">
      <div class="fpp-header">
        <h3>Position "${stage.name || 'Scene'}" on Floor Plan</h3>
        <button class="fpp-close-btn">&times;</button>
      </div>
      <div class="fpp-body">
        <div class="fpp-instructions">Click to place marker, drag red pin to reposition. When zoomed, drag background to pan.</div>
        <div class="fpp-zoom-controls">
          <button class="fpp-zoom-btn fpp-zoom-out" title="Zoom Out">−</button>
          <span class="fpp-zoom-level">100%</span>
          <button class="fpp-zoom-btn fpp-zoom-in" title="Zoom In">+</button>
          <button class="fpp-zoom-reset" title="Reset Zoom">Reset</button>
        </div>
        <div class="fpp-image-wrapper">
          <div class="fpp-image-container">
            <img class="fpp-image" src="${floorPlanUrl}" alt="Floor Plan">
            ${otherMarkers.filter(m => m.hasPosition).map(m => `
              <svg class="fpp-pin-other" viewBox="0 0 24 32" style="left: ${m.x * 100}%; top: ${m.y * 100}%;">
                <path fill="#34C759" d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20c0-6.6-5.4-12-12-12zm0 16c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4z"/>
                <circle fill="white" cx="12" cy="12" r="2"/>
              </svg>
              <span class="fpp-pin-label" style="left: ${m.x * 100}%; top: ${m.y * 100}%;">${m.name}</span>
            `).join('')}
            <svg class="fpp-pin active" viewBox="0 0 24 32" style="left: ${currentX * 100}%; top: ${currentY * 100}%;">
              <path fill="#FF3B30" d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20c0-6.6-5.4-12-12-12zm0 16c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4z"/>
              <circle fill="white" cx="12" cy="12" r="3"/>
            </svg>
            <span class="fpp-pin-label active" style="left: ${currentX * 100}%; top: ${currentY * 100}%;">${stage.name || 'Scene'}</span>
          </div>
        </div>
      </div>
      <div class="fpp-footer">
        <div class="fpp-position-display">Position: ${Math.round(currentX * 100)}%, ${Math.round(currentY * 100)}%</div>
        <div class="fpp-actions">
          <button class="fpp-btn fpp-btn-secondary fpp-cancel-btn">Cancel</button>
          <button class="fpp-btn fpp-btn-primary fpp-confirm-btn">Confirm</button>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  // State
  let selectedX = currentX;
  let selectedY = currentY;
  let isDragging = false;
  let zoomLevel = 1;
  const MIN_ZOOM = 0.5;
  const MAX_ZOOM = 4;
  const ZOOM_STEP = 0.25;

  // Get elements
  const imageContainer = modal.querySelector('.fpp-image-container') as HTMLElement;
  const imageWrapper = modal.querySelector('.fpp-image-wrapper') as HTMLElement;
  const pin = modal.querySelector('.fpp-pin.active') as SVGElement;
  const pinLabel = modal.querySelector('.fpp-pin-label.active') as HTMLElement;
  const positionDisplay = modal.querySelector('.fpp-position-display') as HTMLElement;
  const zoomInBtn = modal.querySelector('.fpp-zoom-in') as HTMLButtonElement;
  const zoomOutBtn = modal.querySelector('.fpp-zoom-out') as HTMLButtonElement;
  const zoomResetBtn = modal.querySelector('.fpp-zoom-reset') as HTMLButtonElement;
  const zoomLevelDisplay = modal.querySelector('.fpp-zoom-level') as HTMLElement;

  // Update zoom level
  const updateZoom = (newZoom: number) => {
    zoomLevel = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));
    imageContainer.style.transform = `scale(${zoomLevel})`;
    zoomLevelDisplay.textContent = `${Math.round(zoomLevel * 100)}%`;
    zoomInBtn.disabled = zoomLevel >= MAX_ZOOM;
    zoomOutBtn.disabled = zoomLevel <= MIN_ZOOM;
  };

  // Zoom controls
  zoomInBtn?.addEventListener('click', () => updateZoom(zoomLevel + ZOOM_STEP));
  zoomOutBtn?.addEventListener('click', () => updateZoom(zoomLevel - ZOOM_STEP));
  zoomResetBtn?.addEventListener('click', () => {
    updateZoom(1);
    imageWrapper.scrollTop = 0;
    imageWrapper.scrollLeft = 0;
  });

  // Mouse wheel zoom
  imageWrapper?.addEventListener('wheel', (e: WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    updateZoom(zoomLevel + delta);
  }, { passive: false });

  // Update pin position helper
  const updatePinPosition = (x: number, y: number) => {
    selectedX = Math.max(0, Math.min(1, x));
    selectedY = Math.max(0, Math.min(1, y));
    pin.style.left = `${selectedX * 100}%`;
    pin.style.top = `${selectedY * 100}%`;
    pinLabel.style.left = `${selectedX * 100}%`;
    pinLabel.style.top = `${selectedY * 100}%`;
    positionDisplay.textContent = `Position: ${Math.round(selectedX * 100)}%, ${Math.round(selectedY * 100)}%`;
  };

  // Get position from mouse event (accounts for zoom)
  const getPositionFromEvent = (e: MouseEvent) => {
    const image = imageContainer.querySelector('.fpp-image') as HTMLImageElement;
    const rect = image.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height
    };
  };

  // Pan state for zoomed view
  let isPanning = false;
  let panStartX = 0;
  let panStartY = 0;
  let scrollStartX = 0;
  let scrollStartY = 0;
  let hasPanned = false;

  // Handle mousedown on image container for panning (when zoomed) or click placement
  imageContainer?.addEventListener('mousedown', (e: MouseEvent) => {
    if (isDragging) return;
    // Only enable panning when zoomed in
    if (zoomLevel > 1) {
      isPanning = true;
      hasPanned = false;
      panStartX = e.clientX;
      panStartY = e.clientY;
      scrollStartX = imageWrapper.scrollLeft;
      scrollStartY = imageWrapper.scrollTop;
      imageContainer.style.cursor = 'grab';
    }
  });

  // Handle click on image container (place marker if not panning)
  imageContainer?.addEventListener('click', (e: MouseEvent) => {
    if (isDragging || hasPanned) return;
    const pos = getPositionFromEvent(e);
    updatePinPosition(pos.x, pos.y);
  });

  // Handle drag start on pin
  pin?.addEventListener('mousedown', (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isDragging = true;
    pin.classList.add('dragging');
  });

  // Handle drag move (for pin) and pan move (for zoomed view)
  const handleMouseMove = (e: MouseEvent) => {
    // Handle pin dragging
    if (isDragging) {
      e.preventDefault();
      const pos = getPositionFromEvent(e);
      updatePinPosition(pos.x, pos.y);
      return;
    }

    // Handle panning when zoomed
    if (isPanning) {
      e.preventDefault();
      const deltaX = e.clientX - panStartX;
      const deltaY = e.clientY - panStartY;

      // Mark as panned if moved more than 5px (to distinguish from click)
      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        hasPanned = true;
        imageContainer.style.cursor = 'grabbing';
      }

      imageWrapper.scrollLeft = scrollStartX - deltaX;
      imageWrapper.scrollTop = scrollStartY - deltaY;
    }
  };

  // Handle drag end (for pin) and pan end (for zoomed view)
  const handleMouseUp = () => {
    if (isDragging) {
      isDragging = false;
      pin.classList.remove('dragging');
    }
    if (isPanning) {
      isPanning = false;
      imageContainer.style.cursor = 'crosshair';
    }
  };

  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);

  // Close handlers
  const closeModal = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    modal.classList.remove('visible');
    setTimeout(() => modal.remove(), 300);
  };

  modal.querySelector('.fpp-close-btn')?.addEventListener('click', closeModal);
  modal.querySelector('.fpp-backdrop')?.addEventListener('click', closeModal);
  modal.querySelector('.fpp-cancel-btn')?.addEventListener('click', closeModal);

  // Confirm handler
  modal.querySelector('.fpp-confirm-btn')?.addEventListener('click', () => {
    updateStageFloorPlanPosition(stageId, selectedX, selectedY);
    markUnsaved();
    closeModal();
  });

  // Escape key
  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeModal();
      document.removeEventListener('keydown', handleEscape);
    }
  };
  document.addEventListener('keydown', handleEscape);

  // Animate in
  requestAnimationFrame(() => modal.classList.add('visible'));
}

/**
 * Update a stage's floor plan marker position in the UI
 */
function updateStageFloorPlanPosition(stageId: string, x: number, y: number) {
  const markerList = document.getElementById('markerList');
  if (!markerList) return;

  // Update the in-memory config object
  if (config) {
    if (!config.navigation) {
      config.navigation = {
        type: 'floorplan',
        displayMode: 'both',
        markers: []
      };
    }
    if (!config.navigation.markers) {
      config.navigation.markers = [];
    }

    const existingMarker = config.navigation.markers.find(m => m.stageId === stageId);
    if (existingMarker) {
      existingMarker.floorPlanPosition = { x, y };
    } else {
      config.navigation.markers.push({
        stageId,
        floorPlanPosition: { x, y }
      });
    }
  }

  // Update hidden inputs
  const xInput = markerList.querySelector(`.marker-x[data-stage-id="${stageId}"]`) as HTMLInputElement;
  const yInput = markerList.querySelector(`.marker-y[data-stage-id="${stageId}"]`) as HTMLInputElement;

  if (xInput) xInput.value = String(x);
  if (yInput) yInput.value = String(y);

  // Update visible display
  const markerItem = markerList.querySelector(`.marker-item[data-stage-id="${stageId}"]`);
  if (markerItem) {
    const positionInfo = markerItem.querySelector('.marker-position-info');
    if (positionInfo) {
      positionInfo.innerHTML = `<span style="font-family: 'SF Mono', Monaco, monospace; font-size: 11px;">Position: ${Math.round(x * 100)}%, ${Math.round(y * 100)}%</span>`;
    }
  }
}

/**
 * Collect navigation settings from the form and update the config object
 * Called by handleSave() to include navigation in main save
 */
function collectNavigationSettings() {
  if (!config) return;

  const navType = document.getElementById('navType') as HTMLSelectElement;
  const floorPlanUrl = document.getElementById('floorPlanUrl') as HTMLInputElement;
  const floorLabel = document.getElementById('floorLabel') as HTMLInputElement;
  const mapStyle = document.getElementById('mapStyle') as HTMLSelectElement;
  const mapZoom = document.getElementById('mapZoom') as HTMLInputElement;
  const showMinimap = document.getElementById('showMinimap') as HTMLInputElement;

  const type = navType?.value as 'none' | 'floorplan' | 'map';

  if (type === 'none') {
    // Remove navigation config (delete property, don't set undefined - Firebase rejects undefined)
    delete config.navigation;
  } else {
    // Build navigation config
    const markers: Array<{
      stageId: string;
      label?: string;
      floorPlanPosition?: { x: number; y: number };
      geoPosition?: { lat: number; lng: number };
      placeName?: string;
    }> = [];

    // Collect marker positions from form
    const markerList = document.getElementById('markerList');
    if (markerList && config.stages) {
      config.stages.forEach(stage => {
        const marker: typeof markers[0] = {
          stageId: stage.id,
          label: stage.name
        };

        if (type === 'floorplan') {
          const xInput = markerList.querySelector(`.marker-x[data-stage-id="${stage.id}"]`) as HTMLInputElement;
          const yInput = markerList.querySelector(`.marker-y[data-stage-id="${stage.id}"]`) as HTMLInputElement;
          if (xInput && yInput) {
            // Hidden inputs store 0-1 normalized values directly
            marker.floorPlanPosition = {
              x: parseFloat(xInput.value) || 0.5,
              y: parseFloat(yInput.value) || 0.5
            };
          }
        } else if (type === 'map') {
          const latInput = markerList.querySelector(`.marker-lat[data-stage-id="${stage.id}"]`) as HTMLInputElement;
          const lngInput = markerList.querySelector(`.marker-lng[data-stage-id="${stage.id}"]`) as HTMLInputElement;
          const placeInput = markerList.querySelector(`.marker-place-name[data-stage-id="${stage.id}"]`) as HTMLInputElement;
          if (latInput && lngInput) {
            marker.geoPosition = {
              lat: parseFloat(latInput.value) || 0,
              lng: parseFloat(lngInput.value) || 0
            };
            // Also update stage location for map type
            stage.location = marker.geoPosition;
            // Save place name if available
            if (placeInput?.value) {
              marker.placeName = placeInput.value;
            }
          }
        }

        markers.push(marker);
      });
    }

    config.navigation = {
      type,
      showMinimap: showMinimap?.checked ?? true,
      markers
    };

    // Add floor plan config
    if (type === 'floorplan' && floorPlanUrl?.value) {
      config.navigation.floorPlan = {
        imageUrl: floorPlanUrl.value
      };
      // Only add floorLabel if it has a value (Firestore doesn't accept undefined)
      if (floorLabel?.value) {
        config.navigation.floorPlan.floorLabel = floorLabel.value;
      }
    }

    // Add map config
    if (type === 'map') {
      config.navigation.map = {
        style: (mapStyle?.value || 'streets') as 'streets' | 'satellite' | 'terrain' | 'dark' | 'light',
        defaultZoom: parseInt(mapZoom?.value || '12')
      };
    }
  }
}

// ============================================================================
// AI 360 IMAGE GENERATION & EDITING
// ============================================================================

// AI DOM Elements
const aiGeneratePrompt = document.getElementById('aiGeneratePrompt') as HTMLTextAreaElement;
const aiGenerateBtn = document.getElementById('aiGenerateBtn') as HTMLButtonElement;
const aiEditPrompt = document.getElementById('aiEditPrompt') as HTMLTextAreaElement;
const aiEditBtn = document.getElementById('aiEditBtn') as HTMLButtonElement;
const aiFixNadirBtn = document.getElementById('aiFixNadirBtn') as HTMLButtonElement;
const aiFixZenithBtn = document.getElementById('aiFixZenithBtn') as HTMLButtonElement;
const aiStagingStyle = document.getElementById('aiStagingStyle') as HTMLSelectElement;
const aiStagingBtn = document.getElementById('aiStagingBtn') as HTMLButtonElement;
const aiReskinPreset = document.getElementById('aiReskinPreset') as HTMLSelectElement;
const aiReskinCustom = document.getElementById('aiReskinCustom') as HTMLTextAreaElement;
const aiReskinBtn = document.getElementById('aiReskinBtn') as HTMLButtonElement;
const aiProcessingStatus = document.getElementById('aiProcessingStatus') as HTMLElement;
const aiStatusText = document.getElementById('aiStatusText') as HTMLElement;
const aiProgressBar = document.getElementById('aiProgressBar') as HTMLElement;
const aiResultPreview = document.getElementById('aiResultPreview') as HTMLElement;
const aiResultImage = document.getElementById('aiResultImage') as HTMLImageElement;
const aiApplyResultBtn = document.getElementById('aiApplyResultBtn') as HTMLButtonElement;
const aiDiscardResultBtn = document.getElementById('aiDiscardResultBtn') as HTMLButtonElement;

// AI State
const currentAIJobId: string | null = null;
let currentAIResultUrl: string | null = null;
let currentAIResultVersionId: string | null = null; // Track version ID for marking as active
let aiProgressInterval: ReturnType<typeof setInterval> | null = null;

// Version History State
const versionHistoryCache: Map<string, AIAPI.StageVersion[]> = new Map(); // Keyed by stageId, not index
let currentVersions: AIAPI.StageVersion[] = [];

// Version History DOM Elements
const versionHistoryToggle = document.getElementById('versionHistoryToggle') as HTMLButtonElement;
const versionHistoryContent = document.getElementById('versionHistoryContent') as HTMLElement;
const versionHistoryLoading = document.getElementById('versionHistoryLoading') as HTMLElement;
const versionHistoryEmpty = document.getElementById('versionHistoryEmpty') as HTMLElement;
const versionHistoryGrid = document.getElementById('versionHistoryGrid') as HTMLElement;
const versionCountBadge = document.getElementById('versionCountBadge') as HTMLElement;
const loadMoreVersionsBtn = document.getElementById('loadMoreVersionsBtn') as HTMLButtonElement;

// AI Tools Collapsible DOM Elements
const aiToolsToggle = document.getElementById('aiToolsToggle') as HTMLButtonElement;
const aiToolsContent = document.getElementById('aiToolsContent') as HTMLElement;
const aiModelSelect = document.getElementById('aiModelSelect') as HTMLSelectElement;
const aiEditSectionsWrapper = document.getElementById('aiEditSectionsWrapper') as HTMLElement;

// AI Usage Display Elements (per-model)
const aiUsageDisplay = document.getElementById('aiUsageDisplay') as HTMLElement;
const aiUsageProText = document.getElementById('aiUsageProText') as HTMLElement;
const aiUsageProBar = document.getElementById('aiUsageProBar') as HTMLElement;
const aiUsageFlashText = document.getElementById('aiUsageFlashText') as HTMLElement;
const aiUsageFlashBar = document.getElementById('aiUsageFlashBar') as HTMLElement;
const aiUsageSubtext = document.getElementById('aiUsageSubtext') as HTMLElement;

// AI Usage state
let cachedAIUsage: AIAPI.AIUsageStats | null = null;

// BYOK (Bring Your Own Key) DOM Elements
const byokSection = document.getElementById('byokSection') as HTMLElement;
const byokNoKey = document.getElementById('byokNoKey') as HTMLElement;
const byokHasKey = document.getElementById('byokHasKey') as HTMLElement;
const byokKeyInput = document.getElementById('byokKeyInput') as HTMLInputElement;
const byokSaveBtn = document.getElementById('byokSaveBtn') as HTMLButtonElement;
const byokRemoveBtn = document.getElementById('byokRemoveBtn') as HTMLButtonElement;
const byokKeyPreview = document.getElementById('byokKeyPreview') as HTMLElement;
const byokToggleVisibility = document.getElementById('byokToggleVisibility') as HTMLButtonElement;
const byokEyeIcon = document.getElementById('byokEyeIcon') as unknown as SVGElement;

// BYOK State
let hasPersonalKey = false;

// AI Processing Modal Elements
const aiProcessingModal = document.getElementById('aiProcessingModal') as HTMLElement;
const aiModalTitle = document.getElementById('aiModalTitle') as HTMLElement;
const aiModalStatus = document.getElementById('aiModalStatus') as HTMLElement;
const aiModalProgressBar = document.getElementById('aiModalProgressBar') as HTMLElement;
const aiModalPercent = document.getElementById('aiModalPercent') as HTMLElement;

/**
 * Show AI processing status with animated progress (full-screen modal)
 */
function showAIProcessing(message: string, stages?: string[]) {
  // Make sure AI Tools section is expanded
  if (aiToolsContent && aiToolsContent.style.display === 'none') {
    aiToolsContent.style.display = 'block';
    aiToolsToggle?.classList.add('active');
  }

  // Show inline status (for those who scroll)
  if (aiProcessingStatus) {
    aiProcessingStatus.style.display = 'block';
    aiStatusText.textContent = message;
    aiProgressBar.style.width = '5%';
  }

  // Show full-screen modal
  if (aiProcessingModal) {
    aiProcessingModal.style.display = 'flex';
    if (aiModalTitle) aiModalTitle.textContent = message;
    if (aiModalStatus) aiModalStatus.textContent = 'Connecting to AI service...';
    if (aiModalProgressBar) aiModalProgressBar.style.width = '5%';
    if (aiModalPercent) aiModalPercent.textContent = 'Starting...';
  }

  // Disable AI buttons during processing
  setAIButtonsEnabled(false);

  // Start animated progress simulation
  const defaultStages = [
    'Connecting to AI service...',
    'Processing your request...',
    'Generating 4K equirectangular image...',
    'Applying 360° projection...',
    'Finalizing high-quality output...',
  ];
  const progressStages = stages || defaultStages;
  let currentStage = 0;
  let currentProgress = 5;

  // Clear any existing interval
  if (aiProgressInterval) {
    clearInterval(aiProgressInterval);
  }

  aiProgressInterval = setInterval(() => {
    // Slowly increment progress (never reach 100% until actually done)
    if (currentProgress < 85) {
      currentProgress += Math.random() * 3 + 1;
      const progress = Math.min(currentProgress, 85);

      if (aiProgressBar) {
        aiProgressBar.style.width = `${progress}%`;
      }
      if (aiModalProgressBar) {
        aiModalProgressBar.style.width = `${progress}%`;
      }
      if (aiModalPercent) {
        aiModalPercent.textContent = `${Math.round(progress)}% complete`;
      }
    }

    // Update stage message every ~15% progress
    if (currentProgress > (currentStage + 1) * 17 && currentStage < progressStages.length - 1) {
      currentStage++;
      if (aiStatusText) {
        aiStatusText.textContent = progressStages[currentStage];
      }
      if (aiModalStatus) {
        aiModalStatus.textContent = progressStages[currentStage];
      }
    }
  }, 800);
}

/**
 * Update AI progress
 */
function updateAIProgress(percent: number, message?: string) {
  if (aiProgressBar) {
    aiProgressBar.style.width = `${percent}%`;
  }
  if (message && aiStatusText) {
    aiStatusText.textContent = message;
  }
}

/**
 * Hide AI processing status
 */
function hideAIProcessing() {
  // Stop progress animation
  if (aiProgressInterval) {
    clearInterval(aiProgressInterval);
    aiProgressInterval = null;
  }

  if (aiProcessingStatus) {
    aiProcessingStatus.style.display = 'none';
  }

  // Hide the full-screen modal
  const aiProcessingModal = document.getElementById('aiProcessingModal');
  if (aiProcessingModal) {
    aiProcessingModal.style.display = 'none';
  }

  setAIButtonsEnabled(true);
}

/**
 * Enable/disable AI buttons with visual feedback
 */
function setAIButtonsEnabled(enabled: boolean) {
  const buttons = [aiGenerateBtn, aiEditBtn, aiFixNadirBtn, aiFixZenithBtn, aiStagingBtn, aiReskinBtn];
  buttons.forEach(btn => {
    if (btn) {
      btn.disabled = !enabled;
      btn.style.opacity = enabled ? '1' : '0.5';
      btn.style.cursor = enabled ? 'pointer' : 'not-allowed';
    }
  });
  document.querySelectorAll('.ai-weather-btn').forEach(btn => {
    const button = btn as HTMLButtonElement;
    button.disabled = !enabled;
    button.style.opacity = enabled ? '1' : '0.5';
    button.style.cursor = enabled ? 'pointer' : 'not-allowed';
  });

  // Also update the AI textareas/inputs to look disabled
  const aiInputs = [aiGeneratePrompt, aiEditPrompt, aiReskinCustom];
  aiInputs.forEach(input => {
    if (input) {
      input.disabled = !enabled;
      input.style.opacity = enabled ? '1' : '0.6';
    }
  });

  // Update select dropdowns
  const aiSelects = [aiStagingStyle, aiReskinPreset, aiModelSelect];
  aiSelects.forEach(select => {
    if (select) {
      select.disabled = !enabled;
      select.style.opacity = enabled ? '1' : '0.6';
    }
  });
}

/**
 * Update AI edit sections visibility based on whether a skybox exists
 * The edit/fix/staging sections should only show when there's an image to edit
 */
function updateAIEditSectionsVisibility() {
  if (!aiEditSectionsWrapper) return;

  const stage = config?.stages?.[currentStageIndex];
  const hasSkyboxUrl = !!(stage?.skybox?.url || stage?.skybox?.variants?.desktop?.url);

  aiEditSectionsWrapper.style.display = hasSkyboxUrl ? 'block' : 'none';
}

/**
 * Fetch and display AI usage quota
 */
async function loadAIUsage(forceRefresh: boolean = false) {
  // Use cache if available and not forcing refresh
  if (!forceRefresh && cachedAIUsage) {
    renderAIUsage(cachedAIUsage);
    return;
  }

  try {
    const usage = await AIAPI.getAIUsage();
    cachedAIUsage = usage;
    renderAIUsage(usage);
  } catch (error) {
    console.error('Failed to load AI usage:', error);
    // Show error state
    if (aiUsageProText) aiUsageProText.textContent = '--';
    if (aiUsageFlashText) aiUsageFlashText.textContent = '--';
    if (aiUsageSubtext) aiUsageSubtext.textContent = 'Could not fetch usage data';
    if (aiUsageProBar) aiUsageProBar.style.width = '0%';
    if (aiUsageFlashBar) aiUsageFlashBar.style.width = '0%';
  }
}

/**
 * Render AI usage display for both Pro and Flash models
 */
function renderAIUsage(usage: AIAPI.AIUsageStats) {
  if (!aiUsageProText || !aiUsageProBar || !aiUsageFlashText || !aiUsageFlashBar || !aiUsageSubtext) return;

  // Handle backwards compatibility with old API format
  const legacyUsage = usage as any;
  const pro = usage.pro || { used: legacyUsage.monthlyUsed || 0, limit: legacyUsage.monthlyLimit || 0, remaining: legacyUsage.remaining || 0 };
  const flash = usage.flash || { used: 0, limit: 0, remaining: 0 };
  const { tier, canUseAI } = usage;

  // Helper to get bar color based on usage percentage
  const getBarColor = (percent: number, baseGradient: string) => {
    if (percent >= 90) return 'var(--red-primary)';
    if (percent >= 70) return 'linear-gradient(135deg, #f5a623 0%, #f7931e 100%)';
    return baseGradient;
  };

  // Check if tier has no access
  if (pro.limit === 0 && flash.limit === 0) {
    aiUsageProText.textContent = '0 / 0';
    aiUsageProBar.style.width = '0%';
    aiUsageFlashText.textContent = '0 / 0';
    aiUsageFlashBar.style.width = '0%';
    aiUsageSubtext.textContent = 'Upgrade to Pro for AI features';
    setAIButtonsEnabled(false);
    return;
  }

  // Check if admin (unlimited)
  const isAdmin = pro.limit >= 9999;

  // Render Pro model usage
  if (isAdmin) {
    aiUsageProText.textContent = `${pro.used} used`;
  } else {
    aiUsageProText.textContent = `${pro.used} / ${pro.limit}`;
  }
  const proPercent = pro.limit > 0 && pro.limit < 9999 ? Math.min((pro.used / pro.limit) * 100, 100) : 0;
  aiUsageProBar.style.width = `${proPercent}%`;
  aiUsageProBar.style.background = getBarColor(proPercent, 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)');

  // Render Flash model usage
  if (isAdmin) {
    aiUsageFlashText.textContent = `${flash.used} used`;
  } else {
    aiUsageFlashText.textContent = `${flash.used} / ${flash.limit}`;
  }
  const flashPercent = flash.limit > 0 && flash.limit < 9999 ? Math.min((flash.used / flash.limit) * 100, 100) : 0;
  aiUsageFlashBar.style.width = `${flashPercent}%`;
  aiUsageFlashBar.style.background = getBarColor(flashPercent, 'linear-gradient(135deg, #34c759 0%, #30d158 100%)');

  // Update subtext based on status
  if (isAdmin) {
    aiUsageSubtext.textContent = 'Unlimited (Admin)';
  } else if (pro.remaining === 0 && flash.remaining === 0) {
    aiUsageSubtext.textContent = 'All limits reached. Resets on the 1st.';
  } else {
    const parts = [];
    if (pro.remaining > 0) parts.push(`${pro.remaining} Pro`);
    if (flash.remaining > 0) parts.push(`${flash.remaining} Flash`);
    aiUsageSubtext.textContent = `${parts.join(' • ')} remaining`;
  }

  // Update button states if no AI access (unless using personal key)
  if (!canUseAI && !hasPersonalKey) {
    setAIButtonsEnabled(false);
  }
}

/**
 * Refresh AI usage after a generation (called after successful AI operations)
 */
function refreshAIUsageAfterGeneration() {
  // Clear cache and reload
  cachedAIUsage = null;
  // Don't refresh usage for BYOK users (they have unlimited)
  if (!hasPersonalKey) {
    loadAIUsage(true);
  }
}

// ============================================================================
// BYOK (BRING YOUR OWN KEY) FUNCTIONS
// ============================================================================

/**
 * Check and load BYOK key status
 */
async function checkBYOKStatus() {
  if (!byokSection) return;

  try {
    const status = await AIAPI.checkPersonalGeminiKey();
    hasPersonalKey = status.hasKey;
    updateBYOKUI(status);

    // If user has personal key, also enable AI Tools section
    if (hasPersonalKey) {
      enableAIToolsForBYOK();
    }
  } catch (error) {
    console.error('Failed to check BYOK status:', error);
    hasPersonalKey = false;
    updateBYOKUI({ hasKey: false });
  }
}

/**
 * Update BYOK UI based on key status
 */
function updateBYOKUI(status: AIAPI.PersonalKeyStatus) {
  if (!byokNoKey || !byokHasKey) return;

  if (status.hasKey) {
    byokNoKey.style.display = 'none';
    byokHasKey.style.display = 'block';
    if (byokKeyPreview) {
      byokKeyPreview.textContent = status.keyPreview ? `****${status.keyPreview}` : '****';
    }
    // Show quota display but with "unlimited" message for BYOK users
    if (aiUsageDisplay) {
      aiUsageDisplay.style.display = 'block';
      aiUsageDisplay.style.opacity = '1';
      // Update to show unlimited status
      if (aiUsageProText) aiUsageProText.textContent = '∞';
      if (aiUsageFlashText) aiUsageFlashText.textContent = '∞';
      if (aiUsageProBar) {
        aiUsageProBar.style.width = '100%';
        aiUsageProBar.style.background = 'linear-gradient(135deg, #34c759 0%, #30d158 100%)';
      }
      if (aiUsageFlashBar) {
        aiUsageFlashBar.style.width = '100%';
        aiUsageFlashBar.style.background = 'linear-gradient(135deg, #34c759 0%, #30d158 100%)';
      }
      if (aiUsageSubtext) {
        aiUsageSubtext.innerHTML = '✨ <strong>Using your own API key</strong> — no platform limits';
        aiUsageSubtext.style.color = '#34c759';
      }
    }
  } else {
    byokNoKey.style.display = 'block';
    byokHasKey.style.display = 'none';
    // Show quota display when not using personal key
    if (aiUsageDisplay) {
      aiUsageDisplay.style.display = 'block';
    }
    // Reset subtext color
    if (aiUsageSubtext) {
      aiUsageSubtext.style.color = '';
    }
  }
}

/**
 * Enable AI Tools section for BYOK users (even if they don't have ai_generation feature)
 */
function enableAIToolsForBYOK() {
  const aiToolsSection = document.getElementById('aiToolsSection');
  const aiToolsToggle = document.getElementById('aiToolsToggle');

  if (aiToolsSection && aiToolsToggle) {
    aiToolsSection.style.opacity = '1';
    aiToolsSection.style.pointerEvents = 'auto';
    (aiToolsToggle as HTMLElement).style.cursor = 'pointer';
    aiToolsToggle.title = 'Using your own API key';
    console.log('✅ AI Tools enabled via BYOK');
  }

  // Enable all AI buttons
  setAIButtonsEnabled(true);
}

/**
 * Save personal Gemini API key
 */
async function saveBYOKKey() {
  if (!byokKeyInput || !byokSaveBtn) return;

  const apiKey = byokKeyInput.value.trim();
  if (!apiKey) {
    showToast('Please enter an API key', 'error');
    return;
  }

  // Validate key format (Gemini keys start with AIza)
  if (!apiKey.startsWith('AIza')) {
    showToast('Invalid API key format. Gemini keys start with "AIza"', 'error');
    return;
  }

  const originalText = byokSaveBtn.textContent;
  byokSaveBtn.textContent = 'Saving...';
  byokSaveBtn.disabled = true;

  try {
    const result = await AIAPI.setPersonalGeminiKey(apiKey);
    if (result.success) {
      hasPersonalKey = true;
      updateBYOKUI({ hasKey: true, keyPreview: result.keyPreview });
      enableAIToolsForBYOK();
      byokKeyInput.value = '';
      showToast('API key saved successfully! AI features are now unlimited.', 'success');
    }
  } catch (error: any) {
    console.error('Failed to save BYOK key:', error);
    showToast(error.message || 'Failed to save API key. Please check it\'s valid.', 'error');
  } finally {
    byokSaveBtn.textContent = originalText;
    byokSaveBtn.disabled = false;
  }
}

/**
 * Remove personal Gemini API key
 */
async function removeBYOKKey() {
  if (!byokRemoveBtn) return;

  const originalText = byokRemoveBtn.textContent;
  byokRemoveBtn.textContent = 'Removing...';
  byokRemoveBtn.disabled = true;

  try {
    await AIAPI.removePersonalGeminiKey();
    hasPersonalKey = false;
    updateBYOKUI({ hasKey: false });
    showToast('API key removed. You\'re now using platform quota.', 'success');

    // Reload usage to show quota
    loadAIUsage(true);

    // Re-check access (might need to disable AI buttons if no feature access)
    if (currentUserProfile && !hasFeatureAccess(currentUserProfile, 'ai_generation')) {
      // Disable AI buttons but keep section accessible for BYOK
      setAIButtonsEnabled(false);
      const aiToolsToggle = document.getElementById('aiToolsToggle');
      if (aiToolsToggle) {
        aiToolsToggle.title = 'Add your own API key to unlock AI features';
      }
    }
  } catch (error: any) {
    console.error('Failed to remove BYOK key:', error);
    showToast(error.message || 'Failed to remove API key', 'error');
  } finally {
    byokRemoveBtn.textContent = originalText;
    byokRemoveBtn.disabled = false;
  }
}

/**
 * Toggle BYOK key visibility
 */
function toggleBYOKKeyVisibility() {
  if (!byokKeyInput || !byokEyeIcon) return;

  if (byokKeyInput.type === 'password') {
    byokKeyInput.type = 'text';
    // Change icon to "eye-off"
    byokEyeIcon.innerHTML = '<path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/>';
  } else {
    byokKeyInput.type = 'password';
    // Change icon back to "eye"
    byokEyeIcon.innerHTML = '<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>';
  }
}

/**
 * Setup BYOK event listeners
 */
function setupBYOKEventListeners() {
  if (byokSaveBtn) {
    byokSaveBtn.addEventListener('click', saveBYOKKey);
  }

  if (byokRemoveBtn) {
    byokRemoveBtn.addEventListener('click', removeBYOKKey);
  }

  if (byokToggleVisibility) {
    byokToggleVisibility.addEventListener('click', toggleBYOKKeyVisibility);
  }

  // Allow saving with Enter key
  if (byokKeyInput) {
    byokKeyInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        saveBYOKKey();
      }
    });
  }
}

/**
 * Show AI result preview
 */
function showAIResult(imageUrl: string, versionId?: string) {
  currentAIResultUrl = imageUrl;
  currentAIResultVersionId = versionId || null;
  const aiResultLoading = document.getElementById('aiResultLoading');

  // Refresh usage count after successful generation
  refreshAIUsageAfterGeneration();

  if (aiResultPreview && aiResultImage) {
    // Show loading state
    if (aiResultLoading) aiResultLoading.style.display = 'flex';
    aiResultPreview.style.display = 'block';

    // Log the URL for debugging
    console.log('[AI] Showing result image:', imageUrl);

    // Set up load handler to hide loading when image loads
    aiResultImage.onload = () => {
      console.log('[AI] Image loaded successfully');
      if (aiResultLoading) aiResultLoading.style.display = 'none';
    };

    aiResultImage.onerror = () => {
      console.error('[AI] Image failed to load:', imageUrl);
      if (aiResultLoading) {
        aiResultLoading.innerHTML = `
          <div style="text-align: center; color: #ff6b6b;">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-bottom: 10px;">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <p style="margin: 0;">Failed to load image</p>
            <p style="margin: 5px 0 0; font-size: 11px; opacity: 0.7;">The image may still be processing</p>
          </div>
        `;
      }
    };

    // Set the source to trigger loading
    aiResultImage.src = imageUrl;
  }
}

/**
 * Hide AI result preview
 */
function hideAIResult() {
  currentAIResultUrl = null;
  currentAIResultVersionId = null;
  if (aiResultPreview) {
    aiResultPreview.style.display = 'none';
  }
}

/**
 * Get the current skybox URL for editing
 */
function getCurrentSkyboxUrl(): string | null {
  if (!config || !config.stages || !config.stages[currentStageIndex]) {
    return null;
  }
  const stage = config.stages[currentStageIndex];
  const skybox = stage.skybox;

  if (!skybox || skybox.type === 'color') {
    return null;
  }

  // Get the fallback or desktop URL
  // Handle both old format (string) and new format ({ url: string, ... })
  if (skybox.variants?.fallback) {
    const fallback = skybox.variants.fallback;
    return typeof fallback === 'string' ? fallback : fallback.url;
  }
  if (skybox.variants?.desktop) {
    const desktop = skybox.variants.desktop;
    return typeof desktop === 'string' ? desktop : desktop.url;
  }
  if (skybox.url) {
    return skybox.url;
  }

  return null;
}

/**
 * Apply AI result to current stage skybox
 */
async function applyAIResultToStage(imageUrl: string) {
  if (!config || !sequenceId) return;

  const stage = config.stages[currentStageIndex];
  if (!stage) return;

  // If we have a version ID, mark it as active first
  if (currentAIResultVersionId && stage.id) {
    try {
      await AIAPI.applyVersion(sequenceId, stage.id, currentAIResultVersionId, currentStageIndex);
      console.log(`[AI] Marked version ${currentAIResultVersionId} as active`);
    } catch (error) {
      console.error('[AI] Failed to mark version as active:', error);
      // Continue anyway - the skybox update will still work
    }
  }

  // Update skybox with the AI-generated image - set all variants to the same URL
  // Each variant needs to be an object with url property (not just a string)
  stage.skybox = {
    type: 'image',
    url: imageUrl,
    variants: {
      fallback: { url: imageUrl, format: 'png', resolution: '4096x2048' },
      desktop: { url: imageUrl, format: 'png', resolution: '4096x2048' },
      mobile: { url: imageUrl, format: 'png', resolution: '4096x2048' },
      tablet: { url: imageUrl, format: 'png', resolution: '4096x2048' },
      vr: { url: imageUrl, format: 'png', resolution: '4096x2048' },
    } as any,
    rotation: stage.skybox?.rotation || 0,
  };

  // Mark as changed
  markUnsaved();

  // Update the preview with the new skybox - AWAIT to avoid race condition
  if (editorPreview) {
    await editorPreview.updateStage(stage, currentStageIndex);
    // Give Babylon.js an extra frame to render the new texture
    await new Promise(resolve => requestAnimationFrame(resolve));
  }

  // Update the form to reflect the new skybox
  loadStageData(stage);

  // Refresh version history to show the new version (now correctly marked as active)
  await loadVersionHistory(true);

  showToast('AI result applied to stage', 'success');
}

// ============================================================================
// VERSION HISTORY FUNCTIONS
// ============================================================================

/**
 * Initialize collapsible section toggles
 */
function initCollapsibleSections() {
  // Version History Toggle
  if (versionHistoryToggle) {
    versionHistoryToggle.addEventListener('click', () => {
      const isExpanded = versionHistoryContent.style.display !== 'none';
      versionHistoryContent.style.display = isExpanded ? 'none' : 'block';
      versionHistoryToggle.classList.toggle('active', !isExpanded);

      // Load history when expanded
      if (!isExpanded && sequenceId) {
        loadVersionHistory();
      }
    });
  }

  // AI Tools Toggle
  if (aiToolsToggle) {
    aiToolsToggle.addEventListener('click', () => {
      const isExpanded = aiToolsContent.style.display !== 'none';
      aiToolsContent.style.display = isExpanded ? 'none' : 'block';
      aiToolsToggle.classList.toggle('active', !isExpanded);

      // Load AI usage when expanding
      if (!isExpanded) {
        loadAIUsage();
      }
    });
  }
}

/**
 * Load version history for current stage
 */
async function loadVersionHistory(forceRefresh: boolean = false) {
  if (!sequenceId || !config) return;

  // Get the current stage's unique ID (stable across reordering)
  const stage = config.stages[currentStageIndex];
  if (!stage?.id) {
    console.warn('[VersionHistory] Stage has no id');
    return;
  }
  const stageId = stage.id;

  // Check cache first (keyed by stageId for stability)
  if (!forceRefresh && versionHistoryCache.has(stageId)) {
    currentVersions = versionHistoryCache.get(stageId) || [];
    renderVersionHistory();
    return;
  }

  // Show loading
  if (versionHistoryLoading) versionHistoryLoading.style.display = 'block';
  if (versionHistoryEmpty) versionHistoryEmpty.style.display = 'none';
  if (versionHistoryGrid) versionHistoryGrid.style.display = 'none';

  try {
    // Use stageId (unique identifier) instead of stageIndex (array position)
    const result = await AIAPI.getStageVersionHistory(sequenceId, stageId, currentStageIndex);
    currentVersions = result.versions || [];

    // Auto-create v1 for existing stages that have a skybox but no version history
    // This handles backwards compatibility for stages created before version tracking
    if (currentVersions.length === 0 && stage.skybox?.url) {
      console.log('[VersionHistory] No versions found but stage has skybox, creating v1...');
      try {
        const uploadResult = await AIAPI.createUploadVersion(
          sequenceId,
          stageId,
          stage.skybox.url,
          currentStageIndex,
          stage.skybox.thumbnailUrl,
          undefined, // No original URL since this is the first version
          true // Set as active
        );
        currentVersions = [uploadResult];
        console.log('[VersionHistory] Created initial v1 for existing skybox');
      } catch (uploadError) {
        console.warn('[VersionHistory] Could not auto-create v1:', uploadError);
        // Don't fail completely if we can't create v1
      }
    }

    versionHistoryCache.set(stageId, currentVersions);

    // Update badge
    if (versionCountBadge) {
      const totalVersions = currentVersions.length;
      versionCountBadge.textContent = String(totalVersions);
      versionCountBadge.style.display = totalVersions > 0 ? 'inline-block' : 'none';
    }

    renderVersionHistory();
  } catch (error) {
    console.error('Failed to load version history:', error);
    if (versionHistoryLoading) versionHistoryLoading.style.display = 'none';
    if (versionHistoryEmpty) {
      versionHistoryEmpty.style.display = 'block';
      versionHistoryEmpty.innerHTML = '<p style="margin: 0; color: var(--red-primary);">Failed to load history</p>';
    }
  }
}

/**
 * Render version history thumbnails
 */
function renderVersionHistory() {
  if (versionHistoryLoading) versionHistoryLoading.style.display = 'none';

  if (!currentVersions || currentVersions.length === 0) {
    if (versionHistoryEmpty) versionHistoryEmpty.style.display = 'block';
    if (versionHistoryGrid) versionHistoryGrid.style.display = 'none';
    return;
  }

  if (versionHistoryEmpty) versionHistoryEmpty.style.display = 'none';
  if (versionHistoryGrid) {
    versionHistoryGrid.style.display = 'grid';
    versionHistoryGrid.innerHTML = currentVersions.map(version => {
      const isAI = version.source.startsWith('ai-');
      const sourceLabel = AIAPI.getVersionSourceLabel(version.source);
      const timeAgo = getTimeAgo(version.createdAt);

      return `
        <div class="version-thumbnail ${version.isActive ? 'active' : ''}" data-version-id="${version.id}" title="Version ${version.versionNumber}: ${sourceLabel}">
          <img src="${version.thumbnailUrl || version.imageUrl}" alt="Version ${version.versionNumber}" loading="lazy">
          <div class="version-thumbnail-overlay">
            <span>v${version.versionNumber}</span>
            <span class="version-source-badge ${isAI ? 'ai' : ''}">${isAI ? 'AI' : 'Upload'}</span>
          </div>
          <div class="version-actions">
            ${!version.isActive ? `
              <button class="version-action-btn apply" data-action="apply" data-version-id="${version.id}" title="Apply this version">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <polyline points="20,6 9,17 4,12"/>
                </svg>
              </button>
              <button class="version-action-btn delete" data-action="delete" data-version-id="${version.id}" title="Delete this version" style="background: rgba(255,59,48,0.1);">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ff3b30" stroke-width="2">
                  <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                </svg>
              </button>
            ` : ''}
            <button class="version-action-btn" data-action="preview" data-version-id="${version.id}" title="Preview">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7z"/>
              </svg>
            </button>
          </div>
        </div>
      `;
    }).join('');

    // Add click handlers
    versionHistoryGrid.querySelectorAll('.version-thumbnail').forEach(thumb => {
      thumb.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const actionBtn = target.closest('[data-action]') as HTMLElement;
        const versionId = thumb.getAttribute('data-version-id');

        if (actionBtn && versionId) {
          const action = actionBtn.getAttribute('data-action');
          if (action === 'apply') {
            applyVersionToStage(versionId);
          } else if (action === 'preview') {
            previewVersion(versionId);
          } else if (action === 'delete') {
            deleteVersionFromStage(versionId);
          }
        } else if (versionId) {
          // Click on thumbnail itself - show preview
          previewVersion(versionId);
        }
      });
    });
  }
}

/**
 * Get time ago string from timestamp
 */
function getTimeAgo(timestamp: any): string {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

/**
 * Preview a version in the editor
 */
async function previewVersion(versionId: string) {
  const version = currentVersions.find(v => v.id === versionId);
  if (!version) return;

  // Show the image in the AI result preview area for consistency
  showAIResult(version.imageUrl);
  showToast(`Previewing version ${version.versionNumber}`, 'success');
}

/**
 * Apply a version to the current stage
 */
async function applyVersionToStage(versionId: string) {
  if (!sequenceId || !config) return;

  // Get the current stage's unique ID
  const stage = config.stages[currentStageIndex];
  if (!stage?.id) {
    showToast('Scene has no ID', 'error');
    return;
  }

  try {
    showToast('Applying version...', 'success');

    // Use stageId (unique identifier) instead of stageIndex (array position)
    const result = await AIAPI.applyVersion(sequenceId, stage.id, versionId, currentStageIndex);

    // Update the stage skybox with the applied version's image
    if (config && config.stages[currentStageIndex]) {
      const stage = config.stages[currentStageIndex];
      const variant = { url: result.imageUrl, format: 'jpg' as const };
      stage.skybox = {
        type: 'image',
        url: result.imageUrl,
        variants: {
          fallback: variant,
          desktop: variant,
          mobile: variant,
          tablet: variant,
          vr: variant,
        },
        rotation: stage.skybox?.rotation || 0,
      };

      markUnsaved();

      // Update preview
      if (editorPreview) {
        await editorPreview.updateStage(stage, currentStageIndex);
      }

      // Update form to reflect the restored version
      loadStageData(stage);
    }

    // Refresh version history to show new active state
    await loadVersionHistory(true);

    // Also refresh the stage list to update thumbnail
    renderStageList();

    showToast(`Applied version ${result.version.versionNumber}`, 'success');
  } catch (error: any) {
    showToast(`Failed to apply version: ${error.message}`, 'error');
  }
}

/**
 * Delete a version from the stage's history
 */
async function deleteVersionFromStage(versionId: string) {
  if (!sequenceId || !config) return;

  const stage = config.stages[currentStageIndex];
  if (!stage?.id) return;

  const version = currentVersions.find(v => v.id === versionId);
  if (!version) return;

  // Confirm deletion
  const confirmed = await showConfirm(
    `Delete version ${version.versionNumber}? This cannot be undone.`,
    'Delete Version',
    '🗑️',
    'Delete',
    'Cancel'
  );

  if (!confirmed) return;

  // Immediately remove from local state to prevent double-clicks
  const versionIndex = currentVersions.findIndex(v => v.id === versionId);
  if (versionIndex !== -1) {
    currentVersions.splice(versionIndex, 1);
    renderVersionHistory(); // Update UI immediately
  }

  try {
    await AIAPI.deleteStageVersion(sequenceId, stage.id, versionId);
    showToast('Version deleted', 'success');

    // Refresh version history to ensure consistency with server
    await loadVersionHistory(true);
  } catch (error: any) {
    // Restore version to local state if delete failed
    if (versionIndex !== -1) {
      currentVersions.splice(versionIndex, 0, version);
      renderVersionHistory();
    }
    showToast(`Failed to delete: ${error.message}`, 'error');
  }
}

/**
 * Clear version history cache when switching stages
 */
function clearVersionHistoryForStage() {
  currentVersions = [];
  if (versionHistoryGrid) versionHistoryGrid.innerHTML = '';
  if (versionCountBadge) versionCountBadge.style.display = 'none';

  // If history panel is open, reload it
  if (versionHistoryContent && versionHistoryContent.style.display !== 'none') {
    loadVersionHistory();
  }
}

/**
 * Initialize AI event handlers
 */
function initAIHandlers() {
  // Initialize collapsible sections
  initCollapsibleSections();

  // Setup BYOK (Bring Your Own Key) event listeners
  setupBYOKEventListeners();

  // Generate 360 from text
  if (aiGenerateBtn) {
    aiGenerateBtn.addEventListener('click', async () => {
      const prompt = aiGeneratePrompt?.value?.trim();
      if (!prompt || prompt.length < 10) {
        showToast('Please enter a detailed description (at least 10 characters)', 'error');
        return;
      }

      if (!sequenceId) {
        showToast('Please save the sequence first', 'error');
        return;
      }

      // Get selected model
      const selectedModel = (aiModelSelect?.value || 'pro') as AIAPI.AIModel;

      // Skip quota check for BYOK users (they have unlimited via their own key)
      if (!hasPersonalKey) {
        // Check AI quota before starting
        try {
          const usage = await AIAPI.getAIUsage();
          if (!usage.canUseAI) {
            showToast('Your AI quota has been exhausted this month. Please upgrade for more generations.', 'error');
            return;
          }
          // Check specific model quota
          const modelUsage = selectedModel === 'pro' ? usage.pro : usage.flash;
          if (modelUsage.limit !== -1 && modelUsage.remaining <= 0) {
            const otherModel = selectedModel === 'pro' ? 'flash' : 'pro';
            const otherUsage = selectedModel === 'pro' ? usage.flash : usage.pro;
            if (otherUsage.remaining > 0) {
              showToast(`No ${selectedModel === 'pro' ? 'Pro' : 'Flash'} credits remaining. Try switching to ${otherModel === 'pro' ? 'Pro' : 'Flash'} model.`, 'error');
            } else {
              showToast(`AI quota exhausted (${modelUsage.used}/${modelUsage.limit} used). Upgrade for more.`, 'error');
            }
            return;
          }
        } catch (quotaError) {
          console.warn('Could not verify AI quota, proceeding anyway:', quotaError);
          // Continue - backend will enforce quota if check fails
        }
      }

      try {
        const modelName = selectedModel === 'pro' ? 'Nano Banana Pro' : 'Nano Banana';

        showAIProcessing(`Generating 360 image with ${modelName}...`, [
          `Connecting to ${modelName}...`,
          'Analyzing your prompt...',
          'Generating equirectangular panorama...',
          'Applying seamless 360° projection...',
          'Finalizing VR-ready output (this may take 1-3 minutes)...',
        ]);

        // Get current stage for version tracking
        const stage = config!.stages[currentStageIndex];
        const result = await AIAPI.generate360(prompt, sequenceId, stage.id, currentStageIndex, selectedModel);

        // Show which model was actually used
        const usedModelName = result.model?.includes('pro') ? 'Nano Banana Pro' :
                             result.model?.includes('flash') ? 'Nano Banana' : modelName;

        // Log detailed result info
        console.log(`[AI] ✅ Generation complete!`);
        console.log(`[AI]    Model: ${usedModelName} (${result.model || 'unknown'})`);
        console.log(`[AI]    Duration: ${(result as any).duration || 'N/A'}s`);
        console.log(`[AI]    Job ID: ${result.jobId}`);
        console.log(`[AI]    Output URL: ${result.outputUrl?.substring(0, 80)}...`);

        updateAIProgress(100, 'Generation complete!');

        if (result.outputUrl) {
          hideAIProcessing();
          showAIResult(result.outputUrl, result.versionId);
          showToast(`360 image generated with ${usedModelName}!`, 'success');
        }
      } catch (error: any) {
        hideAIProcessing();
        // Provide user-friendly error messages
        let errorMessage = error.message || 'Unknown error';
        if (errorMessage.includes('503') || errorMessage.includes('overloaded')) {
          errorMessage = 'AI service is busy. Please try again in a few moments.';
        } else if (errorMessage.includes('INVALID_ARGUMENT')) {
          errorMessage = 'Invalid request. Please try a different prompt.';
        } else if (errorMessage.includes('permission-denied') || errorMessage.includes('Pro')) {
          errorMessage = 'AI features require a Pro subscription.';
        }
        showToast(`AI generation failed: ${errorMessage}`, 'error');
      }
    });
  }

  // Edit 360 with natural language
  if (aiEditBtn) {
    aiEditBtn.addEventListener('click', async () => {
      const prompt = aiEditPrompt?.value?.trim();
      if (!prompt || prompt.length < 5) {
        showToast('Please describe what you want to change', 'error');
        return;
      }

      const imageUrl = getCurrentSkyboxUrl();
      if (!imageUrl) {
        showToast('No skybox image to edit. Upload an image first.', 'error');
        return;
      }

      if (!sequenceId) {
        showToast('Please save the sequence first', 'error');
        return;
      }

      try {
        showAIProcessing('Editing 360 image...');
        updateAIProgress(20, 'Analyzing image...');

        // Get current stage for version tracking
        const stage = config!.stages[currentStageIndex];
        // Pass imageUrl as originalImageUrl for version history (tracks what was there before edit)
        const result = await AIAPI.edit360(imageUrl, prompt, sequenceId, stage.id, currentStageIndex, imageUrl);

        updateAIProgress(90, 'Edit complete!');

        if (result.outputUrl) {
          hideAIProcessing();
          showAIResult(result.outputUrl, result.versionId);
          showToast('360 image edited successfully!', 'success');
        }
      } catch (error: any) {
        hideAIProcessing();
        showToast(`AI edit failed: ${error.message}`, 'error');
      }
    });
  }

  // Fix Nadir
  if (aiFixNadirBtn) {
    aiFixNadirBtn.addEventListener('click', async () => {
      const imageUrl = getCurrentSkyboxUrl();
      if (!imageUrl) {
        showToast('No skybox image to fix. Upload an image first.', 'error');
        return;
      }

      if (!sequenceId) {
        showToast('Please save the sequence first', 'error');
        return;
      }

      try {
        showAIProcessing('Fixing nadir (bottom)...');
        updateAIProgress(30, 'Detecting tripod shadow...');

        // Get current stage for version tracking
        const stage = config!.stages[currentStageIndex];
        const selectedModel = (aiModelSelect?.value || 'pro') as AIAPI.AIModel;
        const result = await AIAPI.fixNadir(imageUrl, sequenceId, stage.id, currentStageIndex, imageUrl, selectedModel);

        if (result.outputUrl) {
          hideAIProcessing();
          showAIResult(result.outputUrl, result.versionId);
          showToast('Nadir fixed successfully!', 'success');
        }
      } catch (error: any) {
        hideAIProcessing();
        showToast(`Fix nadir failed: ${error.message}`, 'error');
      }
    });
  }

  // Fix Zenith
  if (aiFixZenithBtn) {
    aiFixZenithBtn.addEventListener('click', async () => {
      const imageUrl = getCurrentSkyboxUrl();
      if (!imageUrl) {
        showToast('No skybox image to fix. Upload an image first.', 'error');
        return;
      }

      if (!sequenceId) {
        showToast('Please save the sequence first', 'error');
        return;
      }

      try {
        showAIProcessing('Fixing zenith (top)...');
        updateAIProgress(30, 'Detecting stitching issues...');

        // Get current stage for version tracking
        const stage = config!.stages[currentStageIndex];
        const selectedModel = (aiModelSelect?.value || 'pro') as AIAPI.AIModel;
        const result = await AIAPI.fixZenith(imageUrl, sequenceId, stage.id, currentStageIndex, imageUrl, selectedModel);

        if (result.outputUrl) {
          hideAIProcessing();
          showAIResult(result.outputUrl, result.versionId);
          showToast('Zenith fixed successfully!', 'success');
        }
      } catch (error: any) {
        hideAIProcessing();
        showToast(`Fix zenith failed: ${error.message}`, 'error');
      }
    });
  }

  // Virtual Staging
  if (aiStagingBtn) {
    aiStagingBtn.addEventListener('click', async () => {
      const style = aiStagingStyle?.value as AIAPI.StagingStyle;
      if (!style) {
        showToast('Please select a staging style', 'error');
        return;
      }

      const imageUrl = getCurrentSkyboxUrl();
      if (!imageUrl) {
        showToast('No skybox image to stage. Upload an image first.', 'error');
        return;
      }

      if (!sequenceId) {
        showToast('Please save the sequence first', 'error');
        return;
      }

      try {
        showAIProcessing(`Applying ${style} staging...`);
        updateAIProgress(20, 'Adding furniture...');

        // Get current stage for version tracking
        const stage = config!.stages[currentStageIndex];
        const selectedModel = (aiModelSelect?.value || 'pro') as AIAPI.AIModel;
        const result = await AIAPI.virtualStaging(imageUrl, style, sequenceId, stage.id, currentStageIndex, imageUrl, selectedModel);

        if (result.outputUrl) {
          hideAIProcessing();
          showAIResult(result.outputUrl, result.versionId);
          showToast('Virtual staging applied!', 'success');
        }
      } catch (error: any) {
        hideAIProcessing();
        showToast(`Virtual staging failed: ${error.message}`, 'error');
      }
    });
  }

  // Weather Effects
  document.querySelectorAll('.ai-weather-btn').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const weather = (e.target as HTMLButtonElement).dataset.weather as AIAPI.WeatherEffect;
      if (!weather) return;

      const imageUrl = getCurrentSkyboxUrl();
      if (!imageUrl) {
        showToast('No skybox image to modify. Upload an image first.', 'error');
        return;
      }

      if (!sequenceId) {
        showToast('Please save the sequence first', 'error');
        return;
      }

      try {
        showAIProcessing(`Adding ${weather} effect...`);
        updateAIProgress(20, 'Applying weather...');

        // Get current stage for version tracking
        const stage = config!.stages[currentStageIndex];
        const selectedModel = (aiModelSelect?.value || 'pro') as AIAPI.AIModel;
        const result = await AIAPI.addWeather(imageUrl, weather, sequenceId, stage.id, currentStageIndex, imageUrl, selectedModel);

        if (result.outputUrl) {
          hideAIProcessing();
          showAIResult(result.outputUrl, result.versionId);
          showToast(`${weather.charAt(0).toUpperCase() + weather.slice(1)} effect applied!`, 'success');
        }
      } catch (error: any) {
        hideAIProcessing();
        showToast(`Weather effect failed: ${error.message}`, 'error');
      }
    });
  });

  // Reskin preset dropdown - show custom input when "custom" selected
  if (aiReskinPreset) {
    aiReskinPreset.addEventListener('change', () => {
      if (aiReskinCustom) {
        aiReskinCustom.style.display = aiReskinPreset.value === 'custom' ? 'block' : 'none';
      }
    });
  }

  // Reskin / Style Transfer
  if (aiReskinBtn) {
    aiReskinBtn.addEventListener('click', async () => {
      let style = aiReskinPreset?.value;
      if (style === 'custom') {
        style = aiReskinCustom?.value?.trim();
      }

      if (!style || style.length < 5) {
        showToast('Please select or describe a style', 'error');
        return;
      }

      const imageUrl = getCurrentSkyboxUrl();
      if (!imageUrl) {
        showToast('No skybox image to reskin. Upload an image first.', 'error');
        return;
      }

      if (!sequenceId) {
        showToast('Please save the sequence first', 'error');
        return;
      }

      try {
        showAIProcessing('Applying style transfer...');
        updateAIProgress(20, 'Transforming scene...');

        // Get current stage for version tracking
        const stage = config!.stages[currentStageIndex];
        const selectedModel = (aiModelSelect?.value || 'pro') as AIAPI.AIModel;
        const result = await AIAPI.reskin360(imageUrl, style, sequenceId, stage.id, currentStageIndex, selectedModel);

        if (result.outputUrl) {
          hideAIProcessing();
          showAIResult(result.outputUrl, result.versionId);
          showToast('Style transfer complete!', 'success');
        }
      } catch (error: any) {
        hideAIProcessing();
        showToast(`Style transfer failed: ${error.message}`, 'error');
      }
    });
  }

  // Apply AI Result
  if (aiApplyResultBtn) {
    aiApplyResultBtn.addEventListener('click', async () => {
      if (currentAIResultUrl) {
        await applyAIResultToStage(currentAIResultUrl);
        hideAIResult();
      }
    });
  }

  // Discard AI Result
  if (aiDiscardResultBtn) {
    aiDiscardResultBtn.addEventListener('click', () => {
      hideAIResult();
      showToast('AI result discarded', 'success');
    });
  }
}

// Initialize AI handlers when DOM is ready
document.addEventListener('DOMContentLoaded', initAIHandlers);

// ============================================================================
// LEADS DASHBOARD FUNCTIONS
// ============================================================================

// Leads state
let leadsSubmissions: any[] = [];
let leadsLastDoc: string | null = null;
let leadsHasMore = false;
let leadsFilterFormType = '';
let leadsFilterDateRange = '';

/**
 * Load leads submissions from Firestore via Cloud Function
 */
async function loadLeadsSubmissions(append = false) {
  if (!sequenceId) {
    console.log('[Editor] No sequence ID, cannot load leads');
    return;
  }

  const listEl = document.getElementById('leadsList');
  const emptyEl = document.getElementById('leadsEmptyState');
  const loadingEl = document.getElementById('leadsLoading');
  const loadMoreEl = document.getElementById('leadsLoadMore');

  if (!listEl || !emptyEl || !loadingEl) return;

  // Show loading state
  if (!append) {
    listEl.innerHTML = '';
    loadingEl.style.display = 'block';
    emptyEl.style.display = 'none';
  }

  try {
    const functions = getFunctions();
    const getFormSubmissionsCallable = httpsCallable(functions, 'getFormSubmissions');

    const result = await getFormSubmissionsCallable({
      sequenceId,
      limit: 20,
      startAfter: append ? leadsLastDoc : undefined
    });

    const data = result.data as { submissions: any[]; hasMore: boolean };

    loadingEl.style.display = 'none';

    if (append) {
      leadsSubmissions = [...leadsSubmissions, ...data.submissions];
    } else {
      leadsSubmissions = data.submissions;
    }

    leadsHasMore = data.hasMore;
    if (data.submissions.length > 0) {
      leadsLastDoc = data.submissions[data.submissions.length - 1].id;
    }

    // Apply client-side filters
    const filtered = filterLeadsSubmissions(leadsSubmissions);

    // Update stats
    updateLeadsStats(leadsSubmissions);

    // Render list
    if (filtered.length === 0) {
      listEl.innerHTML = '';
      emptyEl.style.display = 'block';
    } else {
      emptyEl.style.display = 'none';
      renderLeadsList(filtered, append);
    }

    // Show/hide load more button
    if (loadMoreEl) {
      loadMoreEl.style.display = leadsHasMore ? 'block' : 'none';
    }
  } catch (error: any) {
    console.error('[Editor] Failed to load leads:', error);
    loadingEl.style.display = 'none';

    if (error.code === 'functions/unauthenticated') {
      listEl.innerHTML = '<div class="leads-empty-text" style="padding: 20px; text-align: center;">Please sign in to view leads.</div>';
    } else {
      listEl.innerHTML = `<div class="leads-empty-text" style="padding: 20px; text-align: center; color: #ff3b30;">Failed to load leads: ${error.message}</div>`;
    }
  }
}

/**
 * Filter submissions client-side based on form type and date range
 */
function filterLeadsSubmissions(submissions: any[]): any[] {
  return submissions.filter(sub => {
    // Filter by form type
    if (leadsFilterFormType && sub.formType !== leadsFilterFormType) {
      return false;
    }

    // Filter by date range
    if (leadsFilterDateRange && sub.submittedAt) {
      const submittedDate = sub.submittedAt.toDate ? sub.submittedAt.toDate() : new Date(sub.submittedAt._seconds * 1000);
      const now = new Date();

      if (leadsFilterDateRange === 'today') {
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        if (submittedDate < today) return false;
      } else if (leadsFilterDateRange === 'week') {
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        if (submittedDate < weekAgo) return false;
      } else if (leadsFilterDateRange === 'month') {
        const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        if (submittedDate < monthAgo) return false;
      }
    }

    return true;
  });
}

/**
 * Update leads stats display
 */
function updateLeadsStats(submissions: any[]) {
  const totalEl = document.getElementById('leadsTotalCount');
  const weekEl = document.getElementById('leadsThisWeek');

  if (totalEl) {
    totalEl.textContent = String(submissions.length);
  }

  if (weekEl) {
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const thisWeek = submissions.filter(sub => {
      if (!sub.submittedAt) return false;
      const submittedDate = sub.submittedAt.toDate ? sub.submittedAt.toDate() : new Date(sub.submittedAt._seconds * 1000);
      return submittedDate >= weekAgo;
    });
    weekEl.textContent = String(thisWeek.length);
  }
}

/**
 * Render the leads list
 */
function renderLeadsList(submissions: any[], append = false) {
  const listEl = document.getElementById('leadsList');
  if (!listEl) return;

  if (!append) {
    listEl.innerHTML = '';
  }

  submissions.forEach(sub => {
    const item = document.createElement('div');
    item.className = 'leads-item';
    item.dataset.submissionId = sub.id;

    // Extract display fields
    const formData = sub.formData || {};
    const name = formData.name || formData.Name || formData.full_name || 'Anonymous';
    const email = formData.email || formData.Email || '';
    const initials = name.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase();

    // Format date
    let dateStr = '';
    if (sub.submittedAt) {
      const date = sub.submittedAt.toDate ? sub.submittedAt.toDate() : new Date(sub.submittedAt._seconds * 1000);
      dateStr = formatRelativeTime(date);
    }

    // Device icon
    const deviceIcon = getDeviceIcon(sub.deviceType);

    item.innerHTML = `
      <div class="leads-item-header">
        <div class="leads-item-avatar">${initials || '?'}</div>
        <div class="leads-item-info">
          <div class="leads-item-name">${escapeHtml(name)}</div>
          ${email ? `<div class="leads-item-email">${escapeHtml(email)}</div>` : ''}
        </div>
      </div>
      <div class="leads-item-meta">
        <span class="leads-item-badge">${formatFormType(sub.formType)}</span>
        <span class="leads-item-device">
          ${deviceIcon}
          ${dateStr}
        </span>
      </div>
    `;

    item.addEventListener('click', () => showLeadDetail(sub));
    listEl.appendChild(item);
  });
}

/**
 * Format form type for display
 */
function formatFormType(type: string): string {
  const types: Record<string, string> = {
    'email_capture': 'Email',
    'contact': 'Contact',
    'inquiry': 'Inquiry',
    'custom': 'Custom'
  };
  return types[type] || type || 'Form';
}

/**
 * Get device icon SVG
 */
function getDeviceIcon(deviceType: string): string {
  switch (deviceType) {
    case 'mobile':
      return '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12" y2="18.01"/></svg>';
    case 'tablet':
      return '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12" y2="18.01"/></svg>';
    case 'vr':
      return '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-5l-1.5 2h-3l-1.5-2H4a2 2 0 0 1-2-2V8z"/><circle cx="8" cy="12" r="2"/><circle cx="16" cy="12" r="2"/></svg>';
    default:
      return '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>';
  }
}

/**
 * Format relative time
 */
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

/**
 * Escape HTML special characters
 */
function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

/**
 * Show lead detail modal
 */
function showLeadDetail(submission: any) {
  // Remove existing modal if present
  let modal = document.querySelector('.leads-detail-modal');
  if (modal) {
    modal.remove();
  }

  // Create modal
  modal = document.createElement('div');
  modal.className = 'leads-detail-modal';

  const formData = submission.formData || {};
  const name = formData.name || formData.Name || formData.full_name || 'Anonymous';

  // Format date
  let dateStr = '';
  if (submission.submittedAt) {
    const date = submission.submittedAt.toDate ? submission.submittedAt.toDate() : new Date(submission.submittedAt._seconds * 1000);
    dateStr = date.toLocaleString();
  }

  // Build form fields HTML
  const fieldsHtml = Object.entries(formData)
    .map(([key, value]) => `
      <div class="leads-detail-field">
        <div class="leads-detail-label">${escapeHtml(key)}</div>
        <div class="leads-detail-value">${escapeHtml(String(value))}</div>
      </div>
    `)
    .join('');

  modal.innerHTML = `
    <div class="leads-detail-content">
      <div class="leads-detail-header">
        <div class="leads-detail-title">${escapeHtml(name)}</div>
        <button class="leads-detail-close">&times;</button>
      </div>
      <div class="leads-detail-body">
        ${fieldsHtml}
        <div class="leads-detail-field">
          <div class="leads-detail-label">Submitted</div>
          <div class="leads-detail-value">${dateStr}</div>
        </div>
        <div class="leads-detail-field">
          <div class="leads-detail-label">Device</div>
          <div class="leads-detail-value">${submission.deviceType || 'Unknown'}</div>
        </div>
        <div class="leads-detail-field">
          <div class="leads-detail-label">Form Type</div>
          <div class="leads-detail-value">${formatFormType(submission.formType)}</div>
        </div>
      </div>
      <div class="leads-detail-footer">
        <button class="ios-button ios-button-secondary" style="flex: 1;" id="copyLeadDataBtn">
          Copy Data
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  // Animate in
  requestAnimationFrame(() => {
    modal!.classList.add('active');
  });

  // Close handlers
  const closeBtn = modal.querySelector('.leads-detail-close');
  closeBtn?.addEventListener('click', () => closeLeadDetailModal(modal as HTMLElement));

  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      closeLeadDetailModal(modal as HTMLElement);
    }
  });

  // Copy data button
  const copyBtn = modal.querySelector('#copyLeadDataBtn');
  copyBtn?.addEventListener('click', () => {
    const text = Object.entries(formData)
      .map(([k, v]) => `${k}: ${v}`)
      .join('\n');
    navigator.clipboard.writeText(text);
    showToast('Data copied to clipboard', 'success');
  });
}

/**
 * Close lead detail modal
 */
function closeLeadDetailModal(modal: HTMLElement) {
  modal.classList.remove('active');
  setTimeout(() => modal.remove(), 300);
}

/**
 * Export leads to CSV
 */
async function exportLeadsToCsv() {
  if (!sequenceId) return;

  const exportBtn = document.getElementById('exportLeadsBtn') as HTMLButtonElement;
  if (exportBtn) {
    exportBtn.disabled = true;
    exportBtn.innerHTML = '<span class="leads-spinner" style="width: 14px; height: 14px; border-width: 2px; display: inline-block; vertical-align: middle;"></span>';
  }

  try {
    const functions = getFunctions();
    const exportFormSubmissionsCallable = httpsCallable(functions, 'exportFormSubmissions');

    const result = await exportFormSubmissionsCallable({ sequenceId });
    const data = result.data as { csv: string; count: number; filename: string };

    if (data.count === 0) {
      showToast('No leads to export', 'warning');
      return;
    }

    // Download CSV
    const blob = new Blob([data.csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = data.filename || 'leads-export.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    showToast(`Exported ${data.count} leads`, 'success');
  } catch (error: any) {
    console.error('[Editor] Failed to export leads:', error);
    showToast('Failed to export leads', 'error');
  } finally {
    if (exportBtn) {
      exportBtn.disabled = false;
      exportBtn.innerHTML = `
        <svg class="hero-icon hero-icon-sm" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
        Export
      `;
    }
  }
}

/**
 * Initialize leads dashboard handlers
 */
function initLeadsDashboard() {
  // Export button
  const exportBtn = document.getElementById('exportLeadsBtn');
  exportBtn?.addEventListener('click', exportLeadsToCsv);

  // Load more button
  const loadMoreBtn = document.getElementById('loadMoreLeadsBtn');
  loadMoreBtn?.addEventListener('click', () => loadLeadsSubmissions(true));

  // Filter: Form Type
  const formTypeFilter = document.getElementById('leadsFilterFormType') as HTMLSelectElement;
  formTypeFilter?.addEventListener('change', (e) => {
    leadsFilterFormType = (e.target as HTMLSelectElement).value;
    // Re-render with current data (client-side filter)
    const filtered = filterLeadsSubmissions(leadsSubmissions);
    const listEl = document.getElementById('leadsList');
    const emptyEl = document.getElementById('leadsEmptyState');
    if (listEl && emptyEl) {
      if (filtered.length === 0) {
        listEl.innerHTML = '';
        emptyEl.style.display = 'block';
      } else {
        emptyEl.style.display = 'none';
        listEl.innerHTML = '';
        renderLeadsList(filtered, false);
      }
    }
  });

  // Filter: Date Range
  const dateFilter = document.getElementById('leadsFilterDateRange') as HTMLSelectElement;
  dateFilter?.addEventListener('change', (e) => {
    leadsFilterDateRange = (e.target as HTMLSelectElement).value;
    // Re-render with current data (client-side filter)
    const filtered = filterLeadsSubmissions(leadsSubmissions);
    const listEl = document.getElementById('leadsList');
    const emptyEl = document.getElementById('leadsEmptyState');
    if (listEl && emptyEl) {
      if (filtered.length === 0) {
        listEl.innerHTML = '';
        emptyEl.style.display = 'block';
      } else {
        emptyEl.style.display = 'none';
        listEl.innerHTML = '';
        renderLeadsList(filtered, false);
      }
    }
  });
}

// Initialize leads dashboard when DOM is ready
document.addEventListener('DOMContentLoaded', initLeadsDashboard);

// ============================================================================
// POST-PROCESSING (EFFECTS TAB) HANDLERS
// ============================================================================

/**
 * Load post-processing settings into Effects tab UI
 */
function loadPostProcessingSettings(pp: PostProcessingConfig | undefined): void {
  // Bloom
  const bloomEnabled = document.getElementById('bloomEnabled') as HTMLInputElement;
  const bloomThreshold = document.getElementById('bloomThreshold') as HTMLInputElement;
  const bloomWeight = document.getElementById('bloomWeight') as HTMLInputElement;
  if (bloomEnabled) bloomEnabled.checked = pp?.bloom?.enabled ?? false;
  if (bloomThreshold) bloomThreshold.value = String(pp?.bloom?.threshold ?? 0.9);
  if (bloomWeight) bloomWeight.value = String(pp?.bloom?.weight ?? 0.15);
  updateSliderValue('bloomThreshold', 'bloomThresholdValue');
  updateSliderValue('bloomWeight', 'bloomWeightValue');

  // Depth of Field
  const dofEnabled = document.getElementById('dofEnabled') as HTMLInputElement;
  const dofFocusDistance = document.getElementById('dofFocusDistance') as HTMLInputElement;
  const dofFStop = document.getElementById('dofFStop') as HTMLInputElement;
  const dofBlurLevel = document.getElementById('dofBlurLevel') as HTMLSelectElement;
  if (dofEnabled) dofEnabled.checked = pp?.depthOfField?.enabled ?? false;
  if (dofFocusDistance) dofFocusDistance.value = String(pp?.depthOfField?.focusDistance ?? 2000);
  if (dofFStop) dofFStop.value = String(pp?.depthOfField?.fStop ?? 1.4);
  if (dofBlurLevel) dofBlurLevel.value = pp?.depthOfField?.blurLevel ?? 'medium';
  updateSliderValue('dofFocusDistance', 'dofFocusValue');
  updateSliderValue('dofFStop', 'dofFStopValue');

  // Vignette
  const vignetteEnabled = document.getElementById('vignetteEnabled') as HTMLInputElement;
  const vignetteWeight = document.getElementById('vignetteWeight') as HTMLInputElement;
  const vignetteColor = document.getElementById('vignetteColor') as HTMLInputElement;
  if (vignetteEnabled) vignetteEnabled.checked = pp?.vignette?.enabled ?? false;
  if (vignetteWeight) vignetteWeight.value = String(pp?.vignette?.weight ?? 1.5);
  if (vignetteColor) vignetteColor.value = pp?.vignette?.color ?? '#000000';
  updateSliderValue('vignetteWeight', 'vignetteWeightValue');

  // Chromatic Aberration
  const chromaticEnabled = document.getElementById('chromaticEnabled') as HTMLInputElement;
  const chromaticAmount = document.getElementById('chromaticAmount') as HTMLInputElement;
  if (chromaticEnabled) chromaticEnabled.checked = pp?.chromaticAberration?.enabled ?? false;
  if (chromaticAmount) chromaticAmount.value = String(pp?.chromaticAberration?.amount ?? 30);
  updateSliderValue('chromaticAmount', 'chromaticAmountValue');

  // Grain
  const grainEnabled = document.getElementById('grainEnabled') as HTMLInputElement;
  const grainIntensity = document.getElementById('grainIntensity') as HTMLInputElement;
  const grainAnimated = document.getElementById('grainAnimated') as HTMLInputElement;
  if (grainEnabled) grainEnabled.checked = pp?.grain?.enabled ?? false;
  if (grainIntensity) grainIntensity.value = String(pp?.grain?.intensity ?? 30);
  if (grainAnimated) grainAnimated.checked = pp?.grain?.animated ?? false;
  updateSliderValue('grainIntensity', 'grainIntensityValue');

  // Sharpen
  const sharpenEnabled = document.getElementById('sharpenEnabled') as HTMLInputElement;
  const sharpenEdgeAmount = document.getElementById('sharpenEdgeAmount') as HTMLInputElement;
  if (sharpenEnabled) sharpenEnabled.checked = pp?.sharpen?.enabled ?? false;
  if (sharpenEdgeAmount) sharpenEdgeAmount.value = String(pp?.sharpen?.edgeAmount ?? 0.3);
  updateSliderValue('sharpenEdgeAmount', 'sharpenEdgeValue');

  // SSAO
  const ssaoEnabled = document.getElementById('ssaoEnabled') as HTMLInputElement;
  const ssaoRatio = document.getElementById('ssaoRatio') as HTMLInputElement;
  if (ssaoEnabled) ssaoEnabled.checked = pp?.ssao?.enabled ?? false;
  if (ssaoRatio) ssaoRatio.value = String(pp?.ssao?.ratio ?? 0.75);
  updateSliderValue('ssaoRatio', 'ssaoRatioValue');

  // Image Processing
  const imgExposure = document.getElementById('imgExposure') as HTMLInputElement;
  const imgContrast = document.getElementById('imgContrast') as HTMLInputElement;
  const toneMappingEnabled = document.getElementById('toneMappingEnabled') as HTMLInputElement;
  const toneMappingType = document.getElementById('toneMappingType') as HTMLSelectElement;
  if (imgExposure) imgExposure.value = String(pp?.imageProcessing?.exposure ?? 1);
  if (imgContrast) imgContrast.value = String(pp?.imageProcessing?.contrast ?? 1);
  if (toneMappingEnabled) toneMappingEnabled.checked = pp?.imageProcessing?.toneMappingEnabled ?? false;
  if (toneMappingType) toneMappingType.value = pp?.imageProcessing?.toneMappingType ?? 'standard';
  updateSliderValue('imgExposure', 'exposureValue');
  updateSliderValue('imgContrast', 'contrastValue');

  // Antialiasing
  const fxaaEnabled = document.getElementById('fxaaEnabled') as HTMLInputElement;
  const msaaSamples = document.getElementById('msaaSamples') as HTMLSelectElement;
  if (fxaaEnabled) fxaaEnabled.checked = pp?.fxaa ?? false;
  if (msaaSamples) msaaSamples.value = String(pp?.msaaSamples ?? 4);

  // Glow Layer
  const glowEnabled = document.getElementById('glowEnabled') as HTMLInputElement;
  const glowIntensity = document.getElementById('glowIntensity') as HTMLInputElement;
  if (glowEnabled) glowEnabled.checked = pp?.glowLayer?.enabled ?? false;
  if (glowIntensity) glowIntensity.value = String(pp?.glowLayer?.intensity ?? 1);
  updateSliderValue('glowIntensity', 'glowIntensityValue');

  // Apply post-processing to preview so effects are visible immediately when loading a stage
  editorPreview?.applyPostProcessing?.(pp);
}

/**
 * Helper to update slider value display
 */
function updateSliderValue(sliderId: string, valueId: string): void {
  const slider = document.getElementById(sliderId) as HTMLInputElement;
  const valueSpan = document.getElementById(valueId);
  if (slider && valueSpan) {
    valueSpan.textContent = slider.value;
  }
}

/**
 * Get current post-processing config from UI
 */
function getPostProcessingConfigFromUI(): PostProcessingConfig {
  return {
    bloom: {
      enabled: (document.getElementById('bloomEnabled') as HTMLInputElement)?.checked ?? false,
      threshold: parseFloat((document.getElementById('bloomThreshold') as HTMLInputElement)?.value) || 0.9,
      weight: parseFloat((document.getElementById('bloomWeight') as HTMLInputElement)?.value) || 0.15,
    },
    depthOfField: {
      enabled: (document.getElementById('dofEnabled') as HTMLInputElement)?.checked ?? false,
      focusDistance: parseFloat((document.getElementById('dofFocusDistance') as HTMLInputElement)?.value) || 2000,
      fStop: parseFloat((document.getElementById('dofFStop') as HTMLInputElement)?.value) || 1.4,
      blurLevel: ((document.getElementById('dofBlurLevel') as HTMLSelectElement)?.value || 'medium') as 'low' | 'medium' | 'high',
    },
    vignette: {
      enabled: (document.getElementById('vignetteEnabled') as HTMLInputElement)?.checked ?? false,
      weight: parseFloat((document.getElementById('vignetteWeight') as HTMLInputElement)?.value) || 1.5,
      color: (document.getElementById('vignetteColor') as HTMLInputElement)?.value || '#000000',
    },
    chromaticAberration: {
      enabled: (document.getElementById('chromaticEnabled') as HTMLInputElement)?.checked ?? false,
      amount: parseFloat((document.getElementById('chromaticAmount') as HTMLInputElement)?.value) || 30,
    },
    grain: {
      enabled: (document.getElementById('grainEnabled') as HTMLInputElement)?.checked ?? false,
      intensity: parseFloat((document.getElementById('grainIntensity') as HTMLInputElement)?.value) || 30,
      animated: (document.getElementById('grainAnimated') as HTMLInputElement)?.checked ?? false,
    },
    sharpen: {
      enabled: (document.getElementById('sharpenEnabled') as HTMLInputElement)?.checked ?? false,
      edgeAmount: parseFloat((document.getElementById('sharpenEdgeAmount') as HTMLInputElement)?.value) || 0.3,
    },
    ssao: {
      enabled: (document.getElementById('ssaoEnabled') as HTMLInputElement)?.checked ?? false,
      ratio: parseFloat((document.getElementById('ssaoRatio') as HTMLInputElement)?.value) || 0.75,
    },
    imageProcessing: {
      exposure: parseFloat((document.getElementById('imgExposure') as HTMLInputElement)?.value) || 1,
      contrast: parseFloat((document.getElementById('imgContrast') as HTMLInputElement)?.value) || 1,
      toneMappingEnabled: (document.getElementById('toneMappingEnabled') as HTMLInputElement)?.checked ?? false,
      toneMappingType: ((document.getElementById('toneMappingType') as HTMLSelectElement)?.value || 'standard') as 'standard' | 'aces' | 'khr_pbr_neutral',
    },
    fxaa: (document.getElementById('fxaaEnabled') as HTMLInputElement)?.checked ?? false,
    msaaSamples: parseInt((document.getElementById('msaaSamples') as HTMLSelectElement)?.value) || 4,
    glowLayer: {
      enabled: (document.getElementById('glowEnabled') as HTMLInputElement)?.checked ?? false,
      intensity: parseFloat((document.getElementById('glowIntensity') as HTMLInputElement)?.value) || 1,
    },
  };
}

/**
 * Handle post-processing change
 */
function handlePostProcessingChange(): void {
  if (!config?.stages?.[currentStageIndex]) return;

  const ppConfig = getPostProcessingConfigFromUI();
  config.stages[currentStageIndex].postProcessing = ppConfig;
  hasUnsavedChanges = true;

  // Update preview if available
  editorPreview?.applyPostProcessing?.(ppConfig);
}

/**
 * Initialize Effects tab event handlers
 */
function initEffectsTabHandlers(): void {
  // Setup collapsible sections
  const effectsTab = document.getElementById('effects-tab');
  effectsTab?.querySelectorAll('.collapsible-header').forEach((header) => {
    header.addEventListener('click', () => {
      const content = header.nextElementSibling as HTMLElement;
      if (content) {
        content.style.display = content.style.display === 'none' ? 'block' : 'none';
        header.classList.toggle('expanded');
      }
    });
  });

  // All checkboxes and inputs in Effects tab trigger update
  const effectInputs = [
    'bloomEnabled', 'bloomThreshold', 'bloomWeight',
    'dofEnabled', 'dofFocusDistance', 'dofFStop', 'dofBlurLevel',
    'vignetteEnabled', 'vignetteWeight', 'vignetteColor',
    'chromaticEnabled', 'chromaticAmount',
    'grainEnabled', 'grainIntensity', 'grainAnimated',
    'sharpenEnabled', 'sharpenEdgeAmount',
    'ssaoEnabled', 'ssaoRatio',
    'imgExposure', 'imgContrast', 'toneMappingEnabled', 'toneMappingType',
    'fxaaEnabled', 'msaaSamples',
    'glowEnabled', 'glowIntensity',
  ];

  effectInputs.forEach((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.addEventListener('change', handlePostProcessingChange);
      el.addEventListener('input', () => {
        // Update value display for sliders
        const valueId = id.replace('Enabled', 'Value').replace('Threshold', 'ThresholdValue')
          .replace('Weight', 'WeightValue').replace('Amount', 'AmountValue')
          .replace('Intensity', 'IntensityValue').replace('Ratio', 'RatioValue')
          .replace('FocusDistance', 'FocusValue').replace('FStop', 'FStopValue')
          .replace('EdgeAmount', 'EdgeValue').replace('Exposure', 'ExposureValue')
          .replace('Contrast', 'ContrastValue');
        const valueEl = document.getElementById(valueId);
        if (valueEl && el instanceof HTMLInputElement && el.type === 'range') {
          valueEl.textContent = el.value;
        }
      });
    }
  });
}

// ============================================================================
// VR SETTINGS TAB HANDLERS
// ============================================================================

/**
 * Load VR settings into VR tab UI
 */
function loadVRSettings(vr: VRSettingsConfig | undefined): void {
  // Spawn Position
  const vrSpawnX = document.getElementById('vrSpawnX') as HTMLInputElement;
  const vrSpawnY = document.getElementById('vrSpawnY') as HTMLInputElement;
  const vrSpawnZ = document.getElementById('vrSpawnZ') as HTMLInputElement;
  if (vrSpawnX) vrSpawnX.value = String(vr?.spawnPosition?.x ?? 0);
  if (vrSpawnY) vrSpawnY.value = String(vr?.spawnPosition?.y ?? 0);
  if (vrSpawnZ) vrSpawnZ.value = String(vr?.spawnPosition?.z ?? 0);

  // Teleportation
  const vrTeleportEnabled = document.getElementById('vrTeleportEnabled') as HTMLInputElement;
  const vrParabolicRay = document.getElementById('vrParabolicRay') as HTMLInputElement;
  const vrTeleportRotation = document.getElementById('vrTeleportRotation') as HTMLInputElement;
  if (vrTeleportEnabled) vrTeleportEnabled.checked = vr?.teleportation?.enabled ?? true;
  if (vrParabolicRay) vrParabolicRay.checked = vr?.teleportation?.parabolicRayEnabled ?? true;
  if (vrTeleportRotation) vrTeleportRotation.checked = vr?.teleportation?.rotationEnabled ?? true;

  // Movement
  const vrSmoothLocomotion = document.getElementById('vrSmoothLocomotion') as HTMLInputElement;
  const vrSnapTurn = document.getElementById('vrSnapTurn') as HTMLInputElement;
  const vrSnapAngle = document.getElementById('vrSnapAngle') as HTMLInputElement;
  if (vrSmoothLocomotion) vrSmoothLocomotion.checked = vr?.movement?.smoothLocomotion ?? false;
  if (vrSnapTurn) vrSnapTurn.checked = vr?.movement?.snapTurnEnabled ?? true;
  if (vrSnapAngle) vrSnapAngle.value = String(vr?.movement?.snapTurnAngle ?? 45);

  // Update snap angle display
  const vrSnapAngleValue = document.getElementById('vrSnapAngleValue');
  if (vrSnapAngleValue && vrSnapAngle) {
    vrSnapAngleValue.textContent = `${vrSnapAngle.value}°`;
  }

  // Comfort
  const vrVignetteOnMove = document.getElementById('vrVignetteOnMove') as HTMLInputElement;
  if (vrVignetteOnMove) vrVignetteOnMove.checked = vr?.comfort?.vignetteOnMove ?? false;
}

/**
 * Get current VR settings config from UI
 */
function getVRSettingsConfigFromUI(): VRSettingsConfig {
  return {
    spawnPosition: {
      x: parseFloat((document.getElementById('vrSpawnX') as HTMLInputElement)?.value) || 0,
      y: parseFloat((document.getElementById('vrSpawnY') as HTMLInputElement)?.value) || 0,
      z: parseFloat((document.getElementById('vrSpawnZ') as HTMLInputElement)?.value) || 0,
    },
    teleportation: {
      enabled: (document.getElementById('vrTeleportEnabled') as HTMLInputElement)?.checked ?? true,
      parabolicRayEnabled: (document.getElementById('vrParabolicRay') as HTMLInputElement)?.checked ?? true,
      rotationEnabled: (document.getElementById('vrTeleportRotation') as HTMLInputElement)?.checked ?? true,
    },
    movement: {
      smoothLocomotion: (document.getElementById('vrSmoothLocomotion') as HTMLInputElement)?.checked ?? false,
      snapTurnEnabled: (document.getElementById('vrSnapTurn') as HTMLInputElement)?.checked ?? true,
      snapTurnAngle: parseInt((document.getElementById('vrSnapAngle') as HTMLInputElement)?.value) || 45,
    },
    comfort: {
      vignetteOnMove: (document.getElementById('vrVignetteOnMove') as HTMLInputElement)?.checked ?? false,
    },
  };
}

/**
 * Handle VR settings change
 */
function handleVRSettingsChange(): void {
  if (!config?.stages?.[currentStageIndex]) return;

  const vrConfig = getVRSettingsConfigFromUI();
  config.stages[currentStageIndex].vrSettings = vrConfig;
  hasUnsavedChanges = true;
}

/**
 * Initialize VR tab event handlers
 */
function initVRTabHandlers(): void {
  // Setup collapsible sections
  const vrTab = document.getElementById('vr-tab');
  vrTab?.querySelectorAll('.collapsible-header').forEach((header) => {
    header.addEventListener('click', () => {
      const content = header.nextElementSibling as HTMLElement;
      if (content) {
        content.style.display = content.style.display === 'none' ? 'block' : 'none';
        header.classList.toggle('expanded');
      }
    });
  });

  // VR inputs
  const vrInputs = [
    'vrSpawnX', 'vrSpawnY', 'vrSpawnZ',
    'vrTeleportEnabled', 'vrParabolicRay', 'vrTeleportRotation',
    'vrSmoothLocomotion', 'vrSnapTurn', 'vrSnapAngle',
    'vrVignetteOnMove',
  ];

  vrInputs.forEach((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.addEventListener('change', handleVRSettingsChange);
      el.addEventListener('input', () => {
        if (id === 'vrSnapAngle') {
          const valueEl = document.getElementById('vrSnapAngleValue');
          if (valueEl && el instanceof HTMLInputElement) {
            valueEl.textContent = `${el.value}°`;
          }
        }
        handleVRSettingsChange();
      });
    }
  });

  // Set spawn from human scale button
  const setSpawnBtn = document.getElementById('setSpawnFromHumanScale');
  setSpawnBtn?.addEventListener('click', () => {
    // This will be connected to the human scale model position when implemented
    // For now, just show a message
    console.log('Set spawn from human scale - will be implemented with ModelEditorComponent');
  });
}

// Initialize Effects and VR tab handlers when DOM is ready
document.addEventListener('DOMContentLoaded', initEffectsTabHandlers);
document.addEventListener('DOMContentLoaded', initVRTabHandlers);
document.addEventListener('DOMContentLoaded', initModelEditorHandlers);

// =========================================================================
// MODEL EDITOR HANDLERS
// =========================================================================

/**
 * Get URL of the currently editing model
 */
function getEditingModelUrl(): string | null {
  if (editingModelIndex < 0 || !config?.stages?.[currentStageIndex]?.models?.[editingModelIndex]) {
    return null;
  }
  return config.stages[currentStageIndex].models[editingModelIndex].url || null;
}

/**
 * Initialize model editor tab handlers
 */
function initModelEditorHandlers(): void {
  // Close Model Editor button
  const closeBtn = document.getElementById('closeModelEditor');
  closeBtn?.addEventListener('click', () => {
    hideModelEditor();
  });

  // Gizmo mode selector
  const gizmoSelector = document.getElementById('gizmoModeSelector');
  gizmoSelector?.querySelectorAll('button').forEach((btn) => {
    btn.addEventListener('click', () => {
      const mode = btn.getAttribute('data-mode') as 'none' | 'position' | 'rotation' | 'scale';

      // Update active state
      gizmoSelector.querySelectorAll('button').forEach((b) => {
        b.classList.remove('active');
        (b as HTMLElement).style.background = 'transparent';
      });
      btn.classList.add('active');
      btn.style.background = 'white';

      // Update gizmo mode in preview
      if (editorPreview) {
        editorPreview.enableGizmos(mode);
        const modelUrl = getEditingModelUrl();
        if (mode !== 'none' && modelUrl) {
          editorPreview.attachGizmoToModel(modelUrl);
        }
      }
    });
  });

  // Human Scale Reference toggle (inside editor tools)
  const humanScaleToggle = document.getElementById('showHumanScale') as HTMLInputElement;
  humanScaleToggle?.addEventListener('change', () => {
    if (editorPreview) {
      if (humanScaleToggle.checked) {
        editorPreview.showHumanScaleReference();
        editorPreview.showGroundPlane(10, 10); // Show ground plane for spatial reference
      } else {
        editorPreview.hideHumanScaleReference();
        editorPreview.hideGroundPlane();
      }
      // Sync with global toggle
      const globalToggle = document.getElementById('showHumanScaleGlobal') as HTMLInputElement;
      if (globalToggle) globalToggle.checked = humanScaleToggle.checked;
    }
  });

  // Human Scale Reference toggle (global - always visible)
  const humanScaleGlobalToggle = document.getElementById('showHumanScaleGlobal') as HTMLInputElement;
  humanScaleGlobalToggle?.addEventListener('change', () => {
    if (editorPreview) {
      if (humanScaleGlobalToggle.checked) {
        editorPreview.showHumanScaleReference();
        editorPreview.showGroundPlane(10, 10); // Show ground plane for spatial reference
      } else {
        editorPreview.hideHumanScaleReference();
        editorPreview.hideGroundPlane();
      }
      // Sync with editor tools toggle
      if (humanScaleToggle) humanScaleToggle.checked = humanScaleGlobalToggle.checked;
    }
  });

  // Show Skeleton toggle
  const skeletonToggle = document.getElementById('showSkeleton') as HTMLInputElement;
  skeletonToggle?.addEventListener('change', () => {
    const modelUrl = getEditingModelUrl();
    if (editorPreview && modelUrl) {
      if (skeletonToggle.checked) {
        editorPreview.showModelSkeleton(modelUrl);
      } else {
        editorPreview.hideModelSkeleton(modelUrl);
      }
    }
  });

  // Animation controls
  setupAnimationControls();

  // Connect transform callback - updates config and UI when gizmo is used
  if (editorPreview) {
    editorPreview.onModelTransformEnd = (transform) => {
      console.log('[ModelEditor] onModelTransformEnd fired, editingModelIndex:', editingModelIndex, 'transform:', transform);

      if (editingModelIndex >= 0 && config?.stages?.[currentStageIndex]?.models?.[editingModelIndex]) {
        const modelConfig = config.stages[currentStageIndex].models[editingModelIndex];
        modelConfig.position = transform.position;
        modelConfig.rotation = transform.rotation;
        modelConfig.scale = transform.scale;
        hasUnsavedChanges = true;

        // Also update the UI inputs to reflect the gizmo changes
        console.log('[ModelEditor] Calling updateModelCardInputs for index:', editingModelIndex);
        updateModelCardInputs(editingModelIndex, transform);

        console.log('[ModelEditor] Transform saved from gizmo:', transform);
      } else {
        console.warn('[ModelEditor] Cannot save transform - editingModelIndex:', editingModelIndex, 'config exists:', !!config?.stages?.[currentStageIndex]?.models?.[editingModelIndex]);
      }
    };

    // Plane transform callback - updates config and UI when gizmo is used on plane
    editorPreview.onPlaneTransformEnd = (planeIndex, transform) => {
      console.log('[PlaneEditor] onPlaneTransformEnd fired, planeIndex:', planeIndex, 'transform:', transform);

      if (planeIndex >= 0 && config?.stages?.[currentStageIndex]?.planes?.[planeIndex]) {
        const planeConfig = config.stages[currentStageIndex].planes[planeIndex];
        planeConfig.position = transform.position;
        planeConfig.rotation = transform.rotation;
        planeConfig.scale = transform.scale;
        hasUnsavedChanges = true;

        // Also update the UI inputs to reflect the gizmo changes
        console.log('[PlaneEditor] Calling updatePlaneCardInputs for index:', planeIndex);
        updatePlaneCardInputs(planeIndex, transform);

        console.log('[PlaneEditor] Transform saved from gizmo:', transform);
      } else {
        console.warn('[PlaneEditor] Cannot save transform - planeIndex:', planeIndex, 'config exists:', !!config?.stages?.[currentStageIndex]?.planes?.[planeIndex]);
      }
    };

    editorPreview.onHumanScalePositionChanged = (position) => {
      // Update VR spawn position inputs
      const xInput = document.getElementById('vrSpawnX') as HTMLInputElement;
      const yInput = document.getElementById('vrSpawnY') as HTMLInputElement;
      const zInput = document.getElementById('vrSpawnZ') as HTMLInputElement;

      if (xInput) xInput.value = position.x.toFixed(2);
      if (yInput) yInput.value = position.y.toFixed(2);
      if (zInput) zInput.value = position.z.toFixed(2);

      // Also save to config
      handleVRSettingsChange();
    };

    editorPreview.onAnimationFrameUpdate = (currentFrame, totalFrames) => {
      const currentFrameEl = document.getElementById('currentFrame');
      const totalFramesEl = document.getElementById('totalFrames');
      const timelineEl = document.getElementById('animTimeline') as HTMLInputElement;

      if (currentFrameEl) currentFrameEl.textContent = String(currentFrame);
      if (totalFramesEl) totalFramesEl.textContent = String(totalFrames);
      if (timelineEl && totalFrames > 0) {
        timelineEl.value = String((currentFrame / totalFrames) * 100);
      }
    };
  }

}

/**
 * Update model card inputs after gizmo transform
 */
function updateModelCardInputs(modelIndex: number, transform: { position: { x: number; y: number; z: number }; rotation: { x: number; y: number; z: number }; scale: { x: number; y: number; z: number } }): void {
  console.log('[updateModelCardInputs] Updating inputs for modelIndex:', modelIndex);

  // Position inputs
  const posXInput = document.querySelector(`.model-card[data-index="${modelIndex}"] input[data-field="position.x"]`) as HTMLInputElement;
  const posYInput = document.querySelector(`.model-card[data-index="${modelIndex}"] input[data-field="position.y"]`) as HTMLInputElement;
  const posZInput = document.querySelector(`.model-card[data-index="${modelIndex}"] input[data-field="position.z"]`) as HTMLInputElement;

  console.log('[updateModelCardInputs] Found position inputs:', { x: !!posXInput, y: !!posYInput, z: !!posZInput });

  if (posXInput) posXInput.value = transform.position.x.toFixed(2);
  if (posYInput) posYInput.value = transform.position.y.toFixed(2);
  if (posZInput) posZInput.value = transform.position.z.toFixed(2);

  // Rotation inputs (convert radians to degrees)
  const rotXInput = document.querySelector(`.model-card[data-index="${modelIndex}"] input[data-field="rotation.x"]`) as HTMLInputElement;
  const rotYInput = document.querySelector(`.model-card[data-index="${modelIndex}"] input[data-field="rotation.y"]`) as HTMLInputElement;
  const rotZInput = document.querySelector(`.model-card[data-index="${modelIndex}"] input[data-field="rotation.z"]`) as HTMLInputElement;

  console.log('[updateModelCardInputs] Found rotation inputs:', { x: !!rotXInput, y: !!rotYInput, z: !!rotZInput });

  if (rotXInput) rotXInput.value = String(Math.round(radiansToDegrees(transform.rotation.x)));
  if (rotYInput) rotYInput.value = String(Math.round(radiansToDegrees(transform.rotation.y)));
  if (rotZInput) rotZInput.value = String(Math.round(radiansToDegrees(transform.rotation.z)));

  // Scale (use X component for uniform scale display)
  const scaleSlider = document.querySelector(`.model-scale-slider[data-index="${modelIndex}"]`) as HTMLInputElement;
  const scaleInput = document.querySelector(`.model-scale-input[data-index="${modelIndex}"]`) as HTMLInputElement;

  console.log('[updateModelCardInputs] Found scale inputs:', { slider: !!scaleSlider, input: !!scaleInput });

  const scaleValue = transform.scale.x.toFixed(2);
  if (scaleSlider) scaleSlider.value = scaleValue;
  if (scaleInput) scaleInput.value = scaleValue;

  console.log('[updateModelCardInputs] Values set - pos:', transform.position, 'rot:', transform.rotation, 'scale:', scaleValue);
}

/**
 * Setup animation transport controls
 */
function setupAnimationControls(): void {
  // Play button
  document.getElementById('animPlay')?.addEventListener('click', () => {
    const animSelect = document.getElementById('animationSelect') as HTMLSelectElement;
    const loopToggle = document.getElementById('animLoop') as HTMLInputElement;

    if (animSelect?.value && editorPreview) {
      editorPreview.playAnimation(animSelect.value, loopToggle?.checked ?? true);
    }
  });

  // Pause button
  document.getElementById('animPause')?.addEventListener('click', () => {
    editorPreview?.pauseAnimation();
  });

  // Stop button
  document.getElementById('animStop')?.addEventListener('click', () => {
    editorPreview?.stopAnimation();
  });

  // Speed slider
  const speedSlider = document.getElementById('animSpeed') as HTMLInputElement;
  speedSlider?.addEventListener('input', () => {
    const speed = parseFloat(speedSlider.value);
    const speedValueEl = document.getElementById('animSpeedValue');
    if (speedValueEl) speedValueEl.textContent = `${speed.toFixed(1)}x`;
    editorPreview?.setAnimationSpeed(speed);
  });

  // Timeline slider
  const timelineSlider = document.getElementById('animTimeline') as HTMLInputElement;
  timelineSlider?.addEventListener('input', () => {
    if (!editorPreview) return;
    const state = editorPreview.getAnimationState();
    if (state) {
      const frame = (parseFloat(timelineSlider.value) / 100) * state.totalFrames;
      editorPreview.seekAnimation(frame);
    }
  });

  // Animation select
  document.getElementById('animationSelect')?.addEventListener('change', (e) => {
    const select = e.target as HTMLSelectElement;
    const loopToggle = document.getElementById('animLoop') as HTMLInputElement;
    if (select.value && editorPreview) {
      editorPreview.playAnimation(select.value, loopToggle?.checked ?? true);
    }
  });
}

/**
 * Create a mesh toggle row element (safe DOM construction)
 */
function createMeshToggleRow(meshName: string, isVisible: boolean): HTMLLabelElement {
  const row = document.createElement('label');
  row.className = 'toggle-row';
  row.style.cssText = 'display: flex; align-items: center; justify-content: space-between; padding: 6px 0; font-size: 13px;';

  const span = document.createElement('span');
  span.style.cssText = 'overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 70%;';
  span.title = meshName;
  span.textContent = meshName; // Safe: uses textContent

  const checkbox = document.createElement('input');
  checkbox.type = 'checkbox';
  checkbox.className = 'ios-toggle mesh-toggle';
  checkbox.dataset.mesh = meshName;
  checkbox.checked = isVisible;

  row.appendChild(span);
  row.appendChild(checkbox);

  return row;
}

/**
 * Update model editor UI when a model is selected for editing
 * @param modelIndex - Index of the model in the current stage's models array
 */
function updateModelEditorUI(modelIndex: number): void {
  const modelUrl = config?.stages?.[currentStageIndex]?.models?.[modelIndex]?.url;

  const statsPanel = document.getElementById('modelStatsPanel');
  const animPanel = document.getElementById('animationControlsPanel');
  const meshPanel = document.getElementById('meshVisibilityPanel');
  const skeletonToggle = document.getElementById('showSkeleton') as HTMLInputElement;

  if (!modelUrl || !editorPreview) {
    // Hide all panels when no model selected
    if (statsPanel) statsPanel.style.display = 'none';
    if (animPanel) animPanel.style.display = 'none';
    if (meshPanel) meshPanel.style.display = 'none';
    return;
  }

  // Show stats panel and populate
  if (statsPanel) {
    statsPanel.style.display = 'block';
    const stats = editorPreview.getModelStats(modelUrl);
    if (stats) {
      const meshesEl = document.getElementById('statMeshes');
      const verticesEl = document.getElementById('statVertices');
      const facesEl = document.getElementById('statFaces');
      const skeletonEl = document.getElementById('statSkeleton');
      const animationsEl = document.getElementById('statAnimations');
      const boundingBoxEl = document.getElementById('statBoundingBox');

      if (meshesEl) meshesEl.textContent = String(stats.totalMeshes);
      if (verticesEl) verticesEl.textContent = stats.totalVertices.toLocaleString();
      if (facesEl) facesEl.textContent = stats.totalFaces.toLocaleString();
      if (skeletonEl) skeletonEl.textContent = stats.hasSkeleton ? `Yes (${stats.boneCount} bones)` : 'No';
      if (animationsEl) animationsEl.textContent = String(stats.animationCount);
      if (boundingBoxEl) {
        const size = stats.boundingBoxSize;
        boundingBoxEl.textContent = `${size.x.toFixed(2)} x ${size.y.toFixed(2)} x ${size.z.toFixed(2)}`;
      }

      // Enable/disable skeleton toggle based on whether model has skeleton
      if (skeletonToggle) {
        skeletonToggle.disabled = !stats.hasSkeleton;
      }
    }
  }

  // Show animation panel if model has animations
  const animations = editorPreview.getModelAnimations(modelUrl);
  if (animPanel) {
    animPanel.style.display = animations.length > 0 ? 'block' : 'none';

    // Populate animation select using safe DOM methods
    const animSelect = document.getElementById('animationSelect') as HTMLSelectElement;
    if (animSelect) {
      // Clear existing options
      while (animSelect.firstChild) {
        animSelect.removeChild(animSelect.firstChild);
      }

      // Add default option
      const defaultOption = document.createElement('option');
      defaultOption.value = '';
      defaultOption.textContent = '-- Select Animation --';
      animSelect.appendChild(defaultOption);

      // Add animation options
      animations.forEach((anim) => {
        const option = document.createElement('option');
        option.value = anim.name;
        option.textContent = `${anim.name} (${anim.duration.toFixed(1)}s)`;
        animSelect.appendChild(option);
      });
    }
  }

  // Show mesh visibility panel
  if (meshPanel) {
    meshPanel.style.display = 'block';
    const meshList = document.getElementById('meshList');
    if (meshList) {
      const meshes = editorPreview.getModelMeshList(modelUrl);

      // Clear existing content
      while (meshList.firstChild) {
        meshList.removeChild(meshList.firstChild);
      }

      if (meshes.length > 0) {
        meshes.forEach((mesh) => {
          const row = createMeshToggleRow(mesh.name, mesh.isVisible);
          meshList.appendChild(row);
        });

        // Add change handlers for mesh toggles
        meshList.querySelectorAll('.mesh-toggle').forEach((toggle) => {
          toggle.addEventListener('change', (e) => {
            const checkbox = e.target as HTMLInputElement;
            const meshName = checkbox.dataset.mesh;
            const currentModelUrl = getEditingModelUrl();
            if (meshName && currentModelUrl) {
              editorPreview?.setModelMeshVisibility(currentModelUrl, meshName, checkbox.checked);
            }
          });
        });
      } else {
        const emptyMsg = document.createElement('p');
        emptyMsg.style.cssText = 'font-size: 13px; color: var(--ios-gray); text-align: center; padding: 12px;';
        emptyMsg.textContent = 'No meshes found';
        meshList.appendChild(emptyMsg);
      }
    }
  }
}

// Initialize on load
init();
